// src/modules/captazap/components/SupervisaoEquipeCaptaZap.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LogsModalLead from "../../../Components/LogsModalLead";
import AgendamentosModal from "../../../Components/AgendamentosModal";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  BadgeCheck,
  BellRing,
  Bot,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  CreditCard,
  Eye,
  Globe2,
  Headphones,
  Inbox,
  Megaphone,
  MessageCircle,
  MousePointerClick,
  Printer,
  RefreshCw,
  Search,
  ShieldCheck,
  TrendingUp,
  UserRound,
  Users,
  X,
} from "lucide-react";

const TEMPO_ALERTA_SEM_RESPOSTA_MS = 30 * 60 * 1000;
const TEMPO_CRITICO_SEM_RESPOSTA_MS = 2 * 60 * 60 * 1000;
const TEMPO_REAL_LINHA_AMARELA_MS = 5 * 60 * 1000;
const AUTO_REFRESH_SUPERVISAO_MS = 60 * 1000;
const SUPERVISAO_CACHE_AGENDAMENTOS_MS = 5 * 60 * 1000;
const SUPERVISAO_CACHE_LEADS_MS = 20 * 60 * 1000;
const SUPERVISAO_CACHE_CONVERSAS_MS = 60 * 1000;
const SUPERVISAO_CACHE_ORIGEM_ADS_MS = 30 * 60 * 1000;
const SUPERVISAO_MAX_ORIGEM_ADS_LOOKUPS = 48;
const SUPERVISAO_ORIGEM_ADS_LOTE = 6;
const SUPERVISAO_REQUEST_TIMEOUT_MS = 60000;
const LINHAS_POR_PAGINA_PADRAO = 12;
const OPCOES_LINHAS_POR_PAGINA = [12, 24, 48, 96];

const API_BASE_RAW = String(
  import.meta.env?.VITE_API_BASE_URL ||
    import.meta.env?.VITE_API_URL ||
    import.meta.env?.VITE_BACKEND_URL ||
    "",
).trim();

const API_BASE =
  API_BASE_RAW.replace(/\/+$/, "") || getApiBasePadraoSupervisao();

function isAbortErrorSupervisao(error) {
  const message = String(
    error?.message || error?.reason?.message || error?.reason || error || "",
  ).toLowerCase();
  const name = String(error?.name || "").toLowerCase();
  const code = String(error?.code || "").toLowerCase();

  return (
    name === "aborterror" ||
    code === "err_canceled" ||
    code === "abort_err" ||
    message.includes("aborted") ||
    message.includes("signal is aborted") ||
    message.includes("operation was aborted") ||
    message.includes("the user aborted")
  );
}

function getApiBasePadraoSupervisao() {
  if (typeof window === "undefined") return "";

  const protocol = window.location?.protocol || "https:";
  const hostname = window.location?.hostname || "";

  if (hostname === "app.captacrm.com.br") {
    return "https://api.captacrm.com.br";
  }

  if (hostname.startsWith("app.")) {
    return `${protocol}//${hostname.replace(/^app\./, "api.")}`;
  }

  return "";
}

const FILTROS_OPERACIONAIS = [
  { key: "todos", label: "Todos" },
  { key: "agenda_hoje", label: "Agenda hoje" },
  { key: "agendados", label: "Agendados" },
  { key: "agendados_amanha", label: "Próx. agenda" },
  { key: "followup", label: "Follow-up" },
  { key: "fila", label: "Fila" },
  { key: "ads_facebook", label: "Facebook" },
  { key: "ads_google", label: "Google" },
  { key: "critico", label: "Críticos" },
  { key: "abertos_dia", label: "Abertos no dia" },
];

function getHojeISO() {
  const data = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
}

function addDiasISO(dataISO, dias) {
  const [ano, mes, dia] = String(dataISO || getHojeISO())
    .split("-")
    .map(Number);
  const data = new Date(ano, (mes || 1) - 1, dia || 1);
  data.setDate(data.getDate() + Number(dias || 0));
  const pad = (n) => String(n).padStart(2, "0");
  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
}

function getDataAgendaAmanhaOperacionalISO(baseISO = getHojeISO()) {
  // A clínica trabalha sábado, mas domingo não deve aparecer como "agenda amanhã".
  // Se o próximo dia cair em domingo, a supervisão mostra segunda-feira.
  let proxima = addDiasISO(baseISO, 1);
  const [ano, mes, dia] = String(proxima).split("-").map(Number);
  const data = new Date(ano, (mes || 1) - 1, dia || 1);

  if (data.getDay() === 0) {
    proxima = addDiasISO(proxima, 1);
  }

  return proxima;
}

function formatarDiaAgendaOperacionalSupervisao(dataISO = "") {
  const [ano, mes, dia] = String(dataISO || "")
    .split("-")
    .map(Number);
  if (!ano || !mes || !dia) return "próximo dia";

  const data = new Date(ano, mes - 1, dia);
  const nomes = [
    "domingo",
    "segunda-feira",
    "terça-feira",
    "quarta-feira",
    "quinta-feira",
    "sexta-feira",
    "sábado",
  ];

  return nomes[data.getDay()] || "próximo dia";
}

function getPrimeiroDiaMesISO(base = getHojeISO()) {
  const [ano, mes] = String(base || getHojeISO())
    .split("-")
    .map(Number);
  const pad = (n) => String(n).padStart(2, "0");
  return `${ano}-${pad(mes || 1)}-01`;
}

function getPeriodoPresetSupervisao(modo = "hoje") {
  const hoje = getHojeISO();

  if (modo === "ontem") {
    const ontem = addDiasISO(hoje, -1);
    return { inicio: ontem, fim: ontem };
  }

  if (modo === "7d") {
    return { inicio: addDiasISO(hoje, -6), fim: hoje };
  }

  if (modo === "30d") {
    return { inicio: addDiasISO(hoje, -29), fim: hoje };
  }

  if (modo === "mes") {
    return { inicio: getPrimeiroDiaMesISO(hoje), fim: hoje };
  }

  return { inicio: hoje, fim: hoje };
}

function getDatasEntreSupervisao(inicio, fim, limite = 31) {
  const ini = String(inicio || getHojeISO()).slice(0, 10);
  const end = String(fim || ini).slice(0, 10);
  const [ay, am, ad] = ini.split("-").map(Number);
  const [by, bm, bd] = end.split("-").map(Number);
  const a = new Date(ay, (am || 1) - 1, ad || 1);
  const b = new Date(by, (bm || 1) - 1, bd || 1);

  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()))
    return [getHojeISO()];

  const start = a <= b ? a : b;
  const stop = a <= b ? b : a;
  const out = [];

  for (
    let d = new Date(start);
    d <= stop && out.length < limite;
    d.setDate(d.getDate() + 1)
  ) {
    const pad = (n) => String(n).padStart(2, "0");
    out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
  }

  return out;
}

function formatarPeriodoSupervisao(inicio, fim) {
  const i = String(inicio || "").slice(0, 10);
  const f = String(fim || i).slice(0, 10);
  if (!i) return "Período não definido";
  if (i === f) return formatarDataCurta(i);
  return `${formatarDataCurta(i)} até ${formatarDataCurta(f)}`;
}

const PERIODO_PRESETS = [
  { key: "hoje", label: "Hoje" },
  { key: "ontem", label: "Ontem" },
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
  { key: "mes", label: "Mês" },
  { key: "personalizado", label: "Entre datas" },
];

function somarResumoSupervisao(a = {}, b = {}) {
  const keys = [
    "leads_hoje",
    "conversas_abertas_hoje",
    "chamados_abertos",
    "chamados_fechados",
    "em_atendimento",
    "fila",
    "devendo_resposta",
    "sem_retorno",
    "followups_pendentes",
    "followups_vencidos",
    "agendamentos_feitos",
    "agendamentos_hoje",
    "reabertos",
    "anuncios_recebidos",
    "mensagens_recebidas",
    "mensagens_enviadas",
  ];
  const out = { ...a };
  const metricasMensagensB = normalizarMetricasMensagensSupervisao(b);

  for (const key of keys) {
    let valorB = b[key];

    if (key === "mensagens_recebidas" && metricasMensagensB.hasRecebidas) {
      valorB = metricasMensagensB.recebidas;
      out._mensagens_recebidas_oficial = true;
    }

    if (key === "mensagens_enviadas" && metricasMensagensB.hasEnviadas) {
      valorB = metricasMensagensB.enviadas;
      out._mensagens_enviadas_oficial = true;
    }

    out[key] = getNumero(a[key]) + getNumero(valorB);
  }

  const baseAgenda = getNumero(
    out.leads_hoje || out.chamados_abertos || out.conversas_abertas_hoje,
  );
  const agendamentos = getNumero(
    out.agendamentos_feitos ?? out.agendamentos_hoje,
  );
  out.taxa_agendamento =
    baseAgenda > 0 ? Math.round((agendamentos / baseAgenda) * 1000) / 10 : 0;

  return out;
}

function combinarPorChaveSupervisao(listas = [], getKey, merge) {
  const map = new Map();

  for (const lista of listas) {
    for (const item of lista || []) {
      const key = getKey(item);
      if (!key) continue;
      map.set(key, map.has(key) ? merge(map.get(key), item) : { ...item });
    }
  }

  return [...map.values()];
}

function combinarSupervisaoPeriodo(
  respostas = [],
  { inicio, fim, empresaId } = {},
) {
  const validas = respostas.filter(Boolean);

  const extrairAdsRoiResposta = (item) =>
    item?.ads_roi || item?.adsRoi || item?.resumo?.ads_roi || null;

  const inicioEsperado = inicio ? `${inicio} 00:00:00` : null;
  const fimExclusivoEsperado = fim ? `${addDiasISO(fim, 1)} 00:00:00` : null;

  const adsRoiConfiaveis = validas
    .map(extrairAdsRoiResposta)
    .filter((roi) => roi?.confiavel);

  // Para período personalizado, não podemos pegar o primeiro ads_roi diário.
  // O ROI oficial precisa declarar a janela completa selecionada; caso contrário,
  // o card pode mostrar apenas um recorte do período, como 5 anúncios em vez de 571.
  const adsRoiPeriodo =
    adsRoiConfiaveis.find((roi) => {
      if (!inicioEsperado || !fimExclusivoEsperado) return true;
      return (
        String(roi.periodo_inicio || "").slice(0, 19) === inicioEsperado &&
        String(roi.periodo_fim_exclusivo || "").slice(0, 19) ===
          fimExclusivoEsperado
      );
    }) || null;
  const resumo = validas.reduce(
    (acc, item) => somarResumoSupervisao(acc, item.resumo || {}),
    {},
  );
  if (adsRoiPeriodo) resumo.ads_roi = adsRoiPeriodo;

  const captadores = combinarPorChaveSupervisao(
    validas.map((item) => item.captadores || []),
    (item) =>
      String(
        item.responsavel_id ||
          item.usuario_id ||
          item.nome ||
          item.responsavel_nome ||
          "sem-responsavel",
      ),
    (a, b) => ({
      ...a,
      chamados_abertos:
        getNumero(a.chamados_abertos) + getNumero(b.chamados_abertos),
      chamados_fechados:
        getNumero(a.chamados_fechados) + getNumero(b.chamados_fechados),
      monitorados: getNumero(a.monitorados) + getNumero(b.monitorados),
      devendo_resposta:
        getNumero(a.devendo_resposta) + getNumero(b.devendo_resposta),
      sem_retorno: getNumero(a.sem_retorno) + getNumero(b.sem_retorno),
      followups_pendentes:
        getNumero(a.followups_pendentes) + getNumero(b.followups_pendentes),
      followups_vencidos:
        getNumero(a.followups_vencidos) + getNumero(b.followups_vencidos),
      agendamentos_feitos:
        getNumero(a.agendamentos_feitos) + getNumero(b.agendamentos_feitos),
      mensagens_recebidas:
        getNumero(a.mensagens_recebidas) + getNumero(b.mensagens_recebidas),
      mensagens_enviadas:
        getNumero(a.mensagens_enviadas) + getNumero(b.mensagens_enviadas),
      tempo_medio_resposta_min: Math.round(
        (getNumero(a.tempo_medio_resposta_min ?? a.espera_media_minutos) +
          getNumero(b.tempo_medio_resposta_min ?? b.espera_media_minutos)) /
          2,
      ),
    }),
  ).map((item) => {
    const base = getNumero(item.chamados_abertos || item.monitorados);
    const ag = getNumero(item.agendamentos_feitos);
    return {
      ...item,
      taxa_agendamento: base > 0 ? Math.round((ag / base) * 1000) / 10 : 0,
    };
  });

  const anuncios = combinarPorChaveSupervisao(
    validas.map((item) => item.anuncios || []),
    (item) =>
      String(
        item.source_id ||
          item.sourceId ||
          item.titulo ||
          item.campanha ||
          "sem-anuncio",
      ),
    (a, b) => ({
      ...a,
      interacoes: getNumero(a.interacoes) + getNumero(b.interacoes),
      telefones: getNumero(a.telefones) + getNumero(b.telefones),
      leads: getNumero(a.leads) + getNumero(b.leads),
      conversas: getNumero(a.conversas) + getNumero(b.conversas),
      entradas: getNumero(a.entradas) + getNumero(b.entradas),
      agendamentos: getNumero(a.agendamentos) + getNumero(b.agendamentos),
      agendamentos_feitos:
        getNumero(a.agendamentos_feitos) + getNumero(b.agendamentos_feitos),
      ultima_origem_em:
        parseTimestamp(b.ultima_origem_em || b.criado_em) >
        parseTimestamp(a.ultima_origem_em || a.criado_em)
          ? b.ultima_origem_em || b.criado_em
          : a.ultima_origem_em || a.criado_em,
    }),
  );

  const casos = validas
    .flatMap((item) => item.casos || [])
    .sort(
      (a, b) =>
        parseTimestamp(b.ultima_mensagem?.datahora || b.datahora) -
        parseTimestamp(a.ultima_mensagem?.datahora || a.datahora),
    )
    .slice(0, 220);

  const juntarCasosFiltroPeriodo = (...keys) => {
    const temListaBackend = validas.some((item) =>
      keys.some((key) => Array.isArray(item?.[key])),
    );

    if (!temListaBackend) return undefined;

    return validas.flatMap((item) => {
      const lista = keys.map((key) => item?.[key]).find(Array.isArray);
      return Array.isArray(lista) ? lista : [];
    });
  };

  const casosFila = juntarCasosFiltroPeriodo("casos_fila", "casosFila");
  const casosFollowup = juntarCasosFiltroPeriodo(
    "casos_followup",
    "casosFollowup",
  );
  const casosAgendados = juntarCasosFiltroPeriodo(
    "casos_agendados",
    "casosAgendados",
  );
  const casosAgendaHoje = juntarCasosFiltroPeriodo(
    "casos_agenda_hoje",
    "casosAgendaHoje",
  );
  const casosAbertosDia = juntarCasosFiltroPeriodo(
    "casos_abertos_dia",
    "casosAbertosDia",
  );
  const casosReabertos = juntarCasosFiltroPeriodo(
    "casos_reabertos",
    "casosReabertos",
  );
  const casosAnuncios = juntarCasosFiltroPeriodo(
    "casos_anuncios",
    "casosAnuncios",
    "conversas_anuncios",
    "conversasAnuncios",
  );

  return {
    ok: true,
    empresa_id: empresaId,
    periodo: {
      data: fim,
      inicio,
      fim,
      label: formatarPeriodoSupervisao(inicio, fim),
    },
    resumo,
    ads_roi: adsRoiPeriodo,
    adsRoi: adsRoiPeriodo,
    captadores,
    anuncios,
    casos,
    casos_fila: casosFila,
    casosFila,
    casos_followup: casosFollowup,
    casosFollowup,
    casos_agendados: casosAgendados,
    casosAgendados,
    casos_agenda_hoje: casosAgendaHoje,
    casosAgendaHoje,
    casos_abertos_dia: casosAbertosDia,
    casosAbertosDia,
    casos_reabertos: casosReabertos,
    casosReabertos,
    casos_anuncios: casosAnuncios,
    casosAnuncios,
    conversas_anuncios: casosAnuncios,
    conversasAnuncios: casosAnuncios,
    meta: { combinado_frontend: true, dias: validas.length },
  };
}

export default function SupervisaoEquipeCaptaZap({
  isOpen = false,
  empresaId = null,
  empresaNome = "Empresa",
  conversasEquipe = [],
  filaDeEspera = [],
  followUpConversas = [],
  fechadosConversas = [],
  usuarios = [],
  usuarioLogado = null,
  metricasAgendamentoCaptazap = null,
  totalAgendadosCaptazap = null,
  leadsAgendadasCaptazap = [],
  metricasMensagensZapi = null,
  metricasZapi = null,
  estatisticasZapi = null,
  onClose,
  onAtualizar,
  onSelecionarConversa,
  onVisualizarConversa,
}) {
  const [dataReferencia, setDataReferencia] = useState(getHojeISO);
  const [periodoModo, setPeriodoModo] = useState("hoje");
  const [dataInicio, setDataInicio] = useState(getHojeISO);
  const [dataFim, setDataFim] = useState(getHojeISO);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos");
  const [captadorFiltro, setCaptadorFiltro] = useState("todos");
  const [linhasPorPagina, setLinhasPorPagina] = useState(
    LINHAS_POR_PAGINA_PADRAO,
  );
  const [pagina, setPagina] = useState(1);
  const [abaAnalitica, setAbaAnalitica] = useState("captadores");
  const [casoSelecionado, setCasoSelecionado] = useState(null);
  const [leadHistoricoSelecionada, setLeadHistoricoSelecionada] =
    useState(null);
  const [agendaSelecionada, setAgendaSelecionada] = useState(null);
  const [agendaFollowupSelecionado, setAgendaFollowupSelecionado] =
    useState(null);
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState(null);
  const [dadosSupervisao, setDadosSupervisao] = useState(null);
  const [agendamentosEmpresa, setAgendamentosEmpresa] = useState([]);
  const [leadsEmpresaSupervisao, setLeadsEmpresaSupervisao] = useState([]);
  const [chamadosOperacionaisSupervisao, setChamadosOperacionaisSupervisao] =
    useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [adsOrigemMap, setAdsOrigemMap] = useState({});
  const [anunciosAberto, setAnunciosAberto] = useState(false);
  const [adsRoiCarregado, setAdsRoiCarregado] = useState(false);
  const [loadingAdsRoi, setLoadingAdsRoi] = useState(false);
  const [tempoRealAgora, setTempoRealAgora] = useState(() => Date.now());
  const [cardsRecolhidos, setCardsRecolhidos] = useState(false);
  const [adsRoiFiltroTabela, setAdsRoiFiltroTabela] = useState(null);
  const controleCarregamentoSupervisaoRef = useRef({
    seq: 0,
    emExecucao: false,
    caches: {},
  });
  const origemAdsLegacyCacheRef = useRef({
    itens: {},
    emExecucao: new Set(),
  });

  useEffect(() => {
    if (periodoModo === "personalizado") return;

    const periodo = getPeriodoPresetSupervisao(periodoModo);
    setDataInicio(periodo.inicio);
    setDataFim(periodo.fim);
    setDataReferencia(periodo.fim);
  }, [periodoModo]);

  useEffect(() => {
    if (!isOpen) return undefined;

    setTempoRealAgora(Date.now());

    const timer = window.setInterval(() => {
      setTempoRealAgora(Date.now());
    }, 10000);

    return () => window.clearInterval(timer);
  }, [isOpen]);

  const periodoLabel = useMemo(
    () => formatarPeriodoSupervisao(dataInicio, dataFim),
    [dataInicio, dataFim],
  );

  const handleSelecionarAdsRoiCard = useCallback((tipo) => {
    setAdsRoiFiltroTabela(tipo || null);
    setFiltro("todos");
    setPagina(1);
  }, []);

  const handleLimparFiltroAdsRoiTabela = useCallback(() => {
    setAdsRoiFiltroTabela(null);
    setPagina(1);
  }, []);

  const empresaIdEfetiva = useMemo(() => {
    const empresaAtivaLocal = getEmpresaIdLocalStorage();

    const empresaDasConversas =
      conversasEquipe?.[0]?.empresa_id ||
      conversasEquipe?.[0]?.empresaId ||
      filaDeEspera?.[0]?.empresa_id ||
      filaDeEspera?.[0]?.empresaId ||
      followUpConversas?.[0]?.empresa_id ||
      followUpConversas?.[0]?.empresaId ||
      fechadosConversas?.[0]?.empresa_id ||
      fechadosConversas?.[0]?.empresaId ||
      null;

    return (
      Number(
        empresaAtivaLocal ||
          empresaDasConversas ||
          empresaId ||
          usuarioLogado?.empresa_id ||
          usuarioLogado?.empresaId ||
          0,
      ) || null
    );
  }, [
    empresaId,
    usuarioLogado,
    conversasEquipe,
    filaDeEspera,
    followUpConversas,
    fechadosConversas,
  ]);

  const carregarSupervisao = useCallback(
    async (opcoes = {}) => {
      if (!empresaIdEfetiva || !isOpen) return;

      const controle = controleCarregamentoSupervisaoRef.current;
      const forcar = Boolean(opcoes?.forcar);
      const silencioso = Boolean(opcoes?.silencioso);

      // Evita várias atualizações simultâneas quando o usuário clica em atualizar
      // enquanto o auto-refresh ainda está processando. Em modo forçado, uma nova
      // carga pode começar, mas a resposta antiga não sobrescreve a mais recente.
      if (controle.emExecucao && !forcar) return;

      const seq = (controle.seq || 0) + 1;
      controle.seq = seq;
      controle.emExecucao = true;

      const datasPeriodo = getDatasEntreSupervisao(dataInicio, dataFim, 31);
      const inicioPeriodo = datasPeriodo[0] || dataInicio || getHojeISO();
      const fimPeriodo =
        datasPeriodo[datasPeriodo.length - 1] || dataFim || inicioPeriodo;
      const agoraCarga = Date.now();

      if (!silencioso) setLoading(true);
      setErro("");

      const buscarComCache = async ({
        nome,
        chave,
        ttlMs,
        buscar,
        fallback,
      }) => {
        const cacheAtual = controle.caches?.[nome];
        const cacheValido =
          !forcar &&
          cacheAtual &&
          cacheAtual.chave === chave &&
          agoraCarga - cacheAtual.ts < ttlMs;

        if (cacheValido) return cacheAtual.valor;

        try {
          const valor = await buscar();
          controle.caches = {
            ...(controle.caches || {}),
            [nome]: { chave, ts: Date.now(), valor },
          };
          return valor;
        } catch (error) {
          if (isAbortErrorSupervisao(error)) {
            throw error;
          }

          console.warn(
            `CaptaZap Supervisão | usando cache/fallback para ${nome}:`,
            error,
          );
          return cacheAtual?.valor ?? fallback;
        }
      };

      try {
        const montarParamsSupervisaoPeriodo = (dataDia, limit = "220") => {
          const params = new URLSearchParams();
          params.set("empresa_id", String(empresaIdEfetiva));
          params.set("data", dataDia);
          params.set("data_inicio", inicioPeriodo);
          params.set("data_fim", fimPeriodo);
          params.set("start", inicioPeriodo);
          params.set("end", fimPeriodo);
          params.set("limit", limit);
          params.set("_ts", String(agoraCarga));
          return params;
        };

        const buscarDia = async (dataDia) => {
          const params = montarParamsSupervisaoPeriodo(dataDia, "220");

          return requestJsonSupervisao(
            `/whatsapp/captazap/supervisao-v2?${params.toString()}`,
          );
        };

        const buscarRoiPeriodo = async () => {
          const params = montarParamsSupervisaoPeriodo(fimPeriodo, "5000");
          params.set("ads_roi_periodo", "1");
          params.set("somente_ads_roi", "1");

          return requestJsonSupervisao(
            `/whatsapp/captazap/supervisao-v2?${params.toString()}`,
          );
        };

        const paramsConversasOperacionais = new URLSearchParams();
        paramsConversasOperacionais.set("empresa_id", String(empresaIdEfetiva));
        paramsConversasOperacionais.set("data_inicio", inicioPeriodo);
        paramsConversasOperacionais.set("data_fim", fimPeriodo);
        paramsConversasOperacionais.set("start", inicioPeriodo);
        paramsConversasOperacionais.set("end", fimPeriodo);
        const diasOperacionaisBusca = Math.min(
          Math.max(datasPeriodo.length + 7, 15),
          60,
        );
        paramsConversasOperacionais.set("limit", "500");
        paramsConversasOperacionais.set("dias", String(diasOperacionaisBusca));
        paramsConversasOperacionais.set("_ts", String(agoraCarga));

        const [
          respostaDia,
          agendamentosResposta,
          conversasOperacionaisResposta,
          leadsEmpresaResposta,
        ] = await Promise.all([
          buscarDia(fimPeriodo),
          buscarComCache({
            nome: "agendamentosEmpresa",
            chave: `agendamentos:${empresaIdEfetiva}`,
            ttlMs: SUPERVISAO_CACHE_AGENDAMENTOS_MS,
            fallback: null,
            buscar: () =>
              requestJsonSupervisao(
                `/agendamentos/empresa/${empresaIdEfetiva}?_ts=${agoraCarga}`,
              ),
          }),
          buscarComCache({
            nome: "conversasOperacionais",
            chave: `conversas:${empresaIdEfetiva}:${inicioPeriodo}:${fimPeriodo}`,
            ttlMs: SUPERVISAO_CACHE_CONVERSAS_MS,
            fallback: null,
            buscar: () =>
              requestJsonSupervisao(
                `/whatsapp/captazap/conversas?${paramsConversasOperacionais.toString()}`,
              ),
          }),
          buscarComCache({
            nome: "leadsEmpresa",
            chave: `leads:${empresaIdEfetiva}`,
            ttlMs: SUPERVISAO_CACHE_LEADS_MS,
            fallback: null,
            buscar: () =>
              requestJsonSupervisao(
                `/leads?empresa_id=${empresaIdEfetiva}&mostrarInativas=true&limit=10000&_ts=${agoraCarga}`,
              ),
          }),
        ]);

        if (controle.seq !== seq) return;

        // Carregamento ao vivo — sem ROI de anúncios (carregado sob demanda).
        const data = respostaDia || null;

        setDadosSupervisao(data || null);
        setAgendamentosEmpresa(
          extrairListaAgendamentosSupervisao(agendamentosResposta),
        );
        setLeadsEmpresaSupervisao(
          extrairListaLeadsSupervisao(leadsEmpresaResposta),
        );
        setChamadosOperacionaisSupervisao(
          extrairListaConversasOperacionaisSupervisao(
            conversasOperacionaisResposta,
          ),
        );
        setDataReferencia(fimPeriodo);
      } catch (error) {
        if (controle.seq !== seq) return;

        if (isAbortErrorSupervisao(error)) {
          // Cancelamento de requisição é fluxo normal quando o usuário troca filtro,
          // muda o período ou o auto-refresh concorre com uma carga manual.
          // Não exibe "signal is aborted without reason" e não troca os cards
          // por fallback parcial.
          return;
        }

        console.error("CaptaZap Supervisão | erro supervisao-v2:", error);
        setErro(
          error?.message ||
            "Não foi possível carregar a supervisão consolidada. Mostrando dados locais.",
        );
        setDadosSupervisao(null);
        setAgendamentosEmpresa([]);
        setLeadsEmpresaSupervisao([]);
        setChamadosOperacionaisSupervisao([]);
      } finally {
        if (controle.seq === seq) {
          controle.emExecucao = false;
          if (!silencioso) setLoading(false);
        }
      }
    },
    [dataFim, dataInicio, empresaIdEfetiva, isOpen],
  );

  // ── carregarAdsRoi: carregado sob demanda, só quando anunciosAberto ──────
  const carregarAdsRoi = useCallback(
    async (opcoes = {}) => {
      if (!empresaIdEfetiva || !isOpen) return;
      const forcar = Boolean(opcoes?.forcar);
      setLoadingAdsRoi(true);

      const datasPeriodo = getDatasEntreSupervisao(dataInicio, dataFim, 31);
      const inicioPeriodo = datasPeriodo[0] || dataInicio || getHojeISO();
      const fimPeriodo =
        datasPeriodo[datasPeriodo.length - 1] || dataFim || inicioPeriodo;
      const agoraCarga = Date.now();

      try {
        const params = new URLSearchParams();
        params.set("empresa_id", String(empresaIdEfetiva));
        params.set("data", fimPeriodo);
        params.set("data_inicio", inicioPeriodo);
        params.set("data_fim", fimPeriodo);
        params.set("start", inicioPeriodo);
        params.set("end", fimPeriodo);
        params.set("limit", "5000");
        params.set("_ts", String(agoraCarga));
        params.set("ads_roi_periodo", "1");
        params.set("somente_ads_roi", "1");

        const respostaAdsRoi = await requestJsonSupervisao(
          `/whatsapp/captazap/supervisao-v2?${params.toString()}`,
        );

        const adsRoiDireto =
          respostaAdsRoi?.resumo?.ads_roi ||
          respostaAdsRoi?.ads_roi ||
          respostaAdsRoi?.adsRoi ||
          null;

        if (adsRoiDireto) {
          setDadosSupervisao((prev) =>
            prev
              ? {
                  ...prev,
                  resumo: { ...(prev.resumo || {}), ads_roi: adsRoiDireto },
                  ads_roi: adsRoiDireto,
                  adsRoi: adsRoiDireto,
                  casos_anuncios:
                    respostaAdsRoi?.casos_anuncios ||
                    respostaAdsRoi?.casosAnuncios ||
                    prev.casos_anuncios,
                  casosAnuncios:
                    respostaAdsRoi?.casos_anuncios ||
                    respostaAdsRoi?.casosAnuncios ||
                    prev.casosAnuncios,
                }
              : prev,
          );
        }
        setAdsRoiCarregado(true);
      } catch (error) {
        if (!isAbortErrorSupervisao(error)) {
          console.error("CaptaZap Supervisão | erro carregarAdsRoi:", error);
        }
      } finally {
        setLoadingAdsRoi(false);
      }
    },
    [dataFim, dataInicio, empresaIdEfetiva, isOpen],
  );

  // Carrega AdsRoi quando o painel de anúncios é aberto pela primeira vez
  useEffect(() => {
    if (anunciosAberto && !adsRoiCarregado) {
      carregarAdsRoi();
    }
  }, [anunciosAberto, adsRoiCarregado, carregarAdsRoi]);

  // Quando período muda, invalida cache de ads roi
  useEffect(() => {
    setAdsRoiCarregado(false);
  }, [dataInicio, dataFim, empresaIdEfetiva]);

  useEffect(() => {
    if (!isOpen) return;
    carregarSupervisao();
  }, [isOpen, carregarSupervisao]);

  useEffect(() => {
    if (!isOpen || !empresaIdEfetiva) return undefined;

    const timer = window.setInterval(() => {
      carregarSupervisao({ silencioso: true });
    }, AUTO_REFRESH_SUPERVISAO_MS);

    return () => window.clearInterval(timer);
  }, [isOpen, empresaIdEfetiva, carregarSupervisao]);

  useEffect(() => {
    if (!isOpen) return;
    setPagina(1);
  }, [
    isOpen,
    filtro,
    captadorFiltro,
    busca,
    dataReferencia,
    dataInicio,
    dataFim,
  ]);

  const modeloFallback = useMemo(() => {
    return montarSupervisaoFallback({
      empresaNome,
      dataReferencia,
      dataInicio,
      dataFim,
      conversasEquipe,
      filaDeEspera,
      followUpConversas,
      fechadosConversas,
      usuarios,
      metricasAgendamentoCaptazap,
      totalAgendadosCaptazap,
      leadsAgendadasCaptazap,
      metricasMensagensZapi,
      metricasZapi,
      estatisticasZapi,
    });
  }, [
    empresaNome,
    dataReferencia,
    dataInicio,
    dataFim,
    conversasEquipe,
    filaDeEspera,
    followUpConversas,
    fechadosConversas,
    usuarios,
    metricasAgendamentoCaptazap,
    totalAgendadosCaptazap,
    leadsAgendadasCaptazap,
    metricasMensagensZapi,
    metricasZapi,
    estatisticasZapi,
  ]);

  const modelo = useMemo(() => {
    if (dadosSupervisao?.ok) {
      const backend = normalizarSupervisaoBackend(dadosSupervisao, {
        empresaNome,
        dataReferencia,
        dataInicio,
        dataFim,
        metricasMensagensZapi,
        metricasZapi,
        estatisticasZapi,
      });

      return mesclarSupervisaoComOrigemAntiga(backend, modeloFallback);
    }

    return modeloFallback;
  }, [
    dadosSupervisao,
    empresaNome,
    dataReferencia,
    dataInicio,
    dataFim,
    metricasMensagensZapi,
    metricasZapi,
    estatisticasZapi,
    modeloFallback,
  ]);

  const resumoKpis = useMemo(() => {
    const resumo = { ...(modelo?.resumo || {}) };
    const resumoApi = dadosSupervisao?.resumo || {};

    if (dadosSupervisao?.ok) {
      resumo.mensagens_recebidas = getNumero(resumoApi.mensagens_recebidas);
      resumo.mensagens_enviadas = getNumero(resumoApi.mensagens_enviadas);
      resumo.mensagens_enviadas_operador = getNumero(
        resumoApi.mensagens_enviadas_operador,
      );
      resumo.mensagens_enviadas_sistema = getNumero(
        resumoApi.mensagens_enviadas_sistema,
      );
      resumo._mensagens_recebidas_oficial = true;
      resumo._mensagens_enviadas_oficial = true;
      resumo._origem_mensagens = "supervisao-v2";
    }

    return resumo;
  }, [modelo?.resumo, dadosSupervisao]);

  const casosAbertosDiaOperacionais = useMemo(() => {
    const lista = extrairListaConversasOperacionaisSupervisao(
      chamadosOperacionaisSupervisao,
    );

    const casos = lista
      .filter((conversa) => {
        const dataAbertura = getDataAberturaCasoSupervisao({ conversa });
        return isDataNoPeriodoSupervisao(dataAbertura, dataInicio, dataFim);
      })
      .map((conversa) =>
        montarCasoAbertoDiaOperacionalSupervisao(conversa, usuarios),
      );

    return deduplicarCasosPorChaveSupervisao(casos);
  }, [chamadosOperacionaisSupervisao, dataFim, dataInicio, usuarios]);

  useEffect(() => {
    if (!isOpen) return;

    // Performance:
    // A busca legado de origem Ads não pode varrer todas as abas operacionais.
    // Ela é complementar e deve consultar apenas casos realmente candidatos a anúncio.
    // Caso contrário, a tela dispara dezenas/centenas de requisições por telefone a
    // cada refresh e atrasa a atualização visual da supervisão.
    const cacheOrigemAds = origemAdsLegacyCacheRef.current;
    const agora = Date.now();
    const vistos = new Set();
    const resultadosCache = [];
    const candidatos = [];

    const listasPrioritariasAds = [
      modelo.casos_anuncios,
      modelo.casosAnuncios,
      modelo.conversas_anuncios,
      modelo.conversasAnuncios,
    ];

    const listasComSinalDireto = [
      modelo.casos,
      modelo.casos_abertos_dia,
      modelo.casosAbertosDia,
      casosAbertosDiaOperacionais,
    ];

    const registrarCandidato = (caso, prioridade = false) => {
      if (!caso || typeof caso !== "object") return;

      const conversa = caso?.conversa || caso || {};
      const key = getOrigemAdsLookupKey(conversa);
      if (!key || vistos.has(key)) return;
      vistos.add(key);

      if (Object.prototype.hasOwnProperty.call(adsOrigemMap, key)) return;

      // Se a própria conversa já trouxe origem Ads, não chama rota legado.
      // A normalização direta já resolve Facebook/Google/Site sem custo de rede.
      const origemDireta = getOrigemAdsCaso(caso, {});
      if (origemDireta) return;

      const dataBase =
        getDataEntradaAdsFallbackPeriodoSupervisao(caso) ||
        getDataAberturaCasoSupervisao(caso);

      if (
        !prioridade &&
        !isDataNoPeriodoSupervisao(dataBase, dataInicio, dataFim)
      ) {
        return;
      }

      // Fora das listas oficiais de anúncio, só consulta legado quando há algum
      // sinal explícito de Ads. Isso impede varrer agenda/fila/follow-up inteira.
      if (!prioridade && !temSinalAdsLeveCasoSupervisao(caso)) return;

      const cacheItem = cacheOrigemAds.itens?.[key];
      const cacheValido =
        cacheItem &&
        agora - getNumero(cacheItem.ts) < SUPERVISAO_CACHE_ORIGEM_ADS_MS;

      if (cacheValido) {
        resultadosCache.push([key, cacheItem.valor || null]);
        return;
      }

      if (cacheOrigemAds.emExecucao?.has(key)) return;
      candidatos.push({ key, conversa });
    };

    for (const lista of listasPrioritariasAds) {
      for (const caso of Array.isArray(lista) ? lista : []) {
        registrarCandidato(caso, true);
      }
    }

    for (const lista of listasComSinalDireto) {
      for (const caso of Array.isArray(lista) ? lista : []) {
        registrarCandidato(caso, false);
      }
    }

    if (resultadosCache.length) {
      setAdsOrigemMap((atual) => {
        let mudou = false;
        const proximo = { ...atual };

        for (const [key, origem] of resultadosCache) {
          if (!Object.prototype.hasOwnProperty.call(proximo, key)) {
            proximo[key] = origem;
            mudou = true;
          }
        }

        return mudou ? proximo : atual;
      });
    }

    const candidatosLimitados = candidatos.slice(
      0,
      SUPERVISAO_MAX_ORIGEM_ADS_LOOKUPS,
    );
    if (!candidatosLimitados.length) return;

    let cancelado = false;

    async function carregarOrigensAdsLegado() {
      const resultados = [];

      for (const { key } of candidatosLimitados) {
        cacheOrigemAds.emExecucao.add(key);
      }

      try {
        for (
          let i = 0;
          i < candidatosLimitados.length;
          i += SUPERVISAO_ORIGEM_ADS_LOTE
        ) {
          if (cancelado) return;

          const lote = candidatosLimitados.slice(
            i,
            i + SUPERVISAO_ORIGEM_ADS_LOTE,
          );
          const parcial = await Promise.all(
            lote.map(async ({ key, conversa }) => {
              const origem = await buscarOrigemAdsConversa(conversa).catch(
                () => null,
              );
              const normalizada = normalizarOrigemAds(origem) || null;

              cacheOrigemAds.itens[key] = {
                ts: Date.now(),
                valor: normalizada,
              };

              return [key, normalizada];
            }),
          );

          resultados.push(...parcial);
        }

        if (cancelado || !resultados.length) return;

        setAdsOrigemMap((atual) => {
          let mudou = false;
          const proximo = { ...atual };

          for (const [key, origem] of resultados) {
            if (!Object.prototype.hasOwnProperty.call(proximo, key)) {
              proximo[key] = origem;
              mudou = true;
            }
          }

          return mudou ? proximo : atual;
        });
      } finally {
        for (const { key } of candidatosLimitados) {
          cacheOrigemAds.emExecucao.delete(key);
        }
      }
    }

    carregarOrigensAdsLegado();

    return () => {
      cancelado = true;
    };
  }, [
    adsOrigemMap,
    isOpen,
    modelo.casos,
    modelo.casos_abertos_dia,
    modelo.casosAbertosDia,
    modelo.casos_anuncios,
    modelo.casosAnuncios,
    modelo.conversas_anuncios,
    modelo.conversasAnuncios,
    casosAbertosDiaOperacionais,
    dataInicio,
    dataFim,
  ]);

  const aplicarOrigemAdsListaSupervisao = useCallback(
    (lista = []) => {
      return (Array.isArray(lista) ? lista : []).map((caso) => {
        if (!caso || typeof caso !== "object") return caso;

        const origemDireta = caso.origem_ads || caso.origemAds;
        const origemDiretaNormalizada = normalizarOrigemAds(origemDireta);

        // Mantém a mesma referência quando o objeto já veio enriquecido.
        // Isso reduz recálculo de useMemo, paginação e render das linhas.
        if (origemDiretaNormalizada && typeof origemDireta === "object") {
          return caso;
        }

        if (origemDiretaNormalizada) {
          return {
            ...caso,
            origem_ads: origemDiretaNormalizada,
            origemAds: origemDiretaNormalizada,
          };
        }

        const origemMapeada = getOrigemAdsCaso(caso, adsOrigemMap);
        return origemMapeada
          ? { ...caso, origem_ads: origemMapeada, origemAds: origemMapeada }
          : caso;
      });
    },
    [adsOrigemMap],
  );

  const casosComOrigemAds = useMemo(() => {
    return aplicarOrigemAdsListaSupervisao(modelo.casos || []);
  }, [aplicarOrigemAdsListaSupervisao, modelo.casos]);

  const todosCasosComOrigemAds = useMemo(() => {
    const listas = [
      modelo.casos,
      modelo.casos_fila,
      modelo.casosFila,
      modelo.casos_followup,
      modelo.casosFollowup,
      modelo.casos_agendados,
      modelo.casosAgendados,
      modelo.casos_agenda_hoje,
      modelo.casosAgendaHoje,
      modelo.casos_abertos_dia,
      modelo.casosAbertosDia,
      modelo.casos_reabertos,
      modelo.casosReabertos,
      casosAbertosDiaOperacionais,
    ];

    const map = new Map();

    for (const lista of listas) {
      const enriquecida = aplicarOrigemAdsListaSupervisao(lista || []);

      for (const caso of enriquecida) {
        const key =
          getCasoKey(caso) ||
          `${caso?.telefone || ""}-${caso?.lead_id || ""}-${map.size}`;
        if (!map.has(key)) map.set(key, caso);
      }
    }

    return [...map.values()];
  }, [
    aplicarOrigemAdsListaSupervisao,
    modelo.casos,
    modelo.casos_fila,
    modelo.casosFila,
    modelo.casos_followup,
    modelo.casosFollowup,
    modelo.casos_agendados,
    modelo.casosAgendados,
    modelo.casos_agenda_hoje,
    modelo.casosAgendaHoje,
    modelo.casos_abertos_dia,
    modelo.casosAbertosDia,
    modelo.casos_reabertos,
    modelo.casosReabertos,
    casosAbertosDiaOperacionais,
  ]);

  const mapaOperacionalAgendaHoje = useMemo(
    () => criarMapaOperacionalAgendaHojeSupervisao(casosComOrigemAds),
    [casosComOrigemAds],
  );

  const mapaLeadsEmpresaSupervisao = useMemo(
    () =>
      criarMapaLeadsEmpresaSupervisao(
        leadsEmpresaSupervisao,
        casosComOrigemAds,
      ),
    [leadsEmpresaSupervisao, casosComOrigemAds],
  );

  const getCasosModeloSupervisao = useCallback(
    (...keys) => {
      for (const key of keys) {
        if (Array.isArray(modelo?.[key])) {
          return aplicarOrigemAdsListaSupervisao(modelo[key]);
        }
      }

      return null;
    },
    [aplicarOrigemAdsListaSupervisao, modelo],
  );

  const abrirHistoricoCompletoLeadSupervisao = useCallback(
    (caso = {}) => {
      const leadHistorico = montarLeadHistoricoCompletoSupervisao(
        caso,
        empresaIdEfetiva,
      );

      if (leadHistorico?.id) {
        setLeadHistoricoSelecionada(leadHistorico);
        return;
      }

      // Fallback: se não houver lead_id no item, abre o detalhe da supervisão
      // para não deixar o botão sem resposta.
      setCasoSelecionado(caso);
    },
    [empresaIdEfetiva],
  );

  const abrirAgendamentoInternoSupervisao = useCallback((caso = {}) => {
    const leadAgendamento = montarLeadAgendamentoModalSupervisao(caso);

    if (leadAgendamento?.id) {
      setAgendaSelecionada(caso);
      return;
    }

    console.warn(
      "CaptaZap Supervisão | não foi possível abrir agendamentos sem lead_id:",
      caso,
    );

    // Fallback visível: abre o detalhe do caso em vez de apenas selecionar a linha.
    setCasoSelecionado(caso);
  }, []);

  const abrirPagamentosLeadSupervisao = useCallback((caso = {}) => {
    setPagamentoSelecionado(caso);
  }, []);

  const casosAnunciosOperacionais = useMemo(() => {
    const listaBackend = getCasosModeloSupervisao(
      "casos_anuncios",
      "casosAnuncios",
      "conversas_anuncios",
      "conversasAnuncios",
    );

    // REGRA VALIDADA NO BANCO:
    // Anúncios na supervisão = chamados/conversas ABERTOS no período
    // E com origem em whatsapp_ads_origem dentro do mesmo período.
    //
    // IMPORTANTE: não escolher "backend OU fallback". Em produção vimos o número
    // aparecer correto e depois cair para 1 porque uma lista menor sobrescrevia a
    // outra. Aqui fazemos UNIÃO das fontes estritas e deduplicamos por chamado.
    const fallbackEstrito = todosCasosComOrigemAds.filter((caso) => {
      if (!temOrigemAdsCasoSupervisao(caso)) return false;

      const dataEntradaAdsEstrita =
        getDataEntradaAdsEstritaCasoSupervisao(caso);
      const dataEntradaAdsFallback =
        getDataEntradaAdsFallbackPeriodoSupervisao(caso);
      const dataEntradaAds = dataEntradaAdsEstrita || dataEntradaAdsFallback;

      const dataAbertura = getDataAberturaCasoSupervisao(caso);

      // REGRA GOOGLE/SITE:
      // Se a origem Ads tem data própria, ela é a verdade do período.
      // Não exigimos que o chamado também tenha sido aberto no período, porque
      // alguns retornos do backend mantêm aberto_em antigo mesmo para a primeira
      // entrada de anúncio do contato.
      //
      // Se a origem não trouxe data própria, aí sim usamos a abertura/primeira
      // conversa como fallback controlado para não voltar a puxar Site antigo.
      if (!isDataNoPeriodoSupervisao(dataEntradaAds, dataInicio, dataFim)) {
        return false;
      }

      if (dataEntradaAdsEstrita) return true;

      return isDataNoPeriodoSupervisao(dataAbertura, dataInicio, dataFim);
    });

    const base = [
      ...(Array.isArray(listaBackend) ? listaBackend : []),
      ...fallbackEstrito,
    ];

    const baseEnriquecidaDoPeriodo = aplicarOrigemAdsListaSupervisao(
      base,
    ).filter((caso) => {
      if (!temOrigemAdsCasoSupervisao(caso)) return false;

      const dataEntradaAds =
        getDataEntradaAdsEstritaCasoSupervisao(caso) ||
        getDataEntradaAdsFallbackPeriodoSupervisao(caso);

      // Filtro de data = data da primeira entrada válida do anúncio/conversa.
      // Não basta o chamado estar aberto ou ter origem "Site" antiga.
      return isDataNoPeriodoSupervisao(dataEntradaAds, dataInicio, dataFim);
    });

    return deduplicarCasosPorChaveSupervisao(baseEnriquecidaDoPeriodo).sort(
      (a, b) => {
        const dataA =
          getDataEntradaAdsEstritaCasoSupervisao(a) ||
          getDataEntradaAdsFallbackPeriodoSupervisao(a) ||
          getDataAberturaCasoSupervisao(a) ||
          "";
        const dataB =
          getDataEntradaAdsEstritaCasoSupervisao(b) ||
          getDataEntradaAdsFallbackPeriodoSupervisao(b) ||
          getDataAberturaCasoSupervisao(b) ||
          "";
        return parseTimestamp(dataB) - parseTimestamp(dataA);
      },
    );
  }, [
    aplicarOrigemAdsListaSupervisao,
    dataFim,
    dataInicio,
    getCasosModeloSupervisao,
    todosCasosComOrigemAds,
  ]);

  const casosReferenciaAdsAgenda = useMemo(() => {
    return [
      ...(Array.isArray(todosCasosComOrigemAds) ? todosCasosComOrigemAds : []),
      ...(Array.isArray(casosComOrigemAds) ? casosComOrigemAds : []),
      ...(Array.isArray(chamadosOperacionaisSupervisao)
        ? chamadosOperacionaisSupervisao
        : []),
    ];
  }, [
    casosComOrigemAds,
    chamadosOperacionaisSupervisao,
    todosCasosComOrigemAds,
  ]);

  const casosAnunciosComAgendaOperacional = useMemo(() => {
    return vincularAgendamentosAosCasosAdsSupervisao({
      casos: casosAnunciosOperacionais,
      agendamentos: agendamentosEmpresa,
      leads: leadsEmpresaSupervisao,
      casosReferencia: casosReferenciaAdsAgenda,
      dataInicio,
      dataFim,
    });
  }, [
    agendamentosEmpresa,
    casosAnunciosOperacionais,
    casosReferenciaAdsAgenda,
    dataFim,
    dataInicio,
    leadsEmpresaSupervisao,
  ]);

  const anunciosParaExibir = useMemo(() => {
    return agregarAnunciosPorCasosSupervisao(casosAnunciosComAgendaOperacional);
  }, [casosAnunciosComAgendaOperacional]);

  const casosAgendadosHojeOficiais = useMemo(() => {
    const casosBackend = getCasosModeloSupervisao(
      "casos_agendados",
      "casosAgendados",
    );

    if (!Array.isArray(casosBackend)) return null;

    return deduplicarCasosAgendadosUltimoStatusSupervisao(casosBackend);
  }, [getCasosModeloSupervisao]);

  const metricasStatusPeriodo = useMemo(() => {
    return calcularMetricasStatusPeriodoSupervisao({
      casos: todosCasosComOrigemAds,
      casosAgendadosHojeOficiais,
      agendamentosEmpresa,
      resumo: resumoKpis,
      dataInicio,
      dataFim,
    });
  }, [
    agendamentosEmpresa,
    casosAgendadosHojeOficiais,
    dataFim,
    dataInicio,
    resumoKpis,
    todosCasosComOrigemAds,
  ]);

  const metricasAdsPeriodo = useMemo(() => {
    const adsRoiBackend =
      dadosSupervisao?.ads_roi ||
      dadosSupervisao?.adsRoi ||
      dadosSupervisao?.resumo?.ads_roi ||
      resumoKpis?.ads_roi;

    // Fonte oficial: backend calculando conversas iniciadas/reiniciadas no período.
    // O front mantém o cálculo antigo apenas como fallback, porque o vínculo
    // anúncio -> agenda precisa nascer no banco para não depender de listas
    // parciais de casos/chamados.
    if (adsRoiBackend?.confiavel) {
      return {
        entradas: getNumero(adsRoiBackend.entradas),
        conversasIniciadas: getNumero(
          adsRoiBackend.conversasIniciadas ?? adsRoiBackend.entradas,
        ),
        contatosUnicos: getNumero(
          adsRoiBackend.contatosUnicos ??
            adsRoiBackend.contatos_unicos ??
            adsRoiBackend.entradas,
        ),
        conversasNovas: getNumero(
          adsRoiBackend.conversasNovas ?? adsRoiBackend.conversas_novas,
        ),
        conversasReativadas: getNumero(
          adsRoiBackend.conversasReativadas ??
            adsRoiBackend.conversas_reativadas,
        ),
        comRastroAds: getNumero(
          adsRoiBackend.comRastroAds ?? adsRoiBackend.com_rastro_ads,
        ),
        semRastroAds: getNumero(
          adsRoiBackend.semRastroAds ?? adsRoiBackend.sem_rastro_ads,
        ),
        agendamentos: getNumero(adsRoiBackend.agendamentos),
        comparecimentos: getNumero(adsRoiBackend.comparecimentos),
        taxa:
          adsRoiBackend.taxa === null || adsRoiBackend.taxa === undefined
            ? null
            : getNumero(adsRoiBackend.taxa),
        taxaComparecimento:
          adsRoiBackend.taxaComparecimento === null ||
          adsRoiBackend.taxaComparecimento === undefined
            ? null
            : getNumero(adsRoiBackend.taxaComparecimento),
        taxaFechamento:
          adsRoiBackend.taxaFechamento === null ||
          adsRoiBackend.taxaFechamento === undefined
            ? null
            : getNumero(adsRoiBackend.taxaFechamento),
        valorOportunidade: toNumeroMoedaSupervisao(
          adsRoiBackend.valorOportunidade,
        ),
        valorFechamento: toNumeroMoedaSupervisao(adsRoiBackend.valorFechamento),
        quantidadeOportunidades: getNumero(
          adsRoiBackend.quantidadeOportunidades,
        ),
        quantidadeFechamentos: getNumero(adsRoiBackend.quantidadeFechamentos),
        detalhes: adsRoiBackend.detalhes || {},
        fonte: adsRoiBackend.fonte || "backend_ads_roi",
        confiavel: true,
      };
    }

    return calcularMetricasAdsPeriodoSupervisao({
      // Fallback legado caso o backend ainda não devolva ads_roi.
      casos: casosAnunciosComAgendaOperacional,
      anuncios: [],
      agendamentos: agendamentosEmpresa,
      leads: leadsEmpresaSupervisao,
      casosReferencia: casosReferenciaAdsAgenda,
      resumo: resumoKpis,
      dataInicio,
      dataFim,
    });
  }, [
    dadosSupervisao,
    resumoKpis,
    agendamentosEmpresa,
    casosAnunciosComAgendaOperacional,
    casosReferenciaAdsAgenda,
    dataFim,
    dataInicio,
    leadsEmpresaSupervisao,
  ]);

  const metricasFinanceirasPeriodo = useMemo(() => {
    return calcularMetricasFinanceirasPeriodoSupervisao({
      casos: todosCasosComOrigemAds,
      resumo: resumoKpis,
      dataInicio,
      dataFim,
    });
  }, [dataFim, dataInicio, resumoKpis, todosCasosComOrigemAds]);

  const captadoresDisponiveis = useMemo(() => {
    const map = new Map();

    for (const captador of modelo.captadores || []) {
      const key = getCaptadorKey(captador);
      if (!map.has(key)) {
        map.set(key, {
          key,
          nome: captador.nome || captador.responsavel_nome || "Sem responsável",
          total: 0,
        });
      }
      map.get(key).total += Number(
        captador.monitorados || captador.chamados_abertos || 0,
      );
    }

    for (const caso of casosComOrigemAds || []) {
      const key = getFiltroResponsavelKey(caso);
      if (!map.has(key)) {
        map.set(key, {
          key,
          nome: getNomeResponsavelFiltroSupervisao(caso) || "Sem responsável",
          total: 0,
        });
      }
      map.get(key).total += 1;
    }

    // Quando o usuário clica em um card de ROI de anúncios, a tabela pode vir
    // da lista oficial do backend (detalhes.ads_roi) e não da lista operacional.
    // Por isso incluímos também os captadores presentes nos casos de anúncio
    // para o filtro continuar funcionando nessa visão.
    for (const caso of casosAnunciosComAgendaOperacional || []) {
      const key = getFiltroResponsavelKey(caso);
      if (!map.has(key)) {
        map.set(key, {
          key,
          nome: getNomeResponsavelFiltroSupervisao(caso) || "Sem responsável",
          total: 0,
        });
      }
    }

    return [...map.values()].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"),
    );
  }, [modelo.captadores, casosComOrigemAds, casosAnunciosComAgendaOperacional]);

  const casosFiltrados = useMemo(() => {
    const termo = normalizar(busca);
    let lista = Array.isArray(casosComOrigemAds) ? casosComOrigemAds : [];

    if (adsRoiFiltroTabela) {
      lista = montarCasosTabelaAdsRoiSupervisao({
        tipo: adsRoiFiltroTabela,
        metricas: metricasAdsPeriodo,
        fallbackCasos: casosAnunciosComAgendaOperacional,
      });
    } else {
      if (filtro === "fila") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_fila",
          "casosFila",
        );
        const listaFallback = lista.filter(isCasoFilaFiltroSupervisao);

        // Se o backend enviar [] por limitação/inconsistência, não deixa o botão
        // Fila abrir vazio quando o fallback local encontra chamados.
        lista =
          Array.isArray(casosBackend) && casosBackend.length > 0
            ? casosBackend
            : listaFallback;
      }

      if (filtro === "followup") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_followup",
          "casosFollowup",
        );
        const listaFallback = lista.filter(isCasoFollowupFiltroSupervisao);

        // Se o backend enviar [] por algum erro/limitação, não deixa o botão morrer:
        // usa o fallback local quando ele encontra registros de follow-up.
        lista =
          Array.isArray(casosBackend) && casosBackend.length > 0
            ? casosBackend
            : listaFallback;
      }

      if (filtro === "agendados") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_agendados",
          "casosAgendados",
        );
        const listaAgendados =
          casosBackend ??
          lista.filter((caso) =>
            Boolean(getAgendamentoCasoSupervisao(caso)?.has),
          );

        // Agendados Hoje/Agendados precisa mostrar a produção líquida: uma linha
        // por paciente, mantendo o último agendamento válido. Se o mesmo paciente
        // foi agendado e depois cancelou aquele horário, o horário cancelado sai
        // daqui e continua aparecendo na Agenda de hoje como cancelado.
        lista = deduplicarCasosAgendadosUltimoStatusSupervisao(listaAgendados);
      }

      if (filtro === "agendados_amanha") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_agendados_amanha",
          "casosAgendadosAmanha",
          "casos_agenda_amanha",
          "casosAgendaAmanha",
          "casos_previstos_amanha",
          "casosPrevistosAmanha",
        );

        const baseAgendaProximaISO = String(
          dataFim || dataReferencia || dataInicio || getHojeISO(),
        ).slice(0, 10);
        const amanhaISO =
          getDataAgendaAmanhaOperacionalISO(baseAgendaProximaISO);
        const listaBackend = Array.isArray(casosBackend)
          ? deduplicarCasosAgendaFuturaPorPessoaDataSupervisao(
              deduplicarCasosAgendaHojeUltimoStatusSupervisao(
                casosBackend.filter((caso) => {
                  const dataPrevista =
                    getDataPrevistaAgendaHojeCasoSupervisao(caso);
                  const status = getStatusAgendaNormalizadoCasoSupervisao(caso);
                  return (
                    isMesmoDiaSupervisao(dataPrevista, amanhaISO) &&
                    isStatusAgendaAtivoParaAgendaFuturaSupervisao(status)
                  );
                }),
              ),
            )
          : [];

        const listaAgendamentosEmpresa =
          deduplicarCasosAgendaFuturaPorPessoaDataSupervisao(
            montarCasosAgendadosAmanhaSupervisao(
              agendamentosEmpresa,
              baseAgendaProximaISO,
              usuarios,
              mapaLeadsEmpresaSupervisao,
            ),
          );

        const listaFallbackCasos =
          deduplicarCasosAgendaFuturaPorPessoaDataSupervisao(
            deduplicarCasosAgendaHojeUltimoStatusSupervisao(
              lista.filter((caso) => {
                const dataPrevista =
                  getDataPrevistaAgendaHojeCasoSupervisao(caso);
                const status = getStatusAgendaNormalizadoCasoSupervisao(caso);
                return (
                  Boolean(getAgendamentoCasoSupervisao(caso)?.has) &&
                  isMesmoDiaSupervisao(dataPrevista, amanhaISO) &&
                  isStatusAgendaAtivoParaAgendaFuturaSupervisao(status)
                );
              }),
            ),
          );

        lista =
          listaAgendamentosEmpresa.length > 0
            ? listaAgendamentosEmpresa
            : listaBackend.length > 0
              ? listaBackend
              : listaFallbackCasos;
      }

      if (filtro === "agenda_hoje") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_agenda_hoje",
          "casosAgendaHoje",
        );
        const listaAgenda =
          casosBackend ??
          lista.filter((caso) => {
            const agenda = getAgendamentoCasoSupervisao(caso);
            const status = normalizar(agenda?.status || "");
            return (
              Boolean(agenda?.has) &&
              [
                "agendado",
                "reagendado",
                "compareceu",
                "faltou",
                "cancelado",
              ].some((item) => status.includes(item))
            );
          });

        lista = deduplicarCasosAgendaHojeUltimoStatusSupervisao(
          enriquecerAgendaHojeComOperacionalSupervisao(
            listaAgenda,
            mapaOperacionalAgendaHoje,
            usuarios,
          ),
        );
      }

      if (filtro === "abertos_dia") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_abertos_dia",
          "casosAbertosDia",
          "casos_abertos_periodo",
          "casosAbertosPeriodo",
          "casos_chamados_abertos",
          "casosChamadosAbertos",
        );

        const casosBackendValidos =
          Array.isArray(casosBackend) && casosBackend.length > 0
            ? casosBackend
            : null;

        const casosOperacionaisValidos =
          Array.isArray(casosAbertosDiaOperacionais) &&
          casosAbertosDiaOperacionais.length > 0
            ? casosAbertosDiaOperacionais
            : null;

        const baseFallback = Array.isArray(todosCasosComOrigemAds)
          ? todosCasosComOrigemAds
          : lista;

        lista =
          casosBackendValidos ??
          casosOperacionaisValidos ??
          deduplicarCasosPorChaveSupervisao(
            baseFallback.filter((caso) => {
              const dataAbertura = getDataAberturaCasoSupervisao(caso);
              return isDataNoPeriodoSupervisao(
                dataAbertura,
                dataInicio,
                dataFim,
              );
            }),
          );
      }

      if (filtro === "reabertos") {
        const casosBackend = getCasosModeloSupervisao(
          "casos_reabertos",
          "casosReabertos",
        );
        lista = casosBackend ?? lista.filter(isCasoReabertoFiltroSupervisao);
      }

      if (filtro === "ads_facebook" || filtro === "ads_google") {
        const canalFiltro = filtro === "ads_facebook" ? "facebook" : "google";

        // Facebook/Google são abas de ROI de anúncios, não abas operacionais.
        // Por isso a base precisa ser somente a coorte de anúncios do período
        // filtrado, e não todos os chamados abertos/enriquecidos com origem Site.
        lista = deduplicarCasosPorChaveSupervisao(
          (Array.isArray(casosAnunciosComAgendaOperacional)
            ? casosAnunciosComAgendaOperacional
            : []
          ).filter((caso) => isCanalAdsCasoSupervisao(caso, canalFiltro)),
        );
      }

      if (filtro === "critico") {
        lista = lista.filter(
          (caso) =>
            !isChamadoFechadoSupervisao(caso) &&
            String(caso.criticidade || "").includes("critico"),
        );
      }
    }

    if (captadorFiltro !== "todos") {
      const captadorSelecionado = captadoresDisponiveis.find(
        (captador) => String(captador.key) === String(captadorFiltro),
      );

      lista = lista.filter((caso) =>
        isCasoDoCaptadorFiltroSupervisao(
          caso,
          captadorFiltro,
          captadorSelecionado,
        ),
      );
    }

    if (termo) {
      lista = lista.filter((caso) =>
        [
          caso.nome,
          caso.telefone,
          caso.responsavel_nome,
          caso.status_label,
          caso.status_chamado,
          caso.statusChamado,
          caso.chamado_status,
          getStatusChamadoLabel(caso),
          caso.tipo,
          caso.ultima_mensagem?.texto,
          caso.origem_ads?.titulo,
          caso.origem_ads?.campanha,
          getCanalAdsLabelSupervisao(getCanalAdsCasoSupervisao(caso)),
          caso.status_negociacao,
          getAgendamentoCasoSupervisao(caso).label,
          getAgendamentoCasoSupervisao(caso).status,
          caso.valor_oportunidade,
          caso.valorOportunidade,
          getFollowupCasoSupervisao(caso).label,
        ].some((value) => normalizar(value).includes(termo)),
      );
    }

    return lista;
  }, [
    busca,
    captadorFiltro,
    captadoresDisponiveis,
    filtro,
    adsRoiFiltroTabela,
    metricasAdsPeriodo,
    casosComOrigemAds,
    casosAnunciosComAgendaOperacional,
    getCasosModeloSupervisao,
    mapaOperacionalAgendaHoje,
    mapaLeadsEmpresaSupervisao,
    agendamentosEmpresa,
    usuarios,
  ]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(casosFiltrados.length / linhasPorPagina),
  );
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * linhasPorPagina;
  const fim = inicio + linhasPorPagina;
  const casosPagina = casosFiltrados.slice(inicio, fim);

  const captadoresFiltrados = useMemo(() => {
    const termo = normalizar(busca);
    let lista = Array.isArray(modelo.captadores) ? modelo.captadores : [];

    if (captadorFiltro !== "todos") {
      lista = lista.filter(
        (captador) => getCaptadorKey(captador) === captadorFiltro,
      );
    }

    if (termo) {
      lista = lista.filter((captador) =>
        normalizar(captador.nome || captador.responsavel_nome).includes(termo),
      );
    }

    return lista;
  }, [busca, captadorFiltro, modelo.captadores]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] overflow-hidden bg-slate-950/40 p-3 text-slate-950 backdrop-blur-sm sm:p-5">
      <section className="mx-auto flex h-full w-full max-w-[1780px] flex-col overflow-hidden rounded-[30px] border border-slate-300 bg-slate-50 shadow-[0_28px_90px_rgba(15,23,42,0.32)]">
        <header className="shrink-0 border-b border-slate-300 bg-white px-5 py-3 sm:px-6">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-blue-700">
                  <ShieldCheck size={14} /> Supervisão CaptaZap
                </span>
                <span className="rounded-2xl border border-slate-300 bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-500">
                  Somente leitura
                </span>
                <span className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-bold text-emerald-700">
                  {periodoLabel}
                </span>
              </div>

              <h2 className="mt-2 text-xl font-black tracking-tight text-slate-950 sm:text-2xl">
                Central de qualidade da equipe
              </h2>
              <p className="mt-1 max-w-3xl text-xs font-medium leading-5 text-slate-500">
                Controle de chamados, filas, follow-ups, agendamentos, mensagens
                e origem dos anúncios em tempo real
                {empresaNome ? ` · ${empresaNome}` : ""}.
              </p>
            </div>

            <div className="flex w-full flex-col gap-2 xl:w-auto xl:min-w-[860px]">
              <div className="flex w-full flex-col gap-2 rounded-2xl border border-slate-300 bg-slate-50 p-2 lg:flex-row lg:items-center">
                <div className="flex flex-wrap items-center gap-1.5">
                  {PERIODO_PRESETS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setPeriodoModo(item.key)}
                      className={`h-8 rounded-xl px-2.5 text-[10px] font-black transition ${
                        periodoModo === item.key
                          ? "bg-blue-700 text-white shadow-sm"
                          : "border border-slate-300 bg-white text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-nowrap items-center gap-2 overflow-x-auto lg:ml-auto lg:overflow-visible">
                  <label className="inline-flex shrink-0 items-center gap-1.5">
                    <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                      Início
                    </span>
                    <input
                      type="date"
                      value={dataInicio}
                      onChange={(event) => {
                        setPeriodoModo("personalizado");
                        setDataInicio(event.target.value);
                      }}
                      className="h-8 w-[132px] rounded-xl border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </label>
                  <label className="inline-flex shrink-0 items-center gap-1.5">
                    <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
                      Fim
                    </span>
                    <input
                      type="date"
                      value={dataFim}
                      onChange={(event) => {
                        setPeriodoModo("personalizado");
                        setDataFim(event.target.value);
                      }}
                      className="h-8 w-[132px] rounded-xl border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </label>
                </div>
              </div>

              <div className="flex w-full items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    carregarSupervisao({ forcar: true });
                    onAtualizar?.();
                  }}
                  disabled={loading}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 text-xs font-black text-blue-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw
                    size={15}
                    className={loading ? "animate-spin" : ""}
                  />
                  Atualizar
                </button>

                <button
                  type="button"
                  onClick={() => setCardsRecolhidos((atual) => !atual)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                  title={
                    cardsRecolhidos
                      ? "Mostrar cards de indicadores"
                      : "Recolher cards de indicadores"
                  }
                >
                  <BarChart3 size={15} />
                  {cardsRecolhidos ? "Mostrar cards" : "Recolher cards"}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-500 shadow-sm transition hover:-translate-y-0.5 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                  aria-label="Fechar supervisão"
                >
                  <X size={19} />
                </button>
              </div>
            </div>
          </div>

          {erro ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-bold leading-5 text-amber-800">
              {erro}
            </div>
          ) : null}
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain px-5 py-5 sm:px-6">
          {!cardsRecolhidos ? (
            <KpiGrid
              resumo={resumoKpis}
              metricasStatusPeriodo={metricasStatusPeriodo}
              metricasAdsPeriodo={metricasAdsPeriodo}
              metricasFinanceirasPeriodo={metricasFinanceirasPeriodo}
              loading={loading}
              onAdsCardClick={handleSelecionarAdsRoiCard}
            />
          ) : null}

          <section
            className={`mt-5 grid min-h-0 flex-1 grid-cols-1 gap-5 ${
              anunciosAberto
                ? "2xl:grid-cols-[minmax(0,1.65fr)_minmax(380px,0.7fr)]"
                : ""
            }`}
          >
            <section className="flex min-h-0 min-w-0 flex-col rounded-[26px] border border-slate-300 bg-white shadow-sm">
              <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  {FILTROS_OPERACIONAIS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setFiltro(item.key);
                        setAdsRoiFiltroTabela(null);
                        setPagina(1);
                      }}
                      className={`h-8 rounded-xl border px-3 text-[11px] font-black transition ${
                        filtro === item.key
                          ? "border-blue-600 bg-blue-700 text-white shadow-sm"
                          : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}

                  <label className="relative h-8 min-w-[250px] flex-1 sm:max-w-[420px]">
                    <Search
                      size={14}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      value={busca}
                      onChange={(event) => {
                        setBusca(event.target.value);
                        setPagina(1);
                      }}
                      placeholder="Buscar contato, telefone, captador, anúncio ou mensagem"
                      className="h-8 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-[11px] font-bold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </label>

                  <label className="ml-auto inline-flex h-8 items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-3 text-[11px] font-black text-slate-700 shadow-sm">
                    <UserRound size={13} className="text-blue-600" />
                    <select
                      value={captadorFiltro}
                      onChange={(event) =>
                        setCaptadorFiltro(event.target.value)
                      }
                      className="max-w-[220px] bg-transparent outline-none"
                    >
                      <option value="todos">Todos os captadores</option>
                      {captadoresDisponiveis.map((captador) => (
                        <option key={captador.key} value={captador.key}>
                          {captador.nome} ({captador.total})
                        </option>
                      ))}
                    </select>
                  </label>

                  <button
                    type="button"
                    onClick={() => setAnunciosAberto((atual) => !atual)}
                    className={`inline-flex h-8 items-center gap-1.5 rounded-xl border px-3 text-[11px] font-black transition ${
                      anunciosAberto
                        ? "border-violet-600 bg-violet-700 text-white shadow-sm"
                        : "border-violet-200 bg-white text-violet-700 hover:border-violet-300 hover:bg-violet-50"
                    }`}
                    title="Mostrar ou ocultar origem dos anúncios"
                  >
                    <Megaphone size={13} />
                    {anunciosAberto ? "Ocultar anúncios" : "Ver anúncios"}
                    {casosAnunciosComAgendaOperacional?.length ? (
                      <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] text-violet-700">
                        {casosAnunciosComAgendaOperacional.length}
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>

              {adsRoiFiltroTabela ? (
                <div className="border-b border-violet-100 bg-violet-50/80 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-wide text-violet-700">
                        Tabela filtrada pelo ROI de anúncios
                      </div>
                      <div className="mt-0.5 text-sm font-black text-slate-950">
                        {
                          getConfigDetalheAdsRoiSupervisao(adsRoiFiltroTabela)
                            .titulo
                        }
                      </div>
                      <div className="mt-0.5 text-[11px] font-semibold text-slate-500">
                        {casosFiltrados.length} registro(s) no período{" "}
                        {periodoLabel}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <ModalImpressaoAdsRoi
                        titulo={
                          getConfigDetalheAdsRoiSupervisao(adsRoiFiltroTabela)
                            .titulo
                        }
                        periodoLabel={periodoLabel}
                        itens={casosFiltrados}
                        totais={getTotaisDetalheAdsRoiSupervisao(
                          casosFiltrados,
                        )}
                      />

                      <button
                        type="button"
                        onClick={handleLimparFiltroAdsRoiTabela}
                        className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-xs font-black text-slate-700 shadow-sm transition hover:bg-slate-100"
                      >
                        <X size={15} />
                        Limpar filtro
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <CasosTable
                casos={casosPagina}
                agora={tempoRealAgora}
                usuarios={usuarios}
                totalFiltrado={casosFiltrados.length}
                inicio={inicio}
                fim={fim}
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                linhasPorPagina={linhasPorPagina}
                setLinhasPorPagina={setLinhasPorPagina}
                setPagina={setPagina}
                onVerDetalhes={setCasoSelecionado}
                onAbrirAgendaSupervisao={abrirAgendamentoInternoSupervisao}
                onAbrirAgendaFollowup={setAgendaFollowupSelecionado}
                onAbrirHistoricoLead={abrirHistoricoCompletoLeadSupervisao}
                onAbrirPagamentosLead={abrirPagamentosLeadSupervisao}
                onSelecionarConversa={
                  onSelecionarConversa || onVisualizarConversa
                }
              />
            </section>

            {anunciosAberto ? (
              <aside className="min-w-0 self-start rounded-[26px] border border-slate-300 bg-white p-4 shadow-sm">
                {loadingAdsRoi ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600" />
                    <div className="text-xs font-semibold text-slate-500">
                      Carregando dados de anúncios...
                    </div>
                  </div>
                ) : !adsRoiCarregado ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                    <Megaphone size={28} className="text-violet-300" />
                    <div className="text-xs font-semibold text-slate-500">
                      Clique para carregar dados de anúncios
                    </div>
                    <button
                      type="button"
                      onClick={() => carregarAdsRoi({ forcar: true })}
                      className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2 text-xs font-black text-white hover:bg-violet-800"
                    >
                      <RefreshCw size={13} />
                      Carregar anúncios
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => carregarAdsRoi({ forcar: true })}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-500 hover:bg-slate-50"
                        title="Recarregar dados de anúncios"
                      >
                        <RefreshCw size={11} />
                        Atualizar anúncios
                      </button>
                    </div>
                    <AnunciosDashboard
                      anuncios={anunciosParaExibir || []}
                      casos={casosAnunciosComAgendaOperacional || []}
                      periodoLabel={periodoLabel}
                      onSelecionarConversa={
                        onSelecionarConversa || onVisualizarConversa
                      }
                      onVerDetalhes={setCasoSelecionado}
                    />
                  </>
                )}
              </aside>
            ) : null}
          </section>
        </main>
      </section>

      {leadHistoricoSelecionada?.id ? (
        <LogsModalLead
          lead={leadHistoricoSelecionada}
          usuarios={usuarios || []}
          onClose={() => setLeadHistoricoSelecionada(null)}
        />
      ) : null}

      {agendaSelecionada && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[10000]">
              <AgendamentosModal
                key={`supervisao-agendamento-${montarLeadAgendamentoModalSupervisao(agendaSelecionada)?.id || "sem-lead"}`}
                isOpen={Boolean(agendaSelecionada)}
                lead={montarLeadAgendamentoModalSupervisao(agendaSelecionada)}
                onClose={() => setAgendaSelecionada(null)}
                onUltimoAgendamento={() => {}}
                onAgendamentoSalvo={async () => {
                  await carregarSupervisao({ forcar: true }).catch(() => null);
                  onAtualizar?.();
                }}
              />
            </div>,
            document.body,
          )
        : null}

      {agendaFollowupSelecionado ? (
        <AgendaFollowupSupervisaoModal
          caso={agendaFollowupSelecionado?.caso || agendaFollowupSelecionado}
          info={agendaFollowupSelecionado?.info || {}}
          onClose={() => setAgendaFollowupSelecionado(null)}
        />
      ) : null}

      {pagamentoSelecionado ? (
        <HistoricoPagamentosSupervisaoModal
          caso={pagamentoSelecionado}
          empresaIdFallback={empresaIdEfetiva}
          onClose={() => setPagamentoSelecionado(null)}
        />
      ) : null}

      {casoSelecionado ? (
        <CasoDetalheModal
          caso={casoSelecionado}
          onClose={() => setCasoSelecionado(null)}
          onSelecionarConversa={onSelecionarConversa || onVisualizarConversa}
        />
      ) : null}
    </div>
  );
}

function calcularMetricasStatusPeriodoSupervisao({
  casos = [],
  casosAgendadosHojeOficiais = null,
  agendamentosEmpresa = [],
  resumo = {},
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  const hojeISO = getHojeISO();
  const amanhaISO = getDataAgendaAmanhaOperacionalISO(hojeISO);
  const agendaOperacionalEhAmanha = amanhaISO === addDiasISO(hojeISO, 1);

  const chamadosAbertosBackend = getPrimeiroNumeroSupervisao([
    resumo.chamados_abertos_periodo,
    resumo.abertos_periodo,
    resumo.chamados_abertos,
    resumo.abertos,
    resumo.em_atendimento_periodo,
    resumo.em_atendimento,
  ]);

  const chamadosFechadosBackend = getPrimeiroNumeroSupervisao([
    resumo.chamados_fechados_periodo,
    resumo.fechados_periodo,
    resumo.chamados_fechados,
    resumo.fechados,
  ]);

  // Previsto Hoje = agenda/consulta com data marcada para hoje.
  // Não usar "agendamentos_hoje" genérico aqui, porque em muitos retornos ele
  // significa agendamentos criados hoje, não consultas previstas para hoje.
  const previstoHojeBackend = getPrimeiroNumeroSupervisao([
    // Usar apenas campos explicitamente distintos/oficiais.
    // Campos genéricos como previsto_hoje/agenda_prevista_hoje podem representar
    // linhas de agendamento; na prova real havia 23 linhas para 17 pacientes.
    resumo.previsto_hoje_pacientes,
    resumo.previsto_hoje_leads,
    resumo.leads_previstos_hoje,
    resumo.pacientes_previstos_hoje,
    resumo.previsto_hoje_distinto,
    resumo.previstos_hoje_distintos,
    resumo.agenda_prevista_hoje_pacientes,
    resumo.agenda_prevista_hoje_leads,
  ]);

  // Agendados Hoje = produção da equipe: agendamentos criados/marcados hoje.
  // Aqui usamos primeiro o número oficial do backend, porque ele já reflete
  // a regra operacional da tela. A rota geral de agendamentos é usada só como
  // fallback, pois pode trazer registros técnicos/duplicados/importados.
  const agendadosHojeBackend = getPrimeiroNumeroSupervisao([
    resumo.agendamentos_criados_hoje,
    resumo.agendados_criados_hoje,
    resumo.agendamentos_feitos_hoje,
    resumo.agendados_feitos_hoje,
    resumo.agendas_criadas_hoje,
    resumo.agenda_criada_hoje,
    resumo.agendamentos_marcados_hoje,
    resumo.agendados_marcados_hoje,
    resumo.novos_agendamentos_hoje,
    resumo.producao_agenda_hoje,
    resumo.agendados_hoje_pacientes,
    resumo.agendados_hoje_leads,
    resumo.agendados_hoje_distinto,
    resumo.agendamentos_criados_hoje_pacientes,
    resumo.agendamentos_criados_hoje_leads,
    resumo.agendamentos_marcados_hoje_pacientes,
    resumo.agendamentos_marcados_hoje_leads,
    resumo.novos_agendamentos_hoje_pacientes,
    resumo.novos_agendamentos_hoje_leads,
    resumo.producao_agenda_hoje_pacientes,
    resumo.producao_agenda_hoje_leads,
  ]);

  const agendaAmanhaBackend = getPrimeiroNumeroSupervisao([
    resumo.agenda_amanha,
    resumo.agenda_para_amanha,
    resumo.agendados_amanha,
    resumo.agendados_para_amanha,
    resumo.previstos_amanha,
    resumo.previsto_amanha,
    resumo.consultas_amanha,
    resumo.consultas_para_amanha,
    resumo.agenda_amanha_pacientes,
    resumo.agendados_amanha_pacientes,
    resumo.agenda_amanha_leads,
    resumo.agendados_amanha_leads,
  ]);

  const confirmadosAmanhaBackend = getPrimeiroNumeroSupervisao([
    resumo.confirmados_amanha,
    resumo.confirmados_para_amanha,
    resumo.agenda_confirmada_amanha,
    resumo.agenda_amanha_confirmados,
    resumo.confirmacoes_amanha,
    resumo.confirmacoes_para_amanha,
    resumo.pacientes_confirmados_amanha,
    resumo.leads_confirmados_amanha,
  ]);

  const filaBackend = getPrimeiroNumeroSupervisao([
    resumo.fila_periodo,
    resumo.fila,
    resumo.casos_fila_total,
    resumo.total_fila,
    resumo.fila_de_espera,
    resumo.filaDeEspera,
  ]);

  const faltasBackend = getPrimeiroNumeroSupervisao([
    resumo.faltas_hoje,
    resumo.faltas_periodo,
    resumo.faltas_agendamento_periodo,
    resumo.faltas_agendamentos_periodo,
    resumo.faltas,
    resumo.pacientes_faltaram_periodo,
  ]);

  const cancelamentosBackend = getPrimeiroNumeroSupervisao([
    resumo.cancelamentos_hoje,
    resumo.cancelamentos_periodo,
    resumo.cancelamentos_agendamento_periodo,
    resumo.cancelamentos_agendamentos_periodo,
    resumo.cancelados_periodo,
    resumo.cancelamentos,
  ]);

  // A conversão da agenda precisa usar a mesma base exibida no card
  // Previsto Hoje. Taxas genéricas do backend podem estar calculadas sobre
  // linhas duplicadas ou período diferente; por isso não entram aqui.
  const taxaAgendaBackend = null;

  const realizadosAgendaBackend = getPrimeiroNumeroSupervisao([
    resumo.realizados_agenda_hoje,
    resumo.comparecimentos_hoje,
    resumo.compareceram_hoje,
    resumo.consultas_realizadas_hoje,
    resumo.agendamentos_realizados_hoje,
  ]);

  const metricasAgendaOficial = calcularMetricasAgendamentosEmpresaSupervisao(
    agendamentosEmpresa,
    hojeISO,
  );

  // A lista da aba "Agendados Hoje" já vem do backend de supervisão com a
  // regra operacional correta: último status, sem cancelados indevidos e sem
  // duplicidade visual. O card precisa usar essa mesma fonte para não mostrar
  // 24 enquanto a tabela mostra 19.
  const agendadosHojeOficiais = Array.isArray(casosAgendadosHojeOficiais)
    ? casosAgendadosHojeOficiais.length
    : null;

  const vistosAbertos = new Set();
  const vistosFechados = new Set();
  const vistosPrevistoHoje = new Set();
  const vistosAgendadosHoje = new Set();
  const vistosAgendaAmanha = new Set();
  const vistosConfirmadosAmanha = new Set();
  const vistosFila = new Set();
  const vistosFaltas = new Set();
  const vistosCancelamentos = new Set();
  const vistosRealizadosAgenda = new Set();

  let chamadosAbertosCasos = 0;
  let chamadosFechadosCasos = 0;
  let previstoHojeCasos = 0;
  let agendadosHojeCasos = 0;
  let agendaAmanhaCasos = 0;
  let confirmadosAmanhaCasos = 0;
  let filaCasos = 0;
  let faltasCasos = 0;
  let cancelamentosCasos = 0;
  let realizadosAgendaCasos = 0;

  for (const caso of Array.isArray(casos) ? casos : []) {
    const key =
      getCasoKey(caso) ||
      getChaveCasoAdsDiaSupervisao(caso, vistosAbertos.size);
    const keyPessoaAgenda = getChavePessoaAgendaMetricaSupervisao(caso) || key;

    const dataAbertura = getDataAberturaCasoSupervisao(caso);
    if (
      !vistosAbertos.has(key) &&
      isDataNoPeriodoSupervisao(dataAbertura, dataInicio, dataFim)
    ) {
      vistosAbertos.add(key);
      chamadosAbertosCasos += 1;
    }

    const dataFechamento = getDataFechamentoCasoSupervisao(caso);
    if (
      isCasoFechadoSupervisao(caso) &&
      !vistosFechados.has(key) &&
      isDataNoPeriodoSupervisao(dataFechamento, dataInicio, dataFim)
    ) {
      vistosFechados.add(key);
      chamadosFechadosCasos += 1;
    }

    if (isCasoFilaFiltroSupervisao(caso) && !vistosFila.has(key)) {
      vistosFila.add(key);
      filaCasos += 1;
    }

    const agenda = getAgendamentoCasoSupervisao(caso);
    const temAgenda = Boolean(agenda?.has || isCasoAgendadoSupervisao(caso));
    const dataPrevistaAgenda = getDataPrevistaAgendaHojeCasoSupervisao(caso);
    const dataCriacaoAgenda = getDataCriacaoAgendamentoCasoSupervisao(caso);
    const statusAgenda = getStatusAgendaNormalizadoCasoSupervisao(caso);
    const dataStatusAgenda = getDataStatusAgendaCasoSupervisao(caso);
    const agendaPrevistaHoje =
      temAgenda && isMesmoDiaSupervisao(dataPrevistaAgenda, hojeISO);

    if (agendaPrevistaHoje && !vistosPrevistoHoje.has(keyPessoaAgenda)) {
      vistosPrevistoHoje.add(keyPessoaAgenda);
      previstoHojeCasos += 1;
    }

    if (
      temAgenda &&
      !vistosAgendadosHoje.has(keyPessoaAgenda) &&
      isMesmoDiaSupervisao(dataCriacaoAgenda, hojeISO) &&
      isStatusAgendaValidoParaProducaoSupervisao(statusAgenda)
    ) {
      vistosAgendadosHoje.add(keyPessoaAgenda);
      agendadosHojeCasos += 1;
    }

    const agendaPrevistaAmanha =
      temAgenda && isMesmoDiaSupervisao(dataPrevistaAgenda, amanhaISO);

    if (
      agendaPrevistaAmanha &&
      !vistosAgendaAmanha.has(keyPessoaAgenda) &&
      isStatusAgendaAtivoParaAgendaFuturaSupervisao(statusAgenda)
    ) {
      vistosAgendaAmanha.add(keyPessoaAgenda);
      agendaAmanhaCasos += 1;
    }

    if (
      agendaPrevistaAmanha &&
      !vistosConfirmadosAmanha.has(keyPessoaAgenda) &&
      isStatusAgendaConfirmadoSupervisao(statusAgenda)
    ) {
      vistosConfirmadosAmanha.add(keyPessoaAgenda);
      confirmadosAmanhaCasos += 1;
    }

    // Faltas/cancelamentos ficam ligados à agenda do dia. Se houver data de status,
    // ela também precisa cair em hoje; se não houver, a data prevista da agenda de hoje
    // serve como fallback seguro.
    const faltaHoje =
      statusAgenda.includes("falt") &&
      (isMesmoDiaSupervisao(dataStatusAgenda, hojeISO) || agendaPrevistaHoje);
    const cancelamentoHoje =
      statusAgenda.includes("cancel") &&
      (isMesmoDiaSupervisao(dataStatusAgenda, hojeISO) || agendaPrevistaHoje);

    if (faltaHoje && !vistosFaltas.has(keyPessoaAgenda)) {
      vistosFaltas.add(keyPessoaAgenda);
      faltasCasos += 1;
    }

    if (cancelamentoHoje && !vistosCancelamentos.has(keyPessoaAgenda)) {
      vistosCancelamentos.add(keyPessoaAgenda);
      cancelamentosCasos += 1;
    }

    const realizadoHoje =
      agendaPrevistaHoje &&
      (statusAgenda.includes("comparec") ||
        statusAgenda.includes("realiz") ||
        statusAgenda.includes("conclu") ||
        statusAgenda.includes("atendid"));

    if (realizadoHoje && !vistosRealizadosAgenda.has(keyPessoaAgenda)) {
      vistosRealizadosAgenda.add(keyPessoaAgenda);
      realizadosAgendaCasos += 1;
    }
  }

  const chamadosAbertos = chamadosAbertosBackend ?? chamadosAbertosCasos;
  const chamadosFechados = chamadosFechadosBackend ?? chamadosFechadosCasos;
  const temCasosOperacionais = Array.isArray(casos) && casos.length > 0;
  const fila = temCasosOperacionais ? filaCasos : (filaBackend ?? 0);
  // A prova real no banco mostrou que a agenda do dia pode ter múltiplas linhas
  // para o mesmo lead. Por isso, quando houver casos carregados, a métrica do
  // frontend prioriza pacientes/leads únicos; campos genéricos do backend não
  // são usados como fonte oficial para este card.
  const previstoHoje = metricasAgendaOficial.temAgendaHoje
    ? metricasAgendaOficial.previstoHoje
    : previstoHojeCasos > 0
      ? previstoHojeCasos
      : previstoHojeBackend || 0;
  const agendadosHoje =
    agendadosHojeOficiais !== null
      ? agendadosHojeOficiais
      : metricasAgendaOficial.temAgendadosHoje
        ? metricasAgendaOficial.agendadosHoje
        : agendadosHojeCasos > 0
          ? agendadosHojeCasos
          : agendadosHojeBackend !== null
            ? agendadosHojeBackend
            : 0;
  const agendaAmanha = metricasAgendaOficial.temAgendaAmanha
    ? metricasAgendaOficial.agendaAmanha
    : agendaAmanhaCasos > 0
      ? agendaAmanhaCasos
      : agendaOperacionalEhAmanha && agendaAmanhaBackend !== null
        ? agendaAmanhaBackend
        : 0;
  const confirmadosAmanhaBruto = metricasAgendaOficial.temAgendaAmanha
    ? metricasAgendaOficial.confirmadosAmanha
    : confirmadosAmanhaCasos > 0
      ? confirmadosAmanhaCasos
      : agendaOperacionalEhAmanha && confirmadosAmanhaBackend !== null
        ? confirmadosAmanhaBackend
        : 0;
  const confirmadosAmanha = Math.min(
    Math.max(getNumero(confirmadosAmanhaBruto), 0),
    agendaAmanha,
  );
  const taxaConfirmadosAmanha =
    agendaAmanha > 0
      ? Math.round((confirmadosAmanha / agendaAmanha) * 1000) / 10
      : null;
  const faltas = metricasAgendaOficial.temAgendaHoje
    ? metricasAgendaOficial.faltas
    : (faltasBackend ?? faltasCasos);
  const cancelamentos = metricasAgendaOficial.temAgendaHoje
    ? metricasAgendaOficial.cancelamentos
    : (cancelamentosBackend ?? cancelamentosCasos);
  const faltasCancelamentos = faltas + cancelamentos;
  const baseConversaoAgenda = previstoHoje;
  const realizadosAgendaCalculado = Math.max(
    baseConversaoAgenda - faltasCancelamentos,
    0,
  );
  const realizadosAgendaBruto =
    realizadosAgendaCasos > 0
      ? realizadosAgendaCasos
      : (realizadosAgendaBackend ?? realizadosAgendaCalculado);
  const realizadosAgenda = Math.min(
    Math.max(getNumero(realizadosAgendaBruto), 0),
    baseConversaoAgenda,
  );
  const taxaAgendamento =
    baseConversaoAgenda > 0
      ? Math.round((realizadosAgenda / baseConversaoAgenda) * 1000) / 10
      : null;

  return {
    chamadosAbertos,
    chamadosFechados,
    fila,
    previstoHoje,
    agendadosHoje,
    agendamentos: agendadosHoje,
    agendaAmanha,
    confirmadosAmanha,
    taxaConfirmadosAmanha,
    baseConfirmadosAmanha: agendaAmanha,
    faltas,
    cancelamentos,
    faltasCancelamentos,
    realizadosAgenda,
    baseConversaoAgenda,
    taxaAgendamento,
    fontePrevistoHoje: metricasAgendaOficial.temAgendaHoje
      ? "agendamentos_empresa"
      : previstoHojeCasos > 0
        ? "casos"
        : previstoHojeBackend !== null
          ? "resumo"
          : "sem_fonte",
    fonteAgendadosHoje:
      agendadosHojeOficiais !== null
        ? "casos_agendados"
        : metricasAgendaOficial.temAgendadosHoje
          ? "agendamentos_empresa"
          : agendadosHojeCasos > 0
            ? "casos"
            : agendadosHojeBackend !== null
              ? "resumo"
              : "sem_fonte",
  };
}

function extrairListaConversasOperacionaisSupervisao(data) {
  if (Array.isArray(data)) return data.filter(Boolean);
  if (!data || typeof data !== "object") return [];

  const candidatos = [
    data.conversas,
    data.items,
    data.data,
    data.results,
    data.registros,
    data.rows,
    data.chamados,
    data.atendimentos,
  ];

  for (const candidato of candidatos) {
    if (Array.isArray(candidato)) return candidato.filter(Boolean);
  }

  return [];
}

function montarCasoAbertoDiaOperacionalSupervisao(
  conversa = {},
  usuarios = [],
) {
  const responsavel = getResponsavelConversa(conversa, usuarios);
  const espera = getTempoSemResposta(conversa, Date.now());
  const esperaMin = msParaMinutos(espera?.ms || 0);
  const tipo = espera?.tipo || "abertos_dia";
  const lead = conversa.lead || {};

  return {
    ...conversa,
    origem: "abertos_dia",
    tipo,
    conversa,
    chamado_id:
      conversa.chamado_id ||
      conversa.id_chamado ||
      conversa.ticket_id ||
      conversa.id ||
      null,
    id: conversa.id || conversa.chamado_id || conversa.id_chamado || null,
    nome: getNomeConversa(conversa),
    telefone: conversa.telefone || conversa.numero || lead.telefone1 || "",
    lead_id:
      conversa.lead_id ||
      conversa.leadId ||
      conversa.lead_contexto_id ||
      conversa.leadContextoId ||
      lead.id ||
      null,
    lead,
    aberto_em:
      conversa.aberto_em ||
      conversa.abertoEm ||
      conversa.chamado_aberto_em ||
      conversa.data_abertura ||
      conversa.created_at ||
      conversa.criado_em ||
      "",
    responsavel_id: responsavel.id,
    responsavel_nome: responsavel.nome,
    status_chamado:
      getStatusChamadoRaw(conversa) || getStatusChamadoLabel(conversa),
    chamado_fechado: isChamadoFechadoSupervisao(conversa),
    espera_minutos: esperaMin,
    criticidade: getCriticidadePorTipo(tipo, esperaMin),
    naoLidas: Number(conversa.naoLidas || conversa.nao_lidas || 0),
    mensagem: getMensagemPreview(conversa),
    ultima_mensagem: conversa.ultima_mensagem || {
      texto: getMensagemPreview(conversa),
      datahora:
        conversa.ultimaMensagemEm ||
        conversa.ultima_mensagem_em ||
        conversa.datahora ||
        conversa.updated_at ||
        conversa.created_at ||
        "",
      direcao: conversa.ultima_mensagem_direcao || conversa.direcao || "",
      autor: conversa.ultima_mensagem_autor || conversa.autor || "",
    },
    origem_ads: getOrigemAdsDiretaConversa(conversa),
  };
}

function deduplicarCasosPorChaveSupervisao(lista = []) {
  const map = new Map();

  for (const caso of Array.isArray(lista) ? lista : []) {
    if (!caso || typeof caso !== "object") continue;
    const key =
      getCasoKey(caso) ||
      getCasoKey(caso.conversa || {}) ||
      `${normalizarTelefone(
        caso.telefone ||
          caso.numero ||
          caso.conversa?.telefone ||
          caso.conversa?.numero ||
          "",
      ).slice(
        -11,
      )}-${caso.aberto_em || caso.conversa?.aberto_em || caso.created_at || map.size}`;

    if (!key) continue;
    if (!map.has(key)) map.set(key, caso);
  }

  return [...map.values()];
}

function extrairListaAgendamentosSupervisao(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const candidatos = [
    payload.agendamentos,
    payload.data,
    payload.items,
    payload.rows,
    payload.result,
    payload.results,
    payload.registros,
    payload.lista,
  ];

  for (const candidato of candidatos) {
    if (Array.isArray(candidato)) return candidato;
    if (candidato && typeof candidato === "object") {
      const interno = extrairListaAgendamentosSupervisao(candidato);
      if (interno.length) return interno;
    }
  }

  return [];
}

function extrairListaLeadsSupervisao(payload) {
  if (!payload) return [];

  const candidatos = [
    payload,
    payload?.data,
    payload?.dados,
    payload?.items,
    payload?.results,
    payload?.registros,
    payload?.rows,
    payload?.leads,
    payload?.contatos,
  ];

  for (const candidato of candidatos) {
    if (Array.isArray(candidato)) return candidato.filter(Boolean);
  }

  return [];
}

function getLeadIdMapaSupervisao(lead = {}) {
  return getPrimeiroValor([
    lead.id,
    lead.lead_id,
    lead.leadId,
    lead.id_lead,
    lead.leads_id,
  ]);
}

function adicionarLeadAoMapaSupervisao(map, lead) {
  if (!lead || typeof lead !== "object") return;

  const leadId = getLeadIdMapaSupervisao(lead);
  if (!leadId) return;

  const key = String(leadId);
  const atual = map.get(key) || {};
  map.set(key, {
    ...atual,
    ...lead,
    id: lead.id || atual.id || leadId,
    lead_id: lead.lead_id || atual.lead_id || leadId,
  });
}

function criarMapaLeadsEmpresaSupervisao(leads = [], casos = []) {
  const map = new Map();

  (Array.isArray(leads) ? leads : []).forEach((lead) =>
    adicionarLeadAoMapaSupervisao(map, lead),
  );

  (Array.isArray(casos) ? casos : []).forEach((caso) => {
    if (!caso || typeof caso !== "object") return;

    const conversa = caso.conversa || {};
    adicionarLeadAoMapaSupervisao(map, caso.lead);
    adicionarLeadAoMapaSupervisao(map, conversa.lead);

    if (caso.lead_id || caso.leadId || caso.id_lead) {
      adicionarLeadAoMapaSupervisao(map, {
        id: caso.lead_id || caso.leadId || caso.id_lead,
        lead_id: caso.lead_id || caso.leadId || caso.id_lead,
        nome_lead: caso.nome_lead || caso.lead_nome || caso.nome,
        nome: caso.nome || caso.nome_lead || caso.lead_nome,
        telefone1: caso.telefone1 || caso.telefone || caso.numero,
        telefone: caso.telefone || caso.numero || caso.telefone1,
        status_negociacao: caso.status_negociacao,
      });
    }

    if (conversa.lead_id || conversa.leadId || conversa.id_lead) {
      adicionarLeadAoMapaSupervisao(map, {
        id: conversa.lead_id || conversa.leadId || conversa.id_lead,
        lead_id: conversa.lead_id || conversa.leadId || conversa.id_lead,
        nome_lead: conversa.nome_lead || conversa.lead_nome || conversa.nome,
        nome: conversa.nome || conversa.nome_lead || conversa.lead_nome,
        telefone1: conversa.telefone1 || conversa.telefone || conversa.numero,
        telefone: conversa.telefone || conversa.numero || conversa.telefone1,
        status_negociacao: conversa.status_negociacao,
      });
    }
  });

  return map;
}

function getStatusLabelAgendadosAmanhaSupervisao(status = "") {
  const normalizado = normalizar(status);

  if (normalizado.includes("confirm")) return "Confirmado";
  if (normalizado.includes("reagend")) return "Reagendado";
  if (normalizado.includes("comparec")) return "Compareceu";
  if (normalizado.includes("realiz")) return "Realizado";
  if (normalizado.includes("conclu")) return "Concluído";
  if (normalizado.includes("atendid")) return "Atendido";
  if (normalizado.includes("agend")) return "Agendado";

  return "Agendado";
}

function montarCasosAgendadosAmanhaSupervisao(
  agendamentos = [],
  hojeISO = getHojeISO(),
  usuarios = [],
  mapaLeadsEmpresa = new Map(),
) {
  const lista = Array.isArray(agendamentos) ? agendamentos : [];
  if (!lista.length) return [];

  const amanhaISO = getDataAgendaAmanhaOperacionalISO(hojeISO);
  const gruposAgenda = new Map();

  lista.forEach((item, index) => {
    if (!item || typeof item !== "object") return;

    const chaveAgenda = getChaveAgendaRegistroSupervisao(item, index);
    const registro = {
      item,
      index,
      status: getStatusAgendamentoRegistroSupervisao(item),
      dataPrevista: getDataPrevistaAgendamentoRegistroSupervisao(item),
      dataCriacao: getDataCriacaoAgendamentoRegistroSupervisao(item),
      ordem: getOrdemAgendamentoRegistroSupervisao(item, index),
      chavePessoa: getChavePessoaAgendamentoRegistroSupervisao(item, index),
    };

    if (!gruposAgenda.has(chaveAgenda)) gruposAgenda.set(chaveAgenda, []);
    gruposAgenda.get(chaveAgenda).push(registro);
  });

  const casos = [];

  gruposAgenda.forEach((registros) => {
    const ordenados = [...registros].sort((a, b) => b.ordem - a.ordem);
    const atual = ordenados[0] || registros[0];
    if (!atual) return;

    const temAgendaAmanha = registros.some((registro) =>
      isMesmoDiaSupervisao(registro.dataPrevista, amanhaISO),
    );

    if (!temAgendaAmanha) return;
    if (!isStatusAgendaAtivoParaAgendaFuturaSupervisao(atual.status)) return;

    const confirmado = isStatusAgendaConfirmadoSupervisao(atual.status);
    const statusLabel = getStatusLabelAgendadosAmanhaSupervisao(atual.status);

    const caso = montarCasoAgendamentoEmpresaSupervisao(
      atual.item,
      atual.index,
      usuarios,
      {
        tipo: "agendados_amanha",
        statusLabel,
        dataPrevista: atual.dataPrevista,
        statusAgendamento: atual.status,
      },
      mapaLeadsEmpresa,
    );

    casos.push({
      ...caso,
      _supervisao_agenda_amanha: true,
      _supervisao_confirmado_amanha: confirmado,
      confirmado_amanha: confirmado,
      confirmacao_amanha_status: confirmado ? "Confirmado" : "Pendente",
      lead: {
        ...(caso.lead || {}),
        confirmado_amanha: confirmado,
        confirmacao_amanha_status: confirmado ? "Confirmado" : "Pendente",
      },
    });
  });

  return casos.sort((a, b) => {
    const dataA = parseTimestamp(getAgendamentoCasoSupervisao(a).data);
    const dataB = parseTimestamp(getAgendamentoCasoSupervisao(b).data);
    return dataA - dataB;
  });
}

function montarCasosConfirmadosAmanhaSupervisao(
  agendamentos = [],
  hojeISO = getHojeISO(),
  usuarios = [],
) {
  const lista = Array.isArray(agendamentos) ? agendamentos : [];
  if (!lista.length) return [];

  const amanhaISO = getDataAgendaAmanhaOperacionalISO(hojeISO);
  const gruposAgenda = new Map();

  lista.forEach((item, index) => {
    if (!item || typeof item !== "object") return;

    const chaveAgenda = getChaveAgendaRegistroSupervisao(item, index);
    const registro = {
      item,
      index,
      status: getStatusAgendamentoRegistroSupervisao(item),
      dataPrevista: getDataPrevistaAgendamentoRegistroSupervisao(item),
      dataCriacao: getDataCriacaoAgendamentoRegistroSupervisao(item),
      ordem: getOrdemAgendamentoRegistroSupervisao(item, index),
      chavePessoa: getChavePessoaAgendamentoRegistroSupervisao(item, index),
    };

    if (!gruposAgenda.has(chaveAgenda)) gruposAgenda.set(chaveAgenda, []);
    gruposAgenda.get(chaveAgenda).push(registro);
  });

  const casos = [];

  gruposAgenda.forEach((registros) => {
    const ordenados = [...registros].sort((a, b) => b.ordem - a.ordem);
    const atual = ordenados[0] || registros[0];
    if (!atual) return;

    const temAgendaAmanha = registros.some((registro) =>
      isMesmoDiaSupervisao(registro.dataPrevista, amanhaISO),
    );

    if (!temAgendaAmanha) return;
    if (!isStatusAgendaConfirmadoSupervisao(atual.status)) return;

    casos.push(
      montarCasoAgendamentoEmpresaSupervisao(
        atual.item,
        atual.index,
        usuarios,
        {
          tipo: "confirmados_amanha",
          statusLabel: "Confirmado",
          dataPrevista: atual.dataPrevista,
          statusAgendamento: atual.status,
        },
      ),
    );
  });

  return casos.sort((a, b) => {
    const dataA = parseTimestamp(getAgendamentoCasoSupervisao(a).data);
    const dataB = parseTimestamp(getAgendamentoCasoSupervisao(b).data);
    return dataA - dataB;
  });
}

function montarCasoAgendamentoEmpresaSupervisao(
  item = {},
  index = 0,
  usuarios = [],
  options = {},
  mapaLeadsEmpresa = new Map(),
) {
  const leadBase = item.lead || item.paciente || item.cliente || {};
  const leadId = getPrimeiroValor([
    item.lead_id,
    item.leadId,
    item.id_lead,
    item.paciente_id,
    item.cliente_id,
    leadBase.id,
    leadBase.lead_id,
    leadBase.id_lead,
  ]);
  const leadMapa = leadId ? mapaLeadsEmpresa.get(String(leadId)) : null;
  const lead = { ...(leadMapa || {}), ...leadBase };
  const nome =
    getPrimeiroValor([
      item.nome_lead,
      item.lead_nome,
      item.nomeLead,
      item.nome,
      item.paciente_nome,
      item.cliente_nome,
      lead.nome_lead,
      lead.nomeLead,
      lead.nome,
      lead.paciente,
    ]) || (leadId ? `Lead ${leadId}` : "Contato agendado");
  const telefone = getPrimeiroValor([
    item.telefone1,
    item.telefone2,
    item.telefone,
    item.celular,
    item.whatsapp,
    item.numero,
    lead.telefone1,
    lead.telefone2,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
    lead.numero,
  ]);
  const usuarioId = getPrimeiroValor([
    item.usuario_id,
    item.id_usuario,
    item.usuario_criador_id,
    item.responsavel_id,
    item.captador_id,
    item.user_id,
  ]);
  const usuario = Array.isArray(usuarios)
    ? usuarios.find(
        (u) => String(getUsuarioId(u) || "") === String(usuarioId || ""),
      )
    : null;
  const usuarioNome = getPrimeiroValor([
    item.usuario_nome,
    item.nome_usuario,
    item.usuario_criador_nome,
    item.responsavel_nome,
    item.captador_nome,
    getNomeUsuario(usuario),
  ]);
  const dataPrevista =
    options.dataPrevista || getDataPrevistaAgendamentoRegistroSupervisao(item);
  const dataCriacao = getDataCriacaoAgendamentoRegistroSupervisao(item);
  const statusOriginal =
    getPrimeiroValor([
      item.status_agendamento,
      item.statusAgendamento,
      item.status,
      item.situacao,
      options.statusLabel,
    ]) || "Confirmado";
  const agendamento = {
    ...item,
    id:
      item.id ||
      item.agendamento_id ||
      item.id_agendamento ||
      `agenda-${index}`,
    data_agendamento: dataPrevista,
    criado_em: dataCriacao,
    status_agendamento: options.statusLabel || statusOriginal,
    usuario_nome: usuarioNome,
  };
  const chaveAgenda = getChaveAgendaRegistroSupervisao(item, index);
  const chamadoId = `agenda-${options.tipo || "item"}-${leadId || normalizarTelefone(telefone) || index}-${parseTimestamp(dataPrevista) || index}`;

  return {
    id: chamadoId,
    chamado_id: chamadoId,
    conversa_id: chamadoId,
    tipo: options.tipo || "agendamento",
    nome,
    telefone,
    numero: telefone,
    lead_id: leadId || null,
    leadId: leadId || null,
    responsavel_id: usuarioId || null,
    responsavel_nome: usuarioNome || "Responsável não informado",
    captador_nome: usuarioNome || "Responsável não informado",
    status: options.statusLabel || statusOriginal,
    status_label: options.statusLabel || statusOriginal,
    status_agendamento: options.statusLabel || statusOriginal,
    status_negociacao: getPrimeiroValor([
      item.status_negociacao,
      item.statusNegociacao,
      lead.status_negociacao,
      lead.statusNegociacao,
    ]),
    data_agendamento: dataPrevista,
    datahora: dataPrevista,
    criado_em: dataCriacao || dataPrevista,
    updated_at:
      getDataAtualizacaoAgendamentoRegistroSupervisao(item) ||
      dataCriacao ||
      dataPrevista,
    espera_minutos: 0,
    esperaMs: 0,
    ultima_mensagem: {
      texto: `${options.statusLabel || statusOriginal} para ${formatarDataHoraCurta(dataPrevista)}`,
      direcao: "sistema",
      datahora:
        getDataAtualizacaoAgendamentoRegistroSupervisao(item) ||
        dataCriacao ||
        dataPrevista,
    },
    lead: {
      ...lead,
      id: lead.id || leadId || null,
      lead_id: lead.lead_id || leadId || null,
      nome_lead: lead.nome_lead || nome,
      nome: lead.nome || nome,
      telefone1: lead.telefone1 || telefone,
      telefone: lead.telefone || telefone,
      status_agendamento: options.statusLabel || statusOriginal,
      data_agendamento: dataPrevista,
      ultimo_agendamento: agendamento,
      _ultimo_agendamento: agendamento,
      agendamentos: [agendamento],
    },
    agendamentos: [agendamento],
    ultimo_agendamento: agendamento,
    _ultimo_agendamento: agendamento,
    _chave_agenda: chaveAgenda,
    _supervisao: true,
    _supervisao_agendamento_direto: true,
    _somente_leitura: true,
    _abrir_agendamento: Boolean(leadId),
  };
}

function calcularMetricasAgendamentosEmpresaSupervisao(
  agendamentos = [],
  hojeISO = getHojeISO(),
) {
  const lista = Array.isArray(agendamentos) ? agendamentos : [];
  const gruposAgenda = new Map();

  for (let index = 0; index < lista.length; index += 1) {
    const item = lista[index] || {};
    const chaveAgenda = getChaveAgendaRegistroSupervisao(item, index);
    const registro = {
      item,
      index,
      status: getStatusAgendamentoRegistroSupervisao(item),
      chavePessoa: getChavePessoaAgendamentoRegistroSupervisao(item, index),
      dataPrevista: getDataPrevistaAgendamentoRegistroSupervisao(item),
      dataCriacao: getDataCriacaoAgendamentoRegistroSupervisao(item),
      ordem: getOrdemAgendamentoRegistroSupervisao(item, index),
    };

    if (!gruposAgenda.has(chaveAgenda)) gruposAgenda.set(chaveAgenda, []);
    gruposAgenda.get(chaveAgenda).push(registro);
  }

  const amanhaISO = getDataAgendaAmanhaOperacionalISO(hojeISO);
  const previstos = new Set();
  const criadosHoje = new Set();
  const previstosAmanha = new Set();
  const confirmadosAmanha = new Set();
  const faltas = new Set();
  const cancelamentos = new Set();
  const realizados = new Set();

  gruposAgenda.forEach((registros, chaveAgenda) => {
    const ordenados = [...registros].sort((a, b) => b.ordem - a.ordem);
    const atual = ordenados[0] || registros[0];
    if (!atual) return;

    const previstoHoje = registros.some((registro) =>
      isMesmoDiaSupervisao(registro.dataPrevista, hojeISO),
    );

    if (previstoHoje) {
      // Previsto Hoje é uma métrica de pacientes/leads únicos na agenda do dia.
      // Mantém falta/cancelamento na base para a conversão da agenda.
      previstos.add(atual.chavePessoa);

      if (atual.status.includes("falt")) {
        faltas.add(atual.chavePessoa);
      } else if (atual.status.includes("cancel")) {
        cancelamentos.add(atual.chavePessoa);
      } else if (
        atual.status.includes("comparec") ||
        atual.status.includes("realiz") ||
        atual.status.includes("conclu") ||
        atual.status.includes("atendid")
      ) {
        realizados.add(atual.chavePessoa);
      }
    }

    const previstoAmanha = registros.some((registro) =>
      isMesmoDiaSupervisao(registro.dataPrevista, amanhaISO),
    );

    if (
      previstoAmanha &&
      isStatusAgendaAtivoParaAgendaFuturaSupervisao(atual.status)
    ) {
      previstosAmanha.add(atual.chavePessoa);

      if (isStatusAgendaConfirmadoSupervisao(atual.status)) {
        confirmadosAmanha.add(atual.chavePessoa);
      }
    }

    const teveCriacaoValidaHoje = registros.some(
      (registro) =>
        isMesmoDiaSupervisao(registro.dataCriacao, hojeISO) &&
        isStatusAgendaValidoParaProducaoSupervisao(registro.status),
    );

    // Agendados Hoje é produção líquida por paciente: primeiro resolve o último
    // status de cada horário/agendamento, depois conta a pessoa uma única vez
    // quando ainda existe pelo menos um agendamento criado hoje com status válido.
    // Ex.: Maria teve um horário de hoje cancelado e outro agendamento criado hoje
    // para 25/05; ela entra uma vez, pelo agendamento válido mais recente.
    if (
      teveCriacaoValidaHoje &&
      isStatusAgendaValidoParaProducaoSupervisao(atual.status)
    ) {
      criadosHoje.add(atual.chavePessoa);
    }
  });

  return {
    temAgendaHoje: previstos.size > 0,
    temAgendadosHoje: criadosHoje.size > 0,
    temAgendaAmanha: previstosAmanha.size > 0,
    previstoHoje: previstos.size,
    agendadosHoje: criadosHoje.size,
    agendaAmanha: previstosAmanha.size,
    confirmadosAmanha: confirmadosAmanha.size,
    taxaConfirmadosAmanha:
      previstosAmanha.size > 0
        ? Math.round((confirmadosAmanha.size / previstosAmanha.size) * 1000) /
          10
        : null,
    faltas: faltas.size,
    cancelamentos: cancelamentos.size,
    realizadosAgenda: realizados.size,
  };
}

function deduplicarCasosAgendaHojeUltimoStatusSupervisao(lista = []) {
  if (!Array.isArray(lista) || lista.length <= 1)
    return Array.isArray(lista) ? lista : [];

  const porAgenda = new Map();

  lista.forEach((caso, index) => {
    const chave = getChaveAgendaCasoSupervisao(caso, index);
    const ordem = getOrdemCasoAgendaStatusSupervisao(caso, index);
    const atual = porAgenda.get(chave);

    if (!atual || ordem > atual.ordem) {
      porAgenda.set(chave, { caso, index, ordem });
    }
  });

  return [...porAgenda.values()]
    .sort((a, b) => a.index - b.index)
    .map((item) => item.caso);
}

function getDiaISOAgendaCasoSupervisao(caso = {}) {
  const data =
    getDataPrevistaAgendaHojeCasoSupervisao(caso) ||
    getDataAgendamentoPeriodoCasoSupervisao(caso) ||
    caso.data_agendamento ||
    caso.dataAgendamento ||
    caso.datahora;
  const timestamp = parseTimestamp(data);
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function deduplicarCasosAgendaFuturaPorPessoaDataSupervisao(lista = []) {
  if (!Array.isArray(lista) || lista.length <= 1)
    return Array.isArray(lista) ? lista : [];

  const porPessoaDia = new Map();

  lista.forEach((caso, index) => {
    const chavePessoa =
      getChavePessoaAgendaMetricaSupervisao(caso) ||
      getCasoKey(caso) ||
      `idx:${index}`;
    const diaISO = getDiaISOAgendaCasoSupervisao(caso) || "sem-dia";
    const chave = `${chavePessoa}|dia:${diaISO}`;
    const ordem = getOrdemCasoAgendaStatusSupervisao(caso, index);
    const atual = porPessoaDia.get(chave);

    if (!atual || ordem > atual.ordem) {
      porPessoaDia.set(chave, { caso, index, ordem });
    }
  });

  return [...porPessoaDia.values()]
    .sort((a, b) => {
      const dataA = parseTimestamp(
        getDataPrevistaAgendaHojeCasoSupervisao(a.caso),
      );
      const dataB = parseTimestamp(
        getDataPrevistaAgendaHojeCasoSupervisao(b.caso),
      );
      if (dataA !== dataB) return dataA - dataB;
      return a.index - b.index;
    })
    .map((item) => item.caso);
}

function deduplicarCasosAgendadosUltimoStatusSupervisao(lista = []) {
  if (!Array.isArray(lista) || lista.length <= 1)
    return Array.isArray(lista) ? lista : [];

  // Primeiro resolve o último status de cada horário/agendamento.
  // Um horário criado hoje e depois cancelado não deve entrar em Agendados.
  const agendaAtual = deduplicarCasosAgendaHojeUltimoStatusSupervisao(lista);
  const porPessoa = new Map();

  agendaAtual.forEach((caso, index) => {
    const status = getStatusAgendaNormalizadoCasoSupervisao(caso);
    const statusValido = isStatusAgendaValidoParaProducaoSupervisao(status);
    const statusFaltaCancelamento =
      isStatusAgendaFaltaOuCancelamentoSupervisao(status);

    if (statusFaltaCancelamento) return;
    if (!statusValido && status) return;

    const chavePessoa =
      getChavePessoaAgendaMetricaSupervisao(caso) ||
      getCasoKey(caso) ||
      `idx:${index}`;
    const ordem = getOrdemCasoAgendaStatusSupervisao(caso, index);
    const atual = porPessoa.get(chavePessoa);

    if (!atual || ordem > atual.ordem) {
      porPessoa.set(chavePessoa, { caso, index, ordem });
    }
  });

  return [...porPessoa.values()]
    .sort((a, b) => a.index - b.index)
    .map((item) => item.caso);
}

function getChaveAgendaCasoSupervisao(caso = {}, index = 0) {
  const chavePessoa =
    getChavePessoaAgendaMetricaSupervisao(caso) ||
    getCasoKey(caso) ||
    `idx:${index}`;
  const dataPrevista =
    getDataPrevistaAgendaHojeCasoSupervisao(caso) ||
    getDataAgendamentoPeriodoCasoSupervisao(caso);
  const timestampPrevisto = parseTimestamp(dataPrevista);

  if (timestampPrevisto) {
    return `${chavePessoa}|agenda:${Math.floor(timestampPrevisto / 60000)}`;
  }

  const agenda = getAgendamentoCasoSupervisao(caso) || {};
  const idAgenda = getPrimeiroValor([
    caso.agendamento_id,
    caso.id_agendamento,
    caso.agendamentoId,
    caso.idAgendamento,
    agenda.id,
    agenda.agendamento_id,
    agenda.id_agendamento,
  ]);

  return `${chavePessoa}|agenda:${idAgenda || getCasoKey(caso) || index}`;
}

function getOrdemCasoAgendaStatusSupervisao(caso = {}, index = 0) {
  const agenda = getAgendamentoCasoSupervisao(caso) || {};
  const timestamps = [
    getDataStatusAgendaCasoSupervisao(caso),
    getDataCriacaoAgendamentoCasoSupervisao(caso),
    getDataPrevistaAgendaHojeCasoSupervisao(caso),
    getDataAgendamentoPeriodoCasoSupervisao(caso),
    caso.atualizado_em,
    caso.atualizadoEm,
    caso.updated_at,
    caso.updatedAt,
    agenda.atualizado_em,
    agenda.atualizadoEm,
    agenda.updated_at,
    agenda.updatedAt,
  ]
    .map((valor) => parseTimestamp(valor))
    .filter(Boolean);

  const timestamp = timestamps.length ? Math.max(...timestamps) : 0;
  const id = Number(
    getPrimeiroValor([
      caso.agendamento_id,
      caso.id_agendamento,
      caso.agendamentoId,
      caso.idAgendamento,
      agenda.id,
      agenda.agendamento_id,
      agenda.id_agendamento,
      caso.id,
      caso.chamado_id,
      caso.conversa_id,
    ]),
  );

  return timestamp * 100000 + (Number.isFinite(id) ? id : index);
}

function getChavePessoaAgendamentoRegistroSupervisao(item = {}, index = 0) {
  const leadId = getPrimeiroValor([
    item.lead_id,
    item.leadId,
    item.id_lead,
    item.idLead,
    item.paciente_id,
    item.pacienteId,
    item.cliente_id,
    item.clienteId,
    item.lead?.id,
    item.paciente?.id,
  ]);
  if (leadId) return `lead:${leadId}`;

  const telefone = normalizarTelefone(
    getPrimeiroValor([
      item.telefone,
      item.celular,
      item.whatsapp,
      item.numero,
      item.lead?.telefone,
      item.lead?.celular,
      item.paciente?.telefone,
      item.paciente?.celular,
    ]),
  );
  if (telefone) return `tel:${telefone}`;

  const documento = String(
    getPrimeiroValor([
      item.cpf,
      item.documento,
      item.lead?.cpf,
      item.paciente?.cpf,
    ]) || "",
  ).replace(/\D+/g, "");
  if (documento) return `doc:${documento}`;

  const nome = normalizar(
    getPrimeiroValor([
      item.nome,
      item.lead_nome,
      item.paciente_nome,
      item.lead?.nome,
      item.paciente?.nome,
    ]),
  );
  if (nome) return `nome:${nome}`;

  return `registro:${item.id || item.agendamento_id || index}`;
}

function getDataPrevistaAgendamentoRegistroSupervisao(item = {}) {
  return getPrimeiroValor([
    item.data_agendamento,
    item.dataAgendamento,
    item.data_consulta,
    item.dataConsulta,
    item.data_prevista,
    item.dataPrevista,
    item.inicio,
    item.start,
    item.start_at,
    item.horario,
  ]);
}

function getDataCriacaoAgendamentoRegistroSupervisao(item = {}) {
  return getPrimeiroValor([
    item.criado_em,
    item.criadoEm,
    item.created_at,
    item.createdAt,
    item.data_criacao,
    item.dataCriacao,
    item.data_criacao_agendamento,
    item.agendamento_criado_em,
    item.agendamentoCriadoEm,
    item.marcado_em,
    item.marcadoEm,
  ]);
}

function getDataAtualizacaoAgendamentoRegistroSupervisao(item = {}) {
  return getPrimeiroValor([
    item.atualizado_em,
    item.atualizadoEm,
    item.updated_at,
    item.updatedAt,
    item.data_atualizacao,
    item.dataAtualizacao,
    item.status_em,
    item.statusEm,
    item.cancelado_em,
    item.canceladoEm,
    item.faltou_em,
    item.faltouEm,
    item.data_status,
    item.dataStatus,
    getDataCriacaoAgendamentoRegistroSupervisao(item),
  ]);
}

function getIdAgendamentoRegistroSupervisao(item = {}, index = 0) {
  const id = Number(
    getPrimeiroValor([
      item.id,
      item.agendamento_id,
      item.agendamentoId,
      item.id_agendamento,
      item.historico_id,
      item.historicoId,
    ]),
  );

  return Number.isFinite(id) ? id : index;
}

function getOrdemAgendamentoRegistroSupervisao(item = {}, index = 0) {
  const timestamp = parseTimestamp(
    getDataAtualizacaoAgendamentoRegistroSupervisao(item),
  );
  const id = getIdAgendamentoRegistroSupervisao(item, index);
  return (timestamp || 0) * 100000 + id;
}

function getChaveAgendaRegistroSupervisao(item = {}, index = 0) {
  const chavePessoa = getChavePessoaAgendamentoRegistroSupervisao(item, index);
  const dataPrevista = getDataPrevistaAgendamentoRegistroSupervisao(item);
  const timestampPrevisto = parseTimestamp(dataPrevista);

  if (timestampPrevisto) {
    // Agrupa o histórico do mesmo horário/consulta para descobrir o último status.
    // Não agrupa só por lead, porque a mesma pessoa pode ter dois agendamentos
    // criados no mesmo dia para datas diferentes.
    const minuto = Math.floor(timestampPrevisto / 60000);
    return `${chavePessoa}|agenda:${minuto}`;
  }

  return `${chavePessoa}|registro:${getIdAgendamentoRegistroSupervisao(item, index)}`;
}

function getStatusAgendamentoRegistroSupervisao(item = {}) {
  return normalizar(
    getPrimeiroValor([
      item.status_agendamento,
      item.statusAgendamento,
      item.status,
      item.situacao,
      item.etapa,
    ]) || "",
  );
}

function vincularAgendamentosAosCasosAdsSupervisao({
  casos = [],
  agendamentos = [],
  leads = [],
  casosReferencia = [],
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  const listaCasos = Array.isArray(casos) ? casos : [];
  if (!listaCasos.length) return [];

  const contextoVinculo = criarContextoVinculoAdsAgendamentoSupervisao({
    leads,
    casos: listaCasos,
    casosReferencia,
  });
  const mapaLeadsPorId = contextoVinculo.mapaLeadsPorId;
  const indiceAgendamentos = criarIndiceAgendamentosAdsPeriodoSupervisao({
    agendamentos,
    mapaLeadsPorId,
    dataInicio,
    dataFim,
  });

  if (!indiceAgendamentos.size) return listaCasos;

  return listaCasos.map((caso, index) => {
    const agendaAtual = getAgendamentoCasoSupervisao(caso);
    if (agendaAtual?.has) return caso;

    const chaves = getChavesCasoAdsComContextoSupervisao(
      caso,
      contextoVinculo,
      index,
    );
    const vinculo = chaves
      .map((chave) => indiceAgendamentos.get(chave))
      .find(Boolean);

    if (!vinculo?.agendamento) return caso;

    const agendaVinculada = montarAgendamentoVinculadoAdsSupervisao(
      vinculo.agendamento,
      vinculo.index,
    );

    return {
      ...caso,
      agendado: true,
      _agendamento_ads_vinculado: true,
      _ultimo_agendamento: agendaVinculada,
      ultimo_agendamento:
        caso.ultimo_agendamento || caso.ultimoAgendamento || agendaVinculada,
      status_agendamento:
        caso.status_agendamento ||
        agendaVinculada.status_agendamento ||
        "Agendado",
      data_agendamento:
        caso.data_agendamento || agendaVinculada.data_agendamento,
      agendamentos: [
        ...(Array.isArray(caso.agendamentos) ? caso.agendamentos : []),
        agendaVinculada,
      ],
    };
  });
}

function criarIndiceAgendamentosAdsPeriodoSupervisao({
  agendamentos = [],
  mapaLeadsPorId = new Map(),
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  const indice = new Map();
  const lista = Array.isArray(agendamentos) ? agendamentos : [];

  lista.forEach((agendamento, index) => {
    if (
      !isAgendamentoCriadoPeriodoAdsSupervisao(agendamento, dataInicio, dataFim)
    ) {
      return;
    }

    const chaves = getChavesAgendamentoAdsSupervisao(
      agendamento,
      mapaLeadsPorId,
      index,
    );

    for (const chave of chaves) {
      const atual = indice.get(chave);
      if (
        !atual ||
        getOrdemAgendamentoRegistroSupervisao(agendamento, index) > atual.ordem
      ) {
        indice.set(chave, {
          agendamento,
          index,
          ordem: getOrdemAgendamentoRegistroSupervisao(agendamento, index),
        });
      }
    }
  });

  return indice;
}

function isAgendamentoCriadoPeriodoAdsSupervisao(
  agendamento = {},
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
) {
  const dataCriacao = getDataCriacaoAgendamentoRegistroSupervisao(agendamento);
  if (!isDataNoPeriodoSupervisao(dataCriacao, dataInicio, dataFim))
    return false;

  const status = getStatusAgendamentoRegistroSupervisao(agendamento);
  if (isStatusAgendaFaltaOuCancelamentoSupervisao(status)) return false;

  return Boolean(
    getPrimeiroValor([
      agendamento.id,
      agendamento.agendamento_id,
      agendamento.id_agendamento,
      agendamento.data_agendamento,
      agendamento.dataAgendamento,
      agendamento.lead_id,
      agendamento.leadId,
    ]) || dataCriacao,
  );
}

function montarAgendamentoVinculadoAdsSupervisao(agendamento = {}, index = 0) {
  const dataPrevista =
    getDataPrevistaAgendamentoRegistroSupervisao(agendamento) ||
    getDataCriacaoAgendamentoRegistroSupervisao(agendamento);
  const statusOriginal =
    getPrimeiroValor([
      agendamento.status_agendamento,
      agendamento.statusAgendamento,
      agendamento.situacao_agendamento,
      agendamento.situacaoAgendamento,
      agendamento.situacao,
    ]) || "Agendado";

  return {
    ...agendamento,
    id:
      agendamento.id ||
      agendamento.agendamento_id ||
      agendamento.id_agendamento ||
      `agenda-ads-${index}`,
    data_agendamento: dataPrevista,
    criado_em: getDataCriacaoAgendamentoRegistroSupervisao(agendamento),
    status_agendamento: statusOriginal,
  };
}

function criarMapaLeadsPorIdAdsSupervisao(leads = []) {
  const map = new Map();

  (Array.isArray(leads) ? leads : []).forEach((lead) => {
    const leadId = getPrimeiroValor([
      lead?.id,
      lead?.lead_id,
      lead?.leadId,
      lead?.id_lead,
      lead?.idLead,
    ]);
    if (leadId) map.set(String(leadId), lead);
  });

  return map;
}

function getChavesCasoAdsParaAgendamentoSupervisao(
  caso = {},
  mapaLeadsPorId = new Map(),
  index = 0,
) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const leadId = getPrimeiroValor([
    caso.lead_id,
    caso.leadId,
    caso.id_lead,
    caso.idLead,
    conversa.lead_id,
    conversa.leadId,
    conversa.id_lead,
    conversa.idLead,
    lead.id,
    lead.lead_id,
    lead.leadId,
    lead.id_lead,
    lead.idLead,
  ]);
  const leadMapa = leadId ? mapaLeadsPorId.get(String(leadId)) : null;

  return limparChavesPessoaAdsSupervisao([
    leadId ? `lead:${leadId}` : "",
    ...getTelefonesObjetoAdsSupervisao(caso).map((tel) => `tel:${tel}`),
    ...getTelefonesObjetoAdsSupervisao(conversa).map((tel) => `tel:${tel}`),
    ...getTelefonesObjetoAdsSupervisao(lead).map((tel) => `tel:${tel}`),
    ...getTelefonesObjetoAdsSupervisao(leadMapa).map((tel) => `tel:${tel}`),
    getChavePessoaAdsSupervisao(caso, index),
  ]);
}

function getChavesAgendamentoAdsSupervisao(
  agendamento = {},
  mapaLeadsPorId = new Map(),
  index = 0,
) {
  const leadObj =
    agendamento.lead || agendamento.paciente || agendamento.cliente || {};
  const leadId = getPrimeiroValor([
    agendamento.lead_id,
    agendamento.leadId,
    agendamento.id_lead,
    agendamento.idLead,
    agendamento.paciente_id,
    agendamento.pacienteId,
    leadObj.id,
    leadObj.lead_id,
    leadObj.leadId,
  ]);
  const leadMapa = leadId ? mapaLeadsPorId.get(String(leadId)) : null;

  return limparChavesPessoaAdsSupervisao([
    leadId ? `lead:${leadId}` : "",
    ...getTelefonesObjetoAdsSupervisao(agendamento).map((tel) => `tel:${tel}`),
    ...getTelefonesObjetoAdsSupervisao(leadObj).map((tel) => `tel:${tel}`),
    ...getTelefonesObjetoAdsSupervisao(leadMapa).map((tel) => `tel:${tel}`),
    getChavePessoaAgendamentoRegistroSupervisao(agendamento, index),
  ]);
}

function getTelefonesObjetoAdsSupervisao(obj = {}) {
  if (!obj || typeof obj !== "object") return [];

  const telefones = [
    obj.telefone,
    obj.telefone1,
    obj.telefone2,
    obj.celular,
    obj.whatsapp,
    obj.numero,
    obj.numero_whatsapp,
    obj.telefone_whatsapp,
    obj.phone,
    obj.mobile,
  ]
    .map((value) => normalizarTelefone(value).slice(-11))
    .filter(Boolean);

  return [...new Set(telefones)];
}

function getLeadIdObjetoAdsSupervisao(obj = {}) {
  if (!obj || typeof obj !== "object") return "";

  return getPrimeiroValor([
    obj.id,
    obj.lead_id,
    obj.leadId,
    obj.id_lead,
    obj.idLead,
    obj.leads_id,
    obj.paciente_id,
    obj.pacienteId,
    obj.cliente_id,
    obj.clienteId,
  ]);
}

function adicionarLeadIdPorTelefoneAdsSupervisao(map, telefone, leadId) {
  const telefoneNorm = normalizarTelefone(telefone).slice(-11);
  if (!telefoneNorm || !leadId) return;

  const key = `tel:${telefoneNorm}`;
  const set = map.get(key) || new Set();
  set.add(String(leadId));
  map.set(key, set);
}

function registrarObjetoContextoAdsAgendamentoSupervisao(contexto, obj = {}) {
  if (!obj || typeof obj !== "object") return;

  const objetos = [
    obj,
    obj.lead,
    obj.paciente,
    obj.cliente,
    obj.conversa,
    obj.conversa?.lead,
    obj.conversa?.paciente,
    obj.conversa?.cliente,
  ].filter((item) => item && typeof item === "object");

  for (const item of objetos) {
    const leadId = getLeadIdObjetoAdsSupervisao(item);
    if (leadId && !contexto.mapaLeadsPorId.has(String(leadId))) {
      contexto.mapaLeadsPorId.set(String(leadId), item);
    }

    const telefones = getTelefonesObjetoAdsSupervisao(item);
    for (const telefone of telefones) {
      if (leadId) {
        adicionarLeadIdPorTelefoneAdsSupervisao(
          contexto.mapaLeadIdsPorTelefone,
          telefone,
          leadId,
        );
      }
    }
  }
}

function criarContextoVinculoAdsAgendamentoSupervisao({
  leads = [],
  casos = [],
  casosReferencia = [],
} = {}) {
  const contexto = {
    mapaLeadsPorId: criarMapaLeadsPorIdAdsSupervisao(leads),
    mapaLeadIdsPorTelefone: new Map(),
  };

  (Array.isArray(leads) ? leads : []).forEach((lead) =>
    registrarObjetoContextoAdsAgendamentoSupervisao(contexto, lead),
  );
  (Array.isArray(casos) ? casos : []).forEach((caso) =>
    registrarObjetoContextoAdsAgendamentoSupervisao(contexto, caso),
  );
  (Array.isArray(casosReferencia) ? casosReferencia : []).forEach((caso) =>
    registrarObjetoContextoAdsAgendamentoSupervisao(contexto, caso),
  );

  return contexto;
}

function getLeadIdsPorTelefonesAdsSupervisao(
  telefones = [],
  mapaLeadIdsPorTelefone = new Map(),
) {
  const out = new Set();

  for (const telefone of telefones || []) {
    const key = `tel:${normalizarTelefone(telefone).slice(-11)}`;
    const set = mapaLeadIdsPorTelefone.get(key);
    if (!set) continue;
    for (const leadId of set) out.add(String(leadId));
  }

  return [...out];
}

function getTelefonesCasoAdsSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);

  return [
    ...getTelefonesObjetoAdsSupervisao(caso),
    ...getTelefonesObjetoAdsSupervisao(conversa),
    ...getTelefonesObjetoAdsSupervisao(lead),
    ...getTelefonesObjetoAdsSupervisao(origem),
  ];
}

function getChavesCasoAdsComContextoSupervisao(
  caso = {},
  contexto = {},
  index = 0,
) {
  const mapaLeadsPorId = contexto.mapaLeadsPorId || new Map();
  const mapaLeadIdsPorTelefone = contexto.mapaLeadIdsPorTelefone || new Map();
  const chavesBase = getChavesCasoAdsParaAgendamentoSupervisao(
    caso,
    mapaLeadsPorId,
    index,
  );
  const telefones = getTelefonesCasoAdsSupervisao(caso);
  const leadIdsPorTelefone = getLeadIdsPorTelefonesAdsSupervisao(
    telefones,
    mapaLeadIdsPorTelefone,
  );

  return limparChavesPessoaAdsSupervisao([
    ...chavesBase,
    ...telefones.map((tel) => `tel:${tel}`),
    ...leadIdsPorTelefone.map((leadId) => `lead:${leadId}`),
  ]);
}

function calcularConversaoAdsPorAgendamentosDiretosSupervisao({
  coorteAds = [],
  agendamentos = [],
  leads = [],
  casosReferencia = [],
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  const listaCoorte = Array.isArray(coorteAds) ? coorteAds : [];
  const listaAgendamentos = Array.isArray(agendamentos) ? agendamentos : [];

  if (!listaCoorte.length || !listaAgendamentos.length) {
    return { agendamentos: 0, comparecimentos: 0 };
  }

  const contexto = criarContextoVinculoAdsAgendamentoSupervisao({
    leads,
    casos: listaCoorte,
    casosReferencia,
  });
  const agendamentosPorChave = new Map();

  listaAgendamentos.forEach((agendamento, index) => {
    if (
      !isAgendamentoCriadoPeriodoAdsSupervisao(agendamento, dataInicio, dataFim)
    ) {
      return;
    }

    const chaves = getChavesAgendamentoAdsSupervisao(
      agendamento,
      contexto.mapaLeadsPorId,
      index,
    );
    const ordem = getOrdemAgendamentoRegistroSupervisao(agendamento, index);

    for (const chave of chaves) {
      const atual = agendamentosPorChave.get(chave);
      if (!atual || ordem > atual.ordem) {
        agendamentosPorChave.set(chave, { agendamento, ordem, index });
      }
    }
  });

  const pessoasAgendadas = new Set();
  const pessoasCompareceram = new Set();

  listaCoorte.forEach((caso, index) => {
    const pessoaKey =
      getChavePessoaAdsSupervisao(caso, index) || `ads:${index}`;
    if (pessoasAgendadas.has(pessoaKey)) return;

    const chaves = getChavesCasoAdsComContextoSupervisao(caso, contexto, index);
    const vinculo = chaves
      .map((chave) => agendamentosPorChave.get(chave))
      .find(Boolean);

    if (!vinculo?.agendamento) return;

    pessoasAgendadas.add(pessoaKey);

    const status = getStatusAgendamentoRegistroSupervisao(vinculo.agendamento);
    if (
      status.includes("comparec") ||
      status.includes("realiz") ||
      status.includes("conclu") ||
      status.includes("atendid")
    ) {
      pessoasCompareceram.add(pessoaKey);
    }
  });

  return {
    agendamentos: pessoasAgendadas.size,
    comparecimentos: pessoasCompareceram.size,
  };
}

function limparChavesPessoaAdsSupervisao(chaves = []) {
  return [...new Set((Array.isArray(chaves) ? chaves : []).filter(Boolean))];
}

function calcularMetricasAdsPeriodoSupervisao({
  casos = [],
  anuncios = [],
  agendamentos = [],
  leads = [],
  casosReferencia = [],
  resumo = {},
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  // REGRA GERENCIAL DE ROI:
  // 1) A base do período é a PRIMEIRA conversa/origem Ads do contato.
  // 2) O mesmo telefone/lead não pode contar duas vezes se clicar em mais de um link.
  // 3) Conversão, comparecimento, oportunidade e fechamento são calculados
  //    somente sobre essa coorte de contatos originados de anúncio.
  // 4) Não usar resumo agregado do backend para estes cards, pois ele pode
  //    misturar anúncios repetidos, criativos agregados ou valores gerais.
  const coorteAdsPorPessoa = new Map();

  for (const caso of Array.isArray(casos) ? casos : []) {
    if (!temOrigemAdsCasoSupervisao(caso)) continue;

    const dataEntradaAds =
      getDataEntradaAdsEstritaCasoSupervisao(caso) ||
      getDataEntradaAdsFallbackPeriodoSupervisao(caso);

    if (
      !dataEntradaAds ||
      !isDataNoPeriodoSupervisao(dataEntradaAds, dataInicio, dataFim)
    ) {
      continue;
    }

    const key = getChavePessoaAdsSupervisao(caso, coorteAdsPorPessoa.size);
    const atual = coorteAdsPorPessoa.get(key);

    if (
      !atual ||
      parseTimestamp(dataEntradaAds) < parseTimestamp(atual.dataEntradaAds)
    ) {
      coorteAdsPorPessoa.set(key, {
        caso,
        dataEntradaAds,
      });
    }
  }

  let entradasPorCasos = coorteAdsPorPessoa.size;
  let agendamentosPorCasos = 0;
  let comparecimentosPorCasos = 0;
  let valorOportunidadeCasos = 0;
  let valorFechamentoCasos = 0;
  let quantidadeOportunidadesCasos = 0;
  let quantidadeFechamentosCasos = 0;

  for (const { caso } of coorteAdsPorPessoa.values()) {
    if (isCasoConvertidoAdsSupervisao(caso)) {
      agendamentosPorCasos += 1;
    }

    if (isCasoCompareceuAdsSupervisao(caso)) {
      comparecimentosPorCasos += 1;
    }

    const valorOportunidade = getValorOportunidadeNumeroCasoSupervisao(caso);
    if (valorOportunidade > 0) {
      valorOportunidadeCasos += valorOportunidade;
      quantidadeOportunidadesCasos += 1;
    }

    const valorFechamento = getValorFechamentoNumeroCasoSupervisao(caso);
    if (valorFechamento > 0) {
      valorFechamentoCasos += valorFechamento;
      quantidadeFechamentosCasos += 1;
    }
  }

  const conversaoDiretaAgendamentos =
    calcularConversaoAdsPorAgendamentosDiretosSupervisao({
      coorteAds: [...coorteAdsPorPessoa.values()].map((item) => item.caso),
      agendamentos,
      leads,
      casosReferencia: [
        ...(Array.isArray(casosReferencia) ? casosReferencia : []),
        ...(Array.isArray(casos) ? casos : []),
      ],
      dataInicio,
      dataFim,
    });

  agendamentosPorCasos = Math.max(
    agendamentosPorCasos,
    conversaoDiretaAgendamentos.agendamentos,
  );
  comparecimentosPorCasos = Math.max(
    comparecimentosPorCasos,
    conversaoDiretaAgendamentos.comparecimentos,
  );

  const vistosAnuncios = new Set();
  let entradasPorAnuncios = 0;
  let agendamentosPorAnuncios = 0;

  // Fallback secundário para quando a rota não trouxer casos/conversas.
  // Ainda assim, conta item a item e deduplica por lead/telefone/msgid.
  for (const anuncio of Array.isArray(anuncios) ? anuncios : []) {
    const dataAnuncio = getDataAnuncioEstritaSupervisao(anuncio);
    if (
      !dataAnuncio ||
      !isDataNoPeriodoSupervisao(dataAnuncio, dataInicio, dataFim)
    ) {
      continue;
    }

    const key = getChaveAnuncioPeriodoSupervisao(anuncio, vistosAnuncios.size);
    if (vistosAnuncios.has(key)) continue;
    vistosAnuncios.add(key);

    entradasPorAnuncios += 1;
    agendamentosPorAnuncios += isAnuncioConvertidoPeriodoSupervisao(anuncio)
      ? 1
      : 0;
  }

  const temCasos = entradasPorCasos > 0;
  const temAnuncios = entradasPorAnuncios > 0 || agendamentosPorAnuncios > 0;

  const entradas = temCasos
    ? entradasPorCasos
    : temAnuncios
      ? entradasPorAnuncios
      : 0;

  const agendamentosAdsCalculados = temCasos
    ? agendamentosPorCasos
    : temAnuncios
      ? agendamentosPorAnuncios
      : 0;

  const comparecimentos = temCasos ? comparecimentosPorCasos : 0;

  const fonte = temCasos
    ? "coorte_primeira_conversa_ads"
    : temAnuncios
      ? "anuncios_ads_periodo"
      : "indisponivel";

  const taxa =
    entradas > 0
      ? Math.round((agendamentosAdsCalculados / entradas) * 1000) / 10
      : null;
  const taxaComparecimento =
    agendamentosAdsCalculados > 0
      ? Math.round((comparecimentos / agendamentosAdsCalculados) * 1000) / 10
      : null;

  return {
    entradas,
    agendamentos: agendamentosAdsCalculados,
    comparecimentos,
    taxa,
    taxaComparecimento,
    valorOportunidade: temCasos ? valorOportunidadeCasos : 0,
    valorFechamento: temCasos ? valorFechamentoCasos : 0,
    quantidadeOportunidades: temCasos ? quantidadeOportunidadesCasos : 0,
    quantidadeFechamentos: temCasos ? quantidadeFechamentosCasos : 0,
    fonte,
    confiavel: fonte !== "indisponivel",
  };
}

function calcularMetricasFinanceirasPeriodoSupervisao({
  casos = [],
  resumo = {},
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
} = {}) {
  const valorOportunidadeBackend = getPrimeiroNumeroMoedaSupervisao([
    resumo.valor_oportunidade_periodo,
    resumo.valor_oportunidades_periodo,
    resumo.oportunidade_valor_periodo,
    resumo.oportunidades_valor_periodo,
    resumo.total_oportunidades_periodo,
    resumo.valor_oportunidade,
    resumo.valor_oportunidades,
    resumo.total_oportunidades,
    resumo.total_abertas,
    resumo.total_orcamento_periodo,
    resumo.valor_orcamento_periodo,
    resumo.valor_oportunidade_hoje,
    resumo.oportunidade_valor_hoje,
    resumo.total_oportunidades_hoje,
    resumo.total_orcamento_hoje,
    resumo.valor_oportunidade_mes,
    resumo.oportunidade_valor_mes,
    resumo.total_oportunidades_mes,
    resumo.total_orcamento_mes,
  ]);

  const valorFechamentoBackend = getPrimeiroNumeroMoedaSupervisao([
    resumo.valor_fechamento_periodo,
    resumo.valor_fechamentos_periodo,
    resumo.fechamento_valor_periodo,
    resumo.fechamentos_valor_periodo,
    resumo.total_fechamento_periodo,
    resumo.total_fechamentos_periodo,
    resumo.total_recebido_periodo,
    resumo.valor_recebimentos_periodo,
    resumo.recebimentos_valor_periodo,
    resumo.valor_fechado_periodo,
    resumo.total_fechado_periodo,
    resumo.valor_pago_periodo,
    resumo.total_pago_periodo,
    resumo.valor_recebido_periodo,
    resumo.total_recebido_periodo,
    resumo.total_recebido,
    resumo.valor_recebido,
    resumo.valor_fechamento_hoje,
    resumo.total_fechamento_hoje,
    resumo.valor_fechado_hoje,
    resumo.total_fechado_hoje,
    resumo.valor_pago_hoje,
    resumo.total_pago_hoje,
    resumo.valor_recebido_hoje,
    resumo.total_recebido_hoje,
    resumo.valor_fechamento_mes,
    resumo.total_fechamento_mes,
    resumo.valor_fechado_mes,
    resumo.total_fechado_mes,
    resumo.valor_pago_mes,
    resumo.total_pago_mes,
    resumo.valor_recebido_mes,
    resumo.total_recebido_mes,
  ]);

  const quantidadeOportunidadesBackend = getPrimeiroNumeroSupervisao([
    resumo.oportunidades_periodo,
    resumo.quantidade_oportunidades_periodo,
    resumo.qtd_oportunidades_periodo,
    resumo.oportunidades_hoje,
    resumo.quantidade_oportunidades_hoje,
    resumo.oportunidades_mes,
    resumo.quantidade_oportunidades_mes,
  ]);

  const quantidadeFechamentosBackend = getPrimeiroNumeroSupervisao([
    resumo.qtd_pagamentos_periodo,
    resumo.pagamentos_periodo,
    resumo.leads_com_pagamento_periodo,
    resumo.fechamentos_periodo,
    resumo.quantidade_fechamentos_periodo,
    resumo.qtd_fechamentos_periodo,
    resumo.fechamentos_hoje,
    resumo.quantidade_fechamentos_hoje,
    resumo.fechamentos_mes,
    resumo.quantidade_fechamentos_mes,
    resumo.chamados_fechados,
  ]);

  const vistosOportunidade = new Set();
  const vistosFechamento = new Set();
  let valorOportunidadeCasos = 0;
  let valorFechamentoCasos = 0;
  let quantidadeOportunidadesCasos = 0;
  let quantidadeFechamentosCasos = 0;

  for (const caso of Array.isArray(casos) ? casos : []) {
    const key =
      getCasoKey(caso) ||
      getChaveCasoAdsDiaSupervisao(caso, vistosOportunidade.size);

    const valorOportunidade = getValorOportunidadeNumeroCasoSupervisao(caso);
    const dataOportunidade = getDataOportunidadeCasoSupervisao(caso);
    if (
      valorOportunidade > 0 &&
      !vistosOportunidade.has(key) &&
      isDataNoPeriodoSupervisao(dataOportunidade, dataInicio, dataFim)
    ) {
      vistosOportunidade.add(key);
      valorOportunidadeCasos += valorOportunidade;
      quantidadeOportunidadesCasos += 1;
    }

    const valorFechamento = getValorFechamentoNumeroCasoSupervisao(caso);
    const dataFechamento = getDataFechamentoCasoSupervisao(caso);
    if (
      valorFechamento > 0 &&
      !vistosFechamento.has(key) &&
      isDataNoPeriodoSupervisao(dataFechamento, dataInicio, dataFim)
    ) {
      vistosFechamento.add(key);
      valorFechamentoCasos += valorFechamento;
      quantidadeFechamentosCasos += 1;
    }
  }

  const temOportunidadeBackend = valorOportunidadeBackend !== null;
  const temFechamentoBackend = valorFechamentoBackend !== null;

  return {
    valorOportunidade: temOportunidadeBackend
      ? valorOportunidadeBackend
      : valorOportunidadeCasos,
    valorFechamento: temFechamentoBackend
      ? valorFechamentoBackend
      : valorFechamentoCasos,
    quantidadeOportunidades:
      quantidadeOportunidadesBackend ?? quantidadeOportunidadesCasos,
    quantidadeFechamentos:
      quantidadeFechamentosBackend ?? quantidadeFechamentosCasos,
    fonteOportunidade: temOportunidadeBackend ? "resumo" : "casos",
    fonteFechamento: temFechamentoBackend ? "resumo" : "casos",
  };
}

function getPrimeiroNumeroMoedaSupervisao(values = []) {
  let primeiroZero = null;

  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numero = toNumeroMoedaSupervisao(value);
    if (!Number.isFinite(numero)) continue;

    if (numero !== 0) return numero;
    if (primeiroZero === null) primeiroZero = 0;
  }

  return primeiroZero;
}

function toNumeroMoedaSupervisao(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let texto = String(value || "").trim();
  if (!texto) return 0;

  const negativo = /^-/.test(texto) || /\(-?/.test(texto);
  texto = texto.replace(/[^0-9,.-]/g, "");

  if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(texto)) {
    texto = texto.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(,\d{3})+\.\d{1,2}$/.test(texto)) {
    texto = texto.replace(/,/g, "");
  } else if (texto.includes(",") && !texto.includes(".")) {
    texto = texto.replace(",", ".");
  } else if (texto.includes(",") && texto.includes(".")) {
    texto = texto.replace(/\./g, "").replace(",", ".");
  }

  const numero = Number(texto);
  if (!Number.isFinite(numero)) return 0;
  return negativo ? -Math.abs(numero) : numero;
}

function formatarMoedaCompactaSupervisao(value) {
  const numero = toNumeroMoedaSupervisao(value);
  if (!Number.isFinite(numero) || numero <= 0) return "R$ 0";

  if (numero >= 1000000) {
    const valor = Math.round((numero / 1000000) * 10) / 10;
    return `R$ ${String(valor).replace(".", ",")} mi`;
  }

  if (numero >= 10000) {
    const valor = Math.round((numero / 1000) * 10) / 10;
    return `R$ ${String(valor).replace(".", ",")} mil`;
  }

  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function getValorOportunidadeNumeroCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);

  return toNumeroMoedaSupervisao(
    getPrimeiroValor([
      caso.valor_oportunidade,
      caso.valorOportunidade,
      caso.oportunidade_valor,
      caso.oportunidadeValor,
      caso.valor_orcamento,
      caso.valorOrcamento,
      caso.total_orcamento,
      caso.totalOrcamento,
      caso.valor_proposta,
      caso.valorProposta,
      conversa.valor_oportunidade,
      conversa.valorOportunidade,
      conversa.oportunidade_valor,
      conversa.oportunidadeValor,
      conversa.valor_orcamento,
      conversa.valorOrcamento,
      conversa.total_orcamento,
      conversa.totalOrcamento,
      conversa.valor_proposta,
      conversa.valorProposta,
      lead.valor_oportunidade,
      lead.valorOportunidade,
      lead.oportunidade_valor,
      lead.oportunidadeValor,
      lead.valor_orcamento,
      lead.valorOrcamento,
      lead.total_orcamento,
      lead.totalOrcamento,
      lead.valor_proposta,
      lead.valorProposta,
      agenda?.valor_oportunidade,
      agenda?.valorOportunidade,
    ]),
  );
}

function getValorFechamentoNumeroCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  return toNumeroMoedaSupervisao(
    getPrimeiroValor([
      caso.valor_fechamento,
      caso.valorFechamento,
      caso.total_fechamento,
      caso.totalFechamento,
      caso.valor_fechado,
      caso.valorFechado,
      caso.total_fechado,
      caso.totalFechado,
      caso.valor_pago,
      caso.valorPago,
      caso.total_pago,
      caso.totalPago,
      caso.valor_recebido,
      caso.valorRecebido,
      caso.total_recebido,
      caso.totalRecebido,
      conversa.valor_fechamento,
      conversa.valorFechamento,
      conversa.total_fechamento,
      conversa.totalFechamento,
      conversa.valor_fechado,
      conversa.valorFechado,
      conversa.total_fechado,
      conversa.totalFechado,
      conversa.valor_pago,
      conversa.valorPago,
      conversa.total_pago,
      conversa.totalPago,
      conversa.valor_recebido,
      conversa.valorRecebido,
      conversa.total_recebido,
      conversa.totalRecebido,
      lead.valor_fechamento,
      lead.valorFechamento,
      lead.total_fechamento,
      lead.totalFechamento,
      lead.valor_fechado,
      lead.valorFechado,
      lead.total_fechado,
      lead.totalFechado,
      lead.valor_pago,
      lead.valorPago,
      lead.total_pago,
      lead.totalPago,
      lead.valor_recebido,
      lead.valorRecebido,
      lead.total_recebido,
      lead.totalRecebido,
    ]),
  );
}

function getDataOportunidadeCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);

  return getPrimeiroValor([
    caso.data_oportunidade,
    caso.dataOportunidade,
    caso.oportunidade_em,
    caso.oportunidadeEm,
    caso.orcamento_em,
    caso.orcamentoEm,
    caso.data_orcamento,
    caso.dataOrcamento,
    caso.proposta_em,
    caso.propostaEm,
    caso.data_proposta,
    caso.dataProposta,
    agenda?.data,
    conversa.data_oportunidade,
    conversa.dataOportunidade,
    conversa.oportunidade_em,
    conversa.oportunidadeEm,
    conversa.orcamento_em,
    conversa.orcamentoEm,
    conversa.data_orcamento,
    conversa.dataOrcamento,
    conversa.proposta_em,
    conversa.propostaEm,
    lead.data_oportunidade,
    lead.dataOportunidade,
    lead.oportunidade_em,
    lead.oportunidadeEm,
    lead.orcamento_em,
    lead.orcamentoEm,
    lead.data_orcamento,
    lead.dataOrcamento,
    lead.proposta_em,
    lead.propostaEm,
    caso.data_entrada_captazap,
    caso.dataEntradaCaptazap,
    conversa.data_entrada_captazap,
    conversa.dataEntradaCaptazap,
    conversa.primeira_conversa_em,
    conversa.data_primeira_conversa,
    conversa.aberto_em,
    conversa.chamado_aberto_em,
    caso.created_at,
    caso.criado_em,
  ]);
}

function getDataFechamentoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  return getPrimeiroValor([
    caso.data_fechamento,
    caso.dataFechamento,
    caso.fechamento_em,
    caso.fechamentoEm,
    caso.fechado_em,
    caso.fechadoEm,
    caso.encerrado_em,
    caso.encerradoEm,
    caso.finalizado_em,
    caso.finalizadoEm,
    caso.data_pagamento,
    caso.dataPagamento,
    caso.pago_em,
    caso.pagoEm,
    caso.recebido_em,
    caso.recebidoEm,
    conversa.data_fechamento,
    conversa.dataFechamento,
    conversa.fechamento_em,
    conversa.fechamentoEm,
    conversa.fechado_em,
    conversa.fechadoEm,
    conversa.encerrado_em,
    conversa.encerradoEm,
    conversa.finalizado_em,
    conversa.finalizadoEm,
    conversa.data_pagamento,
    conversa.dataPagamento,
    conversa.pago_em,
    conversa.pagoEm,
    conversa.recebido_em,
    conversa.recebidoEm,
    lead.data_fechamento,
    lead.dataFechamento,
    lead.fechamento_em,
    lead.fechamentoEm,
    lead.fechado_em,
    lead.fechadoEm,
    lead.data_pagamento,
    lead.dataPagamento,
    lead.pago_em,
    lead.pagoEm,
    lead.recebido_em,
    lead.recebidoEm,
  ]);
}

function isDataNoPeriodoSupervisao(
  value,
  dataInicio = getHojeISO(),
  dataFim = dataInicio,
) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return false;

  const inicio = String(dataInicio || dataFim || getHojeISO()).slice(0, 10);
  const fim = String(dataFim || inicio).slice(0, 10);
  const inicioTs = parseTimestamp(`${inicio} 00:00:00`);
  const fimTs = parseTimestamp(`${fim} 23:59:59`);

  if (!inicioTs || !fimTs) return false;
  return (
    timestamp >= Math.min(inicioTs, fimTs) &&
    timestamp <= Math.max(inicioTs, fimTs)
  );
}

function getPrimeiroNumeroSupervisao(values = []) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numero = Number(value);
    if (Number.isFinite(numero)) return numero;
  }
  return null;
}

function temOrigemAdsCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  const flagDireta = getPrimeiroValor([
    caso.tem_origem_ads,
    caso.temOrigemAds,
    caso.origem_anuncio,
    caso.origemAnuncio,
    caso.ads_origem_id,
    caso.adsOrigemId,
    caso.ads_id,
    caso.adsId,
    caso.anuncio_id,
    caso.anuncioId,
    conversa.tem_origem_ads,
    conversa.temOrigemAds,
    conversa.origem_anuncio,
    conversa.origemAnuncio,
    conversa.ads_origem_id,
    conversa.adsOrigemId,
    conversa.ads_id,
    conversa.adsId,
    conversa.anuncio_id,
    conversa.anuncioId,
  ]);

  if (flagDireta !== null && flagDireta !== undefined && flagDireta !== "") {
    const textoFlag = String(flagDireta).trim().toLowerCase();
    if (
      !["0", "false", "nao", "não", "null", "undefined"].includes(textoFlag)
    ) {
      return true;
    }
  }

  return Boolean(
    normalizarOrigemAds(
      caso.origem_ads ||
        caso.origemAds ||
        caso.ads_origem ||
        caso.adsOrigem ||
        caso.anuncio ||
        caso.anuncioOrigem ||
        conversa.origem_ads ||
        conversa.origemAds ||
        conversa.ads_origem ||
        conversa.adsOrigem ||
        conversa.anuncio ||
        conversa.anuncioOrigem ||
        lead.origem_ads ||
        lead.origemAds ||
        getOrigemAdsDiretaConversa(conversa),
    ),
  );
}

function getDataEntradaAdsCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const origem =
    caso.origem_ads ||
    caso.origemAds ||
    conversa.origem_ads ||
    conversa.origemAds ||
    lead.origem_ads ||
    lead.origemAds ||
    {};

  return getPrimeiroValor([
    caso.data_origem_ads,
    caso.dataOrigemAds,
    caso.origem_ads_em,
    caso.origemAdsEm,
    caso.ads_recebido_em,
    caso.adsRecebidoEm,
    conversa.data_origem_ads,
    conversa.dataOrigemAds,
    conversa.origem_ads_em,
    conversa.origemAdsEm,
    conversa.ads_recebido_em,
    conversa.adsRecebidoEm,
    lead.data_origem_ads,
    lead.dataOrigemAds,
    lead.origem_ads_em,
    lead.origemAdsEm,
    origem.ultima_origem_em,
    origem.criado_em,
    origem.created_at,
    origem.timestamp,
    origem.data,
    origem.recebido_em,
    origem.recebidoEm,
    caso.data_entrada_captazap,
    caso.dataEntradaCaptazap,
    conversa.data_entrada_captazap,
    conversa.dataEntradaCaptazap,
    conversa.primeira_conversa_em,
    conversa.data_primeira_conversa,
    conversa.aberto_em,
    conversa.chamado_aberto_em,
    caso.ultima_mensagem?.datahora,
    conversa.ultimaMensagemEm,
    conversa.ultima_mensagem_em,
    conversa.updated_at,
    conversa.created_at,
    caso.datahora,
    caso.updated_at,
    caso.created_at,
    caso.criado_em,
  ]);
}

function getDataEntradaAdsEstritaCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const origem =
    caso.origem_ads ||
    caso.origemAds ||
    conversa.origem_ads ||
    conversa.origemAds ||
    lead.origem_ads ||
    lead.origemAds ||
    {};

  return getPrimeiroValor([
    caso.data_origem_ads,
    caso.dataOrigemAds,
    caso.origem_ads_em,
    caso.origemAdsEm,
    caso.origem_ads_criado_em,
    caso.origemAdsCriadoEm,
    caso.ads_criado_em,
    caso.adsCriadoEm,
    caso.ads_created_at,
    caso.adsCreatedAt,
    caso.ads_datahora,
    caso.adsDatahora,
    caso.ads_recebido_em,
    caso.adsRecebidoEm,
    caso.ads_received_at,
    caso.adsReceivedAt,
    caso.anuncio_criado_em,
    caso.anuncioCriadoEm,
    caso.whatsapp_ads_criado_em,
    caso.whatsappAdsCriadoEm,
    conversa.data_origem_ads,
    conversa.dataOrigemAds,
    conversa.origem_ads_em,
    conversa.origemAdsEm,
    conversa.origem_ads_criado_em,
    conversa.origemAdsCriadoEm,
    conversa.ads_criado_em,
    conversa.adsCriadoEm,
    conversa.ads_created_at,
    conversa.adsCreatedAt,
    conversa.ads_datahora,
    conversa.adsDatahora,
    conversa.ads_recebido_em,
    conversa.adsRecebidoEm,
    conversa.ads_received_at,
    conversa.adsReceivedAt,
    conversa.anuncio_criado_em,
    conversa.anuncioCriadoEm,
    conversa.whatsapp_ads_criado_em,
    conversa.whatsappAdsCriadoEm,
    lead.data_origem_ads,
    lead.dataOrigemAds,
    lead.origem_ads_em,
    lead.origemAdsEm,
    lead.origem_ads_criado_em,
    lead.origemAdsCriadoEm,
    lead.ads_criado_em,
    lead.adsCriadoEm,
    lead.ads_received_at,
    lead.adsReceivedAt,
    origem.ultima_origem_em,
    origem.criado_em,
    origem.created_at,
    origem.recebido_em,
    origem.recebidoEm,
    origem.received_at,
    origem.timestamp,
    origem.data,
  ]);
}

function getDataEntradaAdsFallbackPeriodoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};

  // Fallback operacional controlado para anúncio do período quando a rota
  // legado de origem ads não devolve criado_em. Evita updated_at genérico,
  // pois ele pode ser atualizado em lote e inflar o card.
  return getPrimeiroValor([
    caso.data_entrada_captazap,
    caso.dataEntradaCaptazap,
    caso.primeira_mensagem_em,
    caso.primeiraMensagemEm,
    caso.primeira_conversa_em,
    caso.data_primeira_conversa,
    caso.aberto_em,
    caso.abertoEm,
    caso.chamado_aberto_em,
    caso.chamadoAbertoEm,
    caso.ultima_mensagem?.datahora,
    caso.ultima_mensagem_em,
    caso.ultimaMensagemEm,
    caso.datahora,
    conversa.data_entrada_captazap,
    conversa.dataEntradaCaptazap,
    conversa.primeira_mensagem_em,
    conversa.primeiraMensagemEm,
    conversa.primeira_conversa_em,
    conversa.data_primeira_conversa,
    conversa.aberto_em,
    conversa.abertoEm,
    conversa.chamado_aberto_em,
    conversa.chamadoAbertoEm,
    conversa.ultimaMensagemEm,
    conversa.ultima_mensagem_em,
    conversa.datahora,
  ]);
}

function getDataAnuncioSupervisao(anuncio = {}) {
  return getPrimeiroValor([
    anuncio.ultima_origem_em,
    anuncio.criado_em,
    anuncio.created_at,
    anuncio.recebido_em,
    anuncio.recebidoEm,
    anuncio.updated_at,
    anuncio.data,
  ]);
}

function getDataAnuncioEstritaSupervisao(anuncio = {}) {
  return getPrimeiroValor([
    anuncio.data_origem_ads,
    anuncio.dataOrigemAds,
    anuncio.origem_ads_em,
    anuncio.origemAdsEm,
    anuncio.ads_recebido_em,
    anuncio.adsRecebidoEm,
    anuncio.ads_received_at,
    anuncio.adsReceivedAt,
    anuncio.ultima_origem_em,
    anuncio.recebido_em,
    anuncio.recebidoEm,
    anuncio.received_at,
    anuncio.timestamp,
    anuncio.data,
    anuncio.criado_em,
    anuncio.created_at,
  ]);
}

function getChaveCasoAdsDiaSupervisao(caso = {}, index = 0) {
  const conversa = caso.conversa || caso || {};
  const telefone = normalizarTelefone(
    caso.telefone || conversa.telefone || conversa.numero || conversa.whatsapp,
  );
  const partes = [
    caso.chamado_id,
    caso.id_chamado,
    caso.ticket_id,
    caso.id,
    caso.conversa_id,
    conversa.chamado_id,
    conversa.id_chamado,
    conversa.ticket_id,
    conversa.id,
    conversa.conversa_id,
    caso.lead_id,
    conversa.lead_id,
    telefone,
  ].filter(Boolean);

  return partes.length ? partes.join("-") : `sem-chave-ads-${index}`;
}

function getChavePessoaAdsSupervisao(caso = {}, index = 0) {
  // Prioridade: lead/paciente -> telefone -> documento/nome -> chamado.
  // Essa chave é a que impede o mesmo paciente de contar duas vezes quando
  // clica repetidamente em anúncios diferentes.
  return (
    getChavePessoaAgendaMetricaSupervisao(caso) ||
    getChaveCasoAdsDiaSupervisao(caso, index)
  );
}

function getChaveAnuncioPeriodoSupervisao(anuncio = {}, index = 0) {
  const telefone = normalizarTelefone(
    anuncio.telefone ||
      anuncio.numero ||
      anuncio.phone ||
      anuncio.whatsapp ||
      anuncio.lead?.telefone1 ||
      anuncio.lead?.telefone,
  );

  const partes = [
    anuncio.id,
    anuncio.origem_id,
    anuncio.origemId,
    anuncio.whatsapp_msg_id,
    anuncio.whatsappMsgId,
    anuncio.msgid,
    anuncio.messageId,
    anuncio.ctwa_clid,
    anuncio.ctwaClid,
    anuncio.lead_id,
    anuncio.leadId,
    telefone,
  ].filter(Boolean);

  return partes.length ? partes.join("-") : `sem-chave-anuncio-${index}`;
}

function isAnuncioConvertidoPeriodoSupervisao(anuncio = {}) {
  const valor = getPrimeiroValor([
    anuncio.agendado,
    anuncio.convertido,
    anuncio.tem_agenda,
    anuncio.temAgenda,
    anuncio.agendamento_id,
    anuncio.agendamentoId,
    anuncio.data_agendamento,
    anuncio.dataAgendamento,
  ]);

  if (valor === null || valor === undefined || valor === "") return false;
  const texto = String(valor).trim().toLowerCase();
  return !["0", "false", "nao", "não", "null", "undefined"].includes(texto);
}

function isCasoConvertidoAdsSupervisao(caso = {}) {
  const agenda = getAgendamentoCasoSupervisao(caso);
  const tipo = String(caso.tipo || caso.tipoEspera || "").toLowerCase();
  return Boolean(
    agenda?.has ||
    caso.agendado ||
    tipo === "agendado" ||
    tipo === "agenda_hoje",
  );
}

function isCasoCompareceuAdsSupervisao(caso = {}) {
  const statusAgenda = getStatusAgendaNormalizadoCasoSupervisao(caso);
  return Boolean(
    statusAgenda.includes("comparec") ||
    statusAgenda.includes("realiz") ||
    statusAgenda.includes("conclu") ||
    statusAgenda.includes("atendid"),
  );
}

function getDataAberturaCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  return getPrimeiroValor([
    caso.data_abertura,
    caso.dataAbertura,
    caso.aberto_em,
    caso.abertoEm,
    caso.chamado_aberto_em,
    caso.chamadoAbertoEm,
    caso.data_entrada_captazap,
    caso.dataEntradaCaptazap,
    caso.primeira_conversa_em,
    caso.data_primeira_conversa,
    conversa.data_abertura,
    conversa.dataAbertura,
    conversa.aberto_em,
    conversa.abertoEm,
    conversa.chamado_aberto_em,
    conversa.chamadoAbertoEm,
    conversa.data_entrada_captazap,
    conversa.dataEntradaCaptazap,
    conversa.primeira_conversa_em,
    conversa.data_primeira_conversa,
    lead.data_entrada_captazap,
    lead.dataEntradaCaptazap,
    lead.primeira_conversa_em,
    lead.data_primeira_conversa,
    caso.created_at,
    caso.criado_em,
    conversa.created_at,
    conversa.criado_em,
    lead.created_at,
    lead.criado_em,
  ]);
}

function isCasoFechadoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const status = normalizar(
    getPrimeiroValor([
      caso.status_chamado,
      caso.statusChamado,
      caso.status_atendimento,
      caso.statusAtendimento,
      caso.status,
      caso.situacao,
      caso.tipo,
      conversa.status_chamado,
      conversa.statusChamado,
      conversa.status_atendimento,
      conversa.statusAtendimento,
      conversa.status,
      conversa.situacao,
      conversa.tipo,
    ]),
  );

  return Boolean(
    caso.fechado ||
    caso.encerrado ||
    caso.finalizado ||
    conversa.fechado ||
    conversa.encerrado ||
    conversa.finalizado ||
    status.includes("fechado") ||
    status.includes("encerrado") ||
    status.includes("finalizado") ||
    status.includes("concluido") ||
    status.includes("concluído"),
  );
}

function isCasoAgendadoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const status = normalizar(
    getPrimeiroValor([
      caso.status_agendamento,
      caso.statusAgendamento,
      caso.status_agenda,
      caso.statusAgenda,
      caso.tipo,
      conversa.status_agendamento,
      conversa.statusAgendamento,
      conversa.status_agenda,
      conversa.statusAgenda,
      conversa.tipo,
    ]),
  );

  return Boolean(
    caso.agendado ||
    caso.agendamento_id ||
    caso.id_agendamento ||
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    conversa.agendado ||
    conversa.agendamento_id ||
    conversa.id_agendamento ||
    status.includes("agend") ||
    status.includes("compareceu") ||
    status.includes("faltou") ||
    status.includes("cancel"),
  );
}

function getDataAgendamentoPeriodoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);
  const ultimo =
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};

  return getPrimeiroValor([
    caso.data_agendamento,
    caso.dataAgendamento,
    caso.agendado_para,
    caso.agendadoPara,
    caso.agendamento_em,
    caso.agendamentoEm,
    caso.data_agenda,
    caso.dataAgenda,
    agenda?.data,
    agenda?.inicio,
    agenda?.start,
    agenda?.data_agendamento,
    ultimo.data_agendamento,
    ultimo.data,
    ultimo.inicio,
    ultimo.start,
    conversa.data_agendamento,
    conversa.dataAgendamento,
    conversa.agendado_para,
    conversa.agendadoPara,
    conversa.agendamento_em,
    conversa.agendamentoEm,
    conversa.data_agenda,
    conversa.dataAgenda,
    lead.data_agendamento,
    lead.dataAgendamento,
    lead.agendado_para,
    lead.agendadoPara,
  ]);
}

function getDataPrevistaAgendaHojeCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);
  const ultimo =
    caso.ultimo_agendamento ||
    caso.ultimoAgendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};

  // Data prevista da consulta/agenda. Não usar updated_at, última mensagem,
  // created_at genérico nem agendamento_em aqui, pois esses campos podem ser
  // data em que o agendamento foi criado e inflamam o card "Previsto Hoje".
  return getPrimeiroValor([
    caso.data_consulta,
    caso.dataConsulta,
    caso.consulta_em,
    caso.consultaEm,
    caso.data_atendimento,
    caso.dataAtendimento,
    caso.data_procedimento,
    caso.dataProcedimento,
    caso.data_agendamento,
    caso.dataAgendamento,
    caso.data_agenda,
    caso.dataAgenda,
    caso.agendado_para,
    caso.agendadoPara,
    caso.agenda_para,
    caso.agendaPara,
    agenda?.data_consulta,
    agenda?.dataConsulta,
    agenda?.consulta_em,
    agenda?.consultaEm,
    agenda?.data_agendamento,
    agenda?.dataAgendamento,
    agenda?.data_agenda,
    agenda?.dataAgenda,
    agenda?.agendado_para,
    agenda?.agendadoPara,
    agenda?.data,
    agenda?.inicio,
    agenda?.start,
    agenda?.start_at,
    ultimo.data_consulta,
    ultimo.dataConsulta,
    ultimo.consulta_em,
    ultimo.consultaEm,
    ultimo.data_agendamento,
    ultimo.dataAgendamento,
    ultimo.data_agenda,
    ultimo.dataAgenda,
    ultimo.agendado_para,
    ultimo.agendadoPara,
    ultimo.data,
    ultimo.inicio,
    ultimo.start,
    ultimo.start_at,
    conversa.data_consulta,
    conversa.dataConsulta,
    conversa.consulta_em,
    conversa.consultaEm,
    conversa.data_agendamento,
    conversa.dataAgendamento,
    conversa.data_agenda,
    conversa.dataAgenda,
    conversa.agendado_para,
    conversa.agendadoPara,
    lead.data_consulta,
    lead.dataConsulta,
    lead.consulta_em,
    lead.consultaEm,
    lead.data_agendamento,
    lead.dataAgendamento,
    lead.data_agenda,
    lead.dataAgenda,
    lead.agendado_para,
    lead.agendadoPara,
  ]);
}

function getDataCriacaoAgendamentoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);
  const ultimo =
    caso.ultimo_agendamento ||
    caso.ultimoAgendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};

  // Data em que a equipe criou/marcou o agendamento.
  // Usar apenas campos explícitos de criação/marcação; agendamento_em/agendado_em são ambíguos e podem ser a data da consulta.
  return getPrimeiroValor([
    caso.data_criacao_agendamento,
    caso.dataCriacaoAgendamento,
    caso.agendamento_criado_em,
    caso.agendamentoCriadoEm,
    caso.agendamento_marcado_em,
    caso.agendamentoMarcadoEm,
    caso.marcado_em,
    caso.marcadoEm,
    agenda?.data_criacao,
    agenda?.dataCriacao,
    agenda?.criado_em,
    agenda?.criadoEm,
    agenda?.created_at,
    agenda?.createdAt,
    agenda?.agendamento_criado_em,
    agenda?.agendamentoCriadoEm,
    agenda?.marcado_em,
    agenda?.marcadoEm,
    ultimo.data_criacao,
    ultimo.dataCriacao,
    ultimo.criado_em,
    ultimo.criadoEm,
    ultimo.created_at,
    ultimo.createdAt,
    ultimo.agendamento_criado_em,
    ultimo.agendamentoCriadoEm,
    ultimo.marcado_em,
    ultimo.marcadoEm,
    conversa.data_criacao_agendamento,
    conversa.dataCriacaoAgendamento,
    conversa.agendamento_criado_em,
    conversa.agendamentoCriadoEm,
    conversa.marcado_em,
    conversa.marcadoEm,
    lead.data_criacao_agendamento,
    lead.dataCriacaoAgendamento,
    lead.agendamento_criado_em,
    lead.agendamentoCriadoEm,
    lead.marcado_em,
    lead.marcadoEm,
  ]);
}

function getStatusAgendaNormalizadoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const agendaResolvida = getAgendamentoCasoSupervisao(caso);
  const agenda =
    agendaResolvida?.item ||
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};

  return normalizar(
    getPrimeiroValor([
      agendaResolvida?.status,
      agenda.status_agendamento,
      agenda.statusAgendamento,
      agenda.agendamento_status,
      agenda.agendamentoStatus,
      agenda.situacao_agendamento,
      agenda.situacaoAgendamento,
      agenda.status,
      agenda.situacao,
      caso.status_agendamento,
      caso.statusAgendamento,
      caso.status_agenda,
      caso.statusAgenda,
      caso.agenda_followup_status,
      caso.agendaFollowupStatus,
      conversa.status_agendamento,
      conversa.statusAgendamento,
      conversa.status_agenda,
      conversa.statusAgenda,
      conversa.agenda_followup_status,
      conversa.agendaFollowupStatus,
    ]),
  );
}

function isStatusAgendaFaltaOuCancelamentoSupervisao(status = "") {
  const normalizado = normalizar(status);
  return normalizado.includes("falt") || normalizado.includes("cancel");
}

function isStatusAgendaValidoParaProducaoSupervisao(status = "") {
  const normalizado = normalizar(status);

  if (!normalizado) return false;
  if (isStatusAgendaFaltaOuCancelamentoSupervisao(normalizado)) return false;

  return (
    normalizado.includes("agend") ||
    normalizado.includes("reagend") ||
    normalizado.includes("confirm") ||
    normalizado.includes("comparec") ||
    normalizado.includes("realiz") ||
    normalizado.includes("conclu") ||
    normalizado.includes("atendid")
  );
}

function isStatusAgendaConfirmadoSupervisao(status = "") {
  const normalizado = normalizar(status);
  return normalizado.includes("confirm");
}

function isStatusAgendaAtivoParaAgendaFuturaSupervisao(status = "") {
  const normalizado = normalizar(status);

  if (!normalizado) return true;
  if (isStatusAgendaFaltaOuCancelamentoSupervisao(normalizado)) return false;

  return (
    normalizado.includes("agend") ||
    normalizado.includes("reagend") ||
    normalizado.includes("confirm") ||
    normalizado.includes("comparec") ||
    normalizado.includes("realiz") ||
    normalizado.includes("conclu") ||
    normalizado.includes("atendid")
  );
}

function getChavePessoaAgendaMetricaSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agendaObjetos = [
    caso.proximo_agendamento,
    caso.proximoAgendamento,
    caso.agendamento_futuro,
    caso.agendamentoFuturo,
    caso.ultimo_agendamento,
    caso.ultimoAgendamento,
    caso._ultimo_agendamento,
    caso.agendamento,
    caso.agenda,
    conversa.proximo_agendamento,
    conversa.proximoAgendamento,
    conversa.agendamento_futuro,
    conversa.agendamentoFuturo,
    conversa.ultimo_agendamento,
    conversa.ultimoAgendamento,
    conversa._ultimo_agendamento,
    conversa.agendamento,
    conversa.agenda,
    lead.proximo_agendamento,
    lead.proximoAgendamento,
    lead.agendamento_futuro,
    lead.agendamentoFuturo,
    lead.ultimo_agendamento,
    lead.ultimoAgendamento,
    lead._ultimo_agendamento,
    lead.agendamento,
    lead.agenda,
    ...(Array.isArray(caso.agendamentos) ? caso.agendamentos : []),
    ...(Array.isArray(conversa.agendamentos) ? conversa.agendamentos : []),
    ...(Array.isArray(lead.agendamentos) ? lead.agendamentos : []),
  ].filter((item) => item && typeof item === "object");

  const valoresAgenda = (campos = []) =>
    agendaObjetos.map((item) =>
      getPrimeiroValor(campos.map((campo) => item?.[campo])),
    );

  const leadId = getPrimeiroValor([
    caso.lead_id,
    caso.leadId,
    caso.id_lead,
    caso.idLead,
    caso.lead_contexto_id,
    caso.leadContextoId,
    caso.paciente_id,
    caso.pacienteId,
    caso.cliente_id,
    caso.clienteId,
    conversa.lead_id,
    conversa.leadId,
    conversa.id_lead,
    conversa.idLead,
    conversa.lead_contexto_id,
    conversa.leadContextoId,
    conversa.paciente_id,
    conversa.pacienteId,
    conversa.cliente_id,
    conversa.clienteId,
    lead.id,
    lead.lead_id,
    lead.leadId,
    lead.id_lead,
    lead.idLead,
    lead.paciente_id,
    lead.pacienteId,
    lead.cliente_id,
    lead.clienteId,
    ...valoresAgenda([
      "lead_id",
      "leadId",
      "id_lead",
      "idLead",
      "lead_contexto_id",
      "leadContextoId",
      "paciente_id",
      "pacienteId",
      "cliente_id",
      "clienteId",
    ]),
  ]);

  if (leadId) return `lead:${leadId}`;

  const telefone = normalizarTelefone(
    getPrimeiroValor([
      caso.telefone,
      caso.numero,
      caso.whatsapp,
      caso.celular,
      caso.telefone1,
      caso.telefone2,
      caso.numero_whatsapp,
      caso.telefone_whatsapp,
      conversa.telefone,
      conversa.numero,
      conversa.whatsapp,
      conversa.celular,
      conversa.telefone1,
      conversa.telefone2,
      conversa.numero_whatsapp,
      conversa.telefone_whatsapp,
      lead.telefone1,
      lead.telefone2,
      lead.telefone,
      lead.celular,
      lead.whatsapp,
      lead.numero_whatsapp,
      lead.telefone_whatsapp,
      ...valoresAgenda([
        "telefone",
        "telefone1",
        "telefone2",
        "numero",
        "whatsapp",
        "celular",
        "numero_whatsapp",
        "telefone_whatsapp",
      ]),
    ]),
  );

  if (telefone) return `tel:${telefone}`;

  const documento = String(
    getPrimeiroValor([
      caso.cpf,
      caso.documento,
      caso.cpf_cnpj,
      conversa.cpf,
      conversa.documento,
      conversa.cpf_cnpj,
      lead.cpf,
      lead.documento,
      lead.cpf_cnpj,
      ...valoresAgenda(["cpf", "documento", "cpf_cnpj"]),
    ]) || "",
  ).replace(/\D+/g, "");

  if (documento) return `doc:${documento}`;

  const nome = normalizar(
    getPrimeiroValor([
      caso.nome,
      caso.nome_lead,
      caso.lead_nome,
      caso.paciente_nome,
      caso.nome_paciente,
      conversa.nome,
      conversa.nome_lead,
      conversa.lead_nome,
      conversa.paciente_nome,
      conversa.nome_paciente,
      lead.nome,
      lead.nome_completo,
      ...valoresAgenda([
        "nome",
        "nome_lead",
        "lead_nome",
        "paciente_nome",
        "nome_paciente",
      ]),
    ]),
  );

  return nome ? `nome:${nome}` : "";
}

function getDataStatusAgendaCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const agendaResolvida = getAgendamentoCasoSupervisao(caso);
  const agenda =
    agendaResolvida?.item ||
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};

  return getPrimeiroValor([
    agenda.status_atualizado_em,
    agenda.statusAtualizadoEm,
    agenda.data_status,
    agenda.status_em,
    agenda.statusEm,
    agenda.confirmado_em,
    agenda.confirmadoEm,
    agenda.cancelado_em,
    agenda.canceladoEm,
    agenda.faltou_em,
    agenda.faltouEm,
    agenda.compareceu_em,
    agenda.compareceuEm,
    caso.data_status_agendamento,
    caso.dataStatusAgendamento,
    caso.agenda_status_em,
    caso.agendaStatusEm,
    caso.cancelado_em,
    caso.canceladoEm,
    caso.faltou_em,
    caso.faltouEm,
    agenda.atualizado_em,
    agenda.atualizadoEm,
    agenda.updated_at,
    agenda.updatedAt,
    agenda.data_agendamento,
    agenda.data,
    conversa.data_status_agendamento,
    conversa.dataStatusAgendamento,
    conversa.agenda_status_em,
    conversa.agendaStatusEm,
    conversa.cancelado_em,
    conversa.canceladoEm,
    conversa.faltou_em,
    conversa.faltouEm,
    getDataAgendamentoPeriodoCasoSupervisao(caso),
  ]);
}

function isMesmoDiaSupervisao(value, dataISO = getHojeISO()) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return false;

  const data = new Date(timestamp);
  const pad = (n) => String(n).padStart(2, "0");
  const iso = `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(data.getDate())}`;
  return iso === String(dataISO || "").slice(0, 10);
}

function KpiGrid({
  resumo = {},
  metricasStatusPeriodo = {},
  metricasAdsPeriodo = {},
  metricasFinanceirasPeriodo = {},
  loading = false,
  onAdsCardClick = null,
}) {
  const dataAgendaOperacionalISO =
    getDataAgendaAmanhaOperacionalISO(getHojeISO());
  const agendaOperacionalEhAmanha =
    dataAgendaOperacionalISO === addDiasISO(getHojeISO(), 1);
  const diaAgendaOperacional = formatarDiaAgendaOperacionalSupervisao(
    dataAgendaOperacionalISO,
  );
  const labelAgendaOperacional = agendaOperacionalEhAmanha
    ? "Agenda Amanhã"
    : "Agenda Seg.";

  const origemMensagensOficial = Boolean(
    resumo._mensagens_recebidas_oficial ||
    resumo._mensagens_enviadas_oficial ||
    ["zapi", "supervisao-v2", "backend"].includes(
      String(resumo._origem_mensagens || "").toLowerCase(),
    ),
  );

  const chamadosAbertosPeriodo = getNumero(
    metricasStatusPeriodo.chamadosAbertos ?? resumo.chamados_abertos,
  );
  const chamadosFechadosPeriodo = getNumero(
    metricasStatusPeriodo.chamadosFechados ?? resumo.chamados_fechados,
  );
  const filaPeriodo = getNumero(
    metricasStatusPeriodo.fila ??
      resumo.fila_periodo ??
      resumo.fila ??
      resumo.casos_fila ??
      resumo.fila_de_espera ??
      resumo.filaDeEspera,
  );
  const previstoHoje = getNumero(
    metricasStatusPeriodo.previstoHoje ??
      resumo.previsto_hoje_pacientes ??
      resumo.previsto_hoje_leads ??
      resumo.leads_previstos_hoje ??
      resumo.pacientes_previstos_hoje ??
      resumo.previsto_hoje_distinto ??
      resumo.previstos_hoje_distintos ??
      resumo.agenda_prevista_hoje_pacientes ??
      resumo.agenda_prevista_hoje_leads,
  );
  const agendadosHoje = getNumero(
    metricasStatusPeriodo.agendadosHoje ??
      resumo.agendados_hoje_pacientes ??
      resumo.agendados_hoje_leads ??
      resumo.agendados_hoje_distinto ??
      resumo.agendamentos_criados_hoje_pacientes ??
      resumo.agendamentos_criados_hoje_leads ??
      resumo.agendamentos_marcados_hoje_pacientes ??
      resumo.agendamentos_marcados_hoje_leads ??
      resumo.novos_agendamentos_hoje_pacientes ??
      resumo.novos_agendamentos_hoje_leads,
  );
  const agendaAmanha = getNumero(
    metricasStatusPeriodo.agendaAmanha ??
      resumo.agenda_amanha ??
      resumo.agenda_para_amanha ??
      resumo.agendados_amanha ??
      resumo.agendados_para_amanha ??
      resumo.previstos_amanha ??
      resumo.previsto_amanha,
  );
  const confirmadosAmanha = getNumero(
    metricasStatusPeriodo.confirmadosAmanha ??
      resumo.confirmados_amanha ??
      resumo.confirmados_para_amanha ??
      resumo.agenda_confirmada_amanha ??
      resumo.agenda_amanha_confirmados,
  );
  const taxaConfirmadosAmanhaValor =
    metricasStatusPeriodo.taxaConfirmadosAmanha;
  const taxaConfirmadosAmanha =
    taxaConfirmadosAmanhaValor === null ||
    taxaConfirmadosAmanhaValor === undefined
      ? agendaAmanha > 0
        ? Math.round((confirmadosAmanha / agendaAmanha) * 1000) / 10
        : null
      : getNumero(taxaConfirmadosAmanhaValor);

  const faltasPeriodo = getNumero(metricasStatusPeriodo.faltas);
  const cancelamentosPeriodo = getNumero(metricasStatusPeriodo.cancelamentos);
  const faltasCancelamentosPeriodo = getNumero(
    metricasStatusPeriodo.faltasCancelamentos ??
      resumo.faltas_cancelamentos_hoje ??
      resumo.faltas_cancelamentos_periodo ??
      resumo.faltas_cancelamentos ??
      faltasPeriodo + cancelamentosPeriodo,
  );
  const taxaAgendamentoValor = metricasStatusPeriodo.taxaAgendamento;
  const taxaAgendamento =
    taxaAgendamentoValor === null || taxaAgendamentoValor === undefined
      ? null
      : getNumero(taxaAgendamentoValor);
  const baseConversaoAgenda = getNumero(
    metricasStatusPeriodo.baseConversaoAgenda ?? previstoHoje,
  );
  const realizadosAgenda = getNumero(metricasStatusPeriodo.realizadosAgenda);

  const adsConfiavel = metricasAdsPeriodo.confiavel !== false;
  const entradasAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.entradas)
    : 0;
  const contatosUnicosAdsPeriodo = adsConfiavel
    ? getNumero(
        metricasAdsPeriodo.contatosUnicos ?? metricasAdsPeriodo.entradas,
      )
    : 0;
  const conversasNovasAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.conversasNovas)
    : 0;
  const conversasReativadasAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.conversasReativadas)
    : 0;
  const comRastroAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.comRastroAds)
    : 0;
  const semRastroAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.semRastroAds)
    : 0;
  const agendamentosAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.agendamentos)
    : 0;
  const comparecimentosAdsPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.comparecimentos)
    : 0;
  const taxaAdsValor = adsConfiavel ? metricasAdsPeriodo.taxa : null;
  const taxaAds =
    taxaAdsValor === null || taxaAdsValor === undefined
      ? null
      : getNumero(taxaAdsValor);
  const taxaComparecimentoAdsValor = adsConfiavel
    ? metricasAdsPeriodo.taxaComparecimento
    : null;
  const taxaComparecimentoAds =
    taxaComparecimentoAdsValor === null ||
    taxaComparecimentoAdsValor === undefined
      ? null
      : getNumero(taxaComparecimentoAdsValor);

  // ROI de anúncio: estes cards não usam mais os totais gerais do período.
  // A base agora é conversa iniciada/reiniciada no período.
  // Ela inclui rastros confirmados da Z-API e conversas sem rastro, porque
  // a Meta mede conversas iniciadas e nem sempre a integração devolve referral/source_id.
  const valorOportunidadePeriodo = adsConfiavel
    ? toNumeroMoedaSupervisao(metricasAdsPeriodo.valorOportunidade)
    : 0;
  const valorFechamentoPeriodo = adsConfiavel
    ? toNumeroMoedaSupervisao(metricasAdsPeriodo.valorFechamento)
    : 0;
  const qtdOportunidadesPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.quantidadeOportunidades)
    : 0;
  const qtdFechamentosPeriodo = adsConfiavel
    ? getNumero(metricasAdsPeriodo.quantidadeFechamentos)
    : 0;
  const qtdPagamentosFechamentoPeriodo = qtdFechamentosPeriodo;
  const taxaFechamentoAdsValor = adsConfiavel
    ? metricasAdsPeriodo.taxaFechamento
    : null;
  const taxaFechamentoAds =
    taxaFechamentoAdsValor === null || taxaFechamentoAdsValor === undefined
      ? qtdOportunidadesPeriodo > 0
        ? Math.round((qtdFechamentosPeriodo / qtdOportunidadesPeriodo) * 1000) /
          10
        : null
      : getNumero(taxaFechamentoAdsValor);

  const itensGerais = [
    {
      key: "monitorados",
      label: "Monitorados",
      value: getNumero(
        resumo.monitorados ??
          resumo.monitorados_periodo ??
          chamadosAbertosPeriodo + chamadosFechadosPeriodo,
      ),
      icon: <Users size={18} />,
      tone: "blue",
      hint: "Total monitorado",
    },
    {
      key: "abertos",
      label: "Chamados abertos",
      value: chamadosAbertosPeriodo,
      icon: <Headphones size={18} />,
      tone: "cyan",
      hint: "No filtro de data",
    },
    {
      key: "fechados",
      label: "Chamados fechados",
      value: chamadosFechadosPeriodo,
      icon: <CheckCircle2 size={18} />,
      tone: "green",
      hint: "No filtro de data",
    },
    {
      key: "fila",
      label: "Fila",
      value: filaPeriodo,
      icon: <Inbox size={18} />,
      tone: "amber",
      hint: "Aguardando responsável",
    },
    {
      key: "followups",
      label: "Follow-up",
      value: getNumero(resumo.followups_pendentes ?? resumo.followups_vencidos),
      icon: <RefreshCw size={18} />,
      tone: "orange",
      hint: "Pendentes ou vencidos",
    },
    {
      key: "previsto_hoje",
      label: "Previsto Hoje",
      value: previstoHoje,
      icon: <CalendarCheck size={18} />,
      tone: "green",
      hint:
        metricasStatusPeriodo.fontePrevistoHoje === "agendamentos_empresa"
          ? "Pacientes únicos da agenda"
          : "Pacientes únicos previstos",
    },
    {
      key: "faltas_cancelamentos",
      label: "Faltas / Cancel.",
      value: faltasCancelamentosPeriodo,
      icon: <AlertTriangle size={18} />,
      tone: "rose",
      hint: `${faltasPeriodo} faltas • ${cancelamentosPeriodo} cancel.`,
    },
    {
      key: "agendados_hoje",
      label: "Agendados Hoje",
      value: agendadosHoje,
      icon: <Clock3 size={18} />,
      tone: "cyan",
      hint: "Marcados hoje pela equipe",
    },
    {
      key: "agenda_amanha",
      label: labelAgendaOperacional,
      value: agendaAmanha,
      icon: <CalendarCheck size={18} />,
      tone: "green",
      hint: `Pacientes previstos ${agendaOperacionalEhAmanha ? "amanhã" : diaAgendaOperacional}`,
    },
    {
      key: "confirmados_amanha",
      label: "Confirmados",
      value: confirmadosAmanha,
      icon: <BadgeCheck size={18} />,
      tone: "emerald",
      hint: `Da agenda de ${agendaOperacionalEhAmanha ? "amanhã" : diaAgendaOperacional}`,
    },
    {
      key: "taxa_confirmados_amanha",
      label: "% Confirmados",
      value:
        taxaConfirmadosAmanha === null
          ? "—"
          : formatPercent(taxaConfirmadosAmanha),
      icon: <TrendingUp size={18} />,
      tone: "cyan",
      hint:
        agendaAmanha > 0
          ? `${confirmadosAmanha} de ${agendaAmanha} previstos`
          : `Sem agenda ${agendaOperacionalEhAmanha ? "amanhã" : diaAgendaOperacional}`,
    },
    {
      key: "conversao_agenda",
      label: "Conv. agenda",
      value: taxaAgendamento === null ? "—" : formatPercent(taxaAgendamento),
      icon: <TrendingUp size={18} />,
      tone: "cyan",
      hint:
        taxaAgendamento === null
          ? "Sem base confiável"
          : `${realizadosAgenda} realizados de ${baseConversaoAgenda} previstos`,
    },
    {
      key: "recebidas",
      label: "Mensagens recebidas",
      value: getNumero(resumo.mensagens_recebidas),
      icon: <MessageCircle size={18} />,
      tone: "blue",
      hint: origemMensagensOficial
        ? "Total oficial Z-API"
        : "No filtro de data",
    },
    {
      key: "enviadas",
      label: "Mensagens enviadas",
      value: getNumero(resumo.mensagens_enviadas),
      icon: <MousePointerClick size={18} />,
      tone: "slate",
      hint: origemMensagensOficial
        ? "Total oficial Z-API"
        : "No filtro de data",
    },
  ];

  const itensAnuncios = [
    {
      key: "anuncios",
      label: "Anúncios",
      value: adsConfiavel ? entradasAdsPeriodo : "—",
      icon: <Megaphone size={18} />,
      tone: "violet",
      hint: adsConfiavel
        ? `${contatosUnicosAdsPeriodo} contatos • ${comRastroAdsPeriodo} com rastro • ${semRastroAdsPeriodo} sem rastro`
        : "Sem conversas iniciadas no período",
      onClick: onAdsCardClick ? () => onAdsCardClick("anuncios") : null,
    },
    {
      key: "conversao_ads",
      label: "Conv. anúncios",
      value: taxaAds === null ? "—" : formatPercent(taxaAds),
      icon: <TrendingUp size={18} />,
      tone: "cyan",
      hint: adsConfiavel
        ? `${agendamentosAdsPeriodo} agendas de ${entradasAdsPeriodo} conversas`
        : "Sem conversas iniciadas no período",
      onClick: onAdsCardClick ? () => onAdsCardClick("conversao_ads") : null,
    },
    {
      key: "comparecimento_ads",
      label: "Comp. anúncios",
      value:
        taxaComparecimentoAds === null
          ? "—"
          : formatPercent(taxaComparecimentoAds),
      icon: <BadgeCheck size={18} />,
      tone: "emerald",
      hint: adsConfiavel
        ? `${comparecimentosAdsPeriodo} compareceram de ${agendamentosAdsPeriodo} agendados`
        : "Sem conversas iniciadas no período",
      onClick: onAdsCardClick
        ? () => onAdsCardClick("comparecimento_ads")
        : null,
    },
    {
      key: "taxa_fechamento_ads",
      label: "Taxa fech.",
      value:
        taxaFechamentoAds === null ? "—" : formatPercent(taxaFechamentoAds),
      icon: <TrendingUp size={18} />,
      tone: "green",
      hint:
        qtdOportunidadesPeriodo > 0
          ? `${qtdFechamentosPeriodo} fechamentos de ${qtdOportunidadesPeriodo} oportunidades`
          : "Sem oportunidades de anúncios",
      onClick: onAdsCardClick
        ? () => onAdsCardClick("taxa_fechamento_ads")
        : null,
    },
    {
      key: "oportunidade_valor",
      label: "Oport. anúncios",
      value: formatarMoedaSupervisao(valorOportunidadePeriodo) || "R$ 0,00",
      valueTitle:
        formatarMoedaSupervisao(valorOportunidadePeriodo) || "R$ 0,00",
      icon: <BarChart3 size={18} />,
      tone: "violet",
      compactValue: true,
      hint:
        qtdOportunidadesPeriodo > 0
          ? `${qtdOportunidadesPeriodo} oportunidades de anúncios`
          : "Somente leads de anúncios",
      onClick: onAdsCardClick
        ? () => onAdsCardClick("oportunidade_valor")
        : null,
    },
    {
      key: "fechamento_valor",
      label: "Fech. anúncios",
      value: formatarMoedaSupervisao(valorFechamentoPeriodo) || "R$ 0,00",
      valueTitle: formatarMoedaSupervisao(valorFechamentoPeriodo) || "R$ 0,00",
      icon: <CheckCircle2 size={18} />,
      tone: "green",
      compactValue: true,
      hint:
        qtdPagamentosFechamentoPeriodo > 0
          ? `${qtdPagamentosFechamentoPeriodo} ${
              qtdPagamentosFechamentoPeriodo === 1
                ? "fechamento"
                : "fechamentos"
            } de anúncios`
          : "Somente leads de anúncios",
      onClick: onAdsCardClick ? () => onAdsCardClick("fechamento_valor") : null,
    },
  ];

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
        {itensGerais.map((item) => (
          <KpiCard key={item.key} item={item} loading={loading} />
        ))}
      </div>

      <div className="rounded-3xl border border-violet-100 bg-violet-50/30 p-2">
        <div className="mb-2 flex items-center justify-between gap-2 px-1 text-[10px] font-black uppercase tracking-wide text-violet-700">
          <span>ROI de anúncios</span>
          <span className="text-violet-400">Facebook / Google / Site</span>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {itensAnuncios.map((item) => (
            <KpiCard key={item.key} item={item} loading={loading} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ item, loading = false }) {
  const tone = getTone(item.tone);
  const clicavel = typeof item.onClick === "function";
  const Wrapper = clicavel ? "button" : "div";

  return (
    <Wrapper
      type={clicavel ? "button" : undefined}
      onClick={clicavel ? item.onClick : undefined}
      className={`relative overflow-hidden rounded-2xl border px-2.5 py-2.5 text-left shadow-sm ${tone.shell} ${
        clicavel
          ? "cursor-pointer transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-blue-500/10"
          : ""
      }`}
      title={clicavel ? "Clique para ver as leads e imprimir" : undefined}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${tone.bar}`} />
      <div className="flex items-start justify-between gap-2 pl-1">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-xl ${tone.icon}`}
        >
          {item.icon}
        </span>
        {loading ? (
          <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
        ) : null}
      </div>
      <div className="mt-1.5 pl-1">
        <div className="truncate text-[9px] font-black uppercase tracking-wide text-slate-500">
          {item.label}
        </div>
        <div
          className={`mt-1 font-black leading-none tracking-tight text-slate-950 ${
            item.compactValue ? "truncate text-lg" : "text-2xl"
          }`}
          title={item.valueTitle || item.value || "0"}
        >
          {item.value ?? 0}
        </div>
        <div className="mt-1 truncate text-[9px] font-semibold text-slate-400">
          {item.hint}
        </div>
      </div>
    </Wrapper>
  );
}

function montarCasosTabelaAdsRoiSupervisao({
  tipo = "anuncios",
  metricas = {},
  fallbackCasos = [],
} = {}) {
  const config = getConfigDetalheAdsRoiSupervisao(tipo);
  const detalhes = metricas?.detalhes || {};
  const itens = Array.isArray(detalhes?.[config.lista])
    ? detalhes[config.lista]
    : [];

  const itensDeduplicados = deduplicarItensAdsRoiTabelaPorTipoSupervisao(
    tipo,
    itens,
  );

  // Se o backend mandou a lista oficial do card, ela é a fonte da tabela.
  // O fallback operacional entra apenas para enriquecer a mesma lead/telefone
  // com o chamado real (#1299, #1275 etc.), sem criar segunda linha sintética.
  if (itensDeduplicados.length) {
    const fallbackFiltrado = filtrarFallbackCasosAdsRoiSupervisao(
      tipo,
      fallbackCasos,
    );
    const indiceFallback =
      criarIndiceCasosOperacionaisAdsRoiSupervisao(fallbackFiltrado);

    const casos = itensDeduplicados.map((item, index) => {
      const casoReal = buscarCasoOperacionalParaDetalheAdsRoiSupervisao(
        item,
        indiceFallback,
      );
      const itemTabela = casoReal
        ? mesclarCasoOperacionalComDetalheAdsRoiSupervisao(casoReal, item)
        : item;
      // Garante que o responsavel_nome do caso operacional sempre prevaleça
      // mesmo quando o detalhe ROI não carrega esse campo
      const itemComResponsavel =
        casoReal?.responsavel_nome && casoReal.responsavel_nome !== "Anúncios"
          ? {
              ...itemTabela,
              responsavel_nome: casoReal.responsavel_nome,
              responsavel_id:
                casoReal.responsavel_id || itemTabela.responsavel_id,
            }
          : itemTabela;
      return montarCasoTabelaAdsRoiSupervisao(itemComResponsavel, tipo, index);
    });

    return deduplicarCasosTabelaAdsRoiPorTipoSupervisao(tipo, casos);
  }

  const fallbackDeduplicado = deduplicarItensAdsRoiTabelaPorTipoSupervisao(
    tipo,
    filtrarFallbackCasosAdsRoiSupervisao(tipo, fallbackCasos),
  );

  // No fallback sem casoReal, ainda tentamos recuperar o responsavel_nome
  // do caso operacional pelo índice de telefone
  const indiceFallbackGeral = criarIndiceCasosOperacionaisAdsRoiSupervisao(
    filtrarFallbackCasosAdsRoiSupervisao(tipo, fallbackCasos),
  );
  return deduplicarCasosTabelaAdsRoiPorTipoSupervisao(
    tipo,
    fallbackDeduplicado.map((item, index) => {
      const casoOp = buscarCasoOperacionalParaDetalheAdsRoiSupervisao(
        item,
        indiceFallbackGeral,
      );
      const itemComResp =
        casoOp?.responsavel_nome && casoOp.responsavel_nome !== "Anúncios"
          ? {
              ...item,
              responsavel_nome: casoOp.responsavel_nome,
              responsavel_id: casoOp.responsavel_id || item.responsavel_id,
            }
          : item;
      return montarCasoTabelaAdsRoiSupervisao(itemComResp, tipo, index);
    }),
  );
}

function getLeadIdAdsRoiTabelaSupervisao(item = {}) {
  return String(
    item.lead_id ||
      item.leadId ||
      item.id_lead ||
      item.idLead ||
      item.lead?.id ||
      item.lead?.lead_id ||
      item.id ||
      "",
  ).trim();
}

function getTelefoneAdsRoiTabelaSupervisao(item = {}) {
  return normalizarTelefone(
    item.telefone_norm ||
      item.telefoneNorm ||
      item.telefone ||
      item.telefone1 ||
      item.telefone2 ||
      item.numero ||
      item.conversa?.telefone ||
      item.conversa?.numero ||
      item.lead?.telefone1 ||
      item.lead?.telefone2 ||
      "",
  ).slice(-11);
}

function getChaveAdsRoiTabelaPorTipoSupervisao(tipo = "anuncios", item = {}) {
  const leadId = getLeadIdAdsRoiTabelaSupervisao(item);
  const telefone = getTelefoneAdsRoiTabelaSupervisao(item);

  // Nos cards de volume/conversão o número do card é por contato único,
  // mas quando existe lead_id ele é o melhor identificador para unir o chamado
  // real e a linha sintética do ROI da mesma pessoa.
  if (["anuncios", "conversao_ads", "comparecimento_ads"].includes(tipo)) {
    return leadId ? `lead:${leadId}` : telefone ? `tel:${telefone}` : "";
  }

  // Cards financeiros são obrigatoriamente por lead para não duplicar valor.
  return leadId ? `lead:${leadId}` : telefone ? `tel:${telefone}` : "";
}

function scoreItemAdsRoiTabelaSupervisao(item = {}) {
  let total = 0;
  if (item.chamado_id || item.chamadoId || item.protocolo_id || item.id)
    total += 400;
  if (item.lead_id || item.leadId || item.id_lead || item.idLead) total += 1000;
  total += Math.min(getNumero(item.qtd_agendamentos), 20) * 100;
  total += Math.min(getNumero(item.qtd_comparecimentos), 20) * 120;
  if (
    toNumeroMoedaSupervisao(item.total_orcamento ?? item.valor_oportunidade) > 0
  )
    total += 60;
  if (toNumeroMoedaSupervisao(item.total_recebido ?? item.valor_recebido) > 0)
    total += 80;
  if (item.nome_lead || item.nome) total += 10;
  return total;
}

function deduplicarItensAdsRoiTabelaPorTipoSupervisao(
  tipo = "anuncios",
  lista = [],
) {
  const map = new Map();
  for (const item of Array.isArray(lista) ? lista : []) {
    const key = getChaveAdsRoiTabelaPorTipoSupervisao(tipo, item);
    if (!key) continue;
    if (
      !map.has(key) ||
      scoreItemAdsRoiTabelaSupervisao(item) >
        scoreItemAdsRoiTabelaSupervisao(map.get(key))
    ) {
      map.set(key, item);
    }
  }
  return [...map.values()];
}

function deduplicarCasosTabelaAdsRoiPorTipoSupervisao(
  tipo = "anuncios",
  lista = [],
) {
  const map = new Map();
  for (const caso of Array.isArray(lista) ? lista : []) {
    const key =
      getChaveAdsRoiTabelaPorTipoSupervisao(tipo, caso) || getCasoKey(caso);
    if (!key) continue;
    if (
      !map.has(key) ||
      scoreItemAdsRoiTabelaSupervisao(caso) >
        scoreItemAdsRoiTabelaSupervisao(map.get(key))
    ) {
      map.set(key, caso);
    }
  }
  return [...map.values()];
}

function criarIndiceCasosOperacionaisAdsRoiSupervisao(casos = []) {
  const indice = new Map();
  const adicionar = (key, caso) => {
    if (!key || !caso) return;
    if (
      !indice.has(key) ||
      scoreItemAdsRoiTabelaSupervisao(caso) >
        scoreItemAdsRoiTabelaSupervisao(indice.get(key))
    ) {
      indice.set(key, caso);
    }
  };

  for (const caso of Array.isArray(casos) ? casos : []) {
    const leadId = getLeadIdAdsRoiTabelaSupervisao(caso);
    const telefone = getTelefoneAdsRoiTabelaSupervisao(caso);
    if (leadId) adicionar(`lead:${leadId}`, caso);
    if (telefone) adicionar(`tel:${telefone}`, caso);
  }

  return indice;
}

function buscarCasoOperacionalParaDetalheAdsRoiSupervisao(
  item = {},
  indice = new Map(),
) {
  const leadId = getLeadIdAdsRoiTabelaSupervisao(item);
  const telefone = getTelefoneAdsRoiTabelaSupervisao(item);
  return (
    (leadId && indice.get(`lead:${leadId}`)) ||
    (telefone && indice.get(`tel:${telefone}`)) ||
    null
  );
}

function mesclarCasoOperacionalComDetalheAdsRoiSupervisao(
  caso = {},
  detalhe = {},
) {
  return {
    ...caso,
    ...detalhe,
    // Preserva identidade e conversa real do chamado operacional.
    id: caso.id || detalhe.id,
    chamado_id:
      caso.chamado_id ||
      caso.chamadoId ||
      detalhe.chamado_id ||
      detalhe.chamadoId,
    protocolo_id: caso.protocolo_id || detalhe.protocolo_id,
    ultima_mensagem: caso.ultima_mensagem || detalhe.ultima_mensagem,
    mensagem: caso.mensagem || detalhe.mensagem,
    responsavel_id:
      caso.responsavel_id ||
      caso.responsavelId ||
      detalhe.responsavel_id ||
      detalhe.responsavelId ||
      detalhe.captador_id ||
      detalhe.usuario_id ||
      null,
    responsavel_nome:
      // Prioridade: caso operacional (quem está atendendo) > detalhe ROI
      // O detalhe do ROI não tem responsavel_nome do atendente
      (caso.responsavel_nome && caso.responsavel_nome !== "Anúncios"
        ? caso.responsavel_nome
        : null) ||
      (detalhe.responsavel_nome && detalhe.responsavel_nome !== "Anúncios"
        ? detalhe.responsavel_nome
        : null) ||
      detalhe.captador_nome ||
      detalhe.captador ||
      detalhe.usuario_nome ||
      caso.responsavel_nome ||
      "",
    captador_id:
      caso.captador_id ||
      detalhe.captador_id ||
      detalhe.usuario_id ||
      caso.responsavel_id ||
      detalhe.responsavel_id ||
      null,
    captador_nome:
      caso.captador_nome ||
      detalhe.captador_nome ||
      detalhe.captador ||
      caso.responsavel_nome ||
      detalhe.responsavel_nome ||
      "",
    status_chamado:
      caso.status_chamado || caso.statusChamado || detalhe.status_chamado,
    status_label: caso.status_label || detalhe.status_label,
    // Mas usa os dados oficiais do ROI para agenda/valores do card.
    lead_id: detalhe.lead_id || caso.lead_id,
    nome_lead: detalhe.nome_lead || caso.nome_lead || caso.nome,
    nome: detalhe.nome_lead || caso.nome || caso.nome_lead,
    telefone:
      detalhe.telefone1 ||
      detalhe.telefone ||
      detalhe.telefone_norm ||
      caso.telefone,
    telefone1: detalhe.telefone1 || caso.telefone1 || caso.telefone,
    telefone2: detalhe.telefone2 || caso.telefone2,
    status_agendamento:
      detalhe.status_agendamentos ||
      detalhe.status_agendamento ||
      caso.status_agendamento,
    data_agendamento:
      detalhe.datas_agendamentos ||
      detalhe.data_agendamento ||
      caso.data_agendamento,
    total_orcamento: toNumeroMoedaSupervisao(
      detalhe.total_orcamento ??
        caso.total_orcamento ??
        caso.valor_oportunidade,
    ),
    valor_oportunidade: toNumeroMoedaSupervisao(
      detalhe.total_orcamento ??
        caso.total_orcamento ??
        caso.valor_oportunidade,
    ),
    total_recebido: toNumeroMoedaSupervisao(
      detalhe.total_recebido ?? caso.total_recebido ?? caso.valor_recebido,
    ),
    valor_recebido: toNumeroMoedaSupervisao(
      detalhe.total_recebido ?? caso.total_recebido ?? caso.valor_recebido,
    ),
    qtd_agendamentos: getNumero(
      detalhe.qtd_agendamentos ?? caso.qtd_agendamentos,
    ),
    qtd_comparecimentos: getNumero(
      detalhe.qtd_comparecimentos ?? caso.qtd_comparecimentos,
    ),
    qtd_pagamentos: getNumero(detalhe.qtd_pagamentos ?? caso.qtd_pagamentos),
    _ads_roi_detalhe: true,
    _ads_roi_mesclado_chamado_real: true,
  };
}

function filtrarFallbackCasosAdsRoiSupervisao(tipo = "anuncios", casos = []) {
  const lista = Array.isArray(casos) ? casos : [];

  if (tipo === "conversao_ads") {
    return lista.filter((caso) =>
      Boolean(getAgendamentoCasoSupervisao(caso)?.has),
    );
  }

  if (tipo === "comparecimento_ads") {
    return lista.filter((caso) => {
      const status = normalizar(
        getAgendamentoCasoSupervisao(caso)?.status ||
          caso.status_agendamento ||
          caso.statusAgendamento ||
          "",
      );
      return ["comparec", "realiz", "conclu", "atendid"].some((item) =>
        status.includes(item),
      );
    });
  }

  if (tipo === "oportunidade_valor") {
    return lista.filter(
      (caso) =>
        toNumeroMoedaSupervisao(
          caso.total_orcamento ??
            caso.totalOrcamento ??
            caso.valor_oportunidade,
        ) > 0,
    );
  }

  if (tipo === "fechamento_valor" || tipo === "taxa_fechamento_ads") {
    return lista.filter(
      (caso) =>
        toNumeroMoedaSupervisao(
          caso.total_recebido ?? caso.totalRecebido ?? caso.valor_recebido,
        ) > 0,
    );
  }

  return lista;
}

function montarCasoTabelaAdsRoiSupervisao(
  item = {},
  tipo = "anuncios",
  index = 0,
) {
  const leadId = getPrimeiroValor([
    item.lead_id,
    item.leadId,
    item.id_lead,
    item.idLead,
    item.id,
  ]);
  const telefone = getPrimeiroValor([
    item.telefone1,
    item.telefone2,
    item.telefone,
    item.telefone_norm,
    item.telefoneNorm,
  ]);
  const nome = getPrimeiroValor([
    item.nome_lead,
    item.nomeLead,
    item.nome,
    item.paciente,
    item.contato,
  ]);
  const entradaAds = getPrimeiroValor([
    item.primeira_entrada_ads,
    item.primeiraEntradaAds,
    item.data_origem_ads,
    item.dataOrigemAds,
    item.criado_em,
  ]);
  const ultimaAds = getPrimeiroValor([
    item.ultima_entrada_ads,
    item.ultimaEntradaAds,
    item.datahora,
    entradaAds,
  ]);
  const dataAgenda = getPrimeiroValor([
    item.datas_agendamentos,
    item.data_agendamento,
    item.dataAgendamento,
    item.agendamento_data,
  ]);
  const statusAgenda = getPrimeiroValor([
    item.status_agendamentos,
    item.status_agendamento,
    item.statusAgendamento,
  ]);
  const totalOrcamento = toNumeroMoedaSupervisao(
    item.total_orcamento ?? item.totalOrcamento ?? item.valor_oportunidade,
  );
  const totalRecebido = toNumeroMoedaSupervisao(
    item.total_recebido ?? item.totalRecebido ?? item.valor_recebido,
  );
  const canal = item.canal || item.canal_ads || item.canalAds || "Anúncio";
  const conversaId =
    item.conversa_id || item.conversaId || `${leadId || telefone || index}`;
  const qtdEntradasAdsTotal = getNumero(
    item.qtd_entradas_anuncio_total ?? item.qtdEntradasAnuncioTotal,
  );
  const textoEntradaAds =
    qtdEntradasAdsTotal > 1
      ? `${qtdEntradasAdsTotal}ª entrada pelo anúncio`
      : "1ª entrada pelo anúncio";

  const primeiraMensagemReal =
    item.primeira_mensagem_real || item.primeiraMensagemReal || null;
  const mensagemAnterior =
    item.mensagem_anterior || item.mensagemAnterior || null;

  // Classificação: se clicou mais de uma vez no anúncio, sempre mostrar o total,
  // independente de ter mensagem anterior ou não no período.
  const classificacaoAds =
    qtdEntradasAdsTotal > 1
      ? `Clicou ${qtdEntradasAdsTotal}x no anúncio`
      : mensagemAnterior
        ? "Reativado por anúncio"
        : "Conversa nova";

  const responsavelId = getPrimeiroValor([
    item.responsavel_id,
    item.responsavelId,
    item.usuario_id,
    item.usuarioId,
    item.captador_id,
    item.captadorId,
    item.atendente_id,
    item.operador_id,
    item.lead?.id_usuario,
    item.lead?.captador_id,
  ]);
  const responsavelNome = getPrimeiroValor([
    item.responsavel_nome,
    item.responsavelNome,
    item.captador_nome,
    item.captadorNome,
    item.captador,
    item.usuario_nome,
    item.nome_usuario,
    item.atendente_nome,
    item.operador_nome,
    item.lead?.captador_nome,
    item.lead?.usuario_nome,
  ]);

  return {
    ...item,
    id: item.id || `ads-roi-${tipo}-${conversaId}`,
    chamado_id:
      item.chamado_id || item.chamadoId || `ads-roi-${tipo}-${conversaId}`,
    conversa_id: conversaId,
    qtd_entradas_anuncio_total: qtdEntradasAdsTotal,
    primeira_mensagem_real: primeiraMensagemReal,
    mensagem_anterior: mensagemAnterior,
    classificacao_ads: classificacaoAds,
    entrada_ads_descricao: textoEntradaAds,
    lead_id: leadId || item.lead_id || null,
    nome: nome || item.nome || "Sem lead vinculado",
    nome_lead: nome || item.nome_lead || "Sem lead vinculado",
    telefone: telefone || item.telefone || "",
    telefone1: item.telefone1 || telefone || "",
    telefone2: item.telefone2 || "",
    responsavel_id: responsavelId || item.responsavel_id || null,
    responsavel_nome: responsavelNome || "",
    captador_id: item.captador_id || item.captadorId || responsavelId || null,
    captador_nome:
      item.captador_nome ||
      item.captadorNome ||
      item.captador ||
      responsavelNome ||
      "",
    usuario_id: item.usuario_id || item.usuarioId || responsavelId || null,
    usuario_nome:
      item.usuario_nome || item.nome_usuario || responsavelNome || "",
    tipo: `ads_roi_${tipo}`,
    status_label:
      item.status_label || item.status_negociacao || classificacaoAds,
    status_chamado:
      item.status_chamado || item.status_negociacao || classificacaoAds,
    status_negociacao: item.status_negociacao || item.statusNegociacao || "—",
    status_agendamento: statusAgenda || item.status_agendamento || "—",
    data_agendamento: dataAgenda || item.data_agendamento || null,
    data_origem_ads: entradaAds || item.data_origem_ads || null,
    ads_criado_em: entradaAds || item.ads_criado_em || null,
    criado_em: item.criado_em || entradaAds || ultimaAds || null,
    datahora: item.datahora || ultimaAds || entradaAds || null,
    total_orcamento: totalOrcamento,
    valor_oportunidade: totalOrcamento,
    total_recebido: totalRecebido,
    valor_recebido: totalRecebido,
    quantidade_pagamentos: getNumero(
      item.qtd_pagamentos ?? item.quantidade_pagamentos,
    ),
    origem_ads: normalizarOrigemAds(item.origem_ads || item.origemAds) || {
      canal,
      titulo: item.anuncio_titulo || item.titulo || "ROI de anúncios",
      campanha: item.campanha || "ROI de anúncios",
      criado_em: entradaAds || null,
      source_url: item.anuncio_url || item.source_url || item.url || "",
      thumbnail_url: item.thumbnail_url || item.thumbnailUrl || "",
    },
    ultima_mensagem: item.ultima_mensagem || {
      texto:
        item.mensagem ||
        item.preview ||
        item.anuncio_titulo ||
        `${textoEntradaAds} • ${classificacaoAds}`,
      datahora: ultimaAds || entradaAds || null,
      direcao: "recebida",
    },
    mensagem:
      item.mensagem ||
      item.preview ||
      item.anuncio_titulo ||
      "Registro filtrado pelo card de ROI de anúncios.",
    _ads_roi_detalhe: true,
    _somente_leitura: true,
  };
}

function AdsRoiDetalhesModal({
  tipo,
  metricas = {},
  periodoLabel = "",
  onClose,
}) {
  const config = getConfigDetalheAdsRoiSupervisao(tipo);
  const detalhes = metricas?.detalhes || {};
  const itens = Array.isArray(detalhes?.[config.lista])
    ? detalhes[config.lista]
    : [];

  const totais = getTotaisDetalheAdsRoiSupervisao(itens);

  const imprimir = () => {
    imprimirDetalhesAdsRoiSupervisao({
      titulo: config.titulo,
      periodoLabel,
      itens,
      totais,
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-[11px] font-black uppercase tracking-wide text-violet-600">
              ROI de anúncios · {periodoLabel}
            </div>
            <h3 className="mt-1 text-lg font-black text-slate-950">
              {config.titulo}
            </h3>
            <p className="mt-1 text-xs font-semibold text-slate-500">
              {itens.length} registro(s) · Orçamento{" "}
              {formatarMoedaSupervisao(totais.orcamento)} · Recebido{" "}
              {formatarMoedaSupervisao(totais.recebido)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={imprimir}
              className="inline-flex h-9 items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 text-xs font-black text-violet-700 transition hover:bg-violet-100"
            >
              <Printer size={15} />
              Imprimir
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-500 transition hover:bg-rose-50 hover:text-rose-600"
              aria-label="Fechar detalhes de anúncios"
            >
              <X size={17} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {!itens.length ? (
            <EmptyInline message="Nenhuma lead encontrada para este card de anúncios." />
          ) : (
            <table className="w-full min-w-[1040px] text-left text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">Lead</th>
                  <th className="px-3 py-3">Telefone</th>
                  <th className="px-3 py-3">Canal</th>
                  <th className="px-3 py-3">Conversa / entrada</th>
                  <th className="px-3 py-3">Agenda</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Orçamento</th>
                  <th className="px-3 py-3 text-right">Recebido</th>
                  <th className="px-3 py-3 text-right">Pag.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {itens.map((item, idx) => (
                  <tr
                    key={`${item.lead_id || item.telefone_norm || idx}-${idx}`}
                    className="hover:bg-violet-50/40"
                  >
                    <td className="px-3 py-3">
                      <div className="font-black text-slate-900">
                        {item.nome_lead || "Sem lead vinculado"}
                      </div>
                      <div className="text-[10px] font-semibold text-slate-400">
                        Lead: {item.lead_id || "—"}
                      </div>
                      {item.qtd_entradas_anuncio_total > 1 ? (
                        <div className="mt-0.5 text-[10px] font-black text-amber-600">
                          Clicou {item.qtd_entradas_anuncio_total}x no anúncio
                          {item.mensagem_anterior
                            ? " · reativado"
                            : " · conversa nova"}
                        </div>
                      ) : item.mensagem_anterior ? (
                        <div className="mt-0.5 text-[10px] font-black text-amber-600">
                          Reativado · {"sem rastro anterior"}
                        </div>
                      ) : (
                        <div className="mt-0.5 text-[10px] font-black text-emerald-600">
                          Conversa nova
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-700">
                      {item.telefone1 ||
                        item.telefone2 ||
                        item.telefone ||
                        item.telefone_norm ||
                        "—"}
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-700">
                      {item.canal || "—"}
                    </td>
                    <td className="px-3 py-3 font-semibold text-slate-500">
                      <div>
                        {item.primeira_entrada_ads
                          ? formatarDataHoraCurta(item.primeira_entrada_ads)
                          : "—"}
                      </div>
                      {item.primeira_mensagem_real ? (
                        <div className="text-[10px] font-bold text-slate-400">
                          1ª real:{" "}
                          {formatarDataHoraCurta(item.primeira_mensagem_real)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 font-semibold text-slate-500">
                      {item.datas_agendamentos || item.data_agendamento || "—"}
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-bold text-slate-700">
                        {item.status_negociacao || "—"}
                      </div>
                      <div className="text-[10px] font-semibold text-slate-400">
                        {item.status_agendamentos ||
                          item.status_agendamento ||
                          "—"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right font-black text-slate-900">
                      {formatarMoedaSupervisao(item.total_orcamento)}
                    </td>
                    <td className="px-3 py-3 text-right font-black text-emerald-700">
                      {formatarMoedaSupervisao(item.total_recebido)}
                    </td>
                    <td className="px-3 py-3 text-right font-bold text-slate-600">
                      {getNumero(item.qtd_pagamentos)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function getConfigDetalheAdsRoiSupervisao(tipo) {
  const mapa = {
    anuncios: {
      titulo: "Leads/contatos que vieram de anúncios",
      lista: "anuncios",
    },
    conversao_ads: {
      titulo: "Leads de anúncios com agendamento",
      lista: "agendamentos",
    },
    comparecimento_ads: {
      titulo: "Leads de anúncios que compareceram",
      lista: "comparecimentos",
    },
    taxa_fechamento_ads: {
      titulo: "Taxa de fechamento dos anúncios",
      lista: "taxa_fechamento",
    },
    oportunidade_valor: {
      titulo: "Oportunidades geradas por anúncios",
      lista: "oportunidades",
    },
    fechamento_valor: {
      titulo: "Fechamentos recebidos de anúncios",
      lista: "fechamentos",
    },
  };

  return mapa[tipo] || mapa.anuncios;
}

function getTotaisDetalheAdsRoiSupervisao(itens = []) {
  return itens.reduce(
    (acc, item) => ({
      orcamento: acc.orcamento + toNumeroMoedaSupervisao(item.total_orcamento),
      recebido: acc.recebido + toNumeroMoedaSupervisao(item.total_recebido),
    }),
    { orcamento: 0, recebido: 0 },
  );
}

// ── Modal de seleção de colunas para impressão ──────────────────────────────
const COLUNAS_IMPRESSAO = [
  { id: "nome", label: "Nome / Lead" },
  { id: "telefone", label: "Telefone" },
  { id: "canal", label: "Canal (Facebook/Google/Site)" },
  { id: "campanha", label: "Campanha" },
  { id: "entrada", label: "Data de entrada" },
  { id: "responsavel", label: "Responsável (atendimento)" },
  { id: "captador", label: "Captador (converteu o lead)" },
  { id: "retorno", label: "Retorno (1x / 2x no anúncio)" },
  { id: "chamado", label: "Status do chamado" },
  { id: "agenda", label: "Agenda / Follow-up" },
  { id: "negociacao", label: "Negociação" },
  { id: "orcamento", label: "Orçamento" },
  { id: "recebido", label: "Pagamento recebido" },
  { id: "sla", label: "SLA" },
];

function ModalImpressaoAdsRoi({ titulo, periodoLabel, itens = [], totais }) {
  const [aberto, setAberto] = useState(false);
  const [colunasSelecionadas, setColunasSelecionadas] = useState(
    () =>
      new Set([
        "nome",
        "telefone",
        "canal",
        "campanha",
        "entrada",
        "responsavel",
        "captador",
        "retorno",
        "chamado",
        "orcamento",
      ]),
  );

  const toggleColuna = (id) => {
    setColunasSelecionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selecionarTodas = () =>
    setColunasSelecionadas(new Set(COLUNAS_IMPRESSAO.map((c) => c.id)));
  const limparTodas = () => setColunasSelecionadas(new Set());

  const imprimir = () => {
    const cols = COLUNAS_IMPRESSAO.filter((c) => colunasSelecionadas.has(c.id));
    if (cols.length === 0) return;

    const getCelula = (item, id) => {
      switch (id) {
        case "nome":
          return escapeHtmlSupervisao(item.nome_lead || "Sem lead vinculado");
        case "telefone":
          return escapeHtmlSupervisao(
            item.telefone1 ||
              item.telefone2 ||
              item.telefone ||
              item.telefone_norm ||
              "—",
          );
        case "canal":
          return escapeHtmlSupervisao(item.canal || "—");
        case "campanha":
          return escapeHtmlSupervisao(item.campanha || item.anuncio || "—");
        case "entrada":
          return escapeHtmlSupervisao(
            item.primeira_entrada_ads
              ? formatarDataHoraCurta(item.primeira_entrada_ads)
              : "—",
          );
        case "responsavel": {
          const resp = item.responsavel_nome || item.captador_nome || "";
          const semResp =
            !resp ||
            resp === "Anúncios" ||
            resp === "AN" ||
            resp === "Sem captador";
          return escapeHtmlSupervisao(semResp ? "Na fila" : resp);
        }
        case "captador": {
          // Captador só existe se virou lead
          const lead = item.lead || {};
          const temLead = item.lead_id || lead.id;
          if (!temLead) return "—";
          const cap = getPrimeiroValor([
            lead.captador_nome,
            lead.usuario_criador_nome,
            lead.usuario_nome,
            item.captador_lead_nome,
          ]);
          return escapeHtmlSupervisao(
            cap || item.responsavel_nome || "Lead sem captador",
          );
        }
        case "retorno":
          return escapeHtmlSupervisao(
            item.classificacao_ads || item.retorno || "—",
          );
        case "chamado":
          return escapeHtmlSupervisao(item.status_chamado || "—");
        case "agenda":
          return escapeHtmlSupervisao(
            item.datas_agendamentos || item.data_agendamento || "—",
          );
        case "negociacao":
          return escapeHtmlSupervisao(item.status_negociacao || "—");
        case "orcamento":
          return escapeHtmlSupervisao(
            formatarMoedaSupervisao(item.total_orcamento),
          );
        case "recebido":
          return escapeHtmlSupervisao(
            formatarMoedaSupervisao(item.total_recebido),
          );
        case "sla":
          return escapeHtmlSupervisao(item.sla || "—");
        default:
          return "—";
      }
    };

    const isNum = (id) => ["orcamento", "recebido"].includes(id);

    const linhas = itens
      .map(
        (item) =>
          `<tr>${cols.map((c) => `<td${isNum(c.id) ? ' class="num"' : ""}>${getCelula(item, c.id)}</td>`).join("")}</tr>`,
      )
      .join("");

    const janela = window.open("", "_blank", "width=1200,height=800");
    if (!janela) return;
    janela.document.write(`
      <!doctype html><html><head>
      <meta charset="utf-8"/>
      <title>${escapeHtmlSupervisao(titulo)}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#0f172a;margin:24px}
        h1{font-size:18px;margin:0 0 4px}
        .meta{color:#64748b;font-size:11px;margin-bottom:10px}
        .totais{font-size:12px;font-weight:700;margin-bottom:14px;padding:8px 12px;background:#f8fafc;border-radius:8px;display:inline-block}
        table{width:100%;border-collapse:collapse;font-size:10.5px}
        th{background:#1e3a5f;color:#fff;text-align:left;font-size:9.5px;text-transform:uppercase;letter-spacing:.5px;padding:7px}
        td{border:1px solid #e2e8f0;padding:6px;vertical-align:top}
        tr:nth-child(even) td{background:#f8fafc}
        .num{text-align:right;white-space:nowrap}
        @media print{body{margin:10mm}.no-print{display:none}}
      </style>
      </head><body>
      <h1>${escapeHtmlSupervisao(titulo)}</h1>
      <div class="meta">Período: ${escapeHtmlSupervisao(periodoLabel || "—")} · ${itens.length} registros · Impresso em ${new Date().toLocaleString("pt-BR")}</div>
      <div class="totais">Orçamento total: ${escapeHtmlSupervisao(formatarMoedaSupervisao(totais?.orcamento))} &nbsp;|&nbsp; Recebido: ${escapeHtmlSupervisao(formatarMoedaSupervisao(totais?.recebido))}</div>
      <table>
        <thead><tr>${cols.map((c) => `<th>${escapeHtmlSupervisao(c.label)}</th>`).join("")}</tr></thead>
        <tbody>${linhas || `<tr><td colspan="${cols.length}">Nenhum registro.</td></tr>`}</tbody>
      </table>
      </body></html>
    `);
    janela.document.close();
    janela.focus();
    setTimeout(() => janela.print(), 300);
    setAberto(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="inline-flex h-9 items-center gap-2 rounded-xl border border-violet-200 bg-white px-3 text-xs font-black text-violet-700 shadow-sm transition hover:bg-violet-100"
      >
        <Printer size={15} />
        Imprimir tabela
      </button>

      {aberto && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <div className="text-sm font-black text-slate-900">
                  Selecionar colunas para impressão
                </div>
                <div className="text-[11px] text-slate-500">
                  {itens.length} registros · {colunasSelecionadas.size} colunas
                  selecionadas
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAberto(false)}
                className="rounded-lg p-1 hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="mb-3 flex gap-2">
                <button
                  type="button"
                  onClick={selecionarTodas}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
                >
                  Selecionar todas
                </button>
                <button
                  type="button"
                  onClick={limparTodas}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
                >
                  Limpar
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {COLUNAS_IMPRESSAO.map((col) => (
                  <label
                    key={col.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-100 p-2 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={colunasSelecionadas.has(col.id)}
                      onChange={() => toggleColuna(col.id)}
                      className="h-4 w-4 accent-violet-600"
                    />
                    <span className="text-[11px] font-semibold text-slate-700">
                      {col.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-3">
              <button
                type="button"
                onClick={() => setAberto(false)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={imprimir}
                disabled={colunasSelecionadas.size === 0}
                className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2 text-xs font-black text-white hover:bg-violet-800 disabled:opacity-50"
              >
                <Printer size={13} />
                Imprimir ({colunasSelecionadas.size} colunas)
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function imprimirDetalhesAdsRoiSupervisao({
  titulo = "Detalhes ROI de anúncios",
  periodoLabel = "",
  itens = [],
  totais = { orcamento: 0, recebido: 0 },
} = {}) {
  if (typeof window === "undefined") return;

  const janela = window.open("", "_blank", "width=1200,height=800");
  if (!janela) return;

  const linhas = itens
    .map(
      (item) => `
      <tr>
        <td>${escapeHtmlSupervisao(item.nome_lead || "Sem lead vinculado")}</td>
        <td>${escapeHtmlSupervisao(item.lead_id || "—")}</td>
        <td>${escapeHtmlSupervisao(item.telefone1 || item.telefone2 || item.telefone || item.telefone_norm || "—")}</td>
        <td>${escapeHtmlSupervisao(item.canal || "—")}</td>
        <td>${escapeHtmlSupervisao(item.primeira_entrada_ads ? formatarDataHoraCurta(item.primeira_entrada_ads) : "—")}</td>
        <td>${escapeHtmlSupervisao(item.datas_agendamentos || item.data_agendamento || "—")}</td>
        <td>${escapeHtmlSupervisao(item.status_negociacao || "—")}</td>
        <td>${escapeHtmlSupervisao(item.status_agendamentos || item.status_agendamento || "—")}</td>
        <td class="num">${escapeHtmlSupervisao(formatarMoedaSupervisao(item.total_orcamento))}</td>
        <td class="num">${escapeHtmlSupervisao(formatarMoedaSupervisao(item.total_recebido))}</td>
      </tr>
    `,
    )
    .join("");

  janela.document.write(`
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtmlSupervisao(titulo)}</title>
        <style>
          body { font-family: Arial, sans-serif; color: #0f172a; margin: 24px; }
          h1 { font-size: 20px; margin: 0 0 4px; }
          .meta { color: #64748b; font-size: 12px; margin-bottom: 14px; }
          .totais { font-size: 13px; font-weight: 700; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #f1f5f9; text-align: left; text-transform: uppercase; font-size: 10px; }
          th, td { border: 1px solid #e2e8f0; padding: 7px; vertical-align: top; }
          .num { text-align: right; white-space: nowrap; }
          @media print { body { margin: 12mm; } }
        </style>
      </head>
      <body>
        <h1>${escapeHtmlSupervisao(titulo)}</h1>
        <div class="meta">Período: ${escapeHtmlSupervisao(periodoLabel || "—")} · Registros: ${itens.length}</div>
        <div class="totais">Orçamento: ${escapeHtmlSupervisao(formatarMoedaSupervisao(totais.orcamento))} · Recebido: ${escapeHtmlSupervisao(formatarMoedaSupervisao(totais.recebido))}</div>
        <table>
          <thead>
            <tr>
              <th>Lead</th><th>ID</th><th>Telefone</th><th>Canal</th><th>Entrada</th><th>Agenda</th><th>Negociação</th><th>Status Agenda</th><th>Orçamento</th><th>Recebido</th>
            </tr>
          </thead>
          <tbody>${linhas || '<tr><td colspan="10">Nenhum registro encontrado.</td></tr>'}</tbody>
        </table>
      </body>
    </html>
  `);
  janela.document.close();
  janela.focus();
  setTimeout(() => janela.print(), 250);
}

function escapeHtmlSupervisao(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function CaptadoresTable({ captadores = [] }) {
  if (!captadores.length) {
    return <EmptyInline message="Nenhum captador encontrado no período." />;
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-300">
      <table className="w-full min-w-[980px] text-left text-sm">
        <thead className="bg-slate-50 text-[11px] font-black uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-3">Captador</th>
            <th className="px-3 py-3 text-right">Monitorados</th>
            <th className="px-3 py-3 text-right">Abertos</th>
            <th className="px-3 py-3 text-right">Fechados</th>
            <th className="px-3 py-3 text-right">Devendo</th>
            <th className="px-3 py-3 text-right">Sem retorno</th>
            <th className="px-3 py-3 text-right">Follow-ups</th>
            <th className="px-3 py-3 text-right">Agend.</th>
            <th className="px-3 py-3 text-right">Taxa</th>
            <th className="px-3 py-3 text-right">Receb.</th>
            <th className="px-3 py-3 text-right">Env.</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {captadores.map((captador) => {
            const nome =
              captador.nome || captador.responsavel_nome || "Sem responsável";
            const initials = getInitials(nome);

            return (
              <tr
                key={getCaptadorKey(captador)}
                className="hover:bg-blue-50/45"
              >
                <td className="px-3 py-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-blue-700 text-xs font-black text-white shadow-sm">
                      {initials}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-black text-slate-900">
                        {nome}
                      </div>
                      <div className="text-[11px] font-semibold text-slate-400">
                        Tempo médio:{" "}
                        {formatarMinutos(
                          captador.tempo_medio_resposta_min ??
                            captador.espera_media_minutos,
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <MetricCell
                  value={captador.monitorados ?? captador.chamados_abertos}
                />
                <MetricCell value={captador.chamados_abertos} />
                <MetricCell value={captador.chamados_fechados} />
                <MetricCell value={captador.devendo_resposta} tone="rose" />
                <MetricCell value={captador.sem_retorno} tone="emerald" />
                <MetricCell
                  value={
                    captador.followups_pendentes ?? captador.followups_vencidos
                  }
                  tone="amber"
                />
                <MetricCell value={captador.agendamentos_feitos} tone="green" />
                <MetricCell
                  value={formatPercent(captador.taxa_agendamento)}
                  tone="cyan"
                />
                <MetricCell value={captador.mensagens_recebidas} />
                <MetricCell value={captador.mensagens_enviadas} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MetricCell({ value, tone = "slate" }) {
  const className =
    {
      slate: "text-slate-700",
      rose: "text-rose-700",
      emerald: "text-emerald-700",
      amber: "text-amber-700",
      green: "text-green-700",
      cyan: "text-cyan-700",
    }[tone] || "text-slate-700";

  return (
    <td className={`px-3 py-3 text-right font-black ${className}`}>
      {value ?? 0}
    </td>
  );
}

function AnunciosDashboard({
  anuncios = [],
  casos = [],
  periodoLabel = "",
  onSelecionarConversa,
  onVerDetalhes,
}) {
  const listaCasos = Array.isArray(casos) ? casos : [];
  const listaAnuncios = Array.isArray(anuncios) ? anuncios : [];

  const casosOrdenados = [...listaCasos].sort((a, b) => {
    const dataA =
      getDataEntradaAdsEstritaCasoSupervisao(a) ||
      getDataEntradaAdsFallbackPeriodoSupervisao(a) ||
      getDataAberturaCasoSupervisao(a) ||
      "";
    const dataB =
      getDataEntradaAdsEstritaCasoSupervisao(b) ||
      getDataEntradaAdsFallbackPeriodoSupervisao(b) ||
      getDataAberturaCasoSupervisao(b) ||
      "";
    return parseTimestamp(dataB) - parseTimestamp(dataA);
  });

  const totalConversas = casosOrdenados.length;
  const totalAgendamentos = casosOrdenados.reduce(
    (sum, caso) => sum + (isCasoConvertidoAdsSupervisao(caso) ? 1 : 0),
    0,
  );
  const anunciosDiferentes = new Set(
    casosOrdenados
      .map((caso) => getSourceIdCasoAdsSupervisao(caso))
      .filter(Boolean),
  ).size;
  const taxa =
    totalConversas > 0
      ? Math.round((totalAgendamentos / totalConversas) * 1000) / 10
      : 0;

  const grupos = agruparCasosAnuncioOperacionalSupervisao(casosOrdenados);
  const agrupadoLegado = !casosOrdenados.length
    ? [...listaAnuncios].sort((a, b) => {
        const entradasA = getNumero(
          a.telefones ?? a.leads ?? a.conversas ?? a.interacoes ?? a.entradas,
        );
        const entradasB = getNumero(
          b.telefones ?? b.leads ?? b.conversas ?? b.interacoes ?? b.entradas,
        );
        return entradasB - entradasA;
      })
    : [];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-black text-slate-950">
            Conversas de anúncio
          </h3>
          <p className="mt-0.5 text-xs font-semibold leading-5 text-slate-500">
            Chamados que chegaram com origem de anúncio no período{" "}
            {periodoLabel ? `· ${periodoLabel}` : "selecionado"}.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-2xl border border-violet-100 bg-violet-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-violet-700">
          <Megaphone size={12} /> Ads
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <AdsSummaryCard label="Conversas" value={totalConversas} tone="blue" />
        <AdsSummaryCard
          label="Anúncios"
          value={anunciosDiferentes}
          tone="amber"
        />
        <AdsSummaryCard
          label="Agend."
          value={totalAgendamentos}
          tone="emerald"
        />
      </div>

      {totalConversas > 0 ? (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-600">
          Conversão: <span className="font-black text-slate-900">{taxa}%</span>{" "}
          · {totalAgendamentos} agenda{totalAgendamentos === 1 ? "" : "s"} de{" "}
          {totalConversas} conversa{totalConversas === 1 ? "" : "s"}.
        </div>
      ) : null}

      {!casosOrdenados.length && !agrupadoLegado.length ? (
        <EmptyInline message="Nenhuma conversa de anúncio registrada no período." />
      ) : null}

      {grupos.length ? (
        <div className="mt-4 space-y-3">
          {grupos.map((grupo, index) => (
            <AnuncioGrupoConversasCard
              key={grupo.key || index}
              grupo={grupo}
              index={index}
              onSelecionarConversa={onSelecionarConversa}
              onVerDetalhes={onVerDetalhes}
            />
          ))}
        </div>
      ) : null}

      {agrupadoLegado.length ? (
        <div className="mt-4 max-h-[520px] space-y-3 overflow-y-auto pr-1">
          {agrupadoLegado.slice(0, 24).map((anuncio, index) => (
            <AnuncioCard
              key={getAnuncioKey(anuncio, index)}
              anuncio={anuncio}
              index={index}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function agruparCasosAnuncioOperacionalSupervisao(casos = []) {
  const map = new Map();

  for (const caso of Array.isArray(casos) ? casos : []) {
    const sourceId = getSourceIdCasoAdsSupervisao(caso) || "sem-source-id";
    const titulo = getTituloCasoAdsSupervisao(caso);
    const key = `${sourceId}-${titulo || "anuncio"}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        sourceId,
        titulo,
        corpo: getCorpoCasoAdsSupervisao(caso),
        thumb: getThumbCasoAdsSupervisao(caso),
        sourceUrl: getUrlCasoAdsSupervisao(caso),
        casos: [],
        agendamentos: 0,
        primeiraEntrada: "",
        ultimaEntrada: "",
      });
    }

    const grupo = map.get(key);
    grupo.casos.push(caso);
    grupo.agendamentos += isCasoConvertidoAdsSupervisao(caso) ? 1 : 0;

    const dataEntrada =
      getDataEntradaAdsEstritaCasoSupervisao(caso) ||
      getDataEntradaAdsFallbackPeriodoSupervisao(caso) ||
      getDataAberturaCasoSupervisao(caso) ||
      "";

    if (
      dataEntrada &&
      (!grupo.primeiraEntrada ||
        parseTimestamp(dataEntrada) < parseTimestamp(grupo.primeiraEntrada))
    ) {
      grupo.primeiraEntrada = dataEntrada;
    }

    if (
      dataEntrada &&
      (!grupo.ultimaEntrada ||
        parseTimestamp(dataEntrada) > parseTimestamp(grupo.ultimaEntrada))
    ) {
      grupo.ultimaEntrada = dataEntrada;
    }
  }

  return [...map.values()].sort(
    (a, b) =>
      b.casos.length - a.casos.length ||
      parseTimestamp(b.ultimaEntrada) - parseTimestamp(a.ultimaEntrada),
  );
}

function AnuncioGrupoConversasCard({
  grupo = {},
  index = 0,
  onSelecionarConversa,
  onVerDetalhes,
}) {
  const thumbSeguro = grupo.thumb
    ? resolverImagemUrlSupervisao(grupo.thumb)
    : "";
  const taxa = grupo.casos?.length
    ? Math.round((getNumero(grupo.agendamentos) / grupo.casos.length) * 1000) /
      10
    : 0;

  return (
    <div className="overflow-hidden rounded-[22px] border border-slate-300 bg-gradient-to-br from-white via-white to-violet-50/40 shadow-sm">
      <div className="flex items-start gap-3 border-b border-slate-200 p-3">
        {thumbSeguro ? (
          <a
            href={grupo.sourceUrl || thumbSeguro}
            target="_blank"
            rel="noreferrer"
            className="block h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm"
            title="Abrir criativo/origem"
          >
            <ImagemAdsMiniSupervisao
              src={thumbSeguro}
              alt={grupo.titulo || "Anúncio"}
              className="h-full w-full object-cover"
            />
          </a>
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-[10px] font-black uppercase text-violet-700 ring-1 ring-violet-200">
            Ads
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-violet-700 px-1.5 text-[10px] font-black text-white">
              {index + 1}
            </span>
            <div
              className="min-w-0 truncate text-sm font-black text-slate-950"
              title={grupo.titulo}
            >
              {grupo.titulo || "Anúncio sem título"}
            </div>
          </div>

          {grupo.corpo ? (
            <div
              className="mt-1 line-clamp-2 text-[11px] font-medium leading-4 text-slate-500"
              title={grupo.corpo}
            >
              {grupo.corpo}
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-1.5">
            <MiniStat label="Conversas" value={grupo.casos?.length || 0} />
            <MiniStat label="Agend." value={grupo.agendamentos || 0} />
            <MiniStat label="Taxa" value={`${taxa}%`} />
            {grupo.sourceId ? (
              <MiniStat
                label="ID"
                value={String(grupo.sourceId).slice(0, 12)}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="max-h-[360px] divide-y divide-slate-100 overflow-y-auto">
        {(grupo.casos || []).map((caso, casoIndex) => (
          <AnuncioConversaLinha
            key={getCasoKey(caso) || `${grupo.key}-${casoIndex}`}
            caso={caso}
            onSelecionarConversa={onSelecionarConversa}
            onVerDetalhes={onVerDetalhes}
          />
        ))}
      </div>
    </div>
  );
}

function AnuncioConversaLinha({
  caso = {},
  onSelecionarConversa,
  onVerDetalhes,
}) {
  const telefone =
    caso.telefone ||
    caso.numero ||
    caso.conversa?.telefone ||
    caso.conversa?.numero ||
    "";
  const nome =
    caso.nome ||
    caso.nome_lead ||
    caso.paciente ||
    caso.lead?.nome_lead ||
    caso.lead?.nome ||
    telefone ||
    "Contato";
  const responsavelRaw =
    caso.responsavel_nome || caso.captador_nome || caso.usuario_nome || "";
  const responsavel =
    !responsavelRaw || responsavelRaw === "Anúncios" || responsavelRaw === "AN"
      ? "Na fila"
      : responsavelRaw;
  const status =
    getStatusChamadoLabel(caso) || caso.status_chamado || caso.status || "—";
  const entrada =
    getDataEntradaAdsEstritaCasoSupervisao(caso) ||
    getDataEntradaAdsFallbackPeriodoSupervisao(caso) ||
    getDataAberturaCasoSupervisao(caso) ||
    "";
  const agendado = isCasoConvertidoAdsSupervisao(caso);

  const abrirConversa = () => {
    if (typeof onSelecionarConversa === "function") {
      onSelecionarConversa(montarPayloadChamadoSupervisao(caso));
      return;
    }

    if (typeof onVerDetalhes === "function") onVerDetalhes(caso);
  };

  return (
    <div className="bg-white px-3 py-2.5 hover:bg-violet-50/40">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={abrirConversa}
          className="min-w-0 text-left"
          title="Abrir conversa"
        >
          <div className="truncate text-xs font-black text-slate-900">
            {nome}
          </div>
          <div className="mt-0.5 text-[11px] font-bold text-slate-500">
            {formatTelefoneCurto(telefone) || telefone || "Sem telefone"}
          </div>
        </button>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black ${agendado ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100" : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"}`}
        >
          {agendado ? "Agendou" : "Sem agenda"}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] font-bold text-slate-500">
        <span className="truncate" title={responsavel}>
          Resp.: {responsavel}
        </span>
        <span className="truncate text-right" title={status}>
          Status: {status}
        </span>
        <span className="col-span-2 truncate text-slate-400">
          Entrada: {entrada ? formatarDataHoraCurta(entrada) : "—"}
        </span>
      </div>
    </div>
  );
}

function getOrigemObjetoCasoAdsSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = caso.lead || conversa.lead || {};
  return (
    normalizarOrigemAds(
      caso.origem_ads || caso.origemAds || caso.ads_origem || caso.adsOrigem,
    ) ||
    normalizarOrigemAds(
      conversa.origem_ads ||
        conversa.origemAds ||
        conversa.ads_origem ||
        conversa.adsOrigem,
    ) ||
    normalizarOrigemAds(
      lead.origem_ads || lead.origemAds || lead.ads_origem || lead.adsOrigem,
    ) ||
    {}
  );
}

function getSourceIdCasoAdsSupervisao(caso = {}) {
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  return getPrimeiroValor([
    caso.source_id,
    caso.sourceId,
    caso.ads_source_id,
    caso.adsSourceId,
    caso.anuncio_source_id,
    caso.anuncioSourceId,
    caso.origem_ads_source_id,
    caso.origemAdsSourceId,
    origem.source_id,
    origem.sourceId,
    origem.ad_id,
    origem.adId,
  ]);
}

function getTituloCasoAdsSupervisao(caso = {}) {
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  return (
    getPrimeiroValor([
      caso.anuncio,
      caso.anuncio_titulo,
      caso.titulo_anuncio,
      caso.ads_titulo,
      caso.titulo,
      origem.titulo,
      origem.title,
      origem.anuncio,
      origem.anuncio_titulo,
    ]) || "Anúncio sem título"
  );
}

function getCorpoCasoAdsSupervisao(caso = {}) {
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  return (
    getPrimeiroValor([
      caso.texto_anuncio,
      caso.corpo_anuncio,
      caso.ads_corpo,
      caso.corpo,
      origem.corpo,
      origem.body,
      origem.texto,
    ]) || ""
  );
}

function getThumbCasoAdsSupervisao(caso = {}) {
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  return (
    getPrimeiroValor([
      caso.thumbnail_url,
      caso.thumbnailUrl,
      caso.ads_thumbnail_url,
      caso.adsThumbnailUrl,
      caso.image_url,
      caso.imageUrl,
      origem.thumbnail_url,
      origem.thumbnailUrl,
      origem.image_url,
      origem.imageUrl,
    ]) || ""
  );
}

function getUrlCasoAdsSupervisao(caso = {}) {
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  return (
    getPrimeiroValor([
      caso.source_url,
      caso.sourceUrl,
      caso.ads_source_url,
      caso.adsSourceUrl,
      caso.url_anuncio,
      origem.source_url,
      origem.sourceUrl,
      origem.url,
    ]) || ""
  );
}

function getTextoCanalAdsSupervisao(...partes) {
  return partes
    .flatMap((parte) => {
      if (!parte) return [];
      if (typeof parte === "string" || typeof parte === "number") {
        return [String(parte)];
      }
      if (Array.isArray(parte)) return parte;
      if (typeof parte === "object") {
        const payload = parsePayloadJsonAds(
          parte.payload_json || parte.payloadJson || parte.raw_payload,
        );
        return [
          parte.canal,
          parte.canal_ads,
          parte.ads_canal,
          parte.origem_canal,
          parte.plataforma,
          parte.platform,
          parte.publisher_platform,
          parte.source,
          parte.source_type,
          parte.sourceType,
          parte.origem,
          parte.origem_lead,
          parte.referrer,
          parte.referer,
          parte.page_url,
          parte.landing_page,
          parte.source_url,
          parte.sourceUrl,
          parte.url,
          parte.link,
          parte.utm_source,
          parte.utm_medium,
          parte.utm_campaign,
          parte.campaign_name,
          parte.campaign,
          parte.campanha,
          parte.nome_campanha,
          parte.titulo,
          parte.title,
          parte.corpo,
          parte.body,
          parte.texto,
          parte.thumbnail_url,
          parte.thumbnailUrl,
          parte.image_url,
          parte.imageUrl,
          parte.source_id,
          parte.sourceId,
          parte.ad_id,
          parte.adId,
          payload?.canal,
          payload?.platform,
          payload?.publisher_platform,
          payload?.source,
          payload?.source_type,
          payload?.source_url,
          payload?.utm_source,
          payload?.utm_medium,
          payload?.utm_campaign,
          payload?.campaign_name,
          payload?.campaign,
          payload?.source?.platform,
          payload?.source?.source_type,
          payload?.source?.url,
          payload?.source?.campaign_name,
          payload?.referral?.source_type,
          payload?.referral?.source_url,
          payload?.referral?.campaign_name,
        ];
      }
      return [];
    })
    .filter((value) => value !== null && value !== undefined && value !== "")
    .join(" ");
}

function classificarCanalAdsSupervisao(textoBase = "", sourceIdBase = "") {
  const texto = normalizar(textoBase);
  const textoLower = String(textoBase || "").toLowerCase();
  const sourceId = String(sourceIdBase || "").trim();

  const sinaisGoogle = [
    "google",
    "gclid",
    "gbraid",
    "wbraid",
    "adwords",
    "googleads",
    "googleadservices",
    "googlesyndication",
    "doubleclick",
    "pesquisa google",
    "utm_source=google",
    "utm_source google",
  ];

  const sinaisFacebook = [
    "facebook",
    "facebook.com",
    "l.facebook.com",
    "business.facebook.com",
    "fb.me",
    "fb me",
    "m.me",
    "m me",
    "ig.me",
    "ig me",
    "fbclid",
    "fbcdn",
    "instagram",
    "instagram.com",
    "igshid",
    "messenger",
    "meta",
    "utm_source=ig",
    "utm_source=facebook",
    "utm_source=fb",
  ];

  const sinaisSiteGoogle = [
    "website",
    "landing page",
    "landingpage",
    "pagina de captura",
    "pagina captura",
    "formulario site",
    "mundosorriso.com",
    "www.mundosorriso",
  ];

  if (sinaisGoogle.some((sinal) => texto.includes(sinal))) return "google";
  if (sinaisGoogle.some((sinal) => textoLower.includes(sinal))) return "google";

  if (sinaisFacebook.some((sinal) => texto.includes(sinal))) return "facebook";
  if (sinaisFacebook.some((sinal) => textoLower.includes(sinal))) {
    return "facebook";
  }

  // IDs longos iniciados por 120 são comuns nos anúncios Meta/Facebook.
  if (/^120\d{8,}$/.test(sourceId)) return "facebook";

  // No fluxo da clínica, Google Ads pode chegar no WhatsApp como origem do site
  // ou landing page, sem gclid exposto no objeto de conversa.
  if (/\bsite\b/.test(texto) || /\bsite\b/.test(textoLower)) {
    return "google";
  }

  if (sinaisSiteGoogle.some((sinal) => texto.includes(sinal))) return "google";
  if (sinaisSiteGoogle.some((sinal) => textoLower.includes(sinal))) {
    return "google";
  }

  return "outros";
}

function getCanalAdsCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = caso.lead || conversa.lead || {};
  const origem = getOrigemObjetoCasoAdsSupervisao(caso);
  const origemDireta = normalizarOrigemAds(
    caso.origem_ads ||
      caso.origemAds ||
      caso.ads_origem ||
      caso.adsOrigem ||
      conversa.origem_ads ||
      conversa.origemAds ||
      conversa.ads_origem ||
      conversa.adsOrigem ||
      lead.origem_ads ||
      lead.origemAds ||
      lead.ads_origem ||
      lead.adsOrigem,
  );

  const sourceId = getPrimeiroValor([
    caso.source_id,
    caso.sourceId,
    caso.ads_source_id,
    caso.adsSourceId,
    caso.anuncio_source_id,
    caso.anuncioSourceId,
    origem.source_id,
    origem.sourceId,
    origemDireta?.source_id,
    origemDireta?.sourceId,
  ]);

  const texto = getTextoCanalAdsSupervisao(
    caso,
    conversa,
    lead,
    origem,
    origemDireta,
    getOrigemAdsDiretaConversa(conversa || caso || {}),
  );

  return classificarCanalAdsSupervisao(texto, sourceId);
}

function getCanalAdsAnuncioSupervisao(anuncio = {}) {
  const sourceId = getPrimeiroValor([
    anuncio.source_id,
    anuncio.sourceId,
    anuncio.ad_id,
    anuncio.adId,
  ]);

  return classificarCanalAdsSupervisao(
    getTextoCanalAdsSupervisao(anuncio, normalizarOrigemAds(anuncio)),
    sourceId,
  );
}

function isCanalAdsCasoSupervisao(caso = {}, canal = "") {
  if (!temOrigemAdsCasoSupervisao(caso)) return false;
  return getCanalAdsCasoSupervisao(caso) === canal;
}

function getCanalAdsLabelSupervisao(canal = "") {
  if (canal === "facebook") return "Facebook";
  if (canal === "google") return "Google";
  if (canal === "outros") return "Outros Ads";
  return "Ads";
}

function getCanalAdsBadgeClassSupervisao(canal = "") {
  if (canal === "facebook") {
    return "bg-blue-50 text-blue-700 ring-blue-100";
  }
  if (canal === "google") {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }
  return "bg-violet-50 text-violet-700 ring-violet-100";
}

function AdsSummaryCard({ label, value, tone = "blue" }) {
  const tones = {
    blue: "border-blue-100 bg-blue-50 text-blue-700",
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber: "border-amber-100 bg-amber-50 text-amber-700",
  };

  return (
    <div
      className={`rounded-2xl border px-3 py-2 ${tones[tone] || tones.blue}`}
    >
      <div className="text-[9px] font-black uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="mt-1 text-xl font-black leading-none">{value}</div>
    </div>
  );
}

function AnuncioCard({ anuncio = {}, index = 0 }) {
  const titulo =
    anuncio.titulo ||
    anuncio.anuncio_titulo ||
    anuncio.title ||
    "Anúncio sem título";
  const campanha =
    anuncio.campanha ||
    anuncio.nome_campanha ||
    anuncio.campaign_name ||
    "Sem campanha";
  const corpo = anuncio.corpo || anuncio.body || anuncio.texto || "";
  const thumb =
    anuncio.thumbnail_url ||
    anuncio.thumbnailUrl ||
    anuncio.image_url ||
    anuncio.imageUrl ||
    "";
  const thumbSeguro = thumb ? resolverImagemUrlSupervisao(thumb) : "";
  const sourceType = String(
    anuncio.source_type || anuncio.sourceType || "",
  ).toLowerCase();
  const isOrigemSite = sourceType.includes("site");
  const sourceId = anuncio.source_id || anuncio.sourceId || anuncio.ad_id || "";
  const sourceUrl =
    anuncio.source_url || anuncio.sourceUrl || anuncio.url || "";
  const canalAds = getCanalAdsAnuncioSupervisao(anuncio);
  const canalAdsLabel = getCanalAdsLabelSupervisao(canalAds);
  const entradas = getNumero(
    anuncio.telefones ??
      anuncio.leads ??
      anuncio.conversas ??
      anuncio.interacoes ??
      anuncio.entradas,
  );
  const agendamentos = getNumero(
    anuncio.agendamentos ?? anuncio.agendamentos_feitos ?? anuncio.agendados,
  );
  const taxa =
    entradas > 0 ? Math.round((agendamentos / entradas) * 1000) / 10 : 0;
  const ultima =
    anuncio.ultima_origem_em || anuncio.criado_em || anuncio.updated_at || "";

  return (
    <div className="group overflow-hidden rounded-[22px] border border-slate-300 bg-gradient-to-br from-white via-white to-blue-50/40 p-3 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md">
      <div className="flex items-start gap-3">
        {thumbSeguro ? (
          <a
            href={sourceUrl || thumbSeguro}
            target="_blank"
            rel="noreferrer"
            className="block h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-sm"
            title="Abrir criativo/origem"
          >
            <ImagemAdsMiniSupervisao
              src={thumbSeguro}
              alt={titulo}
              className="h-full w-full object-cover"
            />
          </a>
        ) : isOrigemSite ? (
          <span className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-emerald-50 text-[10px] font-black uppercase text-emerald-700 ring-1 ring-emerald-200">
            <Globe2 size={17} className="mb-1" />
            Site
          </span>
        ) : (
          <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-[10px] font-black uppercase text-blue-700 ring-1 ring-blue-200">
            Ads
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-700 px-1.5 text-[10px] font-black text-white">
              {index + 1}
            </span>
            <div
              className="min-w-0 truncate text-sm font-black text-slate-950"
              title={titulo}
            >
              {titulo}
            </div>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black uppercase ring-1 ${getCanalAdsBadgeClassSupervisao(canalAds)}`}
              title={`Canal: ${canalAdsLabel}`}
            >
              {canalAdsLabel}
            </span>
          </div>
          <div
            className="mt-1 truncate text-xs font-bold text-slate-500"
            title={campanha}
          >
            Campanha: {campanha}
          </div>
          {corpo ? (
            <div
              className="mt-1 line-clamp-2 text-[11px] font-medium leading-4 text-slate-500"
              title={corpo}
            >
              {corpo}
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-1.5">
            <MiniStat label="Entradas" value={entradas} />
            <MiniStat label="Agend." value={agendamentos} />
            <MiniStat label="Taxa" value={`${taxa}%`} />
            {sourceId ? (
              <MiniStat label="ID" value={String(sourceId).slice(0, 10)} />
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-between gap-2 text-[10px] font-bold text-slate-400">
            <span className="whitespace-nowrap">
              Última entrada: {ultima ? formatarDataHoraCurta(ultima) : "—"}
            </span>
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-full border border-blue-100 bg-white px-2 py-0.5 text-blue-700 hover:bg-blue-50"
              >
                Abrir
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function getAnuncioKey(anuncio = {}, index = 0) {
  return [
    anuncio.id,
    anuncio.source_id || anuncio.sourceId,
    anuncio.titulo || anuncio.title,
    anuncio.campanha || anuncio.campaign_name,
    index,
  ]
    .filter(Boolean)
    .join("-");
}

function MiniStat({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white px-2 py-1 text-[10px] font-black text-slate-600">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-900">{value ?? 0}</span>
    </span>
  );
}

const SUPERVISAO_GRID_COLUMNS =
  "minmax(280px,1.65fr) minmax(82px,0.48fr) minmax(112px,0.62fr) minmax(112px,0.62fr) minmax(145px,0.78fr) minmax(180px,1fr) minmax(92px,0.5fr) minmax(92px,0.5fr) 58px 174px";

const SUPERVISAO_GRID_COLUNAS = [
  { key: "contato", label: "Contato", align: "left" },
  { key: "entrada", label: "Anúncio", align: "center" },
  { key: "retorno", label: "Retorno", align: "left" },
  { key: "chamado", label: "Chamado", align: "left" },
  { key: "captador", label: "Captador", align: "left" },
  { key: "agenda", label: "Agenda / Follow-up", align: "left" },
  { key: "oportunidade", label: "Oportunidade", align: "right" },
  { key: "pagamento", label: "Pagamento", align: "right" },
  { key: "espera", label: "SLA", align: "right" },
  { key: "acoes", label: "Ações", align: "center" },
];

function isTextoSelecionadoSupervisao(event) {
  if (event?.target?.closest?.("[data-supervisao-action]")) return false;

  if (typeof window === "undefined" || !window.getSelection) return false;

  const textoSelecionado = String(
    window.getSelection()?.toString() || "",
  ).trim();
  return textoSelecionado.length > 0;
}

function CasosTable({
  casos = [],
  agora = Date.now(),
  usuarios = [],
  totalFiltrado = 0,
  inicio = 0,
  fim = 0,
  paginaAtual = 1,
  totalPaginas = 1,
  linhasPorPagina,
  setLinhasPorPagina,
  setPagina,
  onVerDetalhes,
  onAbrirAgendaSupervisao,
  onAbrirAgendaFollowup,
  onAbrirHistoricoLead,
  onAbrirPagamentosLead,
  onSelecionarConversa,
}) {
  const [linhaSelecionadaKey, setLinhaSelecionadaKey] = useState("");

  useEffect(() => {
    if (!casos.length) {
      setLinhaSelecionadaKey("");
      return;
    }

    const keys = new Set(casos.map(getCasoKey));
    if (!linhaSelecionadaKey || !keys.has(linhaSelecionadaKey)) {
      setLinhaSelecionadaKey(getCasoKey(casos[0]));
    }
  }, [casos, linhaSelecionadaKey]);

  const selecionarLinha = useCallback((caso) => {
    setLinhaSelecionadaKey(getCasoKey(caso));
  }, []);

  const abrirLinhaSelecionada = useCallback(() => {
    const caso = casos.find((item) => getCasoKey(item) === linhaSelecionadaKey);
    if (caso) onVerDetalhes?.(caso);
  }, [casos, linhaSelecionadaKey, onVerDetalhes]);

  const navegarLinhas = useCallback(
    (delta = 1) => {
      if (!casos.length) return;
      const atualIndex = Math.max(
        0,
        casos.findIndex((item) => getCasoKey(item) === linhaSelecionadaKey),
      );
      const proximoIndex = Math.min(
        casos.length - 1,
        Math.max(0, atualIndex + delta),
      );
      setLinhaSelecionadaKey(getCasoKey(casos[proximoIndex]));
    },
    [casos, linhaSelecionadaKey],
  );

  const onKeyDownGrid = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      navegarLinhas(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      navegarLinhas(-1);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      abrirLinhaSelecionada();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <style>{`
        .supervisao-copy-zone,
        .supervisao-copy-zone * {
          -webkit-user-select: text;
          user-select: text;
        }

        .supervisao-copy-zone svg,
        .supervisao-copy-zone svg *,
        .supervisao-copy-zone [data-supervisao-action],
        .supervisao-copy-zone [data-supervisao-action] * {
          -webkit-user-select: none;
          user-select: none;
        }
      `}</style>
      <div
        tabIndex={0}
        onKeyDown={onKeyDownGrid}
        className="min-h-0 flex-1 outline-none"
        aria-label="Grade operacional da supervisão"
      >
        <div className="h-full bg-[#f4f6f8] p-3">
          <div className="supervisao-copy-zone h-full overflow-auto overscroll-contain pr-1">
            <div className="w-full min-w-[1240px]">
              <div
                className="sticky top-0 z-40 grid overflow-hidden rounded-[16px] border border-blue-700 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-700 shadow-[0_8px_18px_rgba(30,64,175,0.24)] ring-1 ring-blue-200/40"
                style={{ gridTemplateColumns: SUPERVISAO_GRID_COLUMNS }}
              >
                {SUPERVISAO_GRID_COLUNAS.map((coluna, index) => (
                  <div
                    key={coluna.key}
                    className={`flex h-11 items-center border-white/45 px-3 text-[10px] font-black uppercase tracking-[0.14em] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] ${
                      index === 0 ? "" : "border-l"
                    } ${coluna.align === "right" ? "justify-end" : coluna.align === "center" ? "justify-center" : "justify-start"}`}
                  >
                    <span className="whitespace-nowrap">{coluna.label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-2 pr-1">
                {casos.length ? (
                  <div className="space-y-2">
                    {casos.map((caso, index) => {
                      const key = getCasoKey(caso) || `caso-${index}`;
                      const rowKey = `${key}-${index}`;
                      return (
                        <CasoRow
                          key={rowKey}
                          caso={caso}
                          agora={agora}
                          usuarios={usuarios}
                          index={index}
                          selected={key === linhaSelecionadaKey}
                          onSelecionarLinha={selecionarLinha}
                          onVerDetalhes={onVerDetalhes}
                          onAbrirAgendaSupervisao={onAbrirAgendaSupervisao}
                          onAbrirAgendaFollowup={onAbrirAgendaFollowup}
                          onAbrirHistoricoLead={onAbrirHistoricoLead}
                          onAbrirPagamentosLead={onAbrirPagamentosLead}
                          onSelecionarConversa={onSelecionarConversa}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-[20px] border border-slate-300 bg-white shadow-sm">
                    <EmptyInline message="Nenhum chamado encontrado para este filtro." />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="flex flex-col gap-3 border-t border-slate-300 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs font-semibold text-slate-500">
          Mostrando{" "}
          <span className="font-black text-slate-800">
            {totalFiltrado ? inicio + 1 : 0}
          </span>{" "}
          a{" "}
          <span className="font-black text-slate-800">
            {Math.min(fim, totalFiltrado)}
          </span>{" "}
          de <span className="font-black text-slate-800">{totalFiltrado}</span>
        </div>

        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setPagina((atual) => Math.max(1, atual - 1))}
            disabled={paginaAtual <= 1}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft size={15} />
          </button>
          <span className="rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-xs font-black text-slate-700">
            {paginaAtual}/{totalPaginas}
          </span>
          <button
            type="button"
            onClick={() =>
              setPagina((atual) => Math.min(totalPaginas, atual + 1))
            }
            disabled={paginaAtual >= totalPaginas}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowRight size={15} />
          </button>
        </div>

        <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          Linhas
          <select
            value={linhasPorPagina}
            onChange={(event) => {
              setLinhasPorPagina(Number(event.target.value));
              setPagina(1);
            }}
            className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-xs font-black text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10"
          >
            {OPCOES_LINHAS_POR_PAGINA.map((opcao) => (
              <option key={opcao} value={opcao}>
                {opcao}
              </option>
            ))}
          </select>
        </label>
      </footer>
    </div>
  );
}

function montarLeadHistoricoCompletoSupervisao(
  caso = {},
  empresaIdFallback = null,
) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  const leadId = getPrimeiroValor([
    lead.id,
    lead.lead_id,
    lead.leadId,
    caso.lead_id,
    caso.leadId,
    caso.lead_contexto_id,
    caso.leadContextoId,
    conversa.lead_id,
    conversa.leadId,
    conversa.lead_contexto_id,
    conversa.leadContextoId,
  ]);

  if (!leadId) return null;

  const telefone = getPrimeiroValor([
    lead.telefone1,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
    caso.telefone,
    caso.numero,
    conversa.telefone,
    conversa.numero,
    conversa.phone,
    conversa.whatsapp,
  ]);

  const nome = getPrimeiroValor([
    lead.nome_lead,
    lead.nomeLead,
    lead.nome,
    caso.nome,
    caso.nome_lead,
    conversa.nome,
    conversa.nome_lead,
    telefone,
  ]);

  const empresaId = getPrimeiroValor([
    lead.empresa_id,
    lead.empresaId,
    caso.empresa_id,
    caso.empresaId,
    conversa.empresa_id,
    conversa.empresaId,
    empresaIdFallback,
  ]);

  const usuarioCriadorId = getPrimeiroValor([
    lead.usuario_criador_id,
    lead.captador_id,
    lead.id_usuario,
    caso.usuario_criador_id,
    caso.captador_id,
    caso.responsavel_id,
    conversa.lead_captador_id,
    conversa.dono_lead_id,
    conversa.captador_id,
    conversa.responsavel_id,
  ]);

  return {
    ...lead,
    id: lead.id || leadId,
    lead_id: lead.lead_id || leadId,
    leadId: lead.leadId || leadId,
    nome_lead: lead.nome_lead || lead.nomeLead || nome,
    nome: lead.nome || lead.nome_lead || nome,
    telefone1: lead.telefone1 || telefone,
    telefone: lead.telefone || telefone,
    celular: lead.celular || telefone,
    whatsapp: lead.whatsapp || telefone,
    empresa_id: empresaId,
    empresaId,
    usuario_criador_id: usuarioCriadorId || lead.usuario_criador_id || null,
    id_usuario: lead.id_usuario || usuarioCriadorId || null,
    criado_em:
      lead.criado_em ||
      lead.created_at ||
      caso.criado_em ||
      caso.created_at ||
      conversa.criado_em ||
      conversa.created_at ||
      "",
    status_negociacao:
      lead.status_negociacao ||
      caso.status_negociacao ||
      conversa.status_negociacao ||
      "",
    total_orcamento:
      lead.total_orcamento ||
      caso.total_orcamento ||
      caso.valor_oportunidade ||
      conversa.total_orcamento ||
      conversa.valor_oportunidade ||
      "",
  };
}

function montarPayloadChamadoSupervisao(caso = {}) {
  const conversaBase = caso.conversa || {};
  const lead = conversaBase.lead || caso.lead || {};
  const chamadoId =
    caso.chamado_id ||
    caso.chamadoId ||
    conversaBase.chamado_id ||
    conversaBase.chamadoId ||
    conversaBase.id ||
    null;
  const leadId =
    caso.lead_id ||
    caso.leadId ||
    caso.lead_contexto_id ||
    caso.leadContextoId ||
    conversaBase.lead_id ||
    conversaBase.leadId ||
    conversaBase.lead_contexto_id ||
    conversaBase.leadContextoId ||
    lead.id ||
    null;
  const telefone =
    caso.telefone ||
    caso.numero ||
    conversaBase.telefone ||
    conversaBase.numero ||
    lead.telefone1 ||
    lead.telefone ||
    lead.celular ||
    lead.whatsapp ||
    "";

  const agendaInfo = getAgendamentoCasoSupervisao(caso);
  const ultimoAgendamento =
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    conversaBase.ultimo_agendamento ||
    conversaBase._ultimo_agendamento ||
    lead.ultimo_agendamento ||
    lead._ultimo_agendamento ||
    null;
  const agendamentosCaso = [
    ...(Array.isArray(caso.agendamentos) ? caso.agendamentos : []),
    ...(Array.isArray(conversaBase.agendamentos)
      ? conversaBase.agendamentos
      : []),
    ...(Array.isArray(lead.agendamentos) ? lead.agendamentos : []),
    ...(ultimoAgendamento ? [ultimoAgendamento] : []),
  ].filter(Boolean);
  const statusAgendamentoAtual =
    agendaInfo?.status ||
    caso.status_agendamento ||
    conversaBase.status_agendamento ||
    lead.status_agendamento ||
    ultimoAgendamento?.status_agendamento ||
    "";
  const dataAgendamentoAtual =
    agendaInfo?.data ||
    caso.data_agendamento ||
    conversaBase.data_agendamento ||
    lead.data_agendamento ||
    ultimoAgendamento?.data_agendamento ||
    "";

  return {
    ...conversaBase,
    ...caso,
    id: chamadoId || caso.id || conversaBase.id || null,
    conversa_id:
      chamadoId || caso.conversa_id || conversaBase.conversa_id || null,
    conversaId: chamadoId || caso.conversaId || conversaBase.conversaId || null,
    chamado_id: chamadoId,
    chamadoId,
    lead_id: leadId,
    leadId,
    lead_contexto_id:
      caso.lead_contexto_id ||
      caso.leadContextoId ||
      conversaBase.lead_contexto_id ||
      conversaBase.leadContextoId ||
      leadId,
    leadContextoId:
      caso.leadContextoId ||
      caso.lead_contexto_id ||
      conversaBase.leadContextoId ||
      conversaBase.lead_contexto_id ||
      leadId,
    telefone,
    numero: telefone,
    nome:
      caso.nome || conversaBase.nome || lead.nome_lead || lead.nome || telefone,
    lead: {
      ...lead,
      id: leadId || lead.id || null,
      nome_lead:
        lead.nome_lead || caso.nome || conversaBase.nome || lead.nome || "",
      telefone1: lead.telefone1 || telefone,
      telefone: lead.telefone || telefone,
      status_agendamento: statusAgendamentoAtual,
      data_agendamento: dataAgendamentoAtual,
      ultimo_agendamento: ultimoAgendamento,
      _ultimo_agendamento: ultimoAgendamento,
      agendamentos: agendamentosCaso,
    },
    agendamentos: agendamentosCaso,
    ultimo_agendamento: ultimoAgendamento,
    _ultimo_agendamento: ultimoAgendamento,
    status_agendamento: statusAgendamentoAtual,
    data_agendamento: dataAgendamentoAtual,
    _supervisao: true,
    _supervisao_bloqueado: true,
    _somente_leitura: true,
    _abrir_chamado: true,
    _abrir_agendamento: Boolean(getAgendamentoCasoSupervisao(caso)?.has),
  };
}

function montarLeadAgendamentoModalSupervisao(caso = {}) {
  const payload = montarPayloadChamadoSupervisao(caso);
  const lead = payload.lead || caso.lead || caso.conversa?.lead || {};

  const leadId =
    payload.lead_id ||
    payload.leadId ||
    payload.lead_contexto_id ||
    payload.leadContextoId ||
    lead.id ||
    lead.lead_id ||
    lead.id_lead ||
    null;

  if (!leadId) return null;

  const empresaId =
    payload.empresa_id ||
    payload.empresaId ||
    lead.empresa_id ||
    lead.empresaId ||
    caso.empresa_id ||
    caso.empresaId ||
    null;

  const telefone =
    payload.telefone ||
    payload.numero ||
    lead.telefone1 ||
    lead.telefone ||
    lead.celular ||
    lead.whatsapp ||
    caso.telefone ||
    caso.numero ||
    "";

  const nome =
    payload.nome ||
    lead.nome_lead ||
    lead.nome ||
    caso.nome ||
    caso.conversa?.nome ||
    telefone ||
    "Lead";

  return {
    ...lead,
    id: leadId,
    lead_id: leadId,
    id_lead: leadId,
    empresa_id: empresaId,
    empresaId: empresaId,
    nome_lead: lead.nome_lead || nome,
    nome: lead.nome || nome,
    telefone1: lead.telefone1 || telefone,
    telefone: lead.telefone || telefone,
    celular: lead.celular || telefone,
    whatsapp: lead.whatsapp || telefone,
    status_agendamento:
      payload.status_agendamento || lead.status_agendamento || "",
    data_agendamento: payload.data_agendamento || lead.data_agendamento || "",
    ultimo_agendamento:
      payload.ultimo_agendamento ||
      lead.ultimo_agendamento ||
      caso.ultimo_agendamento ||
      caso._ultimo_agendamento ||
      null,
    agendamentos: Array.isArray(payload.agendamentos)
      ? payload.agendamentos
      : Array.isArray(lead.agendamentos)
        ? lead.agendamentos
        : [],
  };
}

function montarLeadPagamentoModalSupervisao(
  caso = {},
  empresaIdFallback = null,
) {
  const payload = montarPayloadChamadoSupervisao(caso);
  const lead = payload.lead || caso.lead || caso.conversa?.lead || {};

  const leadId = getPrimeiroValor([
    payload.lead_id,
    payload.leadId,
    payload.lead_contexto_id,
    payload.leadContextoId,
    lead.id,
    lead.lead_id,
    lead.leadId,
    lead.id_lead,
    caso.lead_id,
    caso.leadId,
    caso.lead_contexto_id,
    caso.leadContextoId,
    caso.conversa?.lead_id,
    caso.conversa?.leadId,
    caso.conversa?.lead_contexto_id,
    caso.conversa?.leadContextoId,
  ]);

  const empresaId = getPrimeiroValor([
    payload.empresa_id,
    payload.empresaId,
    lead.empresa_id,
    lead.empresaId,
    caso.empresa_id,
    caso.empresaId,
    caso.conversa?.empresa_id,
    caso.conversa?.empresaId,
    empresaIdFallback,
  ]);

  const telefone = getPrimeiroValor([
    payload.telefone,
    payload.numero,
    lead.telefone1,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
    caso.telefone,
    caso.numero,
    caso.conversa?.telefone,
    caso.conversa?.numero,
  ]);

  const nome = getPrimeiroValor([
    payload.nome,
    lead.nome_lead,
    lead.nomeLead,
    lead.nome,
    caso.nome,
    caso.nome_lead,
    caso.conversa?.nome,
    caso.conversa?.nome_lead,
    telefone,
    leadId ? `Lead ${leadId}` : "Lead",
  ]);

  return {
    ...lead,
    id: leadId || lead.id || null,
    lead_id: leadId || lead.lead_id || null,
    leadId: leadId || lead.leadId || null,
    id_lead: leadId || lead.id_lead || null,
    empresa_id: empresaId,
    empresaId,
    nome_lead: lead.nome_lead || lead.nomeLead || nome,
    nome: lead.nome || nome,
    telefone1: lead.telefone1 || telefone,
    telefone: lead.telefone || telefone,
    celular: lead.celular || telefone,
    whatsapp: lead.whatsapp || telefone,
    total_orcamento:
      lead.total_orcamento ||
      payload.total_orcamento ||
      caso.total_orcamento ||
      "",
    total_contratado:
      lead.total_contratado ||
      payload.total_contratado ||
      caso.total_contratado ||
      "",
    status_negociacao:
      lead.status_negociacao ||
      payload.status_negociacao ||
      caso.status_negociacao ||
      "",
  };
}

function normalizarPagamentoSupervisao(item = {}) {
  const valor = getNumero(item.valor ?? item.valor_recebido ?? item.total ?? 0);
  return {
    ...item,
    id: item.id || item.recebimento_id || item.pagamento_id || null,
    valor,
    valorLabel: formatarMoedaSupervisao(valor) || "R$ 0,00",
    data_recebimento:
      item.data_recebimento ||
      item.dataPagamento ||
      item.data_pagamento ||
      item.created_at ||
      item.criado_em ||
      "",
    usuario_nome:
      item.nome_usuario ||
      item.usuario_nome ||
      item.usuario_lancou_nome ||
      item.criador_nome ||
      "",
    titulo: item.titulo || item.descricao || item.observacao || "Recebimento",
  };
}

async function buscarHistoricoPagamentosSupervisao(lead = {}) {
  const leadId = getPrimeiroValor([
    lead.id,
    lead.lead_id,
    lead.leadId,
    lead.id_lead,
  ]);
  const empresaId = getPrimeiroValor([lead.empresa_id, lead.empresaId]);

  if (!leadId || !empresaId) return [];

  let data = null;

  try {
    data = await requestJsonSupervisao("/receb_leads/consultar", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lead_id: leadId, empresa_id: empresaId }),
    });
  } catch (error) {
    console.warn(
      "CaptaZap Supervisão | falha ao consultar recebimentos por POST, tentando rota por lead:",
      error,
    );
    data = await requestJsonSupervisao(`/receb_leads/${leadId}`);
  }

  const lista = Array.isArray(data)
    ? data
    : Array.isArray(data?.recebimentos)
      ? data.recebimentos
      : Array.isArray(data?.pagamentos)
        ? data.pagamentos
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
            ? data.items
            : [];

  return lista
    .map(normalizarPagamentoSupervisao)
    .sort(
      (a, b) =>
        parseTimestamp(b.data_recebimento) - parseTimestamp(a.data_recebimento),
    );
}

async function buscarConversaOperacionalSupervisao(caso = {}) {
  const payload = montarPayloadChamadoSupervisao(caso);
  const empresaId =
    payload.empresa_id ||
    payload.empresaId ||
    payload.lead?.empresa_id ||
    payload.lead?.empresaId ||
    null;
  const leadId =
    payload.lead_id || payload.leadId || payload.lead_contexto_id || "";
  const telefone = normalizarTelefone(payload.telefone || payload.numero || "");

  if (!empresaId || (!leadId && !telefone)) return null;

  const params = new URLSearchParams();
  params.set("empresa_id", String(empresaId));
  params.set("limit", "500");
  params.set("dias", "365");
  if (leadId) params.set("lead_id", String(leadId));
  if (telefone) params.set("telefone", telefone);

  const data = await requestJsonSupervisao(
    `/whatsapp/captazap/conversas?${params.toString()}`,
  );

  const lista = Array.isArray(data)
    ? data
    : Array.isArray(data?.conversas)
      ? data.conversas
      : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.items)
          ? data.items
          : [];

  const leadKey = String(leadId || "");
  const telefoneKey = telefone.slice(-11) || telefone;

  const encontrada = lista.find((item) => {
    const leadCandidato = String(
      item?.lead_id ||
        item?.leadId ||
        item?.lead_contexto_id ||
        item?.leadContextoId ||
        item?.lead?.id ||
        "",
    );
    if (leadKey && leadCandidato && leadCandidato === leadKey) return true;

    const telCandidato = normalizarTelefone(
      item?.telefone ||
        item?.numero ||
        item?.phone ||
        item?.whatsapp ||
        item?.lead?.telefone1 ||
        item?.lead?.telefone ||
        item?.lead?.celular ||
        item?.lead?.whatsapp ||
        "",
    );
    return Boolean(
      telefoneKey && telCandidato && telCandidato.endsWith(telefoneKey),
    );
  });

  if (!encontrada) return null;

  return {
    ...payload,
    ...encontrada,
    lead: {
      ...(payload.lead || {}),
      ...(encontrada.lead || {}),
      id:
        encontrada.lead?.id ||
        encontrada.lead_id ||
        encontrada.leadId ||
        leadId ||
        null,
    },
    _supervisao: true,
    _supervisao_bloqueado: true,
    _somente_leitura: true,
    _abrir_chamado: true,
    _abrir_agendamento: Boolean(getAgendamentoCasoSupervisao(caso)?.has),
  };
}

function getValorOportunidadeCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const agenda = getAgendamentoCasoSupervisao(caso);

  const valor = getPrimeiroValor([
    caso.valor_oportunidade,
    caso.valorOportunidade,
    caso.oportunidade_valor,
    caso.oportunidadeValor,
    caso.valor_orcamento,
    caso.valorOrcamento,
    caso.total_orcamento,
    caso.totalOrcamento,
    conversa.valor_oportunidade,
    conversa.valorOportunidade,
    conversa.oportunidade_valor,
    conversa.oportunidadeValor,
    lead.valor_oportunidade,
    lead.valorOportunidade,
    lead.oportunidade_valor,
    lead.oportunidadeValor,
    lead.valor_orcamento,
    lead.valorOrcamento,
    lead.total_orcamento,
    lead.totalOrcamento,
    agenda?.valor_oportunidade,
    agenda?.valorOportunidade,
  ]);

  return formatarMoedaSupervisao(valor);
}

function getValorPagamentoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  const valor = getPrimeiroValor([
    caso.valor_pago,
    caso.valorPago,
    caso.total_pago,
    caso.totalPago,
    caso.valor_recebido,
    caso.valorRecebido,
    caso.total_recebido,
    caso.totalRecebido,
    conversa.valor_pago,
    conversa.valorPago,
    conversa.total_pago,
    conversa.totalPago,
    conversa.valor_recebido,
    conversa.valorRecebido,
    lead.valor_pago,
    lead.valorPago,
    lead.total_pago,
    lead.totalPago,
    lead.valor_recebido,
    lead.valorRecebido,
  ]);

  return formatarMoedaSupervisao(valor);
}

function getResumoPagamentoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const quantidade = getNumero(
    caso.quantidade_pagamentos ??
      caso.quantidadePagamentos ??
      lead.quantidade_pagamentos ??
      lead.quantidadePagamentos ??
      conversa.quantidade_pagamentos ??
      conversa.quantidadePagamentos,
  );
  const ultimo = getPrimeiroValor([
    caso.ultimo_pagamento_em,
    caso.ultimoPagamentoEm,
    lead.ultimo_pagamento_em,
    lead.ultimoPagamentoEm,
    conversa.ultimo_pagamento_em,
    conversa.ultimoPagamentoEm,
  ]);

  return {
    quantidade,
    ultimoLabel: ultimo ? formatarDataHoraCurta(ultimo) : "",
  };
}

function CasoRow({
  caso = {},
  agora = Date.now(),
  usuarios = [],
  index = 0,
  selected = false,
  onSelecionarLinha,
  onVerDetalhes,
  onAbrirAgendaSupervisao,
  onAbrirAgendaFollowup,
  onAbrirHistoricoLead,
  onAbrirPagamentosLead,
  onSelecionarConversa,
}) {
  const status = getCasoStatusConfig(caso);
  const statusChamado = getStatusChamadoConfig(caso);
  const onlineAgora = isCasoOnlineAgoraSupervisao(caso, agora);
  const origem =
    normalizarOrigemAds(
      caso.origem_ads ||
        caso.origemAds ||
        caso.conversa?.origem_ads ||
        caso.conversa?.origemAds ||
        caso.lead?.origem_ads ||
        caso.lead?.origemAds ||
        getOrigemAdsDiretaConversa(caso.conversa || caso || {}),
    ) ||
    caso.origem_ads ||
    caso.origemAds ||
    null;
  const anuncioThumb =
    origem?.thumbnailUrl ||
    origem?.thumbnail_url ||
    origem?.imageUrl ||
    origem?.image_url ||
    origem?.imagemUrl ||
    origem?.imagem_url ||
    "";
  const anuncioThumbSeguro = anuncioThumb
    ? resolverImagemUrlSupervisao(anuncioThumb)
    : "";
  const canalAds = getCanalAdsCasoSupervisao(caso);
  const canalAdsLabel = getCanalAdsLabelSupervisao(canalAds);
  const contatoFoto = getContatoFotoSupervisao(caso);
  const ultimaMensagem = caso.ultima_mensagem || {};
  const texto =
    ultimaMensagem.texto ||
    caso.mensagem ||
    caso.preview ||
    "Sem mensagem recente.";
  const entradaInfo = getEntradaCasoSupervisao(caso);
  const agendamentoOperacionalInfo = getAgendamentoCasoSupervisao(caso);
  const agendamentoInfo = getAgendamentoFuturoCasoSupervisao(caso);
  const followupInfo = getFollowupCasoSupervisao(caso);
  const meta = getResumoOperacionalCasoLinhaSupervisao(caso, agora);
  const tone = getLinhaToneSupervisao(caso);
  const captadorNome = getCaptadorNomeSupervisao(caso, usuarios);
  const captadorFoto = getCaptadorFotoSupervisao(caso, usuarios);
  const captadorLead = getCaptadorLeadSupervisao(caso, usuarios);
  const oportunidadeLabel = getValorOportunidadeCasoSupervisao(caso);
  const pagamentoLabel = getValorPagamentoCasoSupervisao(caso);
  const pagamentoResumo = getResumoPagamentoCasoSupervisao(caso);
  const tipoLinha = String(caso.tipo || "").toLowerCase();
  const isAgendaHojeLinha = tipoLinha === "agenda_hoje";
  const isAgendadosAmanhaLinha = tipoLinha === "agendados_amanha";
  const statusAgendaLinha = getStatusAgendaNormalizadoCasoSupervisao(caso);
  const isConfirmadoAmanhaLinha =
    isAgendadosAmanhaLinha &&
    (caso._supervisao_confirmado_amanha ||
      caso.confirmado_amanha ||
      isStatusAgendaConfirmadoSupervisao(statusAgendaLinha));
  const agendaHoraLinha = getHorarioAgendaLinhaSupervisao(caso);
  const agendaStatusRowClass = getAgendaStatusRowBgClassSupervisao(
    statusAgendaLinha,
    {
      agendaHoje: isAgendaHojeLinha,
      agendadosAmanha: isAgendadosAmanhaLinha,
    },
  );

  const rowBackgroundClass = selected
    ? "bg-amber-100"
    : agendaStatusRowClass ||
      (isConfirmadoAmanhaLinha
        ? "bg-gradient-to-r from-emerald-100 via-lime-50 to-white"
        : index % 2 === 0
          ? "bg-white"
          : "bg-slate-50/80");

  const getCellClass = (extra = "") => {
    return `min-h-[96px] select-text border-slate-200 bg-transparent px-3 py-3 transition ${extra}`;
  };

  const handleSelect = (event) => {
    if (isTextoSelecionadoSupervisao(event)) return;
    onSelecionarLinha?.(caso);
  };

  return (
    <div
      className={`relative z-0 grid overflow-hidden rounded-[22px] border shadow-sm transition ${rowBackgroundClass} ${
        selected
          ? "border-amber-500 ring-2 ring-amber-300 shadow-[0_12px_28px_rgba(245,158,11,0.20)]"
          : "border-slate-200 hover:border-slate-300 hover:shadow-md"
      }`}
      style={{ gridTemplateColumns: SUPERVISAO_GRID_COLUMNS }}
      onClick={handleSelect}
      onDoubleClick={(event) => {
        if (isTextoSelecionadoSupervisao(event)) return;
        onVerDetalhes?.(caso);
      }}
    >
      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("relative text-left")}`}
      >
        <span
          className={`absolute inset-y-3 left-0 w-1 rounded-r-full ${tone}`}
        />

        <div className="flex min-w-0 gap-3 pl-2">
          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-2xl border border-slate-300 bg-slate-100 shadow-sm">
            <AvatarSupervisao
              src={contatoFoto}
              alt={caso.nome || getNomeConversa(caso.conversa || {})}
              initials={getInitials(
                caso.nome || getNomeConversa(caso.conversa || {}),
              )}
              className="h-full w-full object-cover"
              fallbackClassName="flex h-full w-full items-center justify-center text-[11px] font-black text-slate-500"
            />
            {onlineAgora ? (
              <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-amber-500" />
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="whitespace-nowrap text-[13px] font-black leading-5 text-slate-950">
                {caso.nome || getNomeConversa(caso.conversa || {})}
              </span>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="rounded-lg bg-slate-100 px-1.5 py-0.5 text-[10px] font-black text-slate-600 ring-1 ring-slate-200">
                {formatTelefoneCurto(meta.telefone)}
              </span>
              {meta.chamadoId ? (
                <span className="rounded-lg bg-blue-50 px-1.5 py-0.5 text-[10px] font-black text-blue-700 ring-1 ring-blue-100">
                  #{meta.chamadoId}
                </span>
              ) : null}
              {meta.leadId ? (
                <span className="rounded-lg bg-violet-50 px-1.5 py-0.5 text-[10px] font-black text-violet-700 ring-1 ring-violet-100">
                  Lead {meta.leadId}
                </span>
              ) : null}
              {caso.qtd_entradas_anuncio_total > 1 ? (
                <span
                  className="rounded-lg bg-amber-50 px-1.5 py-0.5 text-[10px] font-black text-amber-700 ring-1 ring-amber-200"
                  title={`Este contato clicou no anúncio ${caso.qtd_entradas_anuncio_total} vezes`}
                >
                  🔁 {caso.qtd_entradas_anuncio_total}x no anúncio
                </span>
              ) : null}
              {meta.naoLidas > 0 ? (
                <span className="rounded-lg bg-emerald-600 px-1.5 py-0.5 text-[10px] font-black text-white">
                  {meta.naoLidas > 99 ? "99+" : meta.naoLidas} nova(s)
                </span>
              ) : null}
            </div>

            {entradaInfo.label ? (
              <div className="mt-1 whitespace-nowrap text-[10px] font-bold text-slate-500">
                Entrada/conversa: {entradaInfo.label}
              </div>
            ) : null}

            {origem?.titulo || origem?.campanha ? (
              <div className="mt-1 flex min-w-0 items-center gap-1 text-[10px] font-bold text-indigo-700">
                <Megaphone size={11} className="shrink-0" />
                <span
                  className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase ring-1 ${getCanalAdsBadgeClassSupervisao(canalAds)}`}
                  title={`Canal: ${canalAdsLabel}`}
                >
                  {canalAdsLabel}
                </span>
                <span className="min-w-0 truncate">
                  {origem.campanha || origem.titulo || "Origem Ads"}
                </span>
              </div>
            ) : null}

            <div className="mt-2 rounded-xl border border-slate-300 bg-white/80 px-2 py-1.5 text-[11px] font-semibold leading-4 text-slate-700 shadow-inner">
              <span className="block overflow-x-auto whitespace-nowrap pb-0.5">
                {texto}
              </span>
            </div>

            <div className="mt-1.5 text-[10px] font-bold text-slate-400">
              Última: {meta.autorLabel} · {meta.ultimaAtividadeLabel}
            </div>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l flex items-center justify-center text-center")}`}
        title={
          origem?.titulo || origem?.campanha
            ? `${canalAdsLabel}: ${origem?.campanha || origem?.titulo}`
            : "Sem anúncio vinculado"
        }
      >
        <AnuncioThumbSupervisao
          src={anuncioThumbSeguro}
          titulo={origem?.titulo || ""}
          campanha={origem?.campanha || ""}
          sourceType={origem?.sourceType || origem?.source_type || ""}
        />
      </button>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l text-left")}`}
      >
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${status.className}`}
          title={status.title || status.label}
        >
          {status.icon}
          {status.label}
        </span>
        {isAgendaHojeLinha && status.subtitle ? (
          <div className="mt-2 text-[10px] font-black uppercase tracking-wide text-slate-500">
            {status.subtitle}
          </div>
        ) : null}
        <div className={`mt-2 text-xs font-black ${status.tempoClass}`}>
          {isAgendaHojeLinha
            ? agendaHoraLinha || "Horário não informado"
            : formatarMinutos(
                caso.espera_minutos ?? msParaMinutos(caso.esperaMs),
              )}
        </div>
      </button>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l text-left")}`}
      >
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${statusChamado.className}`}
          title={statusChamado.title || statusChamado.label}
        >
          {statusChamado.icon}
          {statusChamado.label}
        </span>
        {statusChamado.subtitle ? (
          <div className="mt-2 whitespace-nowrap text-[10px] font-bold text-slate-500">
            {statusChamado.subtitle}
          </div>
        ) : null}
        {meta.statusNegociacao ? (
          <div className="mt-2 whitespace-nowrap text-[10px] font-bold text-slate-400">
            {meta.statusNegociacao}
          </div>
        ) : null}
      </button>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l text-left")}`}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-[11px] font-black text-slate-600 ring-2 ring-white shadow-sm">
            <AvatarSupervisao
              src={captadorFoto}
              alt={captadorNome}
              initials={getInitials(captadorNome)}
              className="h-full w-full object-cover"
              fallbackClassName="flex h-full w-full items-center justify-center"
            />
          </span>
          <div className="min-w-0">
            <div
              className="max-w-[175px] overflow-hidden text-ellipsis whitespace-nowrap text-xs font-black text-slate-900"
              title={captadorNome}
            >
              {captadorNome}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] font-bold text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Responsável
            </div>
            {captadorLead && captadorLead !== captadorNome ? (
              <div
                className="mt-1.5 max-w-[175px] overflow-hidden text-ellipsis whitespace-nowrap"
                title={`Captador: ${captadorLead}`}
              >
                <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-black text-blue-700 ring-1 ring-blue-100">
                  Cap: {captadorLead}
                </span>
              </div>
            ) : caso.lead_id || caso.lead?.id ? (
              <div className="mt-1.5">
                <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600 ring-1 ring-emerald-100">
                  Lead vinculado
                </span>
              </div>
            ) : (
              <div className="mt-1.5">
                <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 ring-1 ring-slate-100">
                  Sem lead
                </span>
              </div>
            )}
          </div>
        </div>
      </button>

      <div
        role="button"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") handleSelect();
        }}
        className={`${getCellClass("border-l text-left")}`}
      >
        <div className="flex flex-col gap-1.5">
          <SupervisaoMiniAction
            type="agendamento"
            info={agendamentoInfo}
            caso={caso}
            onSelecionarConversa={onSelecionarConversa}
            onAbrirAgendaSupervisao={onAbrirAgendaSupervisao}
          />
          <SupervisaoMiniAction
            type="followup"
            info={followupInfo}
            caso={caso}
            onSelecionarConversa={onSelecionarConversa}
            onAbrirAgendaFollowup={onAbrirAgendaFollowup}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l text-right")}`}
        title={oportunidadeLabel || "Sem valor de oportunidade/orçamento"}
      >
        {oportunidadeLabel ? (
          <>
            <div className="text-sm font-black text-emerald-700">
              {oportunidadeLabel}
            </div>
            <div className="mt-1 text-[10px] font-bold text-slate-400">
              Total orçamento
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-black text-slate-300">—</div>
            <div className="mt-1 text-[10px] font-bold text-slate-400">
              Oportunidade
            </div>
          </>
        )}
      </button>

      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleSelect(event);
          onAbrirPagamentosLead?.(caso);
        }}
        className={`${getCellClass("border-l text-right")}`}
        title={
          pagamentoLabel
            ? `Ver histórico de pagamentos · Pago: ${pagamentoLabel}`
            : "Ver histórico de pagamentos"
        }
      >
        {pagamentoLabel ? (
          <>
            <div className="text-sm font-black text-blue-700">
              {pagamentoLabel}
            </div>
            <div className="mt-1 text-[10px] font-bold text-slate-400">
              {pagamentoResumo.quantidade > 1
                ? `${pagamentoResumo.quantidade} pagamentos`
                : "Pago"}
            </div>
            {pagamentoResumo.ultimoLabel ? (
              <div className="mt-0.5 text-[9px] font-bold text-slate-400">
                {pagamentoResumo.ultimoLabel}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="text-sm font-black text-slate-300">—</div>
            <div className="mt-1 text-[10px] font-bold text-slate-400">
              Pagamento
            </div>
          </>
        )}
      </button>

      <button
        type="button"
        onClick={handleSelect}
        className={`${getCellClass("border-l text-right")}`}
      >
        <div className={`text-sm font-black ${status.tempoClass}`}>
          {formatarMinutos(caso.espera_minutos ?? msParaMinutos(caso.esperaMs))}
        </div>
        <div className="mt-1 text-[10px] font-bold text-slate-400">SLA</div>
      </button>

      <div className={`${getCellClass("border-l text-center")}`}>
        <div className="mx-auto inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 bg-white/90 px-1.5 py-1 shadow-sm ring-1 ring-slate-100">
          <AcaoIconeSupervisao
            title="Ver conversa na Supervisão"
            tone="emerald"
            onClick={(event) => {
              event.stopPropagation();
              handleSelect();
              onVerDetalhes?.(caso);
            }}
          >
            <Eye size={13} />
          </AcaoIconeSupervisao>

          <AcaoIconeSupervisao
            title="Abrir chamado/operação"
            tone="blue"
            onClick={async (event) => {
              event.stopPropagation();
              handleSelect();

              const payload = montarPayloadChamadoSupervisao(caso);
              const conversaOperacional =
                await buscarConversaOperacionalSupervisao(caso).catch(
                  (error) => {
                    console.warn(
                      "CaptaZap Supervisão | não foi possível localizar conversa operacional:",
                      error,
                    );
                    return null;
                  },
                );

              const agenda = getAgendamentoCasoSupervisao(caso);
              onSelecionarConversa?.(conversaOperacional || payload, {
                origem: "supervisao",
                modo: "chamado",
                abrirChamado: true,
                abrirChecklist: true,
                abrirOperacao: true,
                abrirAgendamento: false,
                leadId: payload.lead_id || payload.leadId || null,
                lead_id: payload.lead_id || payload.leadId || null,
                lead_contexto_id:
                  payload.lead_contexto_id || payload.lead_id || null,
                telefone: payload.telefone || payload.numero || "",
                status_agendamento:
                  agenda?.status || payload.status_agendamento || "",
                data_agendamento:
                  agenda?.data || payload.data_agendamento || "",
                ultimo_agendamento: payload.ultimo_agendamento || null,
                agendamentos: payload.agendamentos || [],
                lead: payload.lead || null,
                somenteLeitura: true,
                bloqueado: true,
              });

              window.dispatchEvent(
                new CustomEvent("captazap:supervisao:abrir-chamado", {
                  detail: {
                    caso,
                    conversa: conversaOperacional || payload,
                    origem: "supervisao",
                    abrirChamado: true,
                    abrirChecklist: true,
                    abrirOperacao: true,
                    lead_id: payload.lead_id || payload.leadId || null,
                    telefone: payload.telefone || payload.numero || "",
                    status_agendamento:
                      agenda?.status || payload.status_agendamento || "",
                    data_agendamento:
                      agenda?.data || payload.data_agendamento || "",
                    ultimo_agendamento: payload.ultimo_agendamento || null,
                    agendamentos: payload.agendamentos || [],
                    lead: payload.lead || null,
                    somenteLeitura: true,
                    bloqueado: true,
                  },
                }),
              );
            }}
          >
            <Headphones size={13} />
          </AcaoIconeSupervisao>

          <AcaoIconeSupervisao
            title="Abrir agendamentos da lead na Supervisão"
            tone="violet"
            onClick={(event) => {
              event.stopPropagation();
              onAbrirAgendaSupervisao?.(caso);
            }}
          >
            <CalendarCheck size={13} />
          </AcaoIconeSupervisao>

          <AcaoIconeSupervisao
            title="Histórico completo da lead"
            tone="slate"
            onClick={(event) => {
              event.stopPropagation();
              handleSelect();
              onAbrirHistoricoLead?.(caso);
            }}
          >
            <Clock3 size={13} />
          </AcaoIconeSupervisao>

          <AcaoIconeSupervisao
            title="Histórico de pagamentos"
            tone="amber"
            onClick={(event) => {
              event.stopPropagation();
              handleSelect();
              onAbrirPagamentosLead?.(caso);
            }}
          >
            <CreditCard size={13} />
          </AcaoIconeSupervisao>
        </div>
      </div>
    </div>
  );
}

function AcaoIconeSupervisao({
  children,
  title = "",
  tone = "slate",
  disabled = false,
  onClick,
}) {
  const tones = {
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100",
    blue: "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100",
    violet:
      "border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100",
    slate:
      "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100",
  };

  return (
    <button
      type="button"
      data-supervisao-action="true"
      aria-disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (disabled) return;
        onClick?.(event);
      }}
      title={title}
      aria-label={title}
      className={`inline-flex h-7 w-7 items-center justify-center rounded-full border shadow-sm transition hover:-translate-y-0.5 ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 hover:translate-y-0"
          : tones[tone] || tones.slate
      }`}
    >
      {children}
    </button>
  );
}

function formatarHoraAoVivoSupervisao(value = Date.now()) {
  const timestamp = parseTimestamp(value) || Date.now();
  return new Date(timestamp).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function AvatarSupervisao({
  src = "",
  alt = "",
  initials = "?",
  className = "",
  fallbackClassName = "",
}) {
  const [erro, setErro] = useState(false);
  const safeSrc = String(src || "").trim();

  if (safeSrc && !erro) {
    return (
      <img
        src={safeSrc}
        alt={alt}
        loading="lazy"
        className={className}
        referrerPolicy="no-referrer"
        onError={() => setErro(true)}
      />
    );
  }

  return <span className={fallbackClassName}>{initials || "?"}</span>;
}

function AnuncioThumbSupervisao({
  src = "",
  titulo = "",
  campanha = "",
  sourceType = "",
}) {
  const [modo, setModo] = useState("direto");
  const [erro, setErro] = useState(false);

  const safeSrc = String(src || "").trim();
  const srcDireto = safeSrc ? resolverImagemUrlSupervisao(safeSrc) : "";
  const imagemSrc =
    modo === "proxy" ? getProxyImagemAdsSrcSupervisao(srcDireto) : srcDireto;
  const label = campanha || titulo || "Sem anúncio";
  const isOrigemSite = String(sourceType || "")
    .toLowerCase()
    .includes("site");

  useEffect(() => {
    setModo("direto");
    setErro(false);
  }, [srcDireto]);

  if (imagemSrc && !erro) {
    return (
      <img
        src={imagemSrc}
        alt={titulo || campanha || "Imagem do anúncio"}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="h-20 w-24 rounded-2xl border border-slate-300 bg-white object-cover shadow-sm"
        onError={() => {
          if (
            modo === "direto" &&
            isUrlImagemAdsBloqueavelSupervisao(srcDireto)
          ) {
            setModo("proxy");
            return;
          }

          setErro(true);
        }}
      />
    );
  }

  if (isOrigemSite) {
    return (
      <span className="flex h-20 w-24 flex-col items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-50 px-2 text-center text-[9px] font-black text-emerald-700 shadow-sm">
        <Globe2 size={15} className="mb-1" />
        <span className="line-clamp-3 leading-3">{label || "Site"}</span>
      </span>
    );
  }

  if (titulo || campanha) {
    return (
      <span className="flex h-20 w-24 flex-col items-center justify-center rounded-2xl border border-indigo-200 bg-indigo-50 px-2 text-center text-[9px] font-black text-indigo-700 shadow-sm">
        <Megaphone size={13} className="mb-1" />
        <span className="line-clamp-3 leading-3">{label}</span>
      </span>
    );
  }

  return (
    <span className="flex h-16 w-20 items-center justify-center rounded-2xl border border-slate-300 bg-slate-50 text-[10px] font-black text-slate-400">
      Sem anúncio
    </span>
  );
}

function ImagemAdsMiniSupervisao({ src = "", alt = "", className = "" }) {
  const [modo, setModo] = useState("direto");
  const [erro, setErro] = useState(false);
  const srcDireto = String(src || "").trim();

  useEffect(() => {
    setModo("direto");
    setErro(false);
  }, [srcDireto]);

  const imagemSrc =
    modo === "proxy" ? getProxyImagemAdsSrcSupervisao(srcDireto) : srcDireto;

  if (!imagemSrc || erro) {
    return (
      <span className="flex h-full w-full items-center justify-center bg-blue-100 text-[10px] font-black uppercase text-blue-700">
        Ads
      </span>
    );
  }

  return (
    <img
      src={imagemSrc}
      alt={alt || "Imagem do anúncio"}
      className={className}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        if (
          modo === "direto" &&
          isUrlImagemAdsBloqueavelSupervisao(srcDireto)
        ) {
          setModo("proxy");
          return;
        }

        setErro(true);
      }}
    />
  );
}

function getProxyImagemAdsSrcSupervisao(url = "") {
  const raw = String(url || "").trim();
  if (!raw) return "";
  const base = String(API_BASE || "").replace(/\/+$/, "");
  if (!base) return raw;
  return `${base}/whatsapp/captazap/proxy-imagem-ads?url=${encodeURIComponent(raw)}`;
}

function isUrlImagemAdsBloqueavelSupervisao(url = "") {
  const texto = String(url || "").toLowerCase();

  return (
    texto.includes("scontent.xx.fbcdn.net") ||
    texto.includes("fbcdn.net") ||
    texto.includes("facebook.com")
  );
}

function isNomeSemResponsavelSupervisao(value = "") {
  const text = normalizar(value);
  return (
    !text ||
    text === "sem responsavel" ||
    text === "fila" ||
    text === "anúncios" ||
    text === "anuncios" ||
    text === "an" ||
    text.includes("sem responsavel") ||
    text.includes("nao atribuido") ||
    text.includes("não atribuido")
  );
}

function getCaptadorNomeSupervisao(caso = {}, usuarios = []) {
  const nomeDireto = getPrimeiroValor([
    caso.responsavel_nome,
    caso.responsavelNome,
    caso.captador_nome,
    caso.captadorNome,
    caso.usuario_criador_nome,
    caso.usuarioCriadorNome,
    caso.conversa?.responsavel_nome,
    caso.conversa?.responsavelNome,
    caso.conversa?.captador_nome,
    caso.conversa?.captadorNome,
    caso.responsavel?.nome,
    caso.captador?.nome,
  ]);

  if (!isNomeSemResponsavelSupervisao(nomeDireto)) return nomeDireto;

  const responsavelId = String(
    caso.responsavel_id ||
      caso.responsavelId ||
      caso.usuario_criador_id ||
      caso.usuarioCriadorId ||
      caso.conversa?.responsavel_id ||
      caso.conversa?.responsavelId ||
      caso.conversa?.captador_id ||
      caso.conversa?.captadorId ||
      "",
  );

  if (responsavelId && Array.isArray(usuarios)) {
    const usuario = usuarios.find((item) =>
      [
        item?.id,
        item?.usuario_id,
        item?.id_usuario,
        item?.user_id,
        item?.idUser,
      ].some((value) => String(value || "") === responsavelId),
    );

    const nomeUsuario = getNomeUsuario(usuario);
    if (nomeUsuario) return nomeUsuario;
  }

  return nomeDireto || "Sem responsável";
}

function getCaptadorLeadSupervisao(caso = {}, usuarios = []) {
  // Captador = quem criou o lead (id_usuario / usuario_criador_id do lead)
  // Só existe se o contato virou lead
  const lead = caso.lead || caso.conversa?.lead || {};
  if (!lead || !lead.id) return null; // sem lead = sem captador

  const captadorId = String(
    lead.id_usuario || lead.usuario_criador_id || lead.captador_id || "",
  );

  if (!captadorId) return null;

  // Tenta achar o nome pelo id no array de usuários
  if (Array.isArray(usuarios)) {
    const usuario = usuarios.find((u) =>
      [u?.id, u?.usuario_id, u?.id_usuario, u?.user_id].some(
        (v) => String(v || "") === captadorId,
      ),
    );
    const nomeUsuario = getNomeUsuario(usuario);
    if (nomeUsuario) return nomeUsuario;
  }

  // Fallback: nome direto do lead
  return (
    getPrimeiroValor([
      lead.captador_nome,
      lead.usuario_criador_nome,
      lead.usuario_nome,
    ]) || null
  );
}

function getHorarioAgendaLinhaSupervisao(caso = {}) {
  const agenda = getAgendamentoCasoSupervisao(caso);
  const data =
    agenda?.data ||
    caso.data_agendamento ||
    caso.agendamento_data ||
    caso.ultimo_agendamento?.data_agendamento ||
    caso.ultimo_agendamento?.data ||
    caso.conversa?.data_agendamento ||
    "";
  const ts = parseTimestamp(data);
  if (!ts) return "";

  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getChavesAgendaOperacionalSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};
  const leadId = getPrimeiroValor([
    caso.lead_id,
    caso.leadId,
    caso.lead_contexto_id,
    caso.leadContextoId,
    conversa.lead_id,
    conversa.leadId,
    conversa.lead_contexto_id,
    conversa.leadContextoId,
    lead.id,
  ]);
  const telefone = normalizarTelefone(
    getPrimeiroValor([
      caso.telefone,
      caso.numero,
      caso.whatsapp,
      conversa.telefone,
      conversa.numero,
      conversa.whatsapp,
      lead.telefone1,
      lead.telefone,
      lead.celular,
      lead.whatsapp,
    ]),
  );

  return [
    leadId ? `lead:${leadId}` : "",
    telefone ? `tel:${telefone}` : "",
  ].filter(Boolean);
}

function criarMapaOperacionalAgendaHojeSupervisao(casos = []) {
  const map = new Map();

  for (const caso of Array.isArray(casos) ? casos : []) {
    if (!caso || typeof caso !== "object") continue;
    if (String(caso.tipo || "").toLowerCase() === "agenda_hoje") continue;

    for (const key of getChavesAgendaOperacionalSupervisao(caso)) {
      if (!map.has(key)) map.set(key, caso);
    }
  }

  return map;
}

function getCasoOperacionalRelacionadoAgendaSupervisao(
  caso = {},
  mapa = new Map(),
) {
  for (const key of getChavesAgendaOperacionalSupervisao(caso)) {
    const encontrado = mapa.get(key);
    if (encontrado) return encontrado;
  }
  return null;
}

function mesclarAgendaComOperacionalSupervisao(
  caso = {},
  operacional = null,
  usuarios = [],
) {
  if (!operacional) {
    return {
      ...caso,
      responsavel_nome: getCaptadorNomeSupervisao(caso, usuarios),
    };
  }

  const conversaAgenda = caso.conversa || {};
  const conversaOperacional = operacional.conversa || operacional || {};
  const leadAgenda = conversaAgenda.lead || caso.lead || {};
  const leadOperacional = conversaOperacional.lead || operacional.lead || {};
  const nomeAgenda = caso.responsavel_nome || caso.responsavel?.nome || "";
  const nomeOperacional =
    operacional.responsavel_nome ||
    operacional.responsavel?.nome ||
    conversaOperacional.responsavel_nome ||
    "";
  const usarResponsavelOperacional =
    isNomeSemResponsavelSupervisao(nomeAgenda) &&
    !isNomeSemResponsavelSupervisao(nomeOperacional);

  const merged = {
    ...operacional,
    ...caso,
    conversa: {
      ...conversaOperacional,
      ...conversaAgenda,
      lead: {
        ...leadOperacional,
        ...leadAgenda,
      },
    },
    chamado_id:
      caso.chamado_id ||
      operacional.chamado_id ||
      conversaOperacional.chamado_id ||
      conversaOperacional.id,
    chamadoId:
      caso.chamadoId ||
      operacional.chamadoId ||
      operacional.chamado_id ||
      conversaOperacional.chamado_id ||
      conversaOperacional.id,
    conversa_id:
      caso.conversa_id ||
      operacional.conversa_id ||
      conversaOperacional.conversa_id ||
      conversaOperacional.id,
    conversaId:
      caso.conversaId ||
      operacional.conversaId ||
      operacional.conversa_id ||
      conversaOperacional.conversa_id ||
      conversaOperacional.id,
    status_chamado:
      caso.status_chamado ||
      operacional.status_chamado ||
      operacional.chamado_status ||
      conversaOperacional.status_chamado,
    chamado_status:
      caso.chamado_status ||
      operacional.chamado_status ||
      operacional.status_chamado ||
      conversaOperacional.chamado_status,
    origem_ads:
      caso.origem_ads ||
      caso.origemAds ||
      operacional.origem_ads ||
      operacional.origemAds ||
      conversaOperacional.origem_ads ||
      conversaOperacional.origemAds,
    origemAds:
      caso.origemAds ||
      caso.origem_ads ||
      operacional.origemAds ||
      operacional.origem_ads ||
      conversaOperacional.origemAds ||
      conversaOperacional.origem_ads,
    foto:
      caso.foto ||
      caso.contato_foto ||
      operacional.foto ||
      operacional.contato_foto ||
      conversaOperacional.foto ||
      conversaOperacional.contato_foto,
    contato_foto:
      caso.contato_foto ||
      caso.contatoFoto ||
      operacional.contato_foto ||
      operacional.contatoFoto ||
      conversaOperacional.contato_foto ||
      conversaOperacional.contatoFoto,
    contatoFoto:
      caso.contatoFoto ||
      caso.contato_foto ||
      operacional.contatoFoto ||
      operacional.contato_foto ||
      conversaOperacional.contatoFoto ||
      conversaOperacional.contato_foto,
    responsavel_id: usarResponsavelOperacional
      ? operacional.responsavel_id ||
        operacional.responsavel?.id ||
        conversaOperacional.responsavel_id
      : caso.responsavel_id ||
        caso.responsavel?.id ||
        operacional.responsavel_id ||
        operacional.responsavel?.id ||
        conversaOperacional.responsavel_id,
    responsavel_nome: usarResponsavelOperacional
      ? nomeOperacional
      : getCaptadorNomeSupervisao(caso, usuarios),
    responsavel_foto:
      caso.responsavel_foto ||
      caso.responsavelFoto ||
      operacional.responsavel_foto ||
      operacional.responsavelFoto ||
      conversaOperacional.responsavel_foto ||
      conversaOperacional.responsavelFoto,
    tipo: "agenda_hoje",
    criticidade: "agenda_hoje",
  };

  return merged;
}

function enriquecerAgendaHojeComOperacionalSupervisao(
  lista = [],
  mapa = new Map(),
  usuarios = [],
) {
  return (Array.isArray(lista) ? lista : []).map((caso) =>
    mesclarAgendaComOperacionalSupervisao(
      caso,
      getCasoOperacionalRelacionadoAgendaSupervisao(caso, mapa),
      usuarios,
    ),
  );
}

function getCaptadorFotoSupervisao(caso = {}, usuarios = []) {
  const direto = getPrimeiroValor([
    caso.responsavel_foto,
    caso.responsavelFoto,
    caso.responsavel_avatar,
    caso.responsavelAvatar,
    caso.captador_foto,
    caso.captadorFoto,
    caso.usuario_foto,
    caso.usuarioFoto,
    caso.responsavel?.foto,
    caso.responsavel?.avatar,
    caso.responsavel?.imagem,
    caso.conversa?.responsavel_foto,
    caso.conversa?.responsavelFoto,
    caso.conversa?.captador_foto,
    caso.conversa?.captadorFoto,
  ]);

  if (direto) return resolverImagemUrlSupervisao(direto);

  const responsavelId = String(
    caso.responsavel_id ||
      caso.responsavel?.id ||
      caso.conversa?.responsavel_id ||
      caso.conversa?.captador_id ||
      "",
  );

  if (!responsavelId || !Array.isArray(usuarios)) return "";

  const usuario = usuarios.find((item) => {
    const ids = [
      item?.id,
      item?.usuario_id,
      item?.id_usuario,
      item?.user_id,
      item?.idUser,
    ];
    return ids.some((value) => String(value || "") === responsavelId);
  });

  if (!usuario) return "";

  return resolverImagemUrlSupervisao(
    getPrimeiroValor([
      usuario.foto,
      usuario.avatar,
      usuario.imagem,
      usuario.image,
      usuario.foto_url,
      usuario.fotoUrl,
      usuario.avatar_url,
      usuario.avatarUrl,
      usuario.profile_photo_url,
      usuario.profilePhotoUrl,
      usuario.foto_perfil,
      usuario.fotoPerfil,
      usuario.photoURL,
      usuario.profileUrl,
      usuario.profile_url,
    ]) || "",
  );
}

function getContatoFotoSupervisao(caso = {}) {
  const conversa = caso.conversa || caso || {};
  const lead = conversa.lead || caso.lead || {};

  const foto = getPrimeiroValor([
    caso.lead_foto_url,
    caso.leadFotoUrl,
    caso.contato_foto_url,
    caso.contatoFotoUrl,
    caso.foto,
    caso.avatar,
    caso.imagem,
    caso.foto_url,
    caso.fotoUrl,
    caso.avatar_url,
    caso.avatarUrl,
    caso.profile_photo_url,
    caso.profilePhotoUrl,
    caso.profileUrl,
    caso.profile_url,
    caso.profilePicUrl,
    caso.profile_pic_url,
    caso.profilePictureUrl,
    caso.profile_picture_url,
    caso.foto_whatsapp,
    caso.fotoWhatsapp,
    caso.whatsapp_foto,
    caso.whatsappFoto,
    caso.imagem_perfil,
    caso.imagemPerfil,
    caso.picture,
    caso.photo,
    caso.url_foto,
    caso.urlFoto,
    caso.contato?.foto,
    caso.contato?.avatar,
    caso.contato?.profilePicUrl,
    caso.contato?.profile_pic_url,
    caso.foto_perfil,
    caso.fotoPerfil,
    caso.contato_foto,
    caso.contatoFoto,
    caso.contato_avatar,
    caso.contatoAvatar,
    caso.thumbnail_contato,
    caso.thumbnailContato,
    caso.foto_lead,
    caso.fotoLead,
    caso.lead_foto,
    caso.leadFoto,
    caso.imagem_perfil,
    caso.imagemPerfil,
    caso.profilePicUrl,
    caso.profile_pic_url,
    caso.picture,
    caso.photo,
    caso.url_foto,
    caso.urlFoto,
    conversa.foto,
    conversa.avatar,
    conversa.imagem,
    conversa.foto_url,
    conversa.fotoUrl,
    conversa.avatar_url,
    conversa.avatarUrl,
    conversa.profile_photo_url,
    conversa.profilePhotoUrl,
    conversa.profileUrl,
    conversa.profile_url,
    conversa.profilePicUrl,
    conversa.profile_pic_url,
    conversa.profilePictureUrl,
    conversa.profile_picture_url,
    conversa.foto_whatsapp,
    conversa.fotoWhatsapp,
    conversa.whatsapp_foto,
    conversa.whatsappFoto,
    conversa.imagem_perfil,
    conversa.imagemPerfil,
    conversa.picture,
    conversa.photo,
    conversa.url_foto,
    conversa.urlFoto,
    conversa.contato?.foto,
    conversa.contato?.avatar,
    conversa.contato?.profilePicUrl,
    conversa.contato?.profile_pic_url,
    conversa.foto_perfil,
    conversa.fotoPerfil,
    conversa.contato_foto,
    conversa.contatoFoto,
    conversa.contato_avatar,
    conversa.contatoAvatar,
    lead.foto,
    lead.avatar,
    lead.imagem,
    lead.foto_url,
    lead.fotoUrl,
    lead.avatar_url,
    lead.avatarUrl,
    lead.profile_photo_url,
    lead.profilePhotoUrl,
    lead.foto_perfil,
    lead.profilePicUrl,
    lead.profile_pic_url,
    lead.profilePictureUrl,
    lead.profile_picture_url,
    lead.foto_whatsapp,
    lead.fotoWhatsapp,
    lead.whatsapp_foto,
    lead.whatsappFoto,
    lead.imagem_perfil,
    lead.imagemPerfil,
    lead.picture,
    lead.photo,
    lead.url_foto,
    lead.urlFoto,
    lead.fotoPerfil,
    lead.foto_lead,
    lead.fotoLead,
    lead.lead_foto,
    lead.leadFoto,
    lead.imagem_perfil,
    lead.imagemPerfil,
    lead.profilePicUrl,
    lead.profile_pic_url,
    lead.picture,
    lead.photo,
    lead.url_foto,
    lead.urlFoto,
  ]);

  return resolverImagemUrlSupervisao(foto);
}

function resolverImagemUrlSupervisao(value = "") {
  const raw = String(value || "").trim();

  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("data:") || raw.startsWith("blob:")) return raw;

  const base = String(API_BASE || "").replace(/\/+$/, "");
  if (!base) return raw;

  return `${base}/${raw.replace(/^\/+/, "")}`;
}

function getChamadoIdVisivelSupervisao(candidatos = []) {
  for (const candidato of Array.isArray(candidatos)
    ? candidatos
    : [candidatos]) {
    const texto = String(candidato ?? "").trim();
    if (!texto) continue;

    // IDs internos gerados para linhas artificiais de agenda/filtro não são
    // número de chamado. Eles servem apenas como chave React e não devem
    // aparecer como #agenda-... na tabela.
    const normalizado = texto.toLowerCase();
    if (
      normalizado.startsWith("agenda-") ||
      normalizado.startsWith("agendamento-") ||
      normalizado.startsWith("linha-") ||
      normalizado.startsWith("filtro-") ||
      normalizado.startsWith("ads-roi-") ||
      normalizado.includes("agendados_amanha") ||
      normalizado.includes("agendados-amanha")
    ) {
      continue;
    }

    return texto;
  }

  return "";
}

function getResumoOperacionalCasoLinhaSupervisao(
  caso = {},
  agora = Date.now(),
) {
  const conversa = caso.conversa || caso || {};
  const ultima = caso.ultima_mensagem || conversa.ultima_mensagem || {};
  const telefone =
    caso.telefone ||
    conversa.telefone ||
    conversa.numero ||
    conversa.phone ||
    conversa.whatsapp ||
    "";
  const chamadoId = getChamadoIdVisivelSupervisao([
    caso.chamado_id,
    caso.id_chamado,
    caso.ticket_id,
    caso.conversa_id,
    conversa.chamado_id,
    conversa.id_chamado,
    conversa.ticket_id,
    conversa.conversa_id,
  ]);
  const leadId =
    caso.lead_id ||
    caso.leadId ||
    conversa.lead_contexto_id ||
    conversa.lead_id ||
    conversa.leadId ||
    conversa.lead?.id ||
    "";
  const naoLidas = Number(
    caso.naoLidas ||
      caso.nao_lidas ||
      conversa.naoLidas ||
      conversa.nao_lidas ||
      0,
  );
  const ts = getCasoAtividadeTimestampSupervisao(caso);
  const autorLabel = getAutorUltimaMensagemLabelSupervisao(ultima || caso);

  return {
    telefone,
    chamadoId,
    leadId,
    naoLidas,
    autorLabel,
    ultimaAtividadeLabel: ts ? formatarDataHoraCurta(ts) : "—",
    statusNegociacao:
      caso.status_negociacao ||
      conversa.status_negociacao ||
      conversa.lead?.status_negociacao ||
      "",
  };
}

function getAutorUltimaMensagemLabelSupervisao(mensagem = {}) {
  const direcao = normalizar(
    mensagem.direcao || mensagem.direction || mensagem.sentido || "",
  );
  const autor = normalizar(
    mensagem.autor || mensagem.de || mensagem.from || mensagem.sender || "",
  );

  if (
    direcao.includes("recebid") ||
    autor.includes("cliente") ||
    autor.includes("lead")
  ) {
    return "Cliente";
  }

  if (autor.includes("sistema") || direcao.includes("interno")) {
    return "Sistema";
  }

  if (
    direcao.includes("enviad") ||
    autor.includes("operador") ||
    autor.includes("captador")
  ) {
    return "Captador";
  }

  return "—";
}

function getLinhaToneSupervisao(caso = {}) {
  const status = String(caso.criticidade || caso.tipo || "").toLowerCase();

  if (isChamadoFechadoSupervisao(caso)) return "bg-slate-400";
  if (status.includes("critico")) return "bg-rose-500";
  if (status.includes("devendo")) return "bg-orange-500";
  if (status.includes("sem_retorno")) return "bg-emerald-500";
  if (status.includes("follow")) return "bg-violet-500";
  if (status.includes("fila")) return "bg-amber-500";
  return "bg-blue-500";
}

function SupervisaoMiniAction({
  type = "agendamento",
  info = {},
  caso = {},
  onSelecionarConversa,
  onAbrirAgendaSupervisao,
  onAbrirAgendaFollowup,
}) {
  const isAgenda = type === "agendamento";
  const Icon = isAgenda ? CalendarCheck : RefreshCw;
  const ativo = Boolean(info?.has);
  const label = ativo ? info.label : isAgenda ? "Sem agenda" : "Sem follow-up";
  const title = info?.title || label;
  const tone = ativo
    ? isAgenda
      ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-300 hover:bg-emerald-100"
      : "border-violet-200 bg-violet-50 text-violet-800 hover:border-violet-300 hover:bg-violet-100"
    : "border-slate-300 bg-slate-50 text-slate-400";

  const eventName = isAgenda
    ? "captazap:supervisao:abrir-agendamento"
    : "captazap:supervisao:abrir-followup";

  return (
    <button
      type="button"
      data-supervisao-action="true"
      disabled={!ativo}
      onClick={(event) => {
        event.stopPropagation();

        if (isAgenda) {
          onAbrirAgendaSupervisao?.(caso);
          window.dispatchEvent(
            new CustomEvent("captazap:supervisao:abrir-agendamento-modal", {
              detail: { caso, info, origem: "supervisao" },
            }),
          );
          return;
        }

        if (!isAgenda && info?.agendaStatusFollowup) {
          onAbrirAgendaFollowup?.({ caso, info });
          window.dispatchEvent(
            new CustomEvent("captazap:supervisao:followup-agendamento", {
              detail: { caso, info, origem: "supervisao" },
            }),
          );
          return;
        }

        const detail = {
          caso,
          conversa: caso.conversa || caso,
          lead_id:
            caso.lead_id ||
            caso.conversa?.lead_id ||
            caso.conversa?.lead?.id ||
            null,
          telefone:
            caso.telefone ||
            caso.conversa?.telefone ||
            caso.conversa?.numero ||
            "",
          info,
        };
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
        onSelecionarConversa?.(caso.conversa || caso, {
          abrirAgendamento: isAgenda,
          abrirFollowup: !isAgenda,
          origem: "supervisao",
        });
      }}
      className={`inline-flex min-h-8 w-full items-center gap-1.5 rounded-xl border px-2 py-1 text-left text-[10px] font-black transition disabled:cursor-not-allowed ${tone}`}
      title={title}
    >
      <Icon size={12} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

function HistoricoPagamentosSupervisaoModal({
  caso = {},
  empresaIdFallback = null,
  onClose,
}) {
  const leadPagamento = useMemo(
    () => montarLeadPagamentoModalSupervisao(caso, empresaIdFallback),
    [caso, empresaIdFallback],
  );
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let cancelado = false;

    async function carregarPagamentos() {
      const leadId = leadPagamento?.id || leadPagamento?.lead_id;
      const empresaId = leadPagamento?.empresa_id || leadPagamento?.empresaId;

      if (!leadId || !empresaId) {
        setPagamentos([]);
        setErro(
          "Não foi possível abrir o histórico financeiro porque este chamado não tem lead vinculada.",
        );
        return;
      }

      setLoading(true);
      setErro("");

      try {
        const lista = await buscarHistoricoPagamentosSupervisao(leadPagamento);
        if (!cancelado) setPagamentos(lista);
      } catch (error) {
        if (!cancelado) {
          setErro(
            error?.message ||
              "Não foi possível carregar o histórico de pagamentos desta lead.",
          );
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    }

    carregarPagamentos();

    return () => {
      cancelado = true;
    };
  }, [leadPagamento]);

  const totalPago = useMemo(
    () => pagamentos.reduce((soma, item) => soma + getNumero(item.valor), 0),
    [pagamentos],
  );
  const totalPagoLabel = formatarMoedaSupervisao(totalPago) || "R$ 0,00";
  const nomeLead =
    leadPagamento?.nome_lead ||
    leadPagamento?.nome ||
    leadPagamento?.telefone ||
    "Lead";
  const telefone =
    leadPagamento?.telefone1 ||
    leadPagamento?.telefone ||
    leadPagamento?.celular ||
    leadPagamento?.whatsapp ||
    "";
  const leadId = leadPagamento?.id || leadPagamento?.lead_id || "";

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-br from-amber-50 via-white to-blue-50 px-6 py-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700">
              <CreditCard size={16} />
              Histórico de pagamentos
            </div>
            <h3 className="mt-2 truncate text-lg font-black text-slate-950">
              {nomeLead}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500">
              {leadId ? <span>Lead #{leadId}</span> : null}
              {telefone ? <span>{formatTelefoneCurto(telefone)}</span> : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            title="Fechar histórico de pagamentos"
          >
            <X size={18} />
          </button>
        </header>

        <div className="grid grid-cols-2 gap-3 border-b border-slate-200 bg-white px-6 py-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
              Total pago
            </div>
            <div className="mt-1 text-lg font-black text-blue-700">
              {totalPagoLabel}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
              Lançamentos
            </div>
            <div className="mt-1 text-lg font-black text-slate-900">
              {pagamentos.length}
            </div>
          </div>
          <div className="col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:col-span-1">
            <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
              Status
            </div>
            <div className="mt-1 truncate text-sm font-black text-slate-700">
              {leadPagamento?.status_negociacao || "—"}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-bold text-slate-500">
              Carregando histórico financeiro...
            </div>
          ) : erro ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-bold text-amber-800">
              {erro}
            </div>
          ) : pagamentos.length ? (
            <div className="space-y-2">
              {pagamentos.map((pagamento, index) => (
                <div
                  key={pagamento.id || `${pagamento.data_recebimento}-${index}`}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-black text-slate-900">
                        {pagamento.titulo || "Recebimento"}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-bold text-slate-500">
                        <span>
                          {pagamento.data_recebimento
                            ? formatarDataHoraCurta(pagamento.data_recebimento)
                            : "Data não informada"}
                        </span>
                        {pagamento.usuario_nome ? (
                          <span>· {pagamento.usuario_nome}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-base font-black text-blue-700">
                      {pagamento.valorLabel}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm font-bold text-slate-500">
              Nenhum pagamento lançado para esta lead.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AgendaFollowupSupervisaoModal({ caso = {}, info = {}, onClose }) {
  const nome = caso?.nome || getNomeConversa(caso?.conversa || {}) || "Contato";
  const telefone =
    caso?.telefone || caso?.conversa?.telefone || caso?.conversa?.numero || "";
  const agenda =
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};
  const status =
    info.status ||
    caso.status_agendamento ||
    agenda.status_agendamento ||
    "Agenda";
  const data =
    info.data || caso.data_agendamento || agenda.data_agendamento || "";
  const tarefa =
    info.tarefa || caso.tarefa_sugerida || caso.tarefaSugerida || {};
  const interacao =
    info.interacao || caso.interacao_sugerida || caso.interacaoSugerida || {};

  const tituloTarefa =
    tarefa.titulo ||
    (normalizar(status).includes("faltou")
      ? "Remarcar paciente que faltou"
      : "Recuperar agendamento cancelado");
  const textoInteracao =
    interacao.descricao ||
    interacao.texto ||
    (normalizar(status).includes("faltou")
      ? "Paciente faltou ao agendamento. Necessário contato para remarcar."
      : "Agendamento cancelado. Necessário contato para recuperação/remarcação.");

  return (
    <div
      className="fixed inset-0 z-[135] flex items-start justify-center overflow-y-auto bg-slate-950/45 px-4 py-6 backdrop-blur-sm sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.32)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-300 bg-gradient-to-br from-violet-50 to-white px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-violet-700">
                <RefreshCw size={13} /> Follow-up / Agendamento
              </div>
              <h3 className="mt-3 truncate text-xl font-black text-slate-950">
                {nome}
              </h3>
              <p className="mt-0.5 text-xs font-bold text-slate-500">
                {formatTelefoneCurto(telefone)} · {status}{" "}
                {data ? `· ${formatarDataHoraCurta(data)}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Fechar Follow-up / Agendamento"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="space-y-3 px-5 py-5">
          <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3">
            <div className="text-[11px] font-black uppercase tracking-wide text-violet-700">
              Interação sugerida
            </div>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">
              {textoInteracao}
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-[11px] font-black uppercase tracking-wide text-amber-700">
              Tarefa sugerida
            </div>
            <p className="mt-2 text-sm font-black text-slate-800">
              {tituloTarefa}
            </p>
            <p className="mt-1 text-xs font-semibold leading-5 text-slate-600">
              Prazo sugerido: hoje. Esta fiscalização não entra na regra antiga
              de Follow-up; é uma ação específica de agendamento com status
              Faltou/Cancelado.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-300 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-600"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function AgendaSupervisaoModal({ caso = {}, onClose }) {
  const nome = caso?.nome || getNomeConversa(caso?.conversa || {}) || "Contato";
  const telefone =
    caso?.telefone || caso?.conversa?.telefone || caso?.conversa?.numero || "";
  const agendamentos = getAgendamentosCasoListaSupervisao(caso);
  const ultimo = getAgendamentoCasoSupervisao(caso);

  const lista = agendamentos.length
    ? agendamentos
    : ultimo?.has
      ? [
          {
            id: caso.agendamento_id || caso.ultimo_agendamento?.id || "ultimo",
            data_agendamento: ultimo.data || caso.data_agendamento || "",
            status_agendamento:
              ultimo.status || caso.status_agendamento || "Agendado",
            observacoes:
              caso.observacoes ||
              caso.observacao ||
              caso.ultimo_agendamento?.observacoes ||
              caso.ultimo_agendamento?.observacao ||
              "",
            usuario_nome:
              caso.usuario_nome ||
              caso.usuario_criador_nome ||
              caso.ultimo_agendamento?.usuario_nome ||
              "",
          },
        ]
      : [];

  return (
    <div
      className="fixed inset-0 z-[135] flex items-start justify-center overflow-y-auto bg-slate-950/50 px-4 py-6 backdrop-blur-sm sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.34)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-300 bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-emerald-700">
                <CalendarCheck size={13} /> Agendamentos da lead
              </div>
              <h3 className="mt-3 truncate text-xl font-black text-slate-950">
                {nome}
              </h3>
              <p className="mt-0.5 text-xs font-bold text-slate-500">
                {formatTelefoneCurto(telefone)}
                {caso?.lead_id ? ` · Lead ${caso.lead_id}` : ""}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Fechar agendamentos"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {!lista.length ? (
            <EmptyInline message="Nenhum agendamento carregado para esta lead." />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white">
              <div className="grid grid-cols-[150px_130px_minmax(0,1fr)_150px] bg-slate-50 text-[10px] font-black uppercase tracking-wide text-slate-500">
                <div className="px-3 py-3">Data</div>
                <div className="px-3 py-3">Status</div>
                <div className="px-3 py-3">Observação</div>
                <div className="px-3 py-3">Usuário</div>
              </div>
              <div className="divide-y divide-slate-100">
                {lista.map((item, index) => {
                  const status =
                    getPrimeiroValor([
                      item.status_agendamento,
                      item.statusAgendamento,
                      item.status,
                      item.situacao,
                    ]) || "Agendado";
                  const data = getPrimeiroValor([
                    item.data_agendamento,
                    item.dataAgendamento,
                    item.data,
                    item.inicio,
                    item.start,
                    item.datahora,
                  ]);
                  const observacao = getPrimeiroValor([
                    item.observacoes,
                    item.observacao,
                    item.obs,
                    item.descricao,
                  ]);
                  const usuario = getPrimeiroValor([
                    item.usuario_nome,
                    item.usuarioCriadorNome,
                    item.usuario_criador_nome,
                    item.usuario,
                    item.responsavel_nome,
                  ]);

                  return (
                    <div
                      key={item.id || item.agendamento_id || `${data}-${index}`}
                      className="grid grid-cols-[150px_130px_minmax(0,1fr)_150px] text-xs font-semibold text-slate-700"
                    >
                      <div className="px-3 py-3 font-black text-slate-900">
                        {data ? formatarDataHoraCurta(data) : "—"}
                      </div>
                      <div className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${getAgendaStatusPillClass(status)}`}
                        >
                          {formatarStatusChamadoSupervisao(status)}
                        </span>
                      </div>
                      <div className="min-w-0 px-3 py-3">
                        <span
                          className="block truncate"
                          title={observacao || ""}
                        >
                          {observacao || "—"}
                        </span>
                      </div>
                      <div className="min-w-0 px-3 py-3">
                        <span className="block truncate" title={usuario || ""}>
                          {usuario || "—"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 flex justify-end border-t border-slate-300 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-600 hover:bg-slate-100"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function getAgendamentosCasoListaSupervisao(caso = {}) {
  const conversa = caso.conversa || caso || {};
  const lead = conversa.lead || caso.lead || {};
  const listas = [
    caso.agendamentos,
    conversa.agendamentos,
    lead.agendamentos,
    caso.historico_agendamentos,
    conversa.historico_agendamentos,
    lead.historico_agendamentos,
  ];

  const itens = listas.flatMap((lista) => (Array.isArray(lista) ? lista : []));
  const vistos = new Set();

  return itens
    .filter(Boolean)
    .filter((item) => {
      const key =
        item.id ||
        item.agendamento_id ||
        `${item.data_agendamento || item.data || ""}-${item.status_agendamento || item.status || ""}`;
      if (!key || vistos.has(key)) return false;
      vistos.add(key);
      return true;
    })
    .sort((a, b) => {
      const ta = parseTimestamp(
        a.data_agendamento ||
          a.dataAgendamento ||
          a.data ||
          a.inicio ||
          a.start ||
          a.datahora,
      );
      const tb = parseTimestamp(
        b.data_agendamento ||
          b.dataAgendamento ||
          b.data ||
          b.inicio ||
          b.start ||
          b.datahora,
      );
      return tb - ta;
    });
}

function getAgendaStatusPillClass(status = "") {
  const value = normalizar(status);
  if (value.includes("cancel"))
    return "border-slate-500 bg-gradient-to-r from-slate-200 via-slate-100 to-white text-slate-800 shadow-sm";
  if (value.includes("falt"))
    return "border-rose-400 bg-gradient-to-r from-rose-100 via-red-50 to-white text-rose-800 shadow-sm";
  if (value.includes("comparec"))
    return "border-emerald-400 bg-gradient-to-r from-emerald-100 via-green-50 to-white text-emerald-900 shadow-sm";
  if (value.includes("confirm"))
    return "border-emerald-400 bg-gradient-to-r from-emerald-100 via-lime-50 to-white text-emerald-900 shadow-sm";
  if (value.includes("reagend"))
    return "border-violet-300 bg-gradient-to-r from-violet-100 via-fuchsia-50 to-white text-violet-800 shadow-sm";
  return "border-blue-300 bg-gradient-to-r from-blue-100 via-sky-50 to-white text-blue-800 shadow-sm";
}

function getAgendaStatusRowBgClassSupervisao(status = "", options = {}) {
  const value = normalizar(status);

  if (!options?.agendaHoje && !options?.agendadosAmanha) return "";

  // Agenda hoje: a linha inteira recebe o degradê conforme o último status real.
  // Próx. agenda: pinta a linha inteira somente quando estiver Confirmado/Confirmada.
  if (value.includes("comparec")) {
    return "bg-gradient-to-r from-emerald-100 via-green-50 to-white";
  }

  if (value.includes("confirm")) {
    return "bg-gradient-to-r from-emerald-100 via-lime-50 to-white";
  }

  if (options?.agendadosAmanha) return "";

  if (value.includes("falt")) {
    return "bg-gradient-to-r from-rose-100 via-red-50 to-white";
  }

  if (value.includes("cancel")) {
    return "bg-gradient-to-r from-slate-200 via-slate-100/80 to-white";
  }

  if (value.includes("reagend")) {
    return "bg-gradient-to-r from-violet-100 via-fuchsia-50 to-white";
  }

  if (value.includes("agend")) {
    return "bg-gradient-to-r from-blue-50 via-sky-50/70 to-white";
  }

  return "";
}

function CasoDetalheModal({ caso, onClose, onSelecionarConversa }) {
  const conversa = caso?.conversa || caso || {};
  const nome = caso?.nome || getNomeConversa(conversa);
  const telefone = caso?.telefone || conversa.telefone || conversa.numero || "";
  const [historicoMensagens, setHistoricoMensagens] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState("");

  useEffect(() => {
    let cancelado = false;

    async function carregarHistorico() {
      setLoadingHistorico(true);
      setErroHistorico("");

      try {
        const mensagens = await buscarHistoricoCompletoSupervisao(conversa);
        if (!cancelado) setHistoricoMensagens(mensagens);
      } catch (error) {
        console.error("CaptaZap Supervisão | histórico:", error);
        if (!cancelado)
          setErroHistorico(
            error?.message || "Não foi possível carregar o histórico.",
          );
      } finally {
        if (!cancelado) setLoadingHistorico(false);
      }
    }

    carregarHistorico();

    return () => {
      cancelado = true;
    };
  }, [
    conversa?.id,
    conversa?.conversa_id,
    conversa?.chamado_id,
    conversa?.empresa_id,
    conversa?.empresaId,
    conversa?.telefone,
    conversa?.numero,
  ]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mensagens = historicoMensagens.length
    ? historicoMensagens
    : [
        {
          id: "fallback",
          texto:
            caso?.ultima_mensagem?.texto ||
            caso?.mensagem ||
            "Sem mensagem recente.",
          autor: caso?.ultima_mensagem?.autor || "",
          direcao: caso?.ultima_mensagem?.direcao || "",
          datahora: caso?.ultima_mensagem?.datahora || caso?.datahora || "",
        },
      ];

  return (
    <div
      className="fixed inset-0 z-[130] flex items-start justify-center overflow-y-auto bg-slate-950/45 px-4 py-6 backdrop-blur-sm sm:items-center"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.32)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-300 bg-gradient-to-br from-white to-blue-50 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-blue-700">
                <Eye size={13} /> Detalhe do chamado
              </div>
              <h3 className="mt-3 truncate text-xl font-black text-slate-950">
                {nome}
              </h3>
              <p className="mt-0.5 text-xs font-bold text-slate-500">
                {formatTelefoneCurto(telefone)} ·{" "}
                {caso?.responsavel_nome ||
                  caso?.responsavel?.nome ||
                  "Sem responsável"}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
              aria-label="Fechar detalhes"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <InfoBox label="Tipo" value={getCasoStatusConfig(caso).label} />
            <InfoBox
              label="Entrada"
              value={getEntradaCasoSupervisao(caso).label || "—"}
            />
            <InfoBox
              label="Espera"
              value={formatarMinutos(
                caso?.espera_minutos ?? msParaMinutos(caso?.esperaMs),
              )}
            />
            <InfoBox
              label="Status"
              value={getStatusChamadoLabel(caso) || "—"}
            />
            <InfoBox
              label="Agenda"
              value={getAgendamentoCasoSupervisao(caso).label || "—"}
            />
            <InfoBox
              label="Follow-up"
              value={getFollowupCasoSupervisao(caso).label || "—"}
            />
          </div>

          <div className="mt-4 rounded-2xl border border-slate-300 bg-slate-50 px-4 py-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="text-xs font-black uppercase tracking-wide text-slate-400">
                Conversa completa
              </span>
              {loadingHistorico ? (
                <span className="text-xs font-black text-blue-700">
                  Carregando...
                </span>
              ) : null}
            </div>

            {erroHistorico ? (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
                {erroHistorico}
              </div>
            ) : null}

            <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
              {mensagens.map((mensagem, index) => (
                <MensagemHistoricoSupervisao
                  key={getMensagemHistoricoSupervisaoKey(mensagem, index)}
                  mensagem={mensagem}
                  conversa={conversa}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 flex flex-col-reverse gap-2 border-t border-slate-300 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-600"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={() => onSelecionarConversa?.(conversa)}
            className="inline-flex h-10 items-center justify-center rounded-2xl bg-blue-700 px-5 text-xs font-black text-white hover:bg-blue-800"
          >
            Abrir atendimento
          </button>
        </div>
      </div>
    </div>
  );
}

function MensagemHistoricoSupervisao({ mensagem = {}, conversa = {} }) {
  const meta = getMensagemMetaHistoricoSupervisao(mensagem, conversa);
  const texto = getTextoMensagemHistoricoSupervisao(mensagem);
  const horario = formatarDataHoraMensagemHistoricoSupervisao(
    mensagem.datahora || mensagem.created_at || mensagem.timestamp,
  );
  const enviadaPorHumano = meta.tipo === "operador";

  return (
    <div
      className={`flex ${enviadaPorHumano ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[82%] rounded-2xl border px-3 py-2 shadow-sm ${
          meta.tipo === "cliente"
            ? "border-emerald-200 bg-white text-slate-800"
            : meta.tipo === "sistema"
              ? "border-indigo-200 bg-indigo-50 text-indigo-900"
              : "border-blue-200 bg-blue-50 text-blue-950"
        }`}
      >
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <span className="text-[10px] font-black uppercase tracking-wide opacity-70">
            {meta.label}
            {meta.nome ? ` · ${meta.nome}` : ""}
          </span>
          {horario ? (
            <span className="text-[10px] font-black opacity-55">{horario}</span>
          ) : null}
        </div>
        <div className="whitespace-pre-wrap break-words text-xs font-medium leading-5">
          {texto || "Mensagem sem texto."}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-300 bg-white px-3 py-2 shadow-sm">
      <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-black text-slate-800">
        {value || "—"}
      </div>
    </div>
  );
}

function EmptyInline({ message }) {
  return (
    <div className="flex min-h-[160px] items-center justify-center px-4 py-8 text-center">
      <div>
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
          <Inbox size={21} />
        </div>
        <p className="text-sm font-bold text-slate-500">{message}</p>
      </div>
    </div>
  );
}

function isCasoFilaFiltroSupervisao(caso = {}) {
  if (!caso || typeof caso !== "object") return false;
  if (isChamadoFechadoSupervisao(caso)) return false;

  const conversa = caso.conversa || {};
  const status = normalizar(
    [
      caso.tipo,
      caso.origem,
      caso.status,
      caso.status_chamado,
      caso.statusChamado,
      caso.chamado_status,
      caso.aba_captazap,
      conversa.tipo,
      conversa.origem,
      conversa.status,
      conversa.status_chamado,
      conversa.statusChamado,
      conversa.chamado_status,
      conversa.aba_captazap,
    ]
      .filter(Boolean)
      .join(" "),
  );

  if (
    caso.em_fila ||
    caso.fila_atendimento ||
    caso.aguardando_atendimento ||
    caso._fila_global ||
    caso._fila_equipe ||
    conversa.em_fila ||
    conversa.fila_atendimento ||
    conversa.aguardando_atendimento ||
    conversa._fila_global ||
    conversa._fila_equipe
  ) {
    return true;
  }

  if (
    status.includes("fila") ||
    status.includes("aguard") ||
    status.includes("pendente") ||
    status.includes("novo")
  ) {
    return true;
  }

  const responsavelId =
    caso.responsavel_id ||
    caso.responsavel?.id ||
    conversa.responsavel_id ||
    conversa.responsavel?.id ||
    conversa.captador_id ||
    conversa.usuario_responsavel_id ||
    null;

  const direcao = normalizar(
    caso.ultima_mensagem?.direcao ||
      caso.ultima_mensagem_direcao ||
      conversa.ultima_mensagem?.direcao ||
      conversa.ultima_mensagem_direcao ||
      conversa.direcao ||
      "",
  );

  return !responsavelId && direcao.includes("recebid");
}

function isCasoReabertoFiltroSupervisao(caso = {}) {
  if (!caso || typeof caso !== "object") return false;

  const conversa = caso.conversa || {};

  const texto = normalizar(
    [
      caso.tipo,
      caso.status,
      caso.status_chamado,
      caso.statusChamado,
      caso.chamado_status,
      caso.aba_captazap,
      conversa.tipo,
      conversa.status,
      conversa.status_chamado,
      conversa.statusChamado,
      conversa.chamado_status,
      conversa.aba_captazap,
      Array.isArray(caso.tags) ? caso.tags.join(" ") : caso.tags,
      Array.isArray(conversa.tags) ? conversa.tags.join(" ") : conversa.tags,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return Boolean(
    caso.reaberto ||
    caso.reabertura_pendente ||
    caso.reaberturaPendente ||
    caso.reaberto_automaticamente ||
    caso.cliente_respondeu_apos_fechamento ||
    caso.respondeu_apos_fechamento ||
    caso.nova_mensagem_apos_fechamento ||
    caso.novaRespostaAposFechamento ||
    caso._fechado_com_resposta ||
    conversa.reaberto ||
    conversa.reabertura_pendente ||
    conversa.reaberturaPendente ||
    conversa.reaberto_automaticamente ||
    conversa.cliente_respondeu_apos_fechamento ||
    conversa.respondeu_apos_fechamento ||
    conversa.nova_mensagem_apos_fechamento ||
    conversa.novaRespostaAposFechamento ||
    conversa._fechado_com_resposta ||
    texto.includes("reabert") ||
    texto.includes("retorno apos fechamento") ||
    texto.includes("nova mensagem apos fechamento"),
  );
}

function getEntradaCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || caso || {};
  const timestamp = Math.max(
    parseTimestamp(
      caso.aberto_em ||
        caso.abertoEm ||
        caso.chamado_aberto_em ||
        caso.chamadoAbertoEm,
    ),
    parseTimestamp(
      conversa.aberto_em ||
        conversa.abertoEm ||
        conversa.chamado_aberto_em ||
        conversa.chamadoAbertoEm,
    ),
    getDataPrimeiraConversaContato(conversa),
    getDataPrimeiraConversaContato(caso),
    0,
  );

  if (!timestamp) return { label: "", subLabel: "" };

  const data = new Date(timestamp);
  const hoje = new Date();
  const mesmoDia =
    data.getFullYear() === hoje.getFullYear() &&
    data.getMonth() === hoje.getMonth() &&
    data.getDate() === hoje.getDate();

  return {
    timestamp,
    label: data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }),
    subLabel: mesmoDia ? "Hoje" : "Primeira conversa",
  };
}

function getAgendamentoCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || caso || {};
  const lead = conversa.lead || caso.lead || {};

  const candidatoDireto = montarCandidatoAgendamentoDiretoSupervisao(caso);
  const candidatoConversa =
    montarCandidatoAgendamentoDiretoSupervisao(conversa);
  const candidatoLead = montarCandidatoAgendamentoDiretoSupervisao(lead);

  const candidatos = [
    caso.proximo_agendamento,
    caso.proximoAgendamento,
    caso.agendamento_futuro,
    caso.agendamentoFuturo,
    caso.ultimo_agendamento,
    caso.ultimoAgendamento,
    caso._ultimo_agendamento,
    caso.agendamento,
    caso.agenda,
    candidatoDireto,
    conversa.proximo_agendamento,
    conversa.proximoAgendamento,
    conversa.agendamento_futuro,
    conversa.agendamentoFuturo,
    conversa.ultimo_agendamento,
    conversa.ultimoAgendamento,
    conversa._ultimo_agendamento,
    conversa.agendamento,
    conversa.agenda,
    candidatoConversa,
    lead.proximo_agendamento,
    lead.proximoAgendamento,
    lead.agendamento_futuro,
    lead.agendamentoFuturo,
    lead.ultimo_agendamento,
    lead.ultimoAgendamento,
    lead._ultimo_agendamento,
    lead.agendamento,
    lead.agenda,
    candidatoLead,
    ...(Array.isArray(caso.agendamentos) ? caso.agendamentos : []),
    ...(Array.isArray(conversa.agendamentos) ? conversa.agendamentos : []),
    ...(Array.isArray(lead.agendamentos) ? lead.agendamentos : []),
    ...(Array.isArray(caso.historico_agendamentos)
      ? caso.historico_agendamentos
      : []),
    ...(Array.isArray(conversa.historico_agendamentos)
      ? conversa.historico_agendamentos
      : []),
    ...(Array.isArray(lead.historico_agendamentos)
      ? lead.historico_agendamentos
      : []),
  ].filter(Boolean);

  const melhor = getMelhorAgendamentoPorUltimoStatusSupervisao(candidatos);

  const status = getPrimeiroValor([
    melhor?.status,
    melhor?.item?.status_agendamento,
    melhor?.item?.statusAgendamento,
    melhor?.item?.agendamento_status,
    melhor?.item?.agendamentoStatus,
    melhor?.item?.situacao_agendamento,
    melhor?.item?.situacaoAgendamento,
    melhor?.item?.status,
    melhor?.item?.situacao,
    caso.status_agendamento,
    caso.statusAgendamento,
    conversa.status_agendamento,
    conversa.statusAgendamento,
    lead.status_agendamento,
    lead.statusAgendamento,
  ]);

  const statusNormalizado = normalizar(status);
  const agendado = Boolean(
    caso.agendado ||
    conversa.agendado ||
    lead.agendado ||
    isConversaAgendada(conversa) ||
    statusNormalizado.includes("agend") ||
    statusNormalizado.includes("reagend") ||
    statusNormalizado.includes("confirm") ||
    statusNormalizado.includes("comparec") ||
    statusNormalizado.includes("falt") ||
    statusNormalizado.includes("cancel"),
  );

  if (!agendado && !status && !melhor?.ts) {
    return { has: false, label: "", title: "Sem agendamento carregado" };
  }

  const dataLabel = melhor?.ts
    ? formatarDataHoraCurta(melhor.data || melhor.ts)
    : "";
  const isFuturo = Boolean(melhor?.ts && melhor.ts > Date.now());
  const valorOportunidade = getPrimeiroValor([
    caso.valor_oportunidade,
    caso.valorOportunidade,
    conversa.valor_oportunidade,
    conversa.valorOportunidade,
    lead.valor_oportunidade,
    lead.valorOportunidade,
    melhor?.item?.valor_oportunidade,
    melhor?.item?.valorOportunidade,
  ]);
  const valorLabel = statusNormalizado.includes("compareceu")
    ? formatarMoedaSupervisao(valorOportunidade)
    : "";
  const statusLabel = formatarStatusAgendamentoSupervisao(
    status || (isFuturo ? "Agenda futura" : "Agendado"),
  );
  const label = [statusLabel, dataLabel, valorLabel]
    .filter(Boolean)
    .join(" · ");

  return {
    has: true,
    label: label || statusLabel,
    title: label || "Agendamento vinculado",
    data: melhor?.data || "",
    timestamp: melhor?.ts || 0,
    futuro: isFuturo,
    status: statusLabel,
    status_original: status || statusLabel,
    status_resolvido_por: melhor?.motivo || "fallback",
    valor_oportunidade: valorOportunidade || null,
    item: melhor?.item || null,
  };
}

function getAgendamentoFuturoCasoSupervisao(caso = {}) {
  const info = getAgendamentoCasoSupervisao(caso);
  const ts = Number(info?.timestamp || parseTimestamp(info?.data) || 0);

  if (!info?.has || !ts || ts <= Date.now()) {
    return { has: false, label: "", title: "Sem agendamento futuro" };
  }

  const dataLabel = formatarDataHoraCurta(info.data || ts);
  const status = info.status || "Agenda futura";
  const label = [status, dataLabel].filter(Boolean).join(" · ");

  return {
    ...info,
    has: true,
    futuro: true,
    label,
    title: label || "Agendamento futuro",
  };
}

function montarCandidatoAgendamentoDiretoSupervisao(fonte = {}) {
  if (!fonte || typeof fonte !== "object") return null;

  const data = getPrimeiroValor([
    fonte.data_agendamento,
    fonte.dataAgendamento,
    fonte.data_agenda,
    fonte.dataAgenda,
    fonte.agendamento_data,
    fonte.agendamentoData,
    fonte.proximo_agendamento,
    fonte.proximoAgendamento,
    fonte.proxima_agenda,
    fonte.proximaAgenda,
    fonte.agendado_para,
    fonte.agendadoPara,
  ]);

  const status = getPrimeiroValor([
    fonte.status_agendamento,
    fonte.statusAgendamento,
    fonte.agendamento_status,
    fonte.agendamentoStatus,
    fonte.situacao_agendamento,
    fonte.situacaoAgendamento,
    fonte.proximo_agendamento_status,
    fonte.proximoAgendamentoStatus,
    fonte.agendamento_futuro_status,
    fonte.agendamentoFuturoStatus,
  ]);

  if (!data && !status) return null;

  return {
    data_agendamento: data,
    status_agendamento: status || "Agendado",
    valor_oportunidade: getPrimeiroValor([
      fonte.valor_oportunidade,
      fonte.valorOportunidade,
      fonte.total_orcamento,
      fonte.totalOrcamento,
      fonte.valor_orcamento,
      fonte.valorOrcamento,
    ]),
  };
}

function getMelhorAgendamentoPorUltimoStatusSupervisao(candidatos = []) {
  const itens = (Array.isArray(candidatos) ? candidatos : [])
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;

      const data = getDataAgendamentoItemSupervisao(item);
      const ts = parseTimestamp(data);
      const status = getStatusAgendamentoItemSupervisao(item);
      const atualizadoTs =
        getTimestampAtualizacaoAgendamentoItemSupervisao(item);
      const idNum = getIdAgendamentoItemSupervisao(item);

      if (!ts && !status) return null;

      return {
        item,
        index,
        data,
        ts: ts || 0,
        status,
        atualizadoTs,
        idNum,
        prioridadeStatus: getPrioridadeStatusAgendaDesempateSupervisao(status),
        motivo: "ultimo_status_agendamento",
      };
    })
    .filter(Boolean);

  if (!itens.length) return null;

  return itens.sort((a, b) => {
    // Primeiro escolhe o agendamento de data/horário mais recente.
    if (a.ts !== b.ts) return b.ts - a.ts;

    // Se existirem várias marcações no mesmo horário, o status final é o mais
    // recente do histórico: atualizado_em/criado_em/id. Isso resolve casos como
    // Agendado + Cancelado + Confirmado no mesmo horário.
    if (a.atualizadoTs !== b.atualizadoTs)
      return b.atualizadoTs - a.atualizadoTs;
    if (a.idNum !== b.idNum) return b.idNum - a.idNum;

    // Fallback visual quando o backend não manda id/atualização.
    if (a.prioridadeStatus !== b.prioridadeStatus) {
      return b.prioridadeStatus - a.prioridadeStatus;
    }

    return b.index - a.index;
  })[0];
}

function getDataAgendamentoItemSupervisao(item = {}) {
  return getPrimeiroValor([
    item.data_agendamento,
    item.dataAgendamento,
    item.data_agenda,
    item.dataAgenda,
    item.agendamento_data,
    item.agendamentoData,
    item.data,
    item.inicio,
    item.start,
    item.start_at,
    item.datahora,
    item.agendado_para,
    item.agendadoPara,
  ]);
}

function getStatusAgendamentoItemSupervisao(item = {}) {
  return getPrimeiroValor([
    item.status_agendamento,
    item.statusAgendamento,
    item.agendamento_status,
    item.agendamentoStatus,
    item.situacao_agendamento,
    item.situacaoAgendamento,
    item.proximo_agendamento_status,
    item.proximoAgendamentoStatus,
    item.agendamento_futuro_status,
    item.agendamentoFuturoStatus,
    item.status,
    item.situacao,
  ]);
}

function getTimestampAtualizacaoAgendamentoItemSupervisao(item = {}) {
  return Math.max(
    parseTimestamp(item.status_atualizado_em),
    parseTimestamp(item.statusAtualizadoEm),
    parseTimestamp(item.data_status),
    parseTimestamp(item.status_em),
    parseTimestamp(item.statusEm),
    parseTimestamp(item.cancelado_em),
    parseTimestamp(item.canceladoEm),
    parseTimestamp(item.confirmado_em),
    parseTimestamp(item.confirmadoEm),
    parseTimestamp(item.faltou_em),
    parseTimestamp(item.faltouEm),
    parseTimestamp(item.compareceu_em),
    parseTimestamp(item.compareceuEm),
    parseTimestamp(item.atualizado_em),
    parseTimestamp(item.atualizadoEm),
    parseTimestamp(item.updated_at),
    parseTimestamp(item.updatedAt),
    parseTimestamp(item.criado_em),
    parseTimestamp(item.criadoEm),
    parseTimestamp(item.created_at),
    parseTimestamp(item.createdAt),
    0,
  );
}

function getIdAgendamentoItemSupervisao(item = {}) {
  const raw = getPrimeiroValor([
    item.id,
    item.agendamento_id,
    item.agendamentoId,
    item.id_agendamento,
    item.idAgendamento,
  ]);

  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

function getPrioridadeStatusAgendaDesempateSupervisao(status = "") {
  const value = normalizar(status);

  if (value.includes("comparec") || value.includes("realiz")) return 80;
  if (value.includes("confirm")) return 70;
  if (value.includes("reagend")) return 60;
  if (value.includes("agend")) return 50;
  if (value.includes("falt")) return 40;
  if (value.includes("cancel")) return 30;

  return 10;
}

function getFollowupCasoSupervisao(caso = {}, options = {}) {
  const incluirAgendaStatus = options?.incluirAgendaStatus !== false;
  const conversa = caso.conversa || caso || {};
  const lead = conversa.lead || caso.lead || {};
  const candidatos = [
    caso,
    conversa,
    lead,
    ...(Array.isArray(caso.followups) ? caso.followups : []),
    ...(Array.isArray(conversa.followups) ? conversa.followups : []),
    ...(Array.isArray(lead.followups) ? lead.followups : []),
  ];
  const fonte = candidatos.find((item) => isFonteFollowupSupervisao(item));

  if (!fonte) {
    const agendaStatusFollowup = incluirAgendaStatus
      ? getAgendaStatusFollowupCasoSupervisao(caso)
      : null;

    if (agendaStatusFollowup?.has) return agendaStatusFollowup;

    return { has: false, label: "", title: "Sem follow-up carregado" };
  }

  const titulo = getPrimeiroValor([
    fonte._follow_up_titulo,
    fonte.followup_titulo,
    fonte.followupTitulo,
    fonte.titulo_followup,
    fonte.tituloFollowup,
    fonte.titulo,
    fonte.status_followup,
    fonte.statusFollowup,
    normalizar(fonte.tipo).includes("follow") ? "Follow-up" : "",
    "Follow-up",
  ]);
  const data = getPrimeiroValor([
    fonte.agendado_para,
    fonte.agendadoPara,
    fonte.data_followup,
    fonte.dataFollowup,
    fonte.followup_data,
    fonte.followupData,
    fonte.followup_em,
    fonte.followupEm,
    fonte.proximo_followup,
    fonte.proximoFollowup,
    fonte._follow_up_liberado_em,
    fonte.followup_liberado_em,
    fonte.janela_inicio,
  ]);
  const dataLabel = data ? formatarDataHoraCurta(data) : "";
  const label = [titulo || "Follow-up", dataLabel].filter(Boolean).join(" · ");

  return {
    has: true,
    label,
    title: label,
    data,
    origem: "followup_existente",
  };
}

function getAgendaStatusFollowupCasoSupervisao(caso = {}) {
  const agenda =
    caso.ultimo_agendamento ||
    caso._ultimo_agendamento ||
    caso.agendamento ||
    {};
  const statusRaw = getPrimeiroValor([
    caso.agenda_followup_status,
    caso.agendaFollowupStatus,
    caso.status_agendamento,
    caso.statusAgendamento,
    agenda.status_agendamento,
    agenda.status,
  ]);
  const status = normalizar(statusRaw);
  const ehFaltou = status.includes("faltou");
  const ehCancelado = status.includes("cancel");
  const flagAgendaFollowup = Boolean(
    caso.agenda_followup ||
    caso.agendaFollowup ||
    caso.agenda_status_followup ||
    caso.agendaStatusFollowup ||
    caso.tarefa_sugerida?.origem === "followup_agendamento" ||
    caso.tarefa_sugerida?.origem === "agenda_status_faltou_cancelado" ||
    caso.interacao_sugerida?.origem === "followup_agendamento" ||
    caso.interacao_sugerida?.origem === "agenda_status_faltou_cancelado",
  );

  if (!flagAgendaFollowup && !ehFaltou && !ehCancelado) {
    return { has: false, label: "", title: "Sem Follow-up / Agendamento" };
  }

  const statusLabel = ehFaltou
    ? "Faltou"
    : ehCancelado
      ? "Cancelado"
      : statusRaw || "Agenda";
  const data = getPrimeiroValor([
    caso.agenda_followup_data,
    caso.agendaFollowupData,
    caso.data_agendamento,
    caso.dataAgendamento,
    agenda.data_agendamento,
    agenda.data,
  ]);
  const dataLabel = data ? formatarDataHoraCurta(data) : "";
  const label = [`Follow-up / Agendamento`, statusLabel, dataLabel]
    .filter(Boolean)
    .join(" · ");
  const motivo = ehFaltou
    ? "Paciente faltou ao agendamento. Registrar interação e criar tarefa para remarcar."
    : "Agendamento cancelado. Registrar interação e criar tarefa de recuperação/remarcação.";

  return {
    has: true,
    label,
    title: motivo,
    data,
    status: statusRaw || statusLabel,
    origem: "followup_agendamento",
    agendaStatusFollowup: true,
    followupAgendamento: true,
    tipoFollowup: "agendamento",
    tarefa: caso.tarefa_sugerida || caso.tarefaSugerida || null,
    interacao: caso.interacao_sugerida || caso.interacaoSugerida || null,
  };
}

function isFonteFollowupSupervisao(item = {}) {
  if (!item || typeof item !== "object") return false;

  const texto = normalizar(
    [
      item.tipo,
      item.origem,
      item.status,
      item.status_followup,
      item.statusFollowup,
      item._follow_up_titulo,
      item.followup_titulo,
      item.followupTitulo,
      item.titulo_followup,
      item.tituloFollowup,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return Boolean(
    item.followup ||
    item.followUp ||
    item._follow_up_pendente ||
    item.followup_pendente ||
    item.followupPendente ||
    item.followup_id ||
    item.followupId ||
    item.follow_up_id ||
    item.followUpId ||
    item._follow_up_id ||
    item.status_followup ||
    item.statusFollowup ||
    item._follow_up_titulo ||
    item.followup_titulo ||
    item.followupTitulo ||
    item.titulo_followup ||
    item.tituloFollowup ||
    item.agendado_para ||
    item.agendadoPara ||
    item.data_followup ||
    item.dataFollowup ||
    item.followup_data ||
    item.followupData ||
    item.followup_em ||
    item.followupEm ||
    item.proximo_followup ||
    item.proximoFollowup ||
    texto.includes("follow"),
  );
}

function isCasoFollowupFiltroSupervisao(caso = {}) {
  return Boolean(
    getFollowupCasoSupervisao(caso, { incluirAgendaStatus: false })?.has,
  );
}

function getTone(tone = "blue") {
  const tones = {
    blue: {
      shell: "border-blue-200 bg-blue-50/70",
      icon: "bg-blue-100 text-blue-700",
      bar: "bg-blue-600",
    },
    indigo: {
      shell: "border-indigo-200 bg-indigo-50/70",
      icon: "bg-indigo-100 text-indigo-700",
      bar: "bg-indigo-600",
    },
    slate: {
      shell: "border-slate-300 bg-slate-50",
      icon: "bg-slate-100 text-slate-700",
      bar: "bg-slate-600",
    },
    amber: {
      shell: "border-amber-200 bg-amber-50/80",
      icon: "bg-amber-100 text-amber-700",
      bar: "bg-amber-500",
    },
    rose: {
      shell: "border-rose-200 bg-rose-50/80",
      icon: "bg-rose-100 text-rose-700",
      bar: "bg-rose-600",
    },
    emerald: {
      shell: "border-emerald-200 bg-emerald-50/80",
      icon: "bg-emerald-100 text-emerald-700",
      bar: "bg-emerald-600",
    },
    orange: {
      shell: "border-orange-200 bg-orange-50/80",
      icon: "bg-orange-100 text-orange-700",
      bar: "bg-orange-500",
    },
    green: {
      shell: "border-green-200 bg-green-50/80",
      icon: "bg-green-100 text-green-700",
      bar: "bg-green-600",
    },
    cyan: {
      shell: "border-cyan-200 bg-cyan-50/80",
      icon: "bg-cyan-100 text-cyan-700",
      bar: "bg-cyan-600",
    },
    violet: {
      shell: "border-violet-200 bg-violet-50/80",
      icon: "bg-violet-100 text-violet-700",
      bar: "bg-violet-600",
    },
  };

  return tones[tone] || tones.blue;
}

function getCasoAtividadeTimestampSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const ultima = caso.ultima_mensagem || {};

  return Math.max(
    parseTimestamp(ultima.datahora || ultima.created_at || ultima.timestamp),
    parseTimestamp(caso.ultima_mensagem_em || caso.ultimaMensagemEm),
    parseTimestamp(caso.atualizado_em || caso.updated_at),
    parseTimestamp(conversa.ultimaMensagemEm || conversa.ultima_mensagem_em),
    parseTimestamp(conversa.updated_at || conversa.created_at),
    parseTimestamp(conversa.datahora || conversa.timestamp),
    0,
  );
}

function isCasoOnlineAgoraSupervisao(caso = {}, agora = Date.now()) {
  const naoLidas = Number(
    caso.naoLidas || caso.nao_lidas || caso.conversa?.naoLidas || 0,
  );
  if (naoLidas > 0) return true;

  const timestamp = getCasoAtividadeTimestampSupervisao(caso);
  if (!timestamp) return false;

  const diff = Number(agora || Date.now()) - timestamp;
  return diff >= 0 && diff <= TEMPO_REAL_LINHA_AMARELA_MS;
}

function getCasoStatusConfig(caso = {}) {
  const criticidade = String(
    caso.criticidade || caso.nivel || "",
  ).toLowerCase();
  const tipo = String(caso.tipo || caso.tipoEspera || "").toLowerCase();

  if (String(caso.tipo || "").toLowerCase() === "agenda_hoje") {
    const agenda = getAgendamentoCasoSupervisao(caso);
    const statusRaw = agenda?.status || "Agendado";
    const statusAgenda = normalizar(statusRaw);
    const statusLabel = formatarStatusAgendamentoSupervisao(statusRaw);

    let className =
      "border-blue-300 bg-gradient-to-r from-blue-100 via-sky-50 to-white text-blue-800 shadow-sm";
    let icon = <CalendarCheck size={14} />;

    if (statusAgenda.includes("compareceu")) {
      className =
        "border-emerald-400 bg-gradient-to-r from-emerald-100 via-green-50 to-white text-emerald-900 shadow-sm";
      icon = <CheckCircle2 size={14} />;
    } else if (statusAgenda.includes("confirm")) {
      className =
        "border-emerald-400 bg-gradient-to-r from-emerald-100 via-lime-50 to-white text-emerald-900 shadow-sm";
      icon = <BadgeCheck size={14} />;
    } else if (statusAgenda.includes("faltou")) {
      className =
        "border-rose-400 bg-gradient-to-r from-rose-100 via-red-50 to-white text-rose-800 shadow-sm";
      icon = <AlertTriangle size={14} />;
    } else if (statusAgenda.includes("cancel")) {
      className =
        "border-slate-500 bg-gradient-to-r from-slate-200 via-slate-100 to-white text-slate-800 shadow-sm";
      icon = <X size={14} />;
    } else if (statusAgenda.includes("reagend")) {
      className =
        "border-violet-300 bg-gradient-to-r from-violet-100 via-fuchsia-50 to-white text-violet-800 shadow-sm";
      icon = <RefreshCw size={14} />;
    }

    return {
      label: "Agendados",
      subtitle: statusLabel,
      title: agenda?.label || `Agenda do dia · ${statusLabel}`,
      className,
      tempoClass: statusAgenda.includes("faltou")
        ? "text-rose-800"
        : statusAgenda.includes("cancel")
          ? "text-slate-800"
          : statusAgenda.includes("confirm") ||
              statusAgenda.includes("compareceu")
            ? "text-emerald-800"
            : "text-slate-700",
      icon,
    };
  }

  if (String(caso.tipo || "").toLowerCase() === "agendados_amanha") {
    const agenda = getAgendamentoCasoSupervisao(caso);
    const statusRaw = agenda?.status || "Agendado";
    const statusLabel = getStatusLabelAgendadosAmanhaSupervisao(statusRaw);
    const confirmado = isStatusAgendaConfirmadoSupervisao(statusRaw);

    return {
      label: statusLabel,
      title: agenda?.label || `Agenda de amanhã · ${statusLabel}`,
      className: confirmado
        ? "border-emerald-400 bg-gradient-to-r from-emerald-100 via-lime-50 to-white text-emerald-900 shadow-sm"
        : "border-blue-300 bg-gradient-to-r from-blue-100 via-sky-50 to-white text-blue-800 shadow-sm",
      tempoClass: confirmado ? "text-emerald-800" : "text-blue-700",
      icon: confirmado ? <BadgeCheck size={14} /> : <CalendarCheck size={14} />,
    };
  }

  if (
    caso.sla_ignorado ||
    caso.slaIgnorado ||
    isCasoMensagemAutomaticaAgendaSupervisao(caso)
  ) {
    return {
      label: "Agenda auto",
      title:
        caso.motivo_sla_ignorado ||
        "Mensagem automática de agenda; não gera pendência de captador",
      className: "border-blue-300 bg-blue-50 text-blue-800",
      tempoClass: "text-slate-500",
      icon: <CalendarCheck size={14} />,
    };
  }

  if (isChamadoFechadoSupervisao(caso)) {
    return {
      label: "Fechado",
      title: "Chamado fechado; não gera pendência para o captador",
      className: "border-slate-300 bg-slate-100 text-slate-800",
      tempoClass: "text-slate-500",
      icon: <CheckCircle2 size={14} />,
    };
  }

  if (
    criticidade.includes("critico_devendo") ||
    criticidade.includes("critico-devendo")
  ) {
    return {
      label: "Crítico · contato",
      title: "Contato respondeu e aguarda retorno urgente do captador",
      className: "border-rose-300 bg-rose-100 text-rose-800",
      tempoClass: "text-rose-700",
      icon: <AlertTriangle size={14} />,
    };
  }

  if (
    criticidade.includes("critico_sem") ||
    criticidade.includes("critico-sem")
  ) {
    return {
      label: "Crítico · captador",
      title:
        "Captador foi o último a falar e o contato não retornou há muito tempo",
      className: "border-red-300 bg-red-100 text-red-800",
      tempoClass: "text-red-700",
      icon: <AlertTriangle size={14} />,
    };
  }

  if (tipo === "devendo_resposta") {
    return {
      label: "Retorno contato",
      title: "Contato foi o último a falar; captador precisa responder",
      className: "border-orange-300 bg-orange-100 text-orange-800",
      tempoClass: "text-orange-700",
      icon: <BellRing size={14} />,
    };
  }

  if (tipo === "sem_retorno") {
    return {
      label: "Retorno captador",
      title: "Captador foi o último a falar; aguardando retorno do contato",
      className: "border-emerald-300 bg-emerald-100 text-emerald-800",
      tempoClass: "text-emerald-700",
      icon: <Clock3 size={14} />,
    };
  }

  if (tipo === "fila") {
    return {
      label: "Fila",
      className: "border-amber-300 bg-amber-100 text-amber-800",
      tempoClass: "text-amber-700",
      icon: <Inbox size={14} />,
    };
  }

  if (tipo === "followup") {
    return {
      label: "Follow-up",
      className: "border-violet-300 bg-violet-100 text-violet-800",
      tempoClass: "text-violet-700",
      icon: <RefreshCw size={14} />,
    };
  }

  return {
    label: "Normal",
    className: "border-slate-300 bg-slate-100 text-slate-800",
    tempoClass: "text-slate-600",
    icon: <CheckCircle2 size={14} />,
  };
}

function formatarStatusAgendamentoSupervisao(value = "") {
  const raw = String(value || "").trim();
  const normalized = normalizar(raw);

  if (normalized.includes("confirm")) return "Confirmado";
  if (normalized.includes("reagend")) return "Reagendado";
  if (normalized.includes("compareceu")) return "Compareceu";
  if (normalized.includes("faltou")) return "Faltou";
  if (normalized.includes("cancel")) return "Cancelado";
  if (normalized.includes("agend")) return "Agendado";

  return raw || "Agendado";
}

function getStatusChamadoConfig(caso = {}) {
  const label = getStatusChamadoLabel(caso);
  const normalized = normalizar(label);

  if (String(caso.tipo || "").toLowerCase() === "agenda_hoje") {
    const temChamado = Boolean(
      caso.chamado_id ||
      caso.chamadoId ||
      caso.conversa?.chamado_id ||
      caso.conversa?.id ||
      caso.conversa_id ||
      caso.conversaId,
    );

    return {
      label: temChamado ? label || "Aberto" : "Lead / agenda",
      subtitle: temChamado ? "Chamado vinculado" : "Sem chamado vinculado",
      title: temChamado
        ? label || "Chamado vinculado à agenda"
        : "Registro vem da tabela de agendamentos; não há chamado vinculado neste item.",
      className: temChamado
        ? "border-blue-300 bg-blue-100 text-blue-800"
        : "border-slate-300 bg-white text-slate-700",
      icon: temChamado ? <Headphones size={14} /> : <CalendarCheck size={14} />,
    };
  }

  if (isChamadoFechadoSupervisao(caso)) {
    return {
      label: "Fechado",
      subtitle: "Sem pendência",
      title: "Chamado fechado; não entra em retorno do contato/captador",
      className: "border-slate-300 bg-slate-100 text-slate-800",
      icon: <CheckCircle2 size={14} />,
    };
  }

  if (normalized.includes("fila")) {
    return {
      label: "Fila",
      title: "Chamado aguardando responsável",
      className: "border-amber-300 bg-amber-100 text-amber-800",
      icon: <Inbox size={14} />,
    };
  }

  if (normalized.includes("agend")) {
    return {
      label: "Agendado",
      title: label,
      className: "border-emerald-300 bg-emerald-100 text-emerald-800",
      icon: <CalendarCheck size={14} />,
    };
  }

  if (normalized.includes("atendimento")) {
    return {
      label: "Em atendimento",
      title: label,
      className: "border-blue-300 bg-blue-100 text-blue-800",
      icon: <Headphones size={14} />,
    };
  }

  if (normalized.includes("aberto") || normalized.includes("open")) {
    return {
      label: "Aberto",
      title: label,
      className: "border-blue-300 bg-blue-100 text-blue-800",
      icon: <Headphones size={14} />,
    };
  }

  return {
    label: label || "Aberto",
    title: label || "Status do chamado",
    className: "border-slate-300 bg-white text-slate-700",
    icon: <ShieldCheck size={14} />,
  };
}

function getStatusChamadoLabel(caso = {}) {
  const conversa = caso.conversa || {};
  const raw = getStatusChamadoRaw(caso);

  if (isChamadoFechadoSupervisao(caso)) return "Fechado";
  if (String(caso.tipo || "").toLowerCase() === "agenda_hoje") {
    if (raw) return formatarStatusChamadoSupervisao(raw);
    if (conversa.id || conversa.conversa_id || caso.chamado_id) return "Aberto";
    return "Lead / agenda";
  }
  if (raw) return formatarStatusChamadoSupervisao(raw);
  if (caso.origem === "fila" || caso.tipo === "fila") return "Fila";
  if (caso.followup || caso.tipo === "followup") return "Follow-up";
  if (caso.agendado || caso.tipo === "agendado") return "Agendado";
  if (conversa.id || conversa.conversa_id || caso.chamado_id) return "Aberto";

  return "—";
}

function getStatusChamadoRaw(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  return getPrimeiroValor([
    caso.status_chamado,
    caso.statusChamado,
    caso.chamado_status,
    caso.status_ticket,
    caso.ticket_status,
    caso.status,
    conversa.status_chamado,
    conversa.statusChamado,
    conversa.chamado_status,
    conversa.status_ticket,
    conversa.ticket_status,
    conversa.status,
    lead.status_chamado,
    lead.statusChamado,
    lead.status_negociacao,
  ]);
}

function formatarStatusChamadoSupervisao(value = "") {
  const texto = String(value || "").trim();
  const normalized = normalizar(texto);

  if (!texto) return "";
  if (normalized.includes("fechado") || normalized.includes("closed"))
    return "Fechado";
  if (normalized.includes("encerrado")) return "Encerrado";
  if (normalized.includes("finalizado")) return "Finalizado";
  if (normalized.includes("atendimento")) return "Em atendimento";
  if (normalized.includes("aberto") || normalized.includes("open"))
    return "Aberto";
  if (normalized.includes("fila")) return "Fila";

  return texto
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isChamadoFechadoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const lead = conversa.lead || caso.lead || {};

  const flags = [
    caso.chamado_fechado,
    caso.chamadoFechado,
    caso.fechado,
    caso.finalizado,
    caso.encerrado,
    conversa.chamado_fechado,
    conversa.chamadoFechado,
    conversa.fechado,
    conversa.finalizado,
    conversa.encerrado,
    lead.chamado_fechado,
    lead.chamadoFechado,
  ];

  if (
    flags.some(
      (value) =>
        value === true ||
        value === 1 ||
        String(value || "").toLowerCase() === "true",
    )
  ) {
    return true;
  }

  const dataFechamento = getPrimeiroValor([
    caso.fechado_em,
    caso.fechadoEm,
    caso.encerrado_em,
    caso.encerradoEm,
    caso.finalizado_em,
    caso.finalizadoEm,
    conversa.fechado_em,
    conversa.fechadoEm,
    conversa.encerrado_em,
    conversa.encerradoEm,
    conversa.finalizado_em,
    conversa.finalizadoEm,
  ]);

  if (dataFechamento) return true;

  const status = normalizar(
    [
      caso.status_chamado,
      caso.statusChamado,
      caso.chamado_status,
      caso.status_ticket,
      caso.ticket_status,
      caso.status,
      conversa.status_chamado,
      conversa.statusChamado,
      conversa.chamado_status,
      conversa.status_ticket,
      conversa.ticket_status,
      conversa.status,
      lead.status_chamado,
      lead.statusChamado,
    ]
      .filter(Boolean)
      .join(" "),
  );

  if (!status) return false;
  if (status.includes("reabert") || status.includes("nao fechado"))
    return false;

  return (
    status.includes("fechado") ||
    status.includes("closed") ||
    status.includes("encerrado") ||
    status.includes("finalizado") ||
    status.includes("concluido") ||
    status.includes("resolvido")
  );
}

function calcularResumoOperacionalCasos(casos = []) {
  const resumo = {
    devendo_resposta: 0,
    sem_retorno: 0,
    mensagens_recebidas: 0,
    mensagens_enviadas: 0,
  };

  for (const caso of casos || []) {
    const fechado = isChamadoFechadoSupervisao(caso);
    const tipo = String(caso.tipo || caso.tipoEspera || "").toLowerCase();
    const mensagens = contarMensagensCasoSupervisao(caso);

    resumo.mensagens_recebidas += mensagens.recebidas;
    resumo.mensagens_enviadas += mensagens.enviadas;

    if (fechado) continue;
    if (tipo === "devendo_resposta") resumo.devendo_resposta += 1;
    if (tipo === "sem_retorno") resumo.sem_retorno += 1;
  }

  return resumo;
}

function aplicarCalculosOperacionaisResumo(resumo = {}, casos = []) {
  if (!Array.isArray(casos) || !casos.length) return resumo;

  const calculado = calcularResumoOperacionalCasos(casos);

  // IMPORTANTE:
  // Recebidas/Enviadas são métricas de mensagem da instância WhatsApp/Z-API,
  // não quantidade de chamados cuja última mensagem foi recebida/enviada.
  // Portanto esta função recalcula apenas pendências operacionais.
  // Os KPIs de mensagens devem vir do backend em resumo.mensagens_recebidas
  // e resumo.mensagens_enviadas, calculados direto da tabela whatsapp.
  return {
    ...resumo,
    devendo_resposta: calculado.devendo_resposta,
    sem_retorno: calculado.sem_retorno,
  };
}

function recalcularCaptadoresOperacionaisSupervisao(
  captadores = [],
  casos = [],
) {
  if (!Array.isArray(casos) || !casos.length) return captadores || [];

  const baseMap = new Map();
  for (const captador of captadores || []) {
    baseMap.set(getCaptadorKey(captador), { ...captador });
  }

  const metricasMap = new Map();

  for (const caso of casos || []) {
    const key = getFiltroResponsavelKey(caso);
    const tipo = String(caso.tipo || caso.tipoEspera || "").toLowerCase();
    const fechado = isChamadoFechadoSupervisao(caso);
    const mensagens = contarMensagensCasoSupervisao(caso);

    if (!metricasMap.has(key)) {
      metricasMap.set(key, {
        responsavel_id: caso.responsavel_id || caso.responsavel?.id || null,
        nome:
          caso.responsavel_nome || caso.responsavel?.nome || "Sem responsável",
        monitorados: 0,
        devendo_resposta: 0,
        sem_retorno: 0,
        mensagens_recebidas: 0,
        mensagens_enviadas: 0,
        soma_espera: 0,
      });
    }

    const metricas = metricasMap.get(key);
    metricas.monitorados += fechado ? 0 : 1;
    metricas.mensagens_recebidas += mensagens.recebidas;
    metricas.mensagens_enviadas += mensagens.enviadas;
    metricas.soma_espera += fechado ? 0 : getNumero(caso.espera_minutos);

    if (fechado) continue;
    if (tipo === "devendo_resposta") metricas.devendo_resposta += 1;
    if (tipo === "sem_retorno") metricas.sem_retorno += 1;
  }

  for (const [key, metricas] of metricasMap.entries()) {
    const atual = baseMap.get(key) || {};
    baseMap.set(key, {
      ...atual,
      responsavel_id: atual.responsavel_id ?? metricas.responsavel_id,
      nome: atual.nome || atual.responsavel_nome || metricas.nome,
      devendo_resposta: metricas.devendo_resposta,
      sem_retorno: metricas.sem_retorno,
      mensagens_recebidas: Math.max(
        getNumero(atual.mensagens_recebidas),
        metricas.mensagens_recebidas,
      ),
      mensagens_enviadas: Math.max(
        getNumero(atual.mensagens_enviadas),
        metricas.mensagens_enviadas,
      ),
      tempo_medio_resposta_min:
        metricas.monitorados > 0
          ? Math.round(metricas.soma_espera / metricas.monitorados)
          : getNumero(
              atual.tempo_medio_resposta_min ?? atual.espera_media_minutos,
            ),
    });
  }

  return [...baseMap.values()];
}

function contarMensagensCasoSupervisao(caso = {}) {
  const conversa = caso.conversa || {};
  const mensagens = [
    ...(Array.isArray(caso.mensagens) ? caso.mensagens : []),
    ...(Array.isArray(conversa.mensagens) ? conversa.mensagens : []),
  ].filter(Boolean);

  const unicas = [];
  const seen = new Set();

  for (const mensagem of mensagens) {
    const key = getMensagemHistoricoSupervisaoKey(mensagem, unicas.length);
    if (seen.has(key)) continue;
    seen.add(key);
    unicas.push(mensagem);
  }

  const base = unicas.length
    ? unicas
    : [
        caso.ultima_mensagem ||
          getUltimaMensagem(conversa, []) || {
            texto: caso.mensagem || caso.preview || "",
            direcao: caso.direcao || caso.autor || "",
            datahora: caso.datahora || caso.ultimaMensagemEm || "",
          },
      ];

  let recebidas = 0;
  let enviadas = 0;

  for (const mensagem of base) {
    if (!mensagem) continue;
    if (isMensagemCliente(mensagem)) {
      recebidas += 1;
      continue;
    }
    if (isMensagemOperador(mensagem)) {
      enviadas += 1;
    }
  }

  if (!recebidas && !enviadas && !unicas.length) {
    const tipo = String(caso.tipo || caso.tipoEspera || "").toLowerCase();
    if (tipo === "devendo_resposta") recebidas = 1;
    if (tipo === "sem_retorno") enviadas = 1;
  }

  return { recebidas, enviadas };
}

function mesclarSupervisaoComOrigemAntiga(backend = {}, fallback = {}) {
  const backendCasos = Array.isArray(backend.casos) ? backend.casos : [];
  const fallbackCasos = Array.isArray(fallback.casos) ? fallback.casos : [];
  const casos = backendCasos.length ? backendCasos : fallbackCasos;

  const anuncios = combinarAnunciosSupervisao(
    backend.anuncios || [],
    fallback.anuncios || [],
    agregarAnunciosPorCasosSupervisao(casos),
  );

  let resumo = {
    ...(fallback.resumo || {}),
    ...(backend.resumo || {}),
  };

  const metricasMensagensBackend = normalizarMetricasMensagensSupervisao(
    backend.resumo || {},
    backend.metricas_mensagens,
    backend.metricasMensagens,
    backend.metricas_zapi,
    backend.metricasZapi,
    backend.estatisticas_zapi,
    backend.estatisticasZapi,
  );

  if (metricasMensagensBackend.hasRecebidas) {
    resumo.mensagens_recebidas = metricasMensagensBackend.recebidas;
    resumo._mensagens_recebidas_oficial = true;
  }

  if (metricasMensagensBackend.hasEnviadas) {
    resumo.mensagens_enviadas = metricasMensagensBackend.enviadas;
    resumo._mensagens_enviadas_oficial = true;
  }

  if (!getNumero(resumo.monitorados) && casos.length) {
    resumo.monitorados = casos.length;
  }

  if (!getNumero(resumo.anuncios_recebidos) && anuncios.length) {
    resumo.anuncios_recebidos = anuncios.reduce(
      (total, item) =>
        total +
        getNumero(
          item.telefones ??
            item.leads ??
            item.conversas ??
            item.interacoes ??
            item.entradas ??
            1,
        ),
      0,
    );
  }

  const baseAgenda = getNumero(
    resumo.chamados_abertos ||
      resumo.conversas_abertas_hoje ||
      resumo.monitorados ||
      fallback.resumo?.monitorados,
  );
  const agendamentos = getNumero(
    resumo.agendamentos_feitos ?? resumo.agendamentos_hoje,
  );

  if (baseAgenda > 0) {
    resumo.taxa_agendamento =
      Math.round((agendamentos / baseAgenda) * 1000) / 10;
  }

  resumo = aplicarCalculosOperacionaisResumo(resumo, casos);

  const captadoresBase =
    Array.isArray(backend.captadores) && backend.captadores.length
      ? backend.captadores
      : fallback.captadores || [];

  const captadores = recalcularCaptadoresOperacionaisSupervisao(
    captadoresBase,
    casos,
  );

  const getListaFiltroBackend = (snakeKey, camelKey) => {
    if (Array.isArray(backend?.[snakeKey])) return backend[snakeKey];
    if (Array.isArray(backend?.[camelKey])) return backend[camelKey];
    return null;
  };

  const getListaFiltroBackendOuFallback = (snakeKey, camelKey) => {
    const listaBackend = getListaFiltroBackend(snakeKey, camelKey);
    if (Array.isArray(listaBackend)) return listaBackend;
    if (Array.isArray(fallback?.[snakeKey])) return fallback[snakeKey];
    if (Array.isArray(fallback?.[camelKey])) return fallback[camelKey];
    return null;
  };

  const casosFila = getListaFiltroBackend("casos_fila", "casosFila");
  const casosFollowup = getListaFiltroBackend(
    "casos_followup",
    "casosFollowup",
  );
  const casosAgendados = getListaFiltroBackend(
    "casos_agendados",
    "casosAgendados",
  );
  const casosAgendaHoje = getListaFiltroBackend(
    "casos_agenda_hoje",
    "casosAgendaHoje",
  );
  const casosAbertosDia = getListaFiltroBackend(
    "casos_abertos_dia",
    "casosAbertosDia",
  );
  const casosReabertos = getListaFiltroBackend(
    "casos_reabertos",
    "casosReabertos",
  );
  const casosAnuncios =
    getListaFiltroBackendOuFallback("casos_anuncios", "casosAnuncios") ||
    getListaFiltroBackendOuFallback("conversas_anuncios", "conversasAnuncios");

  return {
    ...fallback,
    ...backend,
    resumo,
    captadores,
    anuncios,
    casos,
    casos_fila: casosFila,
    casosFila,
    casos_followup: casosFollowup,
    casosFollowup,
    casos_agendados: casosAgendados,
    casosAgendados,
    casos_agenda_hoje: casosAgendaHoje,
    casosAgendaHoje,
    casos_abertos_dia: casosAbertosDia,
    casosAbertosDia,
    casos_reabertos: casosReabertos,
    casosReabertos,
    casos_anuncios: casosAnuncios,
    casosAnuncios,
    conversas_anuncios: casosAnuncios,
    conversasAnuncios: casosAnuncios,
  };
}

function combinarAnunciosSupervisao(...listas) {
  const map = new Map();

  for (const lista of listas) {
    for (const raw of lista || []) {
      if (!raw || typeof raw !== "object") continue;

      const normalizado = normalizarAnuncioSupervisao(raw);
      if (!normalizado) continue;

      const key =
        [
          normalizado.source_id || normalizado.sourceId,
          normalizado.campanha,
          normalizado.titulo,
        ]
          .filter(Boolean)
          .join("|") || `anuncio-${map.size}`;

      const atual = map.get(key);

      if (!atual) {
        map.set(key, normalizado);
        continue;
      }

      map.set(key, {
        ...atual,
        ...normalizado,
        telefones:
          getNumero(atual.telefones) + getNumero(normalizado.telefones),
        leads: getNumero(atual.leads) + getNumero(normalizado.leads),
        conversas:
          getNumero(atual.conversas) + getNumero(normalizado.conversas),
        interacoes:
          getNumero(atual.interacoes) + getNumero(normalizado.interacoes),
        entradas: getNumero(atual.entradas) + getNumero(normalizado.entradas),
        agendamentos:
          getNumero(atual.agendamentos) + getNumero(normalizado.agendamentos),
        agendamentos_feitos:
          getNumero(atual.agendamentos_feitos) +
          getNumero(normalizado.agendamentos_feitos),
        ultima_origem_em:
          parseTimestamp(
            normalizado.ultima_origem_em || normalizado.criado_em,
          ) > parseTimestamp(atual.ultima_origem_em || atual.criado_em)
            ? normalizado.ultima_origem_em || normalizado.criado_em
            : atual.ultima_origem_em || atual.criado_em,
      });
    }
  }

  return [...map.values()].sort((a, b) => {
    const agB = getNumero(
      b.agendamentos ?? b.agendamentos_feitos ?? b.agendados,
    );
    const agA = getNumero(
      a.agendamentos ?? a.agendamentos_feitos ?? a.agendados,
    );
    const entradasB = getNumero(
      b.telefones ?? b.leads ?? b.conversas ?? b.interacoes ?? b.entradas,
    );
    const entradasA = getNumero(
      a.telefones ?? a.leads ?? a.conversas ?? a.interacoes ?? a.entradas,
    );
    return agB - agA || entradasB - entradasA;
  });
}

function normalizarAnuncioSupervisao(raw = {}) {
  const origem = normalizarOrigemAds(raw) || raw;

  const isOrigemSite = String(
    origem.sourceType ||
      origem.source_type ||
      raw.source_type ||
      raw.sourceType ||
      "",
  )
    .toLowerCase()
    .includes("site");
  const titulo =
    origem.titulo ||
    origem.title ||
    raw.anuncio_titulo ||
    raw.nome_anuncio ||
    raw.ad_name ||
    (isOrigemSite ? "Site" : "Anúncio sem título");
  const campanha =
    origem.campanha ||
    raw.nome_campanha ||
    raw.campaign_name ||
    raw.campaign ||
    (isOrigemSite ? "Site" : "Sem campanha");
  const sourceId =
    origem.sourceId || raw.source_id || raw.sourceId || raw.ad_id || "";

  if (!titulo && !campanha && !sourceId) return null;

  return {
    ...raw,
    titulo,
    campanha,
    corpo: origem.corpo || raw.corpo || raw.body || raw.texto || "",
    thumbnail_url:
      origem.thumbnailUrl ||
      raw.thumbnail_url ||
      raw.thumbnailUrl ||
      raw.image_url ||
      raw.imageUrl ||
      "",
    thumbnailUrl:
      origem.thumbnailUrl ||
      raw.thumbnailUrl ||
      raw.thumbnail_url ||
      raw.imageUrl ||
      raw.image_url ||
      "",
    source_id: sourceId,
    sourceId,
    source_type: origem.sourceType || raw.source_type || raw.sourceType || "",
    source_url:
      origem.sourceUrl || raw.source_url || raw.sourceUrl || raw.url || "",
    sourceUrl:
      origem.sourceUrl || raw.sourceUrl || raw.source_url || raw.url || "",
    telefones: getNumero(
      raw.telefones ??
        raw.leads ??
        raw.conversas ??
        raw.entradas ??
        raw.interacoes ??
        1,
    ),
    interacoes: getNumero(raw.interacoes ?? raw.entradas ?? 1),
    agendamentos: getNumero(
      raw.agendamentos ?? raw.agendamentos_feitos ?? raw.agendados ?? 0,
    ),
    agendamentos_feitos: getNumero(
      raw.agendamentos_feitos ?? raw.agendamentos ?? raw.agendados ?? 0,
    ),
    ultima_origem_em:
      raw.ultima_origem_em ||
      raw.criado_em ||
      raw.created_at ||
      raw.updated_at ||
      "",
  };
}

function agregarAnunciosPorCasosSupervisao(casos = []) {
  const map = new Map();

  for (const caso of casos || []) {
    const origem = normalizarOrigemAds(caso.origem_ads || caso.origemAds);
    if (!origem) continue;

    const key = [origem.sourceId, origem.campanha, origem.titulo]
      .filter(Boolean)
      .join("|");

    if (!map.has(key)) {
      map.set(key, {
        titulo: origem.titulo || "Anúncio sem título",
        campanha: origem.campanha || "Sem campanha",
        corpo: origem.corpo || "",
        thumbnail_url: origem.thumbnailUrl || "",
        thumbnailUrl: origem.thumbnailUrl || "",
        source_id: origem.sourceId || "",
        sourceId: origem.sourceId || "",
        source_type: origem.sourceType || "",
        source_url: origem.sourceUrl || "",
        sourceUrl: origem.sourceUrl || "",
        telefones: 0,
        interacoes: 0,
        agendamentos: 0,
        agendamentos_feitos: 0,
        ultima_origem_em: "",
      });
    }

    const item = map.get(key);
    item.telefones += 1;
    item.interacoes += 1;
    if (caso.agendado || caso.tipo === "agendado") {
      item.agendamentos += 1;
      item.agendamentos_feitos += 1;
    }

    const dataCaso =
      caso.ultima_mensagem?.datahora ||
      caso.datahora ||
      caso.conversa?.ultimaMensagemEm ||
      caso.conversa?.updated_at ||
      "";

    if (parseTimestamp(dataCaso) > parseTimestamp(item.ultima_origem_em)) {
      item.ultima_origem_em = dataCaso;
    }
  }

  return [...map.values()];
}

function montarOrigemAdsDeCasoBackendSupervisao(item = {}) {
  const sourceId = getPrimeiroValor([
    item.source_id,
    item.sourceId,
    item.ads_source_id,
    item.adsSourceId,
    item.anuncio_source_id,
    item.anuncioSourceId,
  ]);
  const titulo = getPrimeiroValor([
    item.anuncio,
    item.titulo,
    item.titulo_anuncio,
    item.ads_titulo,
    item.anuncio_titulo,
  ]);
  const corpo = getPrimeiroValor([
    item.texto_anuncio,
    item.corpo,
    item.corpo_anuncio,
    item.ads_corpo,
  ]);
  const sourceUrl = getPrimeiroValor([
    item.source_url,
    item.sourceUrl,
    item.ads_source_url,
    item.adsSourceUrl,
    item.url_anuncio,
  ]);
  const thumbnailUrl = getPrimeiroValor([
    item.thumbnail_url,
    item.thumbnailUrl,
    item.ads_thumbnail_url,
    item.adsThumbnailUrl,
    item.image_url,
    item.imageUrl,
  ]);
  const criadoEm = getPrimeiroValor([
    item.origem_ads_em,
    item.origemAdsEm,
    item.origem_ads_criado_em,
    item.origemAdsCriadoEm,
    item.ads_criado_em,
    item.adsCriadoEm,
    item.ads_recebido_em,
    item.adsRecebidoEm,
  ]);
  const origemId = getPrimeiroValor([
    item.origem_ads_id,
    item.origemAdsId,
    item.ads_id,
    item.adsId,
  ]);

  if (
    !sourceId &&
    !titulo &&
    !corpo &&
    !sourceUrl &&
    !thumbnailUrl &&
    !origemId
  ) {
    return null;
  }

  return {
    id: origemId || null,
    source_id: sourceId || "",
    sourceId: sourceId || "",
    source_type: item.source_type || item.sourceType || "ad",
    sourceType: item.sourceType || item.source_type || "ad",
    titulo: titulo || "Anúncio sem título",
    title: titulo || "Anúncio sem título",
    corpo: corpo || "",
    body: corpo || "",
    source_url: sourceUrl || "",
    sourceUrl: sourceUrl || "",
    thumbnail_url: thumbnailUrl || "",
    thumbnailUrl: thumbnailUrl || "",
    criado_em: criadoEm || "",
    created_at: criadoEm || "",
    ultima_origem_em: criadoEm || "",
  };
}

function normalizarSupervisaoBackend(
  data = {},
  {
    empresaNome,
    dataReferencia,
    dataInicio,
    dataFim,
    metricasMensagensZapi = null,
    metricasZapi = null,
    estatisticasZapi = null,
  } = {},
) {
  const resumoEntrada = data.resumo || {};
  const captadoresEntrada = Array.isArray(data.captadores)
    ? data.captadores
    : [];
  const anunciosEntrada = Array.isArray(data.anuncios) ? data.anuncios : [];
  const casosEntrada = Array.isArray(data.casos) ? data.casos : [];
  const casosAnunciosEntrada = getPrimeiraListaSupervisao(
    data.casos_anuncios,
    data.casosAnuncios,
    data.conversas_anuncios,
    data.conversasAnuncios,
  );
  const metricasMensagens = normalizarMetricasMensagensSupervisao(
    metricasMensagensZapi,
    metricasZapi,
    estatisticasZapi,
    resumoEntrada,
    data.metricas_mensagens,
    data.metricasMensagens,
    data.metricas_zapi,
    data.metricasZapi,
    data.estatisticas_zapi,
    data.estatisticasZapi,
    data.zapi,
    data.zApi,
    data.mensagens,
  );

  const resumo = {
    ...resumoEntrada,
    monitorados: getNumero(
      resumoEntrada.monitorados ??
        resumoEntrada.em_atendimento + resumoEntrada.fila,
    ),
    chamados_abertos: getNumero(
      resumoEntrada.chamados_abertos ?? resumoEntrada.conversas_abertas_hoje,
    ),
    chamados_fechados: getNumero(resumoEntrada.chamados_fechados),
    em_atendimento: getNumero(resumoEntrada.em_atendimento),
    fila: getNumero(resumoEntrada.fila),
    devendo_resposta: getNumero(resumoEntrada.devendo_resposta),
    sem_retorno: getNumero(resumoEntrada.sem_retorno),
    followups_pendentes: getNumero(
      resumoEntrada.followups_pendentes ?? resumoEntrada.followups_vencidos,
    ),
    followups_vencidos: getNumero(
      resumoEntrada.followups_vencidos ?? resumoEntrada.followups_pendentes,
    ),
    agendamentos_feitos: getNumero(
      resumoEntrada.agendamentos_feitos ?? resumoEntrada.agendamentos_hoje,
    ),
    reabertos: getNumero(resumoEntrada.reabertos),
    taxa_agendamento: getNumero(resumoEntrada.taxa_agendamento),
    anuncios_recebidos: getNumero(
      resumoEntrada.anuncios_recebidos ??
        resumoEntrada.anuncios_periodo ??
        resumoEntrada.chamados_anuncios ??
        resumoEntrada.conversas_anuncios ??
        (casosAnunciosEntrada.length || anunciosEntrada.length),
    ),
    mensagens_recebidas: metricasMensagens.hasRecebidas
      ? metricasMensagens.recebidas
      : getNumero(resumoEntrada.mensagens_recebidas),
    mensagens_enviadas: metricasMensagens.hasEnviadas
      ? metricasMensagens.enviadas
      : getNumero(resumoEntrada.mensagens_enviadas),
    _mensagens_recebidas_oficial: metricasMensagens.hasRecebidas,
    _mensagens_enviadas_oficial: metricasMensagens.hasEnviadas,
    _origem_mensagens: metricasMensagens.hasAny ? "zapi" : "backend",
  };

  const captadores = captadoresEntrada.map((item) => ({
    ...item,
    responsavel_id: item.responsavel_id ?? item.usuario_id ?? null,
    nome:
      item.nome ||
      item.responsavel_nome ||
      item.usuario_nome ||
      "Sem responsável",
    monitorados: getNumero(item.monitorados ?? item.chamados_abertos),
    chamados_abertos: getNumero(item.chamados_abertos),
    chamados_fechados: getNumero(item.chamados_fechados),
    devendo_resposta: getNumero(item.devendo_resposta),
    sem_retorno: getNumero(item.sem_retorno),
    followups_pendentes: getNumero(
      item.followups_pendentes ?? item.followups_vencidos,
    ),
    agendamentos_feitos: getNumero(item.agendamentos_feitos),
    taxa_agendamento: getNumero(item.taxa_agendamento),
    mensagens_recebidas: getNumero(item.mensagens_recebidas),
    mensagens_enviadas: getNumero(item.mensagens_enviadas),
    tempo_medio_resposta_min: getNumero(
      item.tempo_medio_resposta_min ?? item.espera_media_minutos,
    ),
  }));

  const normalizarCasoBackend = (item = {}) => {
    const tipo = item.tipo || item.tipoEspera || "normal";
    const esperaMin = getNumero(
      item.espera_minutos ?? msParaMinutos(item.esperaMs),
    );

    const ultimaMensagem = item.ultima_mensagem || {
      texto: item.mensagem || item.preview || "",
      datahora: item.datahora || item.ultimaMensagemEm || null,
      direcao: item.direcao || "",
      autor: item.autor || "",
    };

    return neutralizarSlaAutomaticoAgendaSupervisao({
      ...item,
      conversa: item.conversa || item,
      nome: item.nome || getNomeConversa(item.conversa || item),
      telefone:
        item.telefone || item.conversa?.telefone || item.conversa?.numero || "",
      responsavel_id: item.responsavel_id ?? item.responsavel?.id ?? null,
      responsavel_nome:
        item.responsavel_nome || item.responsavel?.nome || "Sem responsável",
      status_chamado: getStatusChamadoRaw(item) || getStatusChamadoLabel(item),
      chamado_fechado: isChamadoFechadoSupervisao(item),
      tipo,
      espera_minutos: esperaMin,
      criticidade: item.criticidade || getCriticidadePorTipo(tipo, esperaMin),
      ultima_mensagem: ultimaMensagem,
      origem_ads:
        item.origem_ads ||
        item.origemAds ||
        montarOrigemAdsDeCasoBackendSupervisao(item),
      origemAds:
        item.origemAds ||
        item.origem_ads ||
        montarOrigemAdsDeCasoBackendSupervisao(item),
      followup: Boolean(item.followup || tipo === "followup"),
      agendado: Boolean(
        item.agendado || tipo === "agendado" || tipo === "agenda_hoje",
      ),
    });
  };

  const normalizarListaCasosBackend = (...listas) => {
    const lista = listas.find((item) => Array.isArray(item));
    return Array.isArray(lista) ? lista.map(normalizarCasoBackend) : [];
  };

  const casos = normalizarListaCasosBackend(casosEntrada);
  const temCasosFila =
    Array.isArray(data.casos_fila) || Array.isArray(data.casosFila);
  const temCasosFollowup =
    Array.isArray(data.casos_followup) || Array.isArray(data.casosFollowup);
  const temCasosAgendados =
    Array.isArray(data.casos_agendados) || Array.isArray(data.casosAgendados);
  const temCasosAgendaHoje =
    Array.isArray(data.casos_agenda_hoje) ||
    Array.isArray(data.casosAgendaHoje);
  const temCasosAbertosDia =
    Array.isArray(data.casos_abertos_dia) ||
    Array.isArray(data.casosAbertosDia) ||
    Array.isArray(data.casos_abertos_periodo) ||
    Array.isArray(data.casosAbertosPeriodo) ||
    Array.isArray(data.casos_chamados_abertos) ||
    Array.isArray(data.casosChamadosAbertos);
  const temCasosReabertos =
    Array.isArray(data.casos_reabertos) || Array.isArray(data.casosReabertos);
  const temCasosAnuncios =
    Array.isArray(data.casos_anuncios) ||
    Array.isArray(data.casosAnuncios) ||
    Array.isArray(data.conversas_anuncios) ||
    Array.isArray(data.conversasAnuncios);

  const casosFila = temCasosFila
    ? normalizarListaCasosBackend(data.casos_fila, data.casosFila)
    : null;
  const casosFollowup = temCasosFollowup
    ? normalizarListaCasosBackend(data.casos_followup, data.casosFollowup)
    : null;
  const casosAgendados = temCasosAgendados
    ? normalizarListaCasosBackend(data.casos_agendados, data.casosAgendados)
    : null;
  const casosAgendaHoje = temCasosAgendaHoje
    ? normalizarListaCasosBackend(data.casos_agenda_hoje, data.casosAgendaHoje)
    : null;
  const casosAbertosDia = temCasosAbertosDia
    ? normalizarListaCasosBackend(
        data.casos_abertos_dia,
        data.casosAbertosDia,
        data.casos_abertos_periodo,
        data.casosAbertosPeriodo,
        data.casos_chamados_abertos,
        data.casosChamadosAbertos,
      )
    : null;
  const casosReabertos = temCasosReabertos
    ? normalizarListaCasosBackend(data.casos_reabertos, data.casosReabertos)
    : null;
  const casosAnuncios = temCasosAnuncios
    ? normalizarListaCasosBackend(
        data.casos_anuncios,
        data.casosAnuncios,
        data.conversas_anuncios,
        data.conversasAnuncios,
      )
    : null;

  return {
    empresaNome,
    periodo: data.periodo || {
      data: dataReferencia || dataFim || getHojeISO(),
      inicio: dataInicio || dataReferencia || getHojeISO(),
      fim: dataFim || dataReferencia || getHojeISO(),
      label: formatarPeriodoSupervisao(
        dataInicio || dataReferencia,
        dataFim || dataReferencia,
      ),
    },
    resumo,
    captadores,
    anuncios: anunciosEntrada,
    casos,
    casos_fila: casosFila,
    casosFila,
    casos_followup: casosFollowup,
    casosFollowup,
    casos_agendados: casosAgendados,
    casosAgendados,
    casos_agenda_hoje: casosAgendaHoje,
    casosAgendaHoje,
    casos_abertos_dia: casosAbertosDia,
    casosAbertosDia,
    casos_reabertos: casosReabertos,
    casosReabertos,
    casos_anuncios: casosAnuncios,
    casosAnuncios,
    conversas_anuncios: casosAnuncios,
    conversasAnuncios: casosAnuncios,
  };
}

function montarSupervisaoFallback({
  empresaNome,
  dataReferencia,
  dataInicio,
  dataFim,
  conversasEquipe = [],
  filaDeEspera = [],
  followUpConversas = [],
  fechadosConversas = [],
  usuarios = [],
  metricasAgendamentoCaptazap = null,
  totalAgendadosCaptazap = null,
  leadsAgendadasCaptazap = [],
  metricasMensagensZapi = null,
  metricasZapi = null,
  estatisticasZapi = null,
}) {
  const agora = Date.now();
  const equipe = dedupeConversas(
    Array.isArray(conversasEquipe) ? conversasEquipe : [],
  );
  const fila = dedupeConversas(Array.isArray(filaDeEspera) ? filaDeEspera : []);
  const followUps = dedupeConversas(
    Array.isArray(followUpConversas) ? followUpConversas : [],
  );
  const fechados = dedupeConversas(
    Array.isArray(fechadosConversas) ? fechadosConversas : [],
  );
  const agendadosApi = normalizarAgendadosApi(
    metricasAgendamentoCaptazap,
    totalAgendadosCaptazap,
    leadsAgendadasCaptazap,
  );
  const metricasMensagensOficiais = normalizarMetricasMensagensSupervisao(
    metricasMensagensZapi,
    metricasZapi,
    estatisticasZapi,
  );

  const casos = [];

  for (const conversa of equipe) {
    const espera = getTempoSemResposta(conversa, agora);
    const responsavel = getResponsavelConversa(conversa, usuarios);
    const esperaMin = msParaMinutos(espera?.ms || 0);
    const tipo = espera?.tipo || "normal";

    casos.push({
      origem: "equipe",
      tipo,
      conversa,
      nome: getNomeConversa(conversa),
      telefone: conversa.telefone || conversa.numero || "",
      responsavel_id: responsavel.id,
      responsavel_nome: responsavel.nome,
      espera_minutos: esperaMin,
      reaberto: isConversaReaberta(conversa),
      naoLidas: Number(conversa.naoLidas || 0),
      mensagem: getMensagemPreview(conversa),
      ultima_mensagem: {
        texto: getMensagemPreview(conversa),
        datahora:
          conversa.ultimaMensagemEm ||
          conversa.ultima_mensagem_em ||
          conversa.datahora ||
          conversa.updated_at,
        direcao: conversa.ultima_mensagem_direcao || conversa.direcao || "",
        autor: conversa.ultima_mensagem_autor || conversa.autor || "",
      },
      origem_ads: getOrigemAdsDiretaConversa(conversa),
      agendado: isCasoAgendadoCaptazap({ conversa }, agendadosApi),
      criticidade: getCriticidadePorTipo(tipo, esperaMin),
    });
  }

  for (const conversa of fila) {
    const espera = getTempoSemResposta(conversa, agora);
    const esperaMin = msParaMinutos(
      espera?.ms || getConversaIdadeMs(conversa, agora),
    );

    casos.push({
      origem: "fila",
      tipo: "fila",
      conversa,
      nome: getNomeConversa(conversa),
      telefone: conversa.telefone || conversa.numero || "",
      responsavel_id: null,
      responsavel_nome: "Fila / sem responsável",
      espera_minutos: esperaMin,
      naoLidas: Number(conversa.naoLidas || 0),
      mensagem: getMensagemPreview(conversa),
      ultima_mensagem: {
        texto: getMensagemPreview(conversa),
        datahora:
          conversa.ultimaMensagemEm ||
          conversa.ultima_mensagem_em ||
          conversa.datahora ||
          conversa.updated_at,
        direcao: conversa.ultima_mensagem_direcao || conversa.direcao || "",
        autor: conversa.ultima_mensagem_autor || conversa.autor || "",
      },
      origem_ads: getOrigemAdsDiretaConversa(conversa),
      criticidade: "fila",
    });
  }

  for (const conversa of followUps) {
    const esperaMin = msParaMinutos(getConversaIdadeMs(conversa, agora));
    const responsavel = getResponsavelConversa(conversa, usuarios);

    casos.push({
      origem: "followup",
      tipo: "followup",
      followup: true,
      conversa,
      nome: getNomeConversa(conversa),
      telefone: conversa.telefone || conversa.numero || "",
      responsavel_id: responsavel.id,
      responsavel_nome: responsavel.nome,
      espera_minutos: esperaMin,
      mensagem: getMensagemPreview(conversa),
      origem_ads: getOrigemAdsDiretaConversa(conversa),
      criticidade: "followup",
    });
  }

  const agendados = agendadosApi.temTotal
    ? agendadosApi.total
    : casos.filter((caso) => caso.agendado).length;

  const casosAbertos = casos.filter(
    (caso) => !isChamadoFechadoSupervisao(caso),
  );
  const resumoOperacional = calcularResumoOperacionalCasos(casos);

  const resumo = {
    monitorados: casosAbertos.length,
    chamados_abertos: equipe.length + fila.length,
    chamados_fechados: fechados.length,
    em_atendimento: equipe.length,
    fila: fila.length,
    sem_retorno: resumoOperacional.sem_retorno,
    devendo_resposta: resumoOperacional.devendo_resposta,
    followups_pendentes: followUps.length,
    followups_vencidos: followUps.length,
    agendamentos_feitos: agendados,
    taxa_agendamento: casosAbertos.length
      ? Math.round((agendados / casosAbertos.length) * 1000) / 10
      : 0,
    anuncios_recebidos: casos.filter((caso) => caso.origem_ads).length,
    mensagens_recebidas: metricasMensagensOficiais.hasRecebidas
      ? metricasMensagensOficiais.recebidas
      : resumoOperacional.mensagens_recebidas,
    mensagens_enviadas: metricasMensagensOficiais.hasEnviadas
      ? metricasMensagensOficiais.enviadas
      : resumoOperacional.mensagens_enviadas,
    _mensagens_recebidas_oficial: metricasMensagensOficiais.hasRecebidas,
    _mensagens_enviadas_oficial: metricasMensagensOficiais.hasEnviadas,
    _origem_mensagens: metricasMensagensOficiais.hasAny ? "zapi" : "local",
  };

  const captadorMap = new Map();
  for (const caso of casos) {
    const key = getFiltroResponsavelKey(caso);
    if (!captadorMap.has(key)) {
      captadorMap.set(key, {
        responsavel_id: caso.responsavel_id || null,
        nome: caso.responsavel_nome || "Sem responsável",
        monitorados: 0,
        chamados_abertos: 0,
        chamados_fechados: 0,
        devendo_resposta: 0,
        sem_retorno: 0,
        followups_pendentes: 0,
        agendamentos_feitos: 0,
        mensagens_recebidas: 0,
        mensagens_enviadas: 0,
        soma_espera: 0,
      });
    }

    const item = captadorMap.get(key);
    const fechado = isChamadoFechadoSupervisao(caso);
    const mensagens = contarMensagensCasoSupervisao(caso);

    if (!fechado) {
      item.monitorados += 1;
      item.chamados_abertos += caso.origem === "fila" ? 0 : 1;
      if (caso.tipo === "devendo_resposta") item.devendo_resposta += 1;
      if (caso.tipo === "sem_retorno") item.sem_retorno += 1;
      if (caso.tipo === "followup") item.followups_pendentes += 1;
      item.soma_espera += Number(caso.espera_minutos || 0);
    }

    if (caso.agendado) item.agendamentos_feitos += 1;
    item.mensagens_recebidas += mensagens.recebidas;
    item.mensagens_enviadas += mensagens.enviadas;
  }

  const captadores = [...captadorMap.values()].map((item) => ({
    ...item,
    tempo_medio_resposta_min: item.monitorados
      ? Math.round(item.soma_espera / item.monitorados)
      : 0,
    taxa_agendamento: item.monitorados
      ? Math.round((item.agendamentos_feitos / item.monitorados) * 1000) / 10
      : 0,
  }));

  const anunciosMap = new Map();
  for (const caso of casos) {
    const origem = normalizarOrigemAds(caso.origem_ads);
    if (!origem) continue;
    const key = `${origem.campanha || "Sem campanha"}|${origem.titulo || "Anúncio"}`;
    if (!anunciosMap.has(key)) {
      anunciosMap.set(key, {
        campanha: origem.campanha || "Sem campanha",
        titulo: origem.titulo || "Anúncio",
        thumbnail_url: origem.thumbnailUrl || "",
        source_id: origem.sourceId || "",
        telefones: 0,
        interacoes: 0,
        agendamentos: 0,
      });
    }
    const item = anunciosMap.get(key);
    item.telefones += 1;
    item.interacoes += 1;
    if (caso.agendado) item.agendamentos += 1;
  }

  return {
    empresaNome,
    periodo: {
      data: dataReferencia || dataFim || getHojeISO(),
      inicio: dataInicio || dataReferencia || getHojeISO(),
      fim: dataFim || dataReferencia || getHojeISO(),
      label: formatarPeriodoSupervisao(
        dataInicio || dataReferencia,
        dataFim || dataReferencia,
      ),
    },
    resumo,
    captadores,
    anuncios: [...anunciosMap.values()],
    casos: casos.sort((a, b) => {
      const pa = getCriticidadePeso(a.criticidade);
      const pb = getCriticidadePeso(b.criticidade);
      if (pa !== pb) return pb - pa;
      return Number(b.espera_minutos || 0) - Number(a.espera_minutos || 0);
    }),
  };
}

function normalizarAgendadosApi(metricas, total, leads = []) {
  const totalSeguro = getNumero(
    total ??
      metricas?.total_agendados ??
      metricas?.totalAgendados ??
      metricas?.agendados,
  );
  const leadIds = new Set();

  const fontes = [
    ...(Array.isArray(leads) ? leads : []),
    ...(Array.isArray(metricas?.lead_ids) ? metricas.lead_ids : []),
    ...(Array.isArray(metricas?.leadIds) ? metricas.leadIds : []),
    ...(Array.isArray(metricas?.leads) ? metricas.leads : []),
  ];

  for (const item of fontes) {
    const leadId = getLeadIdAgendamentoApi(item);
    if (leadId) leadIds.add(String(leadId));
  }

  return {
    temTotal: Number.isFinite(totalSeguro) && totalSeguro > 0,
    total: totalSeguro,
    leadIds,
    temLeadIds: leadIds.size > 0,
  };
}

function getCriticidadePorTipo(tipo = "", esperaMinutos = 0) {
  if (tipo === "devendo_resposta" && esperaMinutos >= 120)
    return "critico_devendo_resposta";
  if (tipo === "sem_retorno" && esperaMinutos >= 120)
    return "critico_sem_retorno";
  if (tipo === "devendo_resposta") return "alerta_devendo_resposta";
  if (tipo === "sem_retorno") return "alerta_sem_retorno";
  return tipo || "normal";
}

function getCriticidadePeso(value = "") {
  const criticidade = String(value || "").toLowerCase();
  if (criticidade.includes("critico_devendo")) return 7;
  if (criticidade.includes("critico_sem")) return 6;
  if (criticidade.includes("devendo")) return 5;
  if (criticidade.includes("sem_retorno")) return 4;
  if (criticidade.includes("followup")) return 3;
  if (criticidade.includes("fila")) return 2;
  return 1;
}

function getCaptadorKey(captador = {}) {
  const id =
    captador.responsavel_id || captador.usuario_id || captador.id || null;
  const nome = normalizar(
    captador.nome || captador.responsavel_nome || captador.usuario_nome || "",
  );
  if (id) return `id:${id}`;
  if (nome) return `nome:${nome}`;
  return "sem-responsavel";
}

function getIdsResponsavelFiltroSupervisao(caso = {}) {
  return [
    caso.responsavel_id,
    caso.responsavelId,
    caso.responsavel?.id,
    caso.usuario_id,
    caso.usuarioId,
    caso.captador_id,
    caso.captadorId,
    caso.atendente_id,
    caso.operador_id,
    caso.lead?.id_usuario,
    caso.lead?.captador_id,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

function getNomesResponsavelFiltroSupervisao(caso = {}) {
  return [
    caso.responsavel_nome,
    caso.responsavelNome,
    caso.responsavel?.nome,
    caso.captador_nome,
    caso.captadorNome,
    caso.captador,
    caso.usuario_nome,
    caso.nome_usuario,
    caso.atendente_nome,
    caso.operador_nome,
    caso.lead?.captador_nome,
    caso.lead?.usuario_nome,
  ]
    .map((value) => normalizar(value))
    .filter(Boolean);
}

function getNomeResponsavelFiltroSupervisao(caso = {}) {
  return (
    caso.responsavel_nome ||
    caso.responsavelNome ||
    caso.responsavel?.nome ||
    caso.captador_nome ||
    caso.captadorNome ||
    caso.captador ||
    caso.usuario_nome ||
    caso.nome_usuario ||
    caso.atendente_nome ||
    caso.operador_nome ||
    caso.lead?.captador_nome ||
    caso.lead?.usuario_nome ||
    ""
  );
}

function getFiltroResponsavelKey(caso = {}) {
  const ids = getIdsResponsavelFiltroSupervisao(caso);
  const nomes = getNomesResponsavelFiltroSupervisao(caso);

  if (ids.length) return `id:${ids[0]}`;
  if (nomes.length) return `nome:${nomes[0]}`;
  if (caso.origem === "fila") return "fila-sem-responsavel";
  return "sem-responsavel";
}

function isCasoDoCaptadorFiltroSupervisao(
  caso = {},
  captadorFiltro = "todos",
  captadorSelecionado = null,
) {
  if (!captadorFiltro || captadorFiltro === "todos") return true;

  const chaveCaso = getFiltroResponsavelKey(caso);
  if (chaveCaso === captadorFiltro) return true;

  const idsCaso = getIdsResponsavelFiltroSupervisao(caso);
  const nomesCaso = getNomesResponsavelFiltroSupervisao(caso);

  if (String(captadorFiltro).startsWith("id:")) {
    const idFiltro = String(captadorFiltro).replace(/^id:/, "").trim();
    if (idFiltro && idsCaso.includes(idFiltro)) return true;
  }

  if (String(captadorFiltro).startsWith("nome:")) {
    const nomeFiltro = normalizar(String(captadorFiltro).replace(/^nome:/, ""));
    if (nomeFiltro && nomesCaso.includes(nomeFiltro)) return true;
  }

  const nomeSelecionado = normalizar(
    captadorSelecionado?.nome ||
      captadorSelecionado?.responsavel_nome ||
      captadorSelecionado?.usuario_nome ||
      "",
  );

  // No card de ROI de anúncios, algumas linhas oficiais do backend vêm só com
  // nome do captador, enquanto o select pode estar usando id do usuário.
  // Essa comparação por nome mantém o filtro funcionando sem depender do título.
  if (nomeSelecionado && nomesCaso.includes(nomeSelecionado)) return true;

  return false;
}

function getCasoKey(caso = {}) {
  const conversa = caso.conversa || caso;
  return (
    caso.chamado_id ||
    conversa.chamado_id ||
    conversa.id ||
    conversa.conversa_id ||
    `${caso.telefone || conversa.telefone || "sem-tel"}-${caso.tipo || "caso"}`
  );
}

function msParaMinutos(ms = 0) {
  const value = Number(ms || 0);
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value / 60000));
}

const ALIASES_MENSAGENS_RECEBIDAS_SUPERVISAO = [
  "mensagens_recebidas",
  "mensagensRecebidas",
  "recebidas",
  "qtd_recebida",
  "qtdRecebida",
  "quantidade_recebida",
  "quantidadeRecebida",
  "total_recebido",
  "totalRecebido",
  "total_received",
  "received",
  "receivedMessages",
  "inbound",
  "inboundMessages",
  "qtdReceived",
  "countReceived",
];

const ALIASES_MENSAGENS_ENVIADAS_SUPERVISAO = [
  "mensagens_enviadas",
  "mensagensEnviadas",
  "enviadas",
  "qtd_enviada",
  "qtdEnviada",
  "quantidade_enviada",
  "quantidadeEnviada",
  "total_enviado",
  "totalEnviado",
  "total_sent",
  "sent",
  "sentMessages",
  "outbound",
  "outboundMessages",
  "qtdSent",
  "countSent",
];

function normalizarMetricasMensagensSupervisao(...fontes) {
  const resultado = {
    recebidas: 0,
    enviadas: 0,
    hasRecebidas: false,
    hasEnviadas: false,
    hasAny: false,
  };

  for (const fonte of fontes.flat(Infinity)) {
    if (!fonte || typeof fonte !== "object") continue;

    const recebidas = buscarNumeroPorAliasesSupervisao(
      fonte,
      ALIASES_MENSAGENS_RECEBIDAS_SUPERVISAO,
    );
    const enviadas = buscarNumeroPorAliasesSupervisao(
      fonte,
      ALIASES_MENSAGENS_ENVIADAS_SUPERVISAO,
    );

    if (recebidas !== null && !resultado.hasRecebidas) {
      resultado.recebidas = recebidas;
      resultado.hasRecebidas = true;
    }

    if (enviadas !== null && !resultado.hasEnviadas) {
      resultado.enviadas = enviadas;
      resultado.hasEnviadas = true;
    }

    resultado.hasAny = resultado.hasRecebidas || resultado.hasEnviadas;
    if (resultado.hasRecebidas && resultado.hasEnviadas) break;
  }

  return resultado;
}

function buscarNumeroPorAliasesSupervisao(
  obj = {},
  aliases = [],
  profundidade = 0,
) {
  if (!obj || typeof obj !== "object" || profundidade > 4) return null;

  const aliasesNormalizados = new Set(
    aliases.map(normalizarChaveMetricaSupervisao).filter(Boolean),
  );

  for (const [key, value] of Object.entries(obj)) {
    const keyNormalizada = normalizarChaveMetricaSupervisao(key);
    if (!aliasesNormalizados.has(keyNormalizada)) continue;

    const numero = Number(value);
    if (Number.isFinite(numero)) return numero;
  }

  for (const value of Object.values(obj)) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const encontrado = buscarNumeroPorAliasesSupervisao(
      value,
      aliases,
      profundidade + 1,
    );
    if (encontrado !== null) return encontrado;
  }

  return null;
}

function normalizarChaveMetricaSupervisao(value = "") {
  return normalizar(value).replace(/[^a-z0-9]/g, "");
}

function getEmpresaIdLocalStorage() {
  if (typeof window === "undefined") return null;

  const objectKeys = [
    "empresaSelecionada",
    "empresa_atual",
    "empresaAtual",
    "empresaAtiva",
    "selectedEmpresa",
    "selectedCompany",
    "company",
  ];

  for (const key of objectKeys) {
    try {
      const raw = window.localStorage?.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      const id =
        parsed?.id ||
        parsed?.empresa_id ||
        parsed?.empresaId ||
        parsed?.company_id ||
        parsed?.companyId;

      if (Number(id) > 0) return Number(id);
    } catch {
      // ignora valores que não são JSON
    }
  }

  const directKeys = [
    "empresaSelecionadaId",
    "empresa_atual_id",
    "empresaAtualId",
    "empresaAtivaId",
    "selectedEmpresaId",
    "selectedCompanyId",
    "companyId",
    "empresa_id",
    "empresaId",
  ];

  for (const key of directKeys) {
    const value = Number(window.localStorage?.getItem(key) || 0);
    if (value > 0) return value;
  }

  return null;
}

function getNumero(value) {
  const numero = Number(value ?? 0);
  return Number.isFinite(numero) ? numero : 0;
}

function formatPercent(value) {
  const numero = Number(value ?? 0);
  if (!Number.isFinite(numero)) return "0%";
  return `${String(Math.round(numero * 10) / 10).replace(".", ",")}%`;
}

function formatarMoedaSupervisao(value) {
  if (value === null || value === undefined || value === "") return "";

  let texto = String(value).trim();
  if (!texto) return "";

  if (/^\d{1,3}(\.\d{3})*,\d{1,2}$/.test(texto)) {
    texto = texto.replace(/\./g, "").replace(",", ".");
  } else {
    texto = texto.replace(",", ".");
  }

  const numero = Number(texto.replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(numero) || numero <= 0) return "";

  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function getInitials(nome = "") {
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!partes.length) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0] || ""}${partes[1][0] || ""}`.toUpperCase();
}

function formatarMinutos(minutosRaw) {
  const minutos = Number(minutosRaw || 0);
  if (!Number.isFinite(minutos) || minutos <= 0) return "agora";
  if (minutos < 60) return `${Math.round(minutos)}min`;
  const horas = Math.floor(minutos / 60);
  const resto = Math.round(minutos % 60);
  if (horas < 24) return resto ? `${horas}h ${resto}min` : `${horas}h`;
  const dias = Math.floor(horas / 24);
  const horasResto = horas % 24;
  return horasResto ? `${dias}d ${horasResto}h` : `${dias}d`;
}

function getLeadIdAgendamentoApi(item = {}) {
  if (item === null || item === undefined) return null;
  if (typeof item === "number" || typeof item === "string") {
    const numero = Number(item);
    return Number.isFinite(numero) && numero > 0 ? numero : null;
  }
  return getPrimeiroValor([
    item.lead_id,
    item.leadId,
    item.id_lead,
    item.leads_id,
    item.lead?.id,
    item.id,
  ]);
}

function isCasoAgendadoCaptazap(caso = {}, agendadosApi = {}) {
  const conversa = caso.conversa || caso || {};
  if (agendadosApi?.temLeadIds) {
    const leadId = getLeadIdAgendamentoApi(conversa);
    return leadId ? agendadosApi.leadIds.has(String(leadId)) : false;
  }
  return isConversaAgendada(conversa);
}

function isConversaAgendada(conversa = {}) {
  const valorBackend =
    conversa.agendado_captazap ??
    conversa.agendadoCaptazap ??
    conversa.captazap_agendado ??
    conversa.captazapAgendado ??
    conversa.lead?.agendado_captazap ??
    conversa.lead?.agendadoCaptazap;

  if (valorBackend === true || valorBackend === 1) return true;
  if (valorBackend === false || valorBackend === 0) return false;

  const status = normalizar(
    conversa.status_agendamento ||
      conversa.lead?.status_agendamento ||
      conversa.ultimo_agendamento?.status_agendamento ||
      conversa._ultimo_agendamento?.status_agendamento ||
      "",
  );

  return (
    status.includes("agend") ||
    status.includes("reagend") ||
    status.includes("confirm") ||
    status.includes("compareceu")
  );
}

function getChaveAgendamentoUnico(conversa = {}) {
  const empresaId = conversa.empresa_id || conversa.empresaId || "sem-empresa";
  const leadId = conversa.lead_id || conversa.leadId || conversa.lead?.id;
  const telefone = normalizarTelefone(conversa.numero || conversa.telefone);
  if (leadId) return `${empresaId}-lead-${leadId}`;
  if (telefone) return `${empresaId}-tel-${telefone}`;
  return getConversationKey(conversa);
}

function getDataPrimeiraConversaContato(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];
  const datasMensagens = mensagens.map(getMensagemTimestamp).filter(Boolean);
  const candidatos = [
    conversa.data_entrada_captazap,
    conversa.dataEntradaCaptazap,
    conversa.captazap_entrada_em,
    conversa.captazapEntradaEm,
    conversa.entrada_captazap_em,
    conversa.entradaCaptazapEm,
    conversa.primeira_conversa_em,
    conversa.data_primeira_conversa,
    conversa.aberto_em,
    conversa.abertoEm,
    conversa.chamado_aberto_em,
    conversa.iniciado_em,
    conversa.inicio_atendimento,
    conversa.created_at,
    conversa.criado_em,
  ];
  const timestamps = [
    ...candidatos.map(parseTimestamp).filter(Boolean),
    ...datasMensagens,
  ];
  if (!timestamps.length) return 0;
  return Math.min(...timestamps);
}

function formatarDataEntradaContato(timestamp = 0) {
  if (!timestamp) return "";
  const data = new Date(timestamp);
  if (Number.isNaN(data.getTime())) return "";
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMensagemMetaHistoricoSupervisao(mensagem = {}, conversa = {}) {
  const direcao = normalizar(
    mensagem.direcao || mensagem.direction || mensagem.sentido || "",
  );
  const autorRaw = getPrimeiroValor([
    mensagem.autor,
    mensagem.de,
    mensagem.from,
    mensagem.remetente,
    mensagem.sender,
  ]);
  const autor = normalizar(autorRaw);
  const fromMe =
    mensagem.fromMe === true ||
    mensagem.from_me === true ||
    String(mensagem.fromMe || mensagem.from_me || "").toLowerCase() === "true";

  const nomeOperador = getPrimeiroValor([
    mensagem.usuario_nome,
    mensagem.operador_nome,
    mensagem.atendente_nome,
    mensagem.captador_nome,
    mensagem.responsavel_nome,
    conversa.responsavel_nome,
    conversa.captador_nome,
  ]);

  const sistema =
    autor.includes("sistema") ||
    autor.includes("bot") ||
    autor.includes("automacao") ||
    autor.includes("automação") ||
    direcao.includes("interno") ||
    direcao.includes("sistema");

  if (sistema) {
    return {
      tipo: "sistema",
      label: "Sistema",
      nome: limparNomeAutorMensagem(autorRaw) || "Automação/CRM",
    };
  }

  const cliente =
    !fromMe &&
    (direcao.includes("recebid") ||
      direcao.includes("entrada") ||
      direcao.includes("inbound") ||
      autor.includes("cliente") ||
      autor.includes("contato") ||
      autor.includes("lead"));

  if (cliente) {
    return {
      tipo: "cliente",
      label: "Cliente",
      nome: getNomeConversa(conversa),
    };
  }

  return {
    tipo: "operador",
    label: "Captador",
    nome: limparNomeAutorMensagem(nomeOperador || autorRaw) || "Operador",
  };
}

function limparNomeAutorMensagem(value = "") {
  const texto = String(value || "").trim();
  const normalizado = normalizar(texto);
  if (!texto) return "";
  if (["cliente", "contato", "lead"].includes(normalizado)) return "";
  if (
    ["operador", "atendente", "captador", "usuario", "usuário"].includes(
      normalizado,
    )
  )
    return "";
  if (["sistema", "automacao", "automação"].includes(normalizado)) return "";
  return texto;
}

function getTextoMensagemHistoricoSupervisao(mensagem = {}) {
  if (mensagem.texto) return mensagem.texto;
  if (mensagem.mensagem) return mensagem.mensagem;
  if (mensagem.message) return mensagem.message;
  const tipoMidia = String(
    mensagem.tipo_midia || mensagem.tipoMidia || "",
  ).toLowerCase();
  if (tipoMidia.includes("audio")) return "Mensagem de áudio";
  if (tipoMidia.includes("imagem") || tipoMidia.includes("image"))
    return "Imagem";
  if (tipoMidia.includes("video")) return "Vídeo";
  if (tipoMidia.includes("document") || tipoMidia.includes("arquivo"))
    return "Documento";
  return "";
}

function getMensagemHistoricoSupervisaoKey(mensagem = {}, index = 0) {
  return (
    mensagem.id ||
    mensagem.whatsapp_id ||
    mensagem.msgid ||
    mensagem.messageId ||
    mensagem.message_id ||
    `${mensagem.datahora || mensagem.created_at || "sem-data"}-${index}`
  );
}

function formatarDataHoraMensagemHistoricoSupervisao(value) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function buscarHistoricoCompletoSupervisao(conversa = {}) {
  const empresaId =
    conversa.empresa_id || conversa.empresaId || conversa.lead?.empresa_id;
  const leadId =
    conversa.lead_id || conversa.leadId || conversa.lead?.id || null;
  const telefone =
    conversa.numero ||
    conversa.telefone ||
    conversa.phone ||
    conversa.lead?.telefone1 ||
    conversa.lead?.telefone ||
    conversa.lead?.celular ||
    conversa.lead?.whatsapp ||
    "";

  if (!empresaId || (!leadId && !telefone)) return [];

  const params = new URLSearchParams();
  params.set("empresa_id", String(empresaId));
  if (telefone) params.set("telefone", telefone);
  else if (leadId) params.set("lead_id", String(leadId));

  const data = await requestJsonSupervisao(
    `/whatsapp/captazap/mensagens?${params.toString()}&limit=500&dias=365`,
  );

  const lista = Array.isArray(data)
    ? data
    : Array.isArray(data?.mensagens)
      ? data.mensagens
      : Array.isArray(data?.data)
        ? data.data
        : [];

  return lista.filter(Boolean).sort((a, b) => {
    const ta = parseTimestamp(
      a.datahora ||
        a.created_at ||
        a.timestamp ||
        a.enviada_em ||
        a.recebida_em,
    );
    const tb = parseTimestamp(
      b.datahora ||
        b.created_at ||
        b.timestamp ||
        b.enviada_em ||
        b.recebida_em,
    );
    return ta - tb;
  });
}

function getUltimaAutomacaoConversa(conversa = {}) {
  const enviada =
    conversa.ultima_automacao_enviada === true ||
    conversa.ultima_automacao_enviada === 1 ||
    String(conversa.ultima_automacao_enviada || "").toLowerCase() === "true" ||
    Boolean(conversa.ultima_automacao_id);
  if (!enviada) return null;
  return {
    id: conversa.ultima_automacao_id || null,
    titulo:
      conversa.ultima_automacao_titulo ||
      conversa.ultima_automacao_tipo ||
      "Automação enviada",
    texto: conversa.ultima_automacao_texto || "",
    em: conversa.ultima_automacao_em || "",
  };
}

function isMensagemAutomaticaAgendaSupervisao(mensagem = {}, contexto = {}) {
  const texto = normalizar(
    mensagem.texto ||
      mensagem.mensagem ||
      mensagem.preview ||
      mensagem.ultima_mensagem ||
      mensagem.ultimaMensagem ||
      contexto.mensagem ||
      contexto.preview ||
      "",
  );
  const direcao = normalizar(
    mensagem.direcao ||
      mensagem.direction ||
      mensagem.sentido ||
      contexto.ultima_mensagem_direcao ||
      contexto.direcao ||
      "",
  );
  const autor = normalizar(
    mensagem.autor ||
      mensagem.de ||
      mensagem.from ||
      mensagem.sender ||
      contexto.ultima_mensagem_autor ||
      contexto.autor ||
      "",
  );

  const enviada =
    direcao.includes("enviad") ||
    direcao.includes("saida") ||
    direcao.includes("outbound");
  const sistema =
    autor.includes("sistema") ||
    autor.includes("bot") ||
    autor.includes("automacao") ||
    autor.includes("automacao") ||
    autor.includes("automatic");
  const automacaoMarcada =
    contexto.ultima_automacao_enviada === true ||
    contexto.ultimaAutomacaoEnviada === true ||
    Boolean(contexto.ultima_automacao_id || contexto.ultimaAutomacaoId);
  const textoAgenda =
    texto.includes("agendad") ||
    texto.includes("agendamento") ||
    texto.includes("confirmar sua presenca") ||
    texto.includes("confirmar presenca") ||
    texto.includes("sua presenca") ||
    texto.includes("esta agendado") ||
    texto.includes("esta agendada") ||
    texto.includes("atendimento com") ||
    texto.includes("horario reservado") ||
    texto.includes("localizacao");

  return Boolean(
    textoAgenda && enviada && (sistema || automacaoMarcada || !autor),
  );
}

function isCasoMensagemAutomaticaAgendaSupervisao(caso = {}) {
  const conversa = caso.conversa || caso || {};
  const ultima =
    caso.ultima_mensagem ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    getUltimaMensagem(
      conversa,
      Array.isArray(conversa.mensagens) ? conversa.mensagens : [],
    ) ||
    {};

  return isMensagemAutomaticaAgendaSupervisao(ultima, {
    ...conversa,
    ...caso,
  });
}

function neutralizarSlaAutomaticoAgendaSupervisao(caso = {}) {
  if (!isCasoMensagemAutomaticaAgendaSupervisao(caso)) return caso;

  const tipoAtual = String(caso.tipo || caso.tipoEspera || "").toLowerCase();
  const criticidadeAtual = String(
    caso.criticidade || caso.nivel || "",
  ).toLowerCase();
  const deveNeutralizar =
    !tipoAtual ||
    tipoAtual === "normal" ||
    tipoAtual === "sem_retorno" ||
    criticidadeAtual.includes("sem_retorno") ||
    criticidadeAtual.includes("critico_sem") ||
    criticidadeAtual.includes("alerta_sem");

  if (!deveNeutralizar) {
    return {
      ...caso,
      sla_ignorado: true,
      slaIgnorado: true,
      motivo_sla_ignorado:
        "Mensagem automática de agenda não gera pendência de captador",
    };
  }

  return {
    ...caso,
    tipo: "agenda_automatica",
    tipoEspera: "agenda_automatica",
    criticidade: "normal",
    nivel: "normal",
    espera_minutos: 0,
    esperaMs: 0,
    sla_ignorado: true,
    slaIgnorado: true,
    motivo_sla_ignorado:
      "Mensagem automática de agenda não gera pendência de captador",
  };
}

function getTempoSemResposta(conversa = {}, agora = Date.now()) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];
  const ultima = getUltimaMensagem(conversa, mensagens);
  if (!ultima) return null;

  const timestamp =
    getMensagemTimestamp(ultima) ||
    getConversaTimestamp(conversa) ||
    getTimestampFromHoraCurta(conversa.hora);
  if (!timestamp) return null;

  const ms = Math.max(0, Number(agora || Date.now()) - timestamp);
  if (!Number.isFinite(ms)) return null;

  if (isMensagemAutomaticaAgendaSupervisao(ultima, conversa)) return null;

  if (isMensagemOperador(ultima)) return { ms, tipo: "sem_retorno" };
  if (isMensagemCliente(ultima)) return { ms, tipo: "devendo_resposta" };
  return null;
}

function getUltimaMensagem(conversa = {}, mensagens = []) {
  if (mensagens.length) {
    return [...mensagens].sort(
      (a, b) => (getMensagemTimestamp(b) || 0) - (getMensagemTimestamp(a) || 0),
    )[0];
  }

  const texto =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.last_message ||
    conversa.mensagem ||
    "";
  const direcao =
    conversa.direcao ||
    conversa.ultima_direcao ||
    conversa.ultimaDirecao ||
    conversa.last_direction ||
    conversa.autor ||
    conversa.de ||
    "";
  if (!texto && !direcao) return null;

  return {
    ...conversa,
    texto,
    direcao,
    datahora:
      conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.datahora ||
      conversa.timestamp ||
      "",
  };
}

function isMensagemCliente(msg = {}) {
  const direcao = normalizar(msg.direcao || msg.direction || msg.sentido || "");
  const autor = normalizar(
    msg.de || msg.autor || msg.from || msg.remetente || msg.sender || "",
  );
  const fromMe =
    msg.fromMe === true ||
    msg.from_me === true ||
    String(msg.fromMe || msg.from_me || "").toLowerCase() === "true";
  if (fromMe) return false;
  return (
    direcao.includes("recebid") ||
    direcao.includes("entrada") ||
    direcao.includes("inbound") ||
    autor.includes("cliente") ||
    autor.includes("contato") ||
    autor.includes("lead")
  );
}

function isMensagemOperador(msg = {}) {
  const direcao = normalizar(msg.direcao || msg.direction || msg.sentido || "");
  const autor = normalizar(
    msg.de || msg.autor || msg.from || msg.remetente || msg.sender || "",
  );
  const fromMe =
    msg.fromMe === true ||
    msg.from_me === true ||
    String(msg.fromMe || msg.from_me || "").toLowerCase() === "true";
  return (
    fromMe ||
    direcao.includes("enviad") ||
    direcao.includes("saida") ||
    direcao.includes("outbound") ||
    autor.includes("operador") ||
    autor.includes("atendente") ||
    autor.includes("captador") ||
    autor.includes("usuario")
  );
}

function getMensagemTimestamp(msg = {}) {
  if (!msg) return 0;
  return parseTimestamp(
    msg.datahora ||
      msg.created_at ||
      msg.updated_at ||
      msg.enviada_em ||
      msg.recebida_em ||
      msg.timestamp ||
      msg.time ||
      msg.date ||
      "",
  );
}

function getConversaTimestamp(conversa = {}) {
  return parseTimestamp(
    conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.updated_at ||
      conversa.created_at ||
      "",
  );
}

function getConversaIdadeMs(conversa = {}, agora = Date.now()) {
  const timestamp =
    getConversaTimestamp(conversa) || getTimestampFromHoraCurta(conversa.hora);
  return timestamp ? Math.max(0, Number(agora || Date.now()) - timestamp) : 0;
}

function isConversaReaberta(conversa = {}) {
  const texto = normalizar(
    [
      conversa.status,
      conversa.status_chamado,
      conversa.chamado_status,
      conversa.aba_captazap,
      conversa.tags?.join(" "),
      conversa._supervisao_status,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return Boolean(
    conversa.reaberto_automaticamente ||
    conversa.reabertoAutomaticamente ||
    conversa.nova_mensagem_apos_fechamento ||
    conversa.cliente_respondeu_apos_fechamento ||
    texto.includes("reabert") ||
    texto.includes("retorno apos fechamento") ||
    texto.includes("nova mensagem apos fechamento"),
  );
}

function getResponsavelConversa(conversa = {}, usuarios = []) {
  const id = getPrimeiroValor([
    conversa.responsavel_id,
    conversa.captador_id,
    conversa.usuario_responsavel_id,
    conversa.atendente_id,
    conversa.operador_id,
    conversa._captazap_assumida_por,
  ]);

  const usuario = usuarios.find(
    (item) => String(getUsuarioId(item) || "") === String(id || ""),
  );

  const nome = getPrimeiroValor([
    conversa.responsavel_nome,
    conversa.captador_nome,
    conversa.usuario_responsavel_nome,
    conversa.atendente_nome,
    conversa.operador_nome,
    conversa._captazap_assumida_por_nome,
    getNomeUsuario(usuario),
  ]);

  return {
    id: id || getUsuarioId(usuario) || null,
    nome: nome || (id ? "Responsável vinculado" : "Fila"),
    ativo: usuario ? isUsuarioAtivo(usuario) : true,
  };
}

function getMensagemPreview(conversa = {}) {
  const texto =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.last_message ||
    conversa.mensagem ||
    "";
  const limpo = String(texto || "").trim();
  if (!limpo) return "";
  if (normalizar(limpo) === "conversa whatsapp") return "";
  return limpo;
}

function getNomeConversa(conversa = {}) {
  return (
    conversa.nome ||
    conversa.nome_lead ||
    conversa.lead?.nome_lead ||
    conversa.lead?.nome ||
    conversa.numero ||
    conversa.telefone ||
    "Contato sem nome"
  );
}

function temSinalAdsLeveCasoSupervisao(caso = {}) {
  const conversa = caso?.conversa || caso || {};
  const lead = conversa.lead || caso.lead || {};

  const texto = getTextoCanalAdsSupervisao(caso, conversa, lead);
  if (!texto) return false;

  const normalizado = normalizar(texto);
  const bruto = String(texto || "").toLowerCase();

  return [
    "google",
    "gclid",
    "gbraid",
    "wbraid",
    "facebook",
    "fbclid",
    "instagram",
    "meta",
    "site",
    "landing",
    "anuncio",
    "anúncio",
    "ads",
    "utm_source",
    "source_id",
    "mundosorriso.com",
  ].some((sinal) => normalizado.includes(sinal) || bruto.includes(sinal));
}

function getOrigemAdsCaso(caso = {}, adsOrigemMap = {}) {
  const conversa = caso?.conversa || caso || {};

  const direta = normalizarOrigemAds(
    caso?.origem_ads ||
      caso?.origemAds ||
      conversa?.origem_ads ||
      conversa?.origemAds ||
      getOrigemAdsDiretaConversa(conversa),
  );

  if (direta) return direta;

  const key = getOrigemAdsLookupKey(conversa);
  if (!key) return null;

  return normalizarOrigemAds(adsOrigemMap?.[key]);
}

function getOrigemAdsLookupKey(conversa = {}) {
  const empresaId =
    conversa.empresa_id || conversa.empresaId || conversa.lead?.empresa_id;

  const telefone = normalizarTelefone(
    conversa.numero ||
      conversa.telefone ||
      conversa.phone ||
      conversa.whatsapp ||
      conversa.lead?.telefone1 ||
      conversa.lead?.telefone ||
      conversa.lead?.celular ||
      conversa.lead?.whatsapp,
  );

  if (!empresaId || !telefone) return "";

  return `${empresaId}:${telefone}`;
}

function getOrigemAdsDiretaConversa(conversa = {}) {
  const lead = conversa.lead || {};
  return normalizarOrigemAds(
    conversa.origem_ads ||
      conversa.origemAds ||
      conversa.whatsapp_ads_origem ||
      conversa.whatsappAdsOrigem ||
      conversa.ads_origem ||
      conversa.adsOrigem ||
      conversa.meta_ads_origem ||
      conversa.metaAdsOrigem ||
      lead.origem_ads ||
      lead.origemAds || {
        titulo:
          conversa.ads_titulo ||
          conversa.ad_titulo ||
          conversa.anuncio_titulo ||
          lead.ads_titulo ||
          lead.ad_titulo,
        corpo:
          conversa.ads_corpo ||
          conversa.ad_corpo ||
          conversa.anuncio_corpo ||
          lead.ads_corpo ||
          lead.ad_corpo,
        thumbnail_url:
          conversa.thumbnail_url ||
          conversa.ads_thumbnail_url ||
          conversa.ad_thumbnail_url ||
          conversa.anuncio_thumbnail_url ||
          lead.thumbnail_url ||
          lead.ads_thumbnail_url,
        source_id:
          conversa.source_id ||
          conversa.ad_id ||
          conversa.ads_source_id ||
          conversa.anuncio_id ||
          lead.source_id ||
          lead.ad_id,
        source_type:
          conversa.source_type || conversa.ads_source_type || lead.source_type,
        source_url:
          conversa.source_url ||
          conversa.ad_source_url ||
          conversa.anuncio_url ||
          lead.source_url,
        campanha:
          conversa.campanha_ads ||
          conversa.ads_campanha ||
          conversa.campaign_name ||
          conversa.campanha_nome ||
          lead.campanha_ads ||
          lead.ads_campanha ||
          lead.campaign_name ||
          lead.campanha_nome,
      },
  );
}

function normalizarOrigemAds(origem = null) {
  if (!origem) return null;

  // Alguns retornos chegam só como string simples: "Site", "Google",
  // "Landing Page", etc. Antes isso era descartado porque a função aceitava
  // apenas objeto. Resultado: a lead podia até ser Google/Site, mas falhava em
  // temOrigemAdsCasoSupervisao() e não entrava na aba Google.
  if (typeof origem === "string" || typeof origem === "number") {
    const textoOrigem = String(origem || "").trim();
    const normalizado = normalizar(textoOrigem);

    if (
      !textoOrigem ||
      ["0", "false", "nao", "não", "null", "undefined"].includes(normalizado)
    ) {
      return null;
    }

    const ehSinalAds =
      normalizado.includes("site") ||
      normalizado.includes("google") ||
      normalizado.includes("landing") ||
      normalizado.includes("facebook") ||
      normalizado.includes("instagram") ||
      normalizado.includes("meta") ||
      normalizado.includes("anuncio") ||
      normalizado.includes("ads");

    if (!ehSinalAds) return null;

    const isOrigemSite =
      normalizado.includes("site") || normalizado.includes("landing");

    return {
      id: null,
      titulo: textoOrigem || (isOrigemSite ? "Site" : ""),
      corpo: "",
      thumbnailUrl: "",
      thumbnail_url: "",
      sourceId: "",
      source_id: "",
      sourceType: isOrigemSite ? "site" : "",
      source_type: isOrigemSite ? "site" : "",
      sourceUrl: "",
      source_url: "",
      campanha: isOrigemSite ? "Site" : textoOrigem,
      data_origem_ads: "",
      origem_ads_em: "",
      ads_criado_em: "",
      criado_em: "",
      created_at: "",
    };
  }

  if (typeof origem !== "object") return null;

  const payload = parsePayloadJsonAds(
    origem.payload_json || origem.payloadJson,
  );
  const titulo = getPrimeiroValor([
    origem.titulo,
    origem.title,
    origem.headline,
    payload?.title,
    payload?.headline,
    payload?.source?.title,
    payload?.referral?.headline,
  ]);
  const corpo = getPrimeiroValor([
    origem.corpo,
    origem.body,
    origem.texto,
    origem.description,
    payload?.body,
    payload?.description,
    payload?.source?.body,
    payload?.referral?.body,
  ]);
  const thumbnailUrl = getPrimeiroValor([
    origem.thumbnail_url,
    origem.thumbnailUrl,
    origem.image_url,
    origem.imageUrl,
    origem.imagem_url,
    origem.imagemUrl,
    payload?.thumbnail_url,
    payload?.thumbnailUrl,
    payload?.image_url,
    payload?.source?.thumbnail_url,
    payload?.referral?.thumbnail_url,
  ]);
  const sourceId = getPrimeiroValor([
    origem.source_id,
    origem.sourceId,
    origem.ad_id,
    origem.adId,
    origem.anuncio_id,
    payload?.source_id,
    payload?.source?.id,
    payload?.referral?.source_id,
  ]);
  const sourceType = getPrimeiroValor([
    origem.source_type,
    origem.sourceType,
    payload?.source_type,
    payload?.source?.type,
    payload?.referral?.source_type,
  ]);
  const sourceUrl = getPrimeiroValor([
    origem.source_url,
    origem.sourceUrl,
    origem.url,
    origem.link,
    payload?.source_url,
    payload?.source?.url,
    payload?.referral?.source_url,
  ]);
  const campanha = getPrimeiroValor([
    origem.campanha,
    origem.campanha_nome,
    origem.nome_campanha,
    origem.campaign_name,
    origem.campaign,
    origem.utm_campaign,
    payload?.campaign_name,
    payload?.campaign,
    payload?.utm_campaign,
    payload?.source?.campaign_name,
    payload?.referral?.campaign_name,
  ]);
  if (
    !titulo &&
    !corpo &&
    !thumbnailUrl &&
    !sourceId &&
    !sourceUrl &&
    !campanha &&
    !sourceType
  )
    return null;
  const dataOrigem = getPrimeiroValor([
    origem.data_origem_ads,
    origem.dataOrigemAds,
    origem.origem_ads_em,
    origem.origemAdsEm,
    origem.origem_ads_criado_em,
    origem.origemAdsCriadoEm,
    origem.ads_criado_em,
    origem.adsCriadoEm,
    origem.ads_created_at,
    origem.adsCreatedAt,
    origem.ads_datahora,
    origem.adsDatahora,
    origem.ads_recebido_em,
    origem.adsRecebidoEm,
    origem.ads_received_at,
    origem.adsReceivedAt,
    origem.anuncio_criado_em,
    origem.anuncioCriadoEm,
    origem.whatsapp_ads_criado_em,
    origem.whatsappAdsCriadoEm,
    origem.ultima_origem_em,
    origem.criado_em,
    origem.created_at,
    origem.recebido_em,
    origem.recebidoEm,
    origem.received_at,
    origem.timestamp,
    origem.data,
    payload?.created_at,
    payload?.timestamp,
    payload?.source?.created_at,
    payload?.referral?.created_at,
  ]);

  const isOrigemSite = String(sourceType || "")
    .toLowerCase()
    .includes("site");

  return {
    id: origem.id || null,
    titulo: String(titulo || (isOrigemSite ? "Site" : "")).trim(),
    corpo: String(corpo || "").trim(),
    thumbnailUrl: String(thumbnailUrl || "").trim(),
    thumbnail_url: String(thumbnailUrl || "").trim(),
    sourceId: String(sourceId || "").trim(),
    source_id: String(sourceId || "").trim(),
    sourceType: String(sourceType || "").trim(),
    source_type: String(sourceType || "").trim(),
    sourceUrl: String(sourceUrl || "").trim(),
    source_url: String(sourceUrl || "").trim(),
    campanha: String(campanha || (isOrigemSite ? "Site" : "")).trim(),
    data_origem_ads: dataOrigem || "",
    origem_ads_em: dataOrigem || "",
    ads_criado_em: dataOrigem || "",
    criado_em: dataOrigem || "",
    created_at: dataOrigem || "",
  };
}

function parsePayloadJsonAds(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return null;
  }
}

async function buscarOrigemAdsConversa(conversa = {}) {
  const empresaId =
    conversa.empresa_id || conversa.empresaId || conversa.lead?.empresa_id;
  const telefone = normalizarTelefone(
    conversa.numero ||
      conversa.telefone ||
      conversa.phone ||
      conversa.lead?.telefone1 ||
      conversa.lead?.telefone ||
      conversa.lead?.celular ||
      conversa.lead?.whatsapp,
  );
  if (!empresaId || !telefone) return null;
  const params = new URLSearchParams();
  params.set("empresa_id", String(empresaId));
  params.set("telefone", telefone);
  let data = null;

  try {
    data = await requestJsonSupervisao(
      `/whatsapp/captazap/origem-anuncio?${params.toString()}`,
    );
  } catch {
    data = await requestJsonSupervisao(
      `/whatsapp/captazap/origem-ads?${params.toString()}`,
    );
  }

  return normalizarOrigemAds(data?.origem || data?.data || data?.ads || data);
}

async function requestJsonSupervisao(path, options = {}) {
  const bases = path.startsWith("http")
    ? [""]
    : Array.from(
        new Set(
          [API_BASE, getApiBasePadraoSupervisao(), ""]
            .map((base) => String(base || "").replace(/\/+$/, ""))
            .filter(
              (base, index, arr) => index === 0 || base || !arr.some(Boolean),
            ),
        ),
      );

  let ultimoErro = null;
  let ultimoTexto = "";

  for (const base of bases) {
    const url = path.startsWith("http") ? path : base ? `${base}${path}` : path;

    let timeout = null;
    let excedeuTempo = false;

    try {
      const controller =
        typeof AbortController !== "undefined" && !options.signal
          ? new AbortController()
          : null;
      timeout = controller
        ? setTimeout(() => {
            excedeuTempo = true;
            controller.abort();
          }, SUPERVISAO_REQUEST_TIMEOUT_MS)
        : null;

      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
        headers: {
          ...getAuthHeadersSupervisao(),
          Accept: "application/json,text/plain,*/*",
          ...(options.headers || {}),
        },
      });

      const text = await response.text();
      ultimoTexto = text;
      const data = text ? safeJsonSupervisao(text) : null;

      if (!response.ok) {
        const message =
          data?.erro ||
          data?.error ||
          data?.message ||
          `HTTP ${response.status}`;
        ultimoErro = new Error(message);
        continue;
      }

      if (data !== null) return data;

      ultimoErro = new Error(
        `Resposta não JSON em ${url}: ${String(text || "").slice(0, 120)}`,
      );
    } catch (error) {
      if (excedeuTempo && isAbortErrorSupervisao(error)) {
        ultimoErro = new Error(
          `Tempo limite excedido ao carregar a supervisão. A consulta demorou mais de ${Math.round(
            SUPERVISAO_REQUEST_TIMEOUT_MS / 1000,
          )} segundos.`,
        );
      } else {
        ultimoErro = error;
      }
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  if (ultimoErro) throw ultimoErro;
  throw new Error(
    `Não foi possível carregar JSON da supervisão.${ultimoTexto ? ` Resposta: ${ultimoTexto.slice(0, 120)}` : ""}`,
  );
}

function getAuthHeadersSupervisao() {
  if (typeof window === "undefined") return {};
  const raw =
    window.localStorage?.getItem("token") ||
    window.localStorage?.getItem("authToken") ||
    window.localStorage?.getItem("jwt") ||
    "";
  const token = raw ? (raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`) : "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };
}

function safeJsonSupervisao(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function conversaBateBusca(conversa = {}, termo = "") {
  const lead = conversa.lead || {};
  const telefoneTermo = normalizarTelefone(termo);
  const texto = [
    conversa.nome,
    conversa.numero,
    conversa.telefone,
    conversa.preview,
    conversa.status,
    conversa.status_chamado,
    conversa.responsavel_nome,
    conversa.captador_nome,
    conversa.tags?.join(" "),
    lead.nome_lead,
    lead.nome,
    lead.telefone1,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
    lead.status_negociacao,
    lead.status_agendamento,
  ];
  if (texto.some((value) => normalizar(value).includes(termo))) return true;
  if (!telefoneTermo) return false;
  return [
    conversa.numero,
    conversa.telefone,
    lead.telefone1,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
  ]
    .map(normalizarTelefone)
    .filter(Boolean)
    .some((telefone) => telefone.includes(telefoneTermo));
}

function getConversationKey(conversa = {}) {
  const empresaId = conversa.empresa_id || conversa.empresaId || "sem-empresa";
  const leadId = conversa.lead_id || conversa.leadId || conversa.lead?.id;
  const telefone = normalizarTelefone(conversa.numero || conversa.telefone);
  const conversaId =
    conversa.chamado_id ||
    conversa.id_chamado ||
    conversa.ticket_id ||
    conversa.id ||
    conversa.conversa_id ||
    conversa.conversaId ||
    leadId ||
    telefone ||
    "sem-chave";
  return `${empresaId}-${leadId || telefone || conversaId}-${conversaId}`;
}

function dedupeConversas(lista = []) {
  const seen = new Set();
  return (lista || []).filter((item) => {
    const key = getConversationKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function parseTimestamp(value) {
  if (!value) return 0;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? 0 : time;
  }
  if (typeof value === "number")
    return value < 10000000000 ? value * 1000 : value;
  const text = String(value || "").trim();
  if (!text) return 0;

  const mysqlDate = text.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (mysqlDate) {
    const parsedMysql = new Date(
      Number(mysqlDate[1]),
      Number(mysqlDate[2]) - 1,
      Number(mysqlDate[3]),
      Number(mysqlDate[4] || 0),
      Number(mysqlDate[5] || 0),
      Number(mysqlDate[6] || 0),
      0,
    ).getTime();
    if (!Number.isNaN(parsedMysql)) return parsedMysql;
  }

  const brDate = text.match(
    /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{2}|[0-9]{4})(?:\s+([0-9]{1,2}):([0-9]{2}))?/,
  );
  if (brDate) {
    const day = Number(brDate[1]);
    const month = Number(brDate[2]) - 1;
    const yearRaw = Number(brDate[3]);
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const hour = Number(brDate[4] || 0);
    const minute = Number(brDate[5] || 0);
    const parsedBr = new Date(year, month, day, hour, minute, 0, 0).getTime();
    if (!Number.isNaN(parsedBr)) return parsedBr;
  }
  const parsed = new Date(text).getTime();
  if (!Number.isNaN(parsed)) return parsed;
  return getTimestampFromHoraCurta(text);
}

function getTimestampFromHoraCurta(value) {
  const match = String(value || "")
    .trim()
    .match(/^([0-9]{1,2}):([0-9]{2})$/);
  if (!match) return 0;
  const data = new Date();
  data.setHours(Number(match[1]), Number(match[2]), 0, 0);
  if (data.getTime() > Date.now() + 60000) data.setDate(data.getDate() - 1);
  return data.getTime();
}

function formatarDataHoraCurta(value) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return "—";

  return new Date(timestamp).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatarDataCurta(value) {
  if (!value) return "";

  const texto = String(value || "").trim();
  const matchISO = texto.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matchISO) {
    const [, ano, mes, dia] = matchISO;
    return `${dia}/${mes}/${ano.slice(-2)}`;
  }

  const data = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(data.getTime())) return String(value);
  return data.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function formatTelefoneCurto(value = "") {
  const digits = normalizarTelefone(value);
  if (digits.length >= 11) {
    const local = digits.slice(-11);
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (digits.length >= 10) {
    const local = digits.slice(-10);
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return value || "";
}

function getUsuarioId(usuario = {}) {
  return (
    usuario?.id ||
    usuario?.usuario_id ||
    usuario?.id_usuario ||
    usuario?.user_id ||
    usuario?.idUser ||
    null
  );
}

function getNomeUsuario(usuario = {}) {
  return (
    usuario?.nome ||
    usuario?.name ||
    usuario?.nome_usuario ||
    usuario?.usuario ||
    usuario?.email ||
    ""
  );
}

function isUsuarioAtivo(usuario = {}) {
  const ativo =
    usuario.ativo ??
    usuario.active ??
    usuario.usuario_ativo ??
    usuario.status_ativo;
  return !(
    ativo === 0 ||
    ativo === false ||
    String(ativo || "").toLowerCase() === "false" ||
    normalizar(ativo).includes("inativ")
  );
}

function getPrimeiraListaSupervisao(...listas) {
  for (const lista of listas) {
    if (Array.isArray(lista)) return lista;
  }
  return [];
}

function getPrimeiroValor(values = []) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (
      !text ||
      text === "0" ||
      text.toLowerCase() === "null" ||
      text.toLowerCase() === "undefined"
    )
      continue;
    return value;
  }
  return "";
}

function normalizarTelefone(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizar(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}
